import React, { useState, useEffect, useRef, useCallback, useMemo, Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import './src/index.css';
import { supabase } from './src/supabaseClient';
import type { Session } from '@supabase/supabase-js';
import ErrorBoundary from './src/components/ErrorBoundary';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { signUpSchema, signInSchema, profileUpdateSchema, type SignUpFormData, type SignInFormData, type ProfileUpdateFormData } from './src/lib/validation';
import SEOHead from './src/components/SEOHead';
// Temporarily disabled UX components to restore functionality
// TODO: Re-enable after fixing any issues
import OfflineIndicator from './src/components/OfflineIndicator';
import LoadingSpinner from './src/components/LoadingSpinner';
import ErrorMessage from './src/components/ErrorMessage';
import { isRetryableError } from './src/hooks/useRetry';
// i18n
import './src/i18n/config';
import LanguageSwitcher from './src/components/LanguageSwitcher';
import { useTranslation } from 'react-i18next';
import { SkipLink } from './src/components/SkipLink';
import { formatDate } from './src/lib/utils';
import { Toaster } from 'sonner';
import { useKeyboardShortcuts, createAppShortcuts } from './src/hooks/useKeyboardShortcuts';
import { toast } from 'sonner';
import { EmptyCases, EmptyDocuments, EmptyMessages } from './src/components/EmptyState';
import { SearchBar, type SearchFilters } from './src/components/SearchBar';
import { useSearch } from './src/hooks/useSearch';
import { errorTracking, initSentry } from './src/lib/errorTracking';
import { logger } from './src/utils/logger';
import { AdminLayout } from './src/components/admin';
import { canSendChatMessage, canUploadDocument, canCreateCase, getUserPlanName } from './src/lib/subscriptionLimits';
import { addWatermarkToHTML } from './src/lib/pdfWatermark';
import { showUpgradeToast } from './src/components/UpgradePrompt';
import { LegalUpdatesPage } from './src/components/legal/LegalUpdatesPage';
import { LegalDisclaimer } from './src/components/legal/LegalDisclaimer';
import { PLAN_PRICE_IDS } from './shared/plan-config';
import PricingPageComponent from './src/components/PricingPage';
import { TeamManagement } from './src/components/teams/TeamManagement';
import { DeadlineWidget } from './src/components/deadlines/DeadlineWidget';
import { AnalyticsDashboard } from './src/components/analytics/AnalyticsDashboard';
import { NotificationCenter } from './src/components/notifications/NotificationCenter';
import { BatchProcessingPage } from './src/components/BatchProcessingPage';
import { AdvancedSearch } from './src/components/search/AdvancedSearch';

// Configure the worker for react-pdf
// Use a more reliable CDN and handle errors gracefully
try {
    // Configure PDF.js worker - use a more reliable CDN
    try {
        pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;
    } catch (error) {
        logger.warn('Failed to set PDF.js worker from Cloudflare CDN, trying unpkg:', error);
        try {
            pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;
        } catch (error2) {
            logger.warn('Failed to set PDF.js worker, PDF rendering may be limited:', error2);
        }
    }
} catch (error) {
    logger.warn('Failed to set PDF.js worker, falling back to default');
    // Fallback - PDF.js will try to use a default worker
}

// Helper function to handle image errors with fallback
const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    const target = e.target as HTMLImageElement;
    // Use a data URI for a simple placeholder avatar (SVG circle with person silhouette)
    target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Ccircle cx='50' cy='50' r='50' fill='%23d1d5db'/%3E%3Ccircle cx='50' cy='35' r='12' fill='%239ca3af'/%3E%3Cpath d='M30 75 Q30 60 50 60 Q70 60 70 75' stroke='%239ca3af' stroke-width='8' fill='none' stroke-linecap='round'/%3E%3C/svg%3E";
};

const getAvatarAlt = (fullName?: string | null) => {
    const trimmed = fullName?.trim();
    return trimmed ? `${trimmed} avatar` : 'User avatar';
};

const getProfileFullName = (profileData?: Profile | null) =>
    `${profileData?.first_name || ''} ${profileData?.last_name || ''}`.trim() || undefined;

declare global {
    interface Window {
        getAvatarAlt?: typeof getAvatarAlt;
    }
}

if (typeof window !== 'undefined') {
    window.getAvatarAlt = getAvatarAlt;
}

// Custom hook for Intersection Observer based animations
const useAOS = (animationClass: string, options?: IntersectionObserverInit, delay: number = 0) => {
    const elementRef = useRef<HTMLElement | null>(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const node = elementRef.current;
        if (!node) return;

        node.classList.add('animate-on-scroll', animationClass);

        return () => {
            node.classList.remove('animate-on-scroll', animationClass);
        };
    }, [animationClass]);

    useEffect(() => {
        const currentRef = elementRef.current;
        if (!currentRef) return;

        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                setTimeout(() => {
                    setIsVisible(true);
                }, delay);
                observer.unobserve(currentRef); // Observe once
            }
        }, options);

        observer.observe(currentRef);

        return () => {
            observer.unobserve(currentRef);
        };
    }, [options, delay]);

    useEffect(() => {
        const node = elementRef.current;
        if (!node) return;

        if (isVisible) {
            node.classList.add('in-view');
        } else {
            node.classList.remove('in-view');
        }
    }, [isVisible]);

    return elementRef;
};


// --- Routing Utilities ---

/**
 * Permalink structure (Clean URLs):
 * - Public pages: /pricing, /features, /about, /contact, /legal
 * - Dashboard: /dashboard
 * - Case chat: /case/{caseId}
 * - Template library: /templates
 * - Template wizard: /template/{slug}/wizard
 * - Template view: /template/{slug}
 * - Documents: /documents
 * - Settings: /settings
 * - News: /news
 */

interface RouteParams {
    page: string;
    caseId?: string | null;
    templateSlug?: string | null;
    documentId?: string | null;
    tierSlug?: string | null;
}

// Valid routes for validation
const VALID_ROUTES = new Set([
    'dashboard', 'templates', 'documents', 'settings', 'news', 'admin', 'teams',
    'pricing', 'features', 'about', 'contact', 'legal', 'landing', 'benefits', 'collaboration',
    'case', 'template', 'document', 'partnership-tier', 'legal-insurance', 'legal-updates'
]);

const VALID_TEMPLATE_SLUGS = new Set([
    'dsgvo-auskunft', 'dsgvo-loeschung', 'miet-maengelanzeige',
    'miet-minderung', 'abmahnung-widerspruch', 'schufa-bestreitung',
    'schufa-loesung'
]);

const parsePathRoute = (): RouteParams & { isValid: boolean } => {
    const pathname = window.location.pathname;
    const searchParams = new URLSearchParams(window.location.search);

    // Skip routing for static HTML files (let browser handle them)
    if (pathname.endsWith('.html')) {
        // Return a valid route to prevent NotFound, but the browser will handle the file
        return { page: 'Landing', isValid: true };
    }

    // Remove leading slash and split into parts
    const parts = pathname.split('/').filter(Boolean);

    if (parts.length === 0) {
        return { page: 'Landing', isValid: true };
    }

    const [route, ...params] = parts;

    // Check if route is valid
    if (!VALID_ROUTES.has(route)) {
        return { page: 'NotFound', isValid: false };
    }

    // Map route to page name
    const routeMap: { [key: string]: string } = {
        'dashboard': 'Dashboard',
        'templates': 'Templates',
        'documents': 'Documents',
        'settings': 'Settings',
        'news': 'News',
        'admin': 'Admin',
        'teams': 'Teams',
        'pricing': 'Pricing',
        'features': 'Features',
        'about': 'AboutUs',
        'contact': 'Contact',
        'legal': 'Legal',
        'landing': 'Landing',
        'benefits': 'Benefits',
        'collaboration': 'Collaboration',
        'legal-updates': 'LegalUpdates',
        'partnership-tier': 'PartnershipTier',
        'legal-insurance': 'LegalInsurance',
    };

    // Handle special routes with validation
    if (route === 'case' && params[0]) {
        // Validate UUID format (basic check)
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        if (uuidRegex.test(params[0]) && params.length === 1) {
            return { page: 'Chat', caseId: params[0], isValid: true };
        }
        return { page: 'NotFound', isValid: false };
    }

    if (route === 'template' && params[0]) {
        if (!VALID_TEMPLATE_SLUGS.has(params[0])) {
            return { page: 'NotFound', isValid: false };
        }
        if (params[1] === 'wizard' && params.length === 2) {
            return { page: 'TemplateWizard', templateSlug: params[0], isValid: true };
        }
        if (params.length === 1) {
            return { page: 'TemplateView', templateSlug: params[0], isValid: true };
        }
        return { page: 'NotFound', isValid: false };
    }

    if (route === 'document' && params[0]) {
        // Validate UUID format
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        if (uuidRegex.test(params[0]) && params.length === 1) {
            return { page: 'Documents', documentId: params[0], isValid: true };
        }
        return { page: 'NotFound', isValid: false };
    }

    if (route === 'partnership-tier' && params[0]) {
        // Valid tier slugs: standard, premium, enterprise
        const validTiers = ['standard', 'premium', 'enterprise'];
        if (validTiers.includes(params[0]) && params.length === 1) {
            return { page: 'PartnershipTier', tierSlug: params[0], isValid: true };
        }
        return { page: 'NotFound', isValid: false };
    }

    // Default route mapping
    const page = routeMap[route] || 'Landing';

    // Extract query parameters for Documents page (e.g., /documents?case=abc123)
    if (page === 'Documents' && searchParams.has('case')) {
        const caseId = searchParams.get('case');
        if (caseId) {
            return { page, caseId, isValid: true };
        }
    }

    return { page, isValid: true };
};

const generatePermalink = (page: string, params?: { caseId?: string; templateSlug?: string; documentId?: string; tierSlug?: string }): string => {
    const base = window.location.origin;
    let path = '';

    switch (page) {
        case 'Dashboard':
            path = '/dashboard';
            break;
        case 'Templates':
            path = '/templates';
            break;
        case 'Documents':
            if (params?.documentId) {
                path = `/document/${params.documentId}`;
            } else if (params?.caseId) {
                path = `/documents?case=${params.caseId}`;
            } else {
                path = '/documents';
            }
            break;
        case 'Settings':
            path = '/settings';
            break;
        case 'Collaboration':
            path = '/collaboration';
            break;
        case 'LegalInsurance':
            path = '/legal-insurance';
            break;
        case 'PartnershipTier':
            if (params?.tierSlug) {
                path = `/partnership-tier/${params.tierSlug}`;
            } else {
                path = '/collaboration';
            }
            break;
        case 'News':
            path = '/news';
            break;
        case 'Teams':
            path = '/teams';
            break;
        case 'Benefits':
            path = '/benefits';
            break;
        case 'Chat':
            if (params?.caseId) {
                path = `/case/${params.caseId}`;
            } else {
                path = '/dashboard';
            }
            break;
        case 'TemplateWizard':
            if (params?.templateSlug) {
                path = `/template/${params.templateSlug}/wizard`;
            } else {
                path = '/templates';
            }
            break;
        case 'TemplateView':
            if (params?.templateSlug) {
                path = `/template/${params.templateSlug}`;
            } else {
                path = '/templates';
            }
            break;
        case 'Pricing':
            path = '/pricing';
            break;
        case 'Features':
            path = '/features';
            break;
        case 'AboutUs':
            path = '/about';
            break;
        case 'Contact':
            path = '/contact';
            break;
        case 'Legal':
            path = '/legal';
            break;
        case 'LegalUpdates':
            path = '/legal-updates';
            break;
        case 'Landing':
        default:
            path = '/';
            break;
    }

    return `${base}${path}`;
};

const updateURL = (page: string, params?: { caseId?: string; templateSlug?: string; documentId?: string }, replace: boolean = false) => {
    const url = generatePermalink(page, params);
    // Ensure no hash in URL
    const cleanUrl = url.split('#')[0];
    if (replace) {
        window.history.replaceState({}, '', cleanUrl);
    } else {
        window.history.pushState({}, '', cleanUrl);
    }
};

// --- Data Interfaces ---

interface Profile {
    id: string;
    first_name: string | null;
    last_name: string | null;
    role: 'user' | 'admin' | 'super_admin';
    avatar_url: string | null;
    firm: string | null;
    notification_preferences?: {
        caseUpdates?: boolean;
        newMessages?: boolean;
        productNews?: boolean;
        newsletterWeekly?: boolean;
    } | null;
    subscription_id?: string | null;
    subscription?: {
        id: string;
        status: string;
        plan_name: string;
        current_period_end: string;
        cancel_at_period_end: boolean;
        stripe_price_id?: string | null;
        stripe_subscription_id?: string | null;
    } | null;
    ai_provider_preference?: 'gemini' | 'openai' | null;
}

interface Message {
    id: string;
    case_id: string;
    user_id: string;
    content: string;
    sender: 'user' | 'ai';
    citations?: string[];
    created_at: string;
}

interface Case {
    id: string;
    user_id: string;
    title: string;
    type: string;
    status: 'Active' | 'Pending Review' | 'Closed';
    deadline?: string;
    created_at: string;
    folder_id?: string | null;
}

interface Folder {
    id: string;
    user_id: string;
    name: string;
    created_at: string;
}

interface Template {
    id: string;
    icon: string;
    title: string;
    description: string;
    slug: string;
}

const DUMMY_TEMPLATE_CONTENT: { [key: string]: string } = {
    'dsgvo-auskunft': `
[Ihr Name/Ihre Firma]
[Ihre Adresse]
[Ihre PLZ und Stadt]

[Name des Unternehmens]
[Adresse des Unternehmens]
[PLZ und Stadt des Unternehmens]

[Datum]

**Antrag auf Auskunft gemäß Art. 15 DSGVO**

Sehr geehrte Damen und Herren,

hiermit mache ich von meinem Recht auf Auskunft gemäß Artikel 15 der Datenschutz-Grundverordnung (DSGVO) Gebrauch.

Ich bitte um eine vollständige Auskunft über die zu meiner Person gespeicherten Daten.

Mit freundlichen Grüßen,
[Ihr Name]
    `,
    'dsgvo-loeschung': `
[Ihr Name/Ihre Firma]
[Ihre Adresse]
[Ihre PLZ und Stadt]

[Name des Unternehmens]
[Adresse des Unternehmens]
[PLZ und Stadt des Unternehmens]

[Datum]

**Antrag auf Löschung personenbezogener Daten gemäß Art. 17 DSGVO**

Sehr geehrte Damen und Herren,

hiermit beantrage ich die unverzügliche Löschung aller zu meiner Person gespeicherten Daten gemäß Artikel 17 der Datenschutz-Grundverordnung (DSGVO).

Bitte bestätigen Sie mir die erfolgte Löschung schriftlich.

Mit freundlichen Grüßen,
[Ihr Name]
    `,
    'miet-maengelanzeige': `
[Ihr Name]
[Ihre Adresse]
[Ihre PLZ und Stadt]

[Name des Vermieters/der Hausverwaltung]
[Adresse des Vermieters/der Hausverwaltung]
[PLZ und Stadt]

[Datum]

**Mängelanzeige für die Wohnung [Ihre Adresse, Etage, Lage]**

Sehr geehrte/r [Name des Vermieters],

hiermit zeige ich Ihnen folgende Mängel in meiner o.g. Wohnung an:

1. [Beschreibung des Mangels 1]
2. [Beschreibung des Mangels 2]

Ich bitte Sie, die Mängel bis zum [Datum] zu beseitigen.

Mit freundlichen Grüßen,
[Ihr Name]
    `,
    'miet-minderung': `
[Ihr Name]
[Ihre Adresse]
[Ihre PLZ und Stadt]

[Name des Vermieters/der Hausverwaltung]
[Adresse des Vermieters/der Hausverwaltung]
[PLZ und Stadt]

[Datum]

**Ankündigung einer Mietminderung wegen Mängeln**

Sehr geehrte/r [Name des Vermieters],

bezugnehmend auf meine Mängelanzeige vom [Datum der Mängelanzeige] und die weiterhin bestehenden Mängel, kündige ich hiermit eine Minderung der Bruttomiete um [Prozentsatz]% an.

Die Minderung wird ab dem [Datum] wirksam.

Mit freundlichen Grüßen,
[Ihr Name]
    `,
    'abmahnung-widerspruch': `
[Ihr Name]
[Ihre Adresse]
[Ihre PLZ und Stadt]

[Name der abmahnenden Kanzlei/des Unternehmens]
[Adresse]
[PLZ und Stadt]

[Datum]

**Widerspruch gegen Ihre Abmahnung vom [Datum der Abmahnung]**

Sehr geehrte Damen und Herren,

ich widerspreche Ihrer Abmahnung vom [Datum] vollumfänglich. Die gegen mich erhobenen Vorwürfe sind unbegründet.

Ich fordere Sie auf, die beigefügte Unterlassungserklärung nicht weiter zu verwenden.

Mit freundlichen Grüßen,
[Ihr Name]
    `,
    'schufa-bestreitung': `
[Ihr Name]
[Ihre Adresse]
[Ihre PLZ und Stadt]

SCHUFA Holding AG
Privatkunden ServiceCenter
Postfach 10 25 66
44725 Bochum

[Datum]

**Widerspruch und Antrag auf Korrektur eines SCHUFA-Eintrags**

Sehr geehrte Damen und Herren,

in meiner aktuellen SCHUFA-Auskunft habe ich einen fehlerhaften Eintrag festgestellt.

[Genaue Beschreibung des falschen Eintrags]

Ich fordere Sie hiermit auf, diesen Eintrag umgehend zu korrigieren bzw. zu löschen.

Mit freundlichen Grüßen,
[Ihr Name]
    `,
    'schufa-loesung': `
[Ihr Name]
[Ihre Adresse]
[Ihre PLZ und Stadt]

SCHUFA Holding AG
Privatkunden ServiceCenter
Postfach 10 25 66
44725 Bochum

[Datum]

**Antrag auf vorzeitige Löschung nach Erledigung**

Sehr geehrte Damen und Herren,

der Negativeintrag der [Name des Gläubigers] vom [Datum] wurde meinerseits vollständig beglichen. Anbei finden Sie den Erledigungsvermerk des Gläubigers.

Ich beantrage hiermit die vorzeitige Löschung des Eintrags aus meinem Datenbestand.

Mit freundlichen Grüßen,
[Ihr Name]
    `,
};


// --- Components ---

const Navbar: React.FC<{ currentPage: string; onNavigate: (page: string, caseId?: string) => void; isLoggedIn: boolean; onLogout: () => void; onNavigateToAuth?: (mode: 'signup' | 'login') => void; profile: Profile | null; session: Session | null; }> = ({ currentPage, onNavigate, isLoggedIn, onLogout, onNavigateToAuth, profile, session }) => {
    const { t } = useTranslation();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isNotificationOpen, setIsNotificationOpen] = useState(false);
    const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
    const [customLogo, setCustomLogo] = useState<string | null>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const notificationRef = useRef<HTMLDivElement>(null);
    const moreMenuRef = useRef<HTMLDivElement>(null);
    const mobileMenuRef = useRef<HTMLDivElement>(null);

    // Fetch custom logo and color scheme from settings
    useEffect(() => {
        const fetchBrandingSettings = async () => {
            try {
                const { data, error } = await supabase
                    .from('settings')
                    .select('key, value')
                    .in('key', ['logo_url', 'color_scheme', 'font_settings']);

                if (error) {
                    console.error('Error fetching branding settings:', error);
                    return;
                }

                const settings: Record<string, any> = {};
                data.forEach(item => {
                    settings[item.key] = item.value;
                });

                // Set custom logo
                if (settings.logo_url) {
                    const { data: publicUrlData } = supabase.storage.from('branding').getPublicUrl(settings.logo_url);
                    setCustomLogo(publicUrlData.publicUrl);
                }

                // Apply color scheme
                if (settings.color_scheme) {
                    try {
                        const colors = JSON.parse(settings.color_scheme);
                        // Apply colors to CSS variables
                        document.documentElement.style.setProperty('--luna-primary', colors.primary || '#4F46E5');
                        document.documentElement.style.setProperty('--luna-secondary', colors.secondary || '#10B981');
                        document.documentElement.style.setProperty('--luna-accent', colors.accent || '#F59E0B');
                        document.documentElement.style.setProperty('--luna-background', colors.background || '#FFFFFF');
                        document.documentElement.style.setProperty('--luna-text', colors.text || '#1F2937');
                        document.documentElement.style.setProperty('--luna-primary-hover', colors.primaryHover || '#4338CA');
                        document.documentElement.style.setProperty('--luna-secondary-hover', colors.secondaryHover || '#059669');
                        document.documentElement.style.setProperty('--luna-border', colors.borderColor || '#E5E7EB');
                        document.documentElement.style.setProperty('--luna-card-bg', colors.cardBackground || '#FFFFFF');
                        document.documentElement.style.setProperty('--luna-header-bg', colors.headerBackground || '#F9FAFB');
                    } catch (e) {
                        console.error('Error parsing color scheme:', e);
                    }
                }

                // Apply font settings
                if (settings.font_settings) {
                    try {
                        const fonts = JSON.parse(settings.font_settings);
                        // Apply fonts to CSS variables
                        document.documentElement.style.setProperty('--luna-primary-font', fonts.primaryFont || 'Inter, sans-serif');
                        document.documentElement.style.setProperty('--luna-secondary-font', fonts.secondaryFont || 'Inter, sans-serif');
                        document.documentElement.style.setProperty('--luna-base-font-size', fonts.baseFontSize || '16px');
                        document.documentElement.style.setProperty('--luna-heading-font-weight', fonts.headingFontWeight || '700');
                        document.documentElement.style.setProperty('--luna-body-font-weight', fonts.bodyFontWeight || '400');
                    } catch (e) {
                        console.error('Error parsing font settings:', e);
                    }
                }
            } catch (error) {
                console.error('Error fetching branding settings:', error);
            }
        };

        fetchBrandingSettings();
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
            if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
                setIsNotificationOpen(false);
            }
            if (moreMenuRef.current && !moreMenuRef.current.contains(event.target as Node)) {
                setIsMoreMenuOpen(false);
            }
            if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
                setIsMobileMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [dropdownRef, notificationRef, moreMenuRef, mobileMenuRef]);

    // Close mobile menu when navigating
    const handleMobileNavClick = (page: string, e?: React.MouseEvent) => {
        handleNavClick(page, e);
        setIsMobileMenuOpen(false);
    };

    const displayName = profile ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || 'User' : 'Guest';
    const displayFirm = profile?.firm || 'Legal Professional';

    // Handle navigation with proper event prevention
    const handleNavClick = (page: string, e?: React.MouseEvent) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        setIsMoreMenuOpen(false);
        onNavigate(page);
    };

    // Logo navigation: Always go to Landing (home page)
    const handleLogoClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onNavigate('Landing');
    };

    // Menu item styling helper
    const getNavItemClasses = (page: string) => {
        const isActive = currentPage === page;
        return `text-sm font-medium leading-normal px-3 py-1.5 rounded-lg transition-all backdrop-blur-sm
            ${isActive
                ? 'bg-white/30 dark:bg-white/15 text-luna-secondary dark:text-luna-secondary font-semibold'
                : 'text-luna-text-light dark:text-luna-text-dark hover:bg-white/20 dark:hover:bg-white/10 hover:text-luna-secondary dark:hover:text-luna-secondary'
            }`;
    };

    // Define menu items based on login status
    const dashboardMenuItems = [
        { page: 'Dashboard', label: t('nav.dashboard') },
        { page: 'Documents', label: t('nav.documents') },
        { page: 'Templates', label: t('nav.templates') },
        { page: 'BatchProcessing', label: 'Batch Processing' },
    ];

    const publicMenuItems = [
        { page: 'Features', label: t('nav.features') },
        { page: 'Pricing', label: t('nav.pricing') },
        { page: 'Collaboration', label: 'Collaboration' },
        { page: 'LegalUpdates', label: 'Legal Updates' },
        { page: 'LegalInsurance', label: 'Rechtsschutz' },
        { page: 'AboutUs', label: t('nav.about') },
        { page: 'Contact', label: t('nav.contact') },
        { page: 'Legal', label: t('nav.legal') },
    ];

    // When logged in, show both dashboard items and public items
    // When not logged in, show only public items
    const menuItems = isLoggedIn
        ? [...dashboardMenuItems, ...publicMenuItems]
        : publicMenuItems;

    const MAX_PRIMARY_ITEMS = 5;
    const primaryMenuItems = menuItems.slice(0, MAX_PRIMARY_ITEMS);
    const overflowMenuItems = menuItems.slice(MAX_PRIMARY_ITEMS);

    return (
        <>
            <header className="sticky top-0 z-50 flex items-center justify-between whitespace-nowrap px-4 py-2 shrink-0 bg-white/70 dark:bg-luna-primary/70 backdrop-blur-xl backdrop-saturate-150 border-b border-solid border-white/20 dark:border-white/10 shadow-lg shadow-black/5 dark:shadow-black/20 before:absolute before:inset-0 before:bg-gradient-to-b before:from-white/30 before:to-transparent before:dark:from-luna-secondary/5 before:dark:to-transparent before:pointer-events-none">
                {/* Logo and Title - Always navigates to Home (Landing page) */}
                <button
                    className="relative flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity focus:outline-none focus:ring-2 focus:ring-luna-secondary focus:ring-offset-2 rounded-lg px-2 py-0.5 -ml-2 z-10"
                    onClick={handleLogoClick}
                    aria-label="Go to Home page"
                    type="button"
                >
                    <img
                        src={customLogo || "/logo.svg"}
                        alt="LRDEnE logo"
                        width={44}
                        height={44}
                        className="h-9 w-9 drop-shadow-md rounded-2xl border border-white/20 dark:border-white/10 bg-white/5"
                        loading="eager"
                        decoding="async"
                    />
                    <div className="flex flex-col leading-none text-left">
                        <h2 className="text-lg font-black tracking-[-0.02em] text-luna-text-light dark:text-luna-text-dark drop-shadow-sm">LRDEnE</h2>
                        <span className="text-[10px] font-semibold uppercase text-luna-secondary tracking-[0.3em]">Legal AI</span>
                    </div>
                </button>

                {/* Mobile Menu Button - Visible only on screens smaller than 768px */}
                <div className="flex md:hidden items-center gap-2 relative z-[60] ml-auto">
                    {/* Language Switcher - Mobile (hidden on very small screens) */}
                    <div className="hidden sm:flex md:hidden">
                        <LanguageSwitcher userId={profile?.id} />
                    </div>

                    {/* Hamburger Menu Button */}
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            console.log('Mobile menu clicked, current state:', isMobileMenuOpen);
                            setIsMobileMenuOpen(!isMobileMenuOpen);
                            setIsDropdownOpen(false);
                            setIsNotificationOpen(false);
                        }}
                        className="p-3 rounded-lg bg-luna-secondary text-white hover:bg-luna-secondary/90 transition-all border-2 border-luna-secondary shadow-lg min-w-[48px] min-h-[48px] flex items-center justify-center relative z-[60]"
                        type="button"
                        aria-label="Toggle mobile menu"
                        aria-expanded={isMobileMenuOpen}
                        style={{ position: 'relative', zIndex: 60 }}
                    >
                        <span className="material-symbols-outlined text-white text-2xl leading-none font-bold">
                            {isMobileMenuOpen ? 'close' : 'menu'}
                        </span>
                    </button>
                </div>

                {/* Navigation Menu - Desktop */}
                <div className="hidden md:flex flex-1 items-center justify-end gap-4 lg:gap-6 relative z-10">
                    <div className="flex-1 max-w-3xl pr-2">
                        <nav className="flex items-center gap-3 lg:gap-4 overflow-x-auto whitespace-nowrap [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" role="navigation" aria-label="Main navigation">
                            {primaryMenuItems.map((item) => (
                                <button
                                    key={item.page}
                                    className={getNavItemClasses(item.page)}
                                    onClick={(e) => handleNavClick(item.page, e)}
                                    aria-current={currentPage === item.page ? 'page' : undefined}
                                    type="button"
                                >
                                    {item.label}
                                </button>
                            ))}
                        </nav>
                    </div>
                    {overflowMenuItems.length > 0 && (
                        <div className="relative" ref={moreMenuRef}>
                            <button
                                type="button"
                                className={`flex items-center gap-1 rounded-lg border px-3 py-1.5 text-sm font-medium shadow-sm transition-all ${isMoreMenuOpen ? 'bg-white/40 dark:bg-white/10 text-luna-secondary border-white/40 dark:border-white/20' : 'bg-white/20 dark:bg-white/5 text-luna-text-light dark:text-luna-text-dark border-white/20 dark:border-white/10 hover:bg-white/30 dark:hover:bg-white/15'}`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setIsMoreMenuOpen(!isMoreMenuOpen);
                                    setIsDropdownOpen(false);
                                    setIsNotificationOpen(false);
                                }}
                                aria-haspopup="menu"
                                aria-expanded={isMoreMenuOpen}
                            >
                                <span>More</span>
                                <span className="material-symbols-outlined text-base">expand_more</span>
                            </button>
                            {isMoreMenuOpen && (
                                <div className="absolute right-0 mt-2 min-w-[200px] rounded-xl border border-white/30 dark:border-white/10 bg-white/90 dark:bg-luna-primary/90 backdrop-blur-xl shadow-xl p-2 z-30">
                                    {overflowMenuItems.map((item) => (
                                        <button
                                            key={item.page}
                                            className={`block w-full text-left rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${currentPage === item.page ? 'bg-white/60 dark:bg-white/10 text-luna-secondary' : 'text-luna-text-light dark:text-luna-text-dark hover:bg-white/40 dark:hover:bg-white/10'}`}
                                            onClick={(e) => {
                                                handleNavClick(item.page, e);
                                                setIsMoreMenuOpen(false);
                                            }}
                                            type="button"
                                        >
                                            {item.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Language Switcher */}
                    <LanguageSwitcher userId={profile?.id} />

                    {/* Auth Buttons (when not logged in) */}
                    {!isLoggedIn && (
                        <div className="flex gap-2">
                            <button
                                onClick={(e) => { e.preventDefault(); onNavigateToAuth?.('login'); }}
                                className="flex min-w-[84px] max-w-[480px] cursor-pointer items-center justify-center overflow-hidden rounded-lg h-10 px-4 bg-white/30 dark:bg-white/10 backdrop-blur-md border border-white/30 dark:border-white/20 text-sm font-bold leading-normal tracking-[0.015em] hover:bg-white/40 dark:hover:bg-white/15 transition-all shadow-sm"
                                type="button"
                            >
                                <span className="truncate text-luna-text-light dark:text-luna-text-dark">{t('auth.login')}</span>
                            </button>
                            <button
                                onClick={(e) => { e.preventDefault(); onNavigateToAuth?.('signup'); }}
                                className="flex min-w-[84px] max-w-[480px] cursor-pointer items-center justify-center overflow-hidden rounded-lg h-10 px-4 bg-luna-secondary/90 backdrop-blur-md border border-luna-secondary/50 text-luna-primary text-sm font-bold leading-normal tracking-[0.015em] hover:bg-luna-secondary transition-all shadow-lg shadow-luna-secondary/20"
                                type="button"
                            >
                                <span className="truncate">{t('landing.signUpFree')}</span>
                            </button>
                        </div>
                    )}

                    {/* User Menu (when logged in) */}
                    {isLoggedIn && (
                        <div className="flex items-center gap-4">
                            {/* Advanced Search Button */}
                            <button
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setShowAdvancedSearch(true);
                                }}
                                className="p-2 rounded-full hover:bg-white/20 dark:hover:bg-white/10 backdrop-blur-sm transition-all border border-transparent hover:border-white/20 dark:hover:border-white/10"
                                type="button"
                                aria-label="Advanced Search"
                                title="Advanced Search (Ctrl+K)"
                            >
                                <span className="material-symbols-outlined text-luna-text-light dark:text-luna-text-dark">search</span>
                            </button>

                            {/* Notification Bell with Dropdown */}
                            <div className="relative" ref={notificationRef}>
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setIsNotificationOpen(!isNotificationOpen);
                                        setIsDropdownOpen(false); // Close user menu if open
                                    }}
                                    className="relative p-2 rounded-full hover:bg-white/20 dark:hover:bg-white/10 backdrop-blur-sm transition-all border border-transparent hover:border-white/20 dark:hover:border-white/10"
                                    type="button"
                                    aria-label="Notifications"
                                    aria-expanded={isNotificationOpen}
                                >
                                    <span className="material-symbols-outlined text-luna-text-light dark:text-luna-text-dark">notifications</span>
                                    <span className="absolute top-1 right-1 block h-2 w-2 rounded-full bg-red-500 ring-2 ring-white dark:ring-luna-primary"></span>
                                </button>
                                {isNotificationOpen && (
                                    <div className="absolute right-0 mt-2 w-80 bg-white/90 dark:bg-luna-primary/90 backdrop-blur-xl rounded-lg shadow-xl shadow-black/10 dark:shadow-black/30 py-2 border border-white/20 dark:border-white/10 z-20 max-h-96 overflow-y-auto">
                                        <div className="px-4 py-2 border-b border-white/20 dark:border-white/10">
                                            <h3 className="text-sm font-semibold text-luna-text-light dark:text-luna-text-dark">Notifications</h3>
                                        </div>
                                        <div className="py-2">
                                            {/* TODO: Replace with actual notifications from database/backend */}
                                            {/* For now, showing empty state */}
                                            <div className="px-4 py-8 text-center">
                                                <span className="material-symbols-outlined text-luna-text-light/30 dark:text-luna-text-dark/30 text-4xl">notifications_off</span>
                                                <p className="text-sm text-luna-text-light/60 dark:text-luna-text-dark/60 mt-2">No new notifications</p>
                                                <p className="text-xs text-luna-text-light/40 dark:text-luna-text-dark/40 mt-1">You&apos;re all caught up!</p>
                                            </div>
                                        </div>
                                        <div className="px-4 py-2 border-t border-white/20 dark:border-white/10">
                                            <button
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    onNavigate('Settings');
                                                    setIsNotificationOpen(false);
                                                }}
                                                className="text-xs text-luna-secondary hover:text-luna-secondary/80 transition-colors"
                                                type="button"
                                            >
                                                Manage notification settings
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="relative" ref={dropdownRef}>
                                <button
                                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIsDropdownOpen(!isDropdownOpen); }}
                                    className="flex items-center gap-2 cursor-pointer rounded-lg px-2 py-1 hover:bg-white/20 dark:hover:bg-white/10 backdrop-blur-sm transition-all border border-transparent hover:border-white/20 dark:hover:border-white/10"
                                    type="button"
                                    aria-label="User menu"
                                    aria-expanded={isDropdownOpen}
                                >
                                    <img
                                        alt={getAvatarAlt(getProfileFullName(profile))}
                                        className="size-8 rounded-full object-cover ring-2 ring-white/30 dark:ring-white/20"
                                        src={profile?.avatar_url || "https://lh3.googleusercontent.com/aida-public/AB6AXuC6oq33dNp6A-9NrO3YTfoAiNzhgWchFlJuFX0rTR88Ia5vLnjO8OS23dxBfNeB2iFSmP6TfzlTtpIZJPYNQ3aD2g5i5Td_5WlHtD9f8MXuQWh5pUE_Lp26Ayht0Pu3FHP34E3pEN8WfbEA5P2GZzoXMyfkpTBcDhl3WQ56kuDkn-RAOr2xCQpVII4BEDRoLmjtNSthqKVF4uReXtrxQlDVWbQZG_0U2MY9G0Z5smvb9KvpYrbwQhi4-G6kFaqf-OskjR16cIS0_ZaS"}
                                        onError={handleImageError}
                                    />
                                    <div className="hidden md:block text-left">
                                        <p className="text-sm font-semibold text-luna-text-light dark:text-luna-text-dark">{displayName}</p>
                                        <p className="text-xs text-luna-text-light/70 dark:text-luna-text-muted-dark">{displayFirm}</p>
                                    </div>
                                    <span className="material-symbols-outlined text-luna-text-light dark:text-luna-text-dark">expand_more</span>
                                </button>
                                {isDropdownOpen && (
                                    <div className="absolute right-0 mt-2 w-48 bg-white/90 dark:bg-luna-primary/90 backdrop-blur-xl rounded-lg shadow-xl shadow-black/10 dark:shadow-black/30 py-1 border border-white/20 dark:border-white/10 z-20" role="menu">
                                        <button
                                            onClick={(e) => { e.preventDefault(); handleNavClick('Profile', e); setIsDropdownOpen(false); }}
                                            className="block w-full text-left px-4 py-2 text-sm text-luna-text-light dark:text-luna-text-dark hover:bg-white/40 dark:hover:bg-white/10 transition-colors rounded-t-lg"
                                            type="button"
                                            role="menuitem"
                                        >
                                            {t('profile.title')}
                                        </button>
                                        <button
                                            onClick={(e) => { e.preventDefault(); handleNavClick('Settings', e); setIsDropdownOpen(false); }}
                                            className="block w-full text-left px-4 py-2 text-sm text-luna-text-light dark:text-luna-text-dark hover:bg-white/40 dark:hover:bg-white/10 transition-colors"
                                            type="button"
                                            role="menuitem"
                                        >
                                            {t('nav.settings')}
                                        </button>
                                        <div className="border-t border-white/20 dark:border-white/10 my-1"></div>
                                        <button
                                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIsDropdownOpen(false); onLogout(); }}
                                            className="block w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-white/40 dark:hover:bg-white/10 transition-colors rounded-b-lg"
                                            type="button"
                                            role="menuitem"
                                        >
                                            {t('auth.logout')}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Mobile Menu Overlay */}
                {isMobileMenuOpen && (
                    <button
                        type="button"
                        className="fixed inset-0 bg-black/50 dark:bg-black/70 z-40 md:hidden focus:outline-none"
                        onClick={() => setIsMobileMenuOpen(false)}
                        aria-label="Close mobile menu overlay"
                    >
                        <span className="sr-only">Close mobile menu</span>
                    </button>
                )}

                {/* Mobile Menu Panel - Only show when isMobileMenuOpen is true */}
                {isMobileMenuOpen && (
                    <div
                        ref={mobileMenuRef}
                        className="fixed top-0 right-0 h-screen w-80 max-w-[85vw] bg-white/95 dark:bg-luna-primary/95 backdrop-blur-xl z-[100] md:hidden transform transition-transform duration-300 ease-in-out translate-x-0 shadow-2xl border-l border-white/20 dark:border-white/10 overflow-y-auto"
                        style={{ height: '100vh', top: 0 }}
                    >
                        <div className="flex flex-col h-full">
                            {/* Mobile Menu Header */}
                            <div className="flex items-center justify-between p-4 border-b border-white/20 dark:border-white/10">
                                <h3 className="text-lg font-bold text-luna-text-light dark:text-luna-text-dark">Menu</h3>
                                <button
                                    onClick={() => setIsMobileMenuOpen(false)}
                                    className="p-2 rounded-lg hover:bg-white/20 dark:hover:bg-white/10 transition-colors"
                                    type="button"
                                    aria-label="Close menu"
                                >
                                    <span className="material-symbols-outlined text-luna-text-light dark:text-luna-text-dark">close</span>
                                </button>
                            </div>

                            {/* Mobile Menu Content */}
                            <div className="flex-1 flex flex-col p-4 gap-2">
                                {/* Navigation Items */}
                                <nav className="flex flex-col gap-1" role="navigation" aria-label="Mobile navigation">
                                    {menuItems.map((item) => (
                                        <button
                                            key={item.page}
                                            onClick={(e) => handleMobileNavClick(item.page, e)}
                                            className={`text-left px-4 py-3 rounded-lg transition-all ${currentPage === item.page
                                                ? 'bg-luna-secondary/20 text-luna-secondary font-semibold'
                                                : 'text-luna-text-light dark:text-luna-text-dark hover:bg-white/20 dark:hover:bg-white/10'
                                                }`}
                                            aria-current={currentPage === item.page ? 'page' : undefined}
                                            type="button"
                                        >
                                            {item.label}
                                        </button>
                                    ))}
                                </nav>

                                {/* Divider */}
                                <div className="border-t border-white/20 dark:border-white/10 my-2"></div>

                                {/* Auth Section - When not logged in */}
                                {!isLoggedIn && (
                                    <div className="flex flex-col gap-2">
                                        <button
                                            onClick={(e) => {
                                                e.preventDefault();
                                                onNavigateToAuth?.('login');
                                                setIsMobileMenuOpen(false);
                                            }}
                                            className="w-full px-4 py-3 rounded-lg bg-white/30 dark:bg-white/10 backdrop-blur-md border border-white/30 dark:border-white/20 text-sm font-bold text-luna-text-light dark:text-luna-text-dark hover:bg-white/40 dark:hover:bg-white/15 transition-all"
                                            type="button"
                                        >
                                            {t('auth.login')}
                                        </button>
                                        <button
                                            onClick={(e) => {
                                                e.preventDefault();
                                                onNavigateToAuth?.('signup');
                                                setIsMobileMenuOpen(false);
                                            }}
                                            className="w-full px-4 py-3 rounded-lg bg-luna-secondary/90 backdrop-blur-md border border-luna-secondary/50 text-luna-primary text-sm font-bold hover:bg-luna-secondary transition-all shadow-lg shadow-luna-secondary/20"
                                            type="button"
                                        >
                                            {t('landing.signUpFree')}
                                        </button>
                                    </div>
                                )}

                                {/* User Section - When logged in */}
                                {isLoggedIn && (
                                    <div className="flex flex-col gap-2">
                                        {/* User Info */}
                                        <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-white/10 dark:bg-white/5">
                                            <img
                                                alt={getAvatarAlt(getProfileFullName(profile))}
                                                className="size-10 rounded-full object-cover ring-2 ring-white/30 dark:ring-white/20"
                                                src={profile?.avatar_url || "https://lh3.googleusercontent.com/aida-public/AB6AXuC6oq33dNp6A-9NrO3YTfoAiNzhgWchFlJuFX0rTR88Ia5vLnjO8OS23dxBfNeB2iFSmP6TfzlTtpIZJPYNQ3aD2g5i5Td_5WlHtD9f8MXuQWh5pUE_Lp26Ayht0Pu3FHP34E3pEN8WfbEA5P2GZzoXMyfkpTBcDhl3WQ56kuDkn-RAOr2xCQpVII4BEDRoLmjtNSthqKVF4uReXtrxQlDVWbQZG_0U2MY9G0Z5smvb9KvpYrbwQhi4-G6kFaqf-OskjR16cIS0_ZaS"}
                                                onError={handleImageError}
                                            />
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-semibold text-luna-text-light dark:text-luna-text-dark truncate">{displayName}</p>
                                                <p className="text-xs text-luna-text-light/70 dark:text-luna-text-muted-dark truncate">{displayFirm}</p>
                                            </div>
                                        </div>

                                        {/* Notification Button */}
                                        <div className="relative">
                                            <button
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    setIsNotificationOpen(!isNotificationOpen);
                                                    setIsDropdownOpen(false);
                                                }}
                                                className="flex items-center gap-3 px-4 py-3 rounded-lg text-luna-text-light dark:text-luna-text-dark hover:bg-white/20 dark:hover:bg-white/10 transition-colors w-full"
                                                type="button"
                                            >
                                                <span className="material-symbols-outlined">notifications</span>
                                                <span className="text-sm font-medium">Notifications</span>
                                                <span className="ml-auto h-2 w-2 rounded-full bg-red-500"></span>
                                            </button>
                                            {isNotificationOpen && (
                                                <div className="mt-2 mx-4 bg-white/90 dark:bg-luna-primary/90 backdrop-blur-xl rounded-lg shadow-xl shadow-black/10 dark:shadow-black/30 py-2 border border-white/20 dark:border-white/10 max-h-64 overflow-y-auto">
                                                    <div className="px-4 py-2 border-b border-white/20 dark:border-white/10">
                                                        <h3 className="text-sm font-semibold text-luna-text-light dark:text-luna-text-dark">Notifications</h3>
                                                    </div>
                                                    <div className="py-2">
                                                        <div className="px-4 py-8 text-center">
                                                            <span className="material-symbols-outlined text-luna-text-light/30 dark:text-luna-text-dark/30 text-4xl">notifications_off</span>
                                                            <p className="text-sm text-luna-text-light/60 dark:text-luna-text-dark/60 mt-2">No new notifications</p>
                                                            <p className="text-xs text-luna-text-light/40 dark:text-luna-text-dark/40 mt-1">You&apos;re all caught up!</p>
                                                        </div>
                                                    </div>
                                                    <div className="px-4 py-2 border-t border-white/20 dark:border-white/10">
                                                        <button
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                handleMobileNavClick('Settings');
                                                                setIsNotificationOpen(false);
                                                            }}
                                                            className="text-xs text-luna-secondary hover:text-luna-secondary/80 transition-colors"
                                                            type="button"
                                                        >
                                                            Manage notification settings
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Profile Link */}
                                        <button
                                            onClick={(e) => {
                                                e.preventDefault();
                                                handleMobileNavClick('Profile', e);
                                            }}
                                            className="flex items-center gap-3 px-4 py-3 rounded-lg text-luna-text-light dark:text-luna-text-dark hover:bg-white/20 dark:hover:bg-white/10 transition-colors"
                                            type="button"
                                        >
                                            <span className="material-symbols-outlined">person</span>
                                            <span className="text-sm font-medium">{t('profile.title')}</span>
                                        </button>

                                        {/* Settings Link */}
                                        <button
                                            onClick={(e) => {
                                                e.preventDefault();
                                                handleMobileNavClick('Settings', e);
                                            }}
                                            className="flex items-center gap-3 px-4 py-3 rounded-lg text-luna-text-light dark:text-luna-text-dark hover:bg-white/20 dark:hover:bg-white/10 transition-colors"
                                            type="button"
                                        >
                                            <span className="material-symbols-outlined">settings</span>
                                            <span className="text-sm font-medium">{t('nav.settings')}</span>
                                        </button>

                                        {/* Divider */}
                                        <div className="border-t border-white/20 dark:border-white/10 my-2"></div>

                                        {/* Logout Button */}
                                        <button
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                setIsMobileMenuOpen(false);
                                                onLogout();
                                            }}
                                            className="flex items-center gap-3 px-4 py-3 rounded-lg text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                            type="button"
                                        >
                                            <span className="material-symbols-outlined">logout</span>
                                            <span className="text-sm font-medium">{t('auth.logout')}</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </header>

            {/* Advanced Search Modal */}
            {showAdvancedSearch && session && (
                <AdvancedSearch
                    session={session!}
                    onNavigateToCase={(caseId) => {
                        onNavigate('Chat', caseId);
                        setShowAdvancedSearch(false);
                    }}
                    onClose={() => setShowAdvancedSearch(false)}
                />
            )}
        </>
    );
};

interface Document {
    id: string;
    name: string;
    file_path: string;
    file_size: number;
    file_type: string;
    uploaded_at: string;
    case_id: string;
}

type RiskLevel = 'low' | 'medium' | 'high';
type PriorityLevel = 'low' | 'medium' | 'high' | 'critical';

interface DocumentAnalysisFinding {
    id: number | string;
    clause: string;
    page?: number;
    risk: RiskLevel;
    finding: string;
    suggestion?: string;
    applicableLaws?: string[];
    priority?: PriorityLevel;
    summary?: string;
}

interface ComplianceStatus {
    compliant: boolean;
    issues: string[];
}

interface DocumentAnalysisState {
    summary: string;
    overallRisk: RiskLevel;
    riskHeatmap: Record<RiskLevel, number>;
    findings: DocumentAnalysisFinding[];
    compliance: {
        gdpr: ComplianceStatus;
        euAIAct: ComplianceStatus;
        germanCivilCode: ComplianceStatus;
    };
}

interface DocumentAnalysisPayload {
    summary?: string;
    overallRisk?: string;
    riskHeatmap?: Partial<Record<RiskLevel, number>>;
    findings?: Array<Partial<DocumentAnalysisFinding>>;
    compliance?: {
        gdpr?: Partial<ComplianceStatus>;
        euAIAct?: Partial<ComplianceStatus>;
        germanCivilCode?: Partial<ComplianceStatus>;
    };
}

// Combined type for the request
type AnalyzeDocumentRequest = {
    documentId: string;
    userId: string;
    fileName: string;
    storageBucket?: string;
    storagePath?: string;
    fileUrl?: string | null;
};

interface DocumentAnalysisResponse {
    analysis?: DocumentAnalysisPayload;
    error?: string;
    details?: string;
    suggestions?: string[];
}

type AnalyzeDocumentResult = DocumentAnalysisResponse | string | null;


const hasFunctionContext = (error: unknown): error is Error & { context?: { message?: string; body?: unknown } } => {
    return Boolean(
        error &&
        typeof error === 'object' &&
        'context' in error
    );
};

const isDocumentAnalysisResponse = (response: AnalyzeDocumentResult): response is DocumentAnalysisResponse => {
    return typeof response === 'object' && response !== null;
};

const normalizeRiskLevel = (risk?: string): RiskLevel => {
    if (risk === 'low' || risk === 'medium' || risk === 'high') {
        return risk;
    }
    return 'medium';
};

const normalizePriorityLevel = (priority?: string): PriorityLevel => {
    if (priority === 'critical' || priority === 'high' || priority === 'medium' || priority === 'low') {
        return priority;
    }
    return 'medium';
};

const normalizeComplianceStatus = (status?: Partial<ComplianceStatus>): ComplianceStatus => {
    const issues = status?.issues;
    return {
        compliant: status?.compliant ?? true,
        issues: Array.isArray(issues) ? issues : []
    };
};

const toError = (error: unknown): Error => (error instanceof Error ? error : new Error(String(error)));

const CaseChatView: React.FC<{ selectedCase: Case | null; session: Session; profile: Profile | null }> = React.memo(({ selectedCase, session, profile }) => {
    const [messages, setMessages] = useState<Message[]>([]);
    const [input, setInput] = useState('');
    const [documents, setDocuments] = useState<Document[]>([]);
    const [loadingDocuments, setLoadingDocuments] = useState(false);
    const [isWaitingForAI, setIsWaitingForAI] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const isMountedRef = useRef(true);
    const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const scrollToBottom = () => {
        try {
            if (messagesEndRef.current) {
                messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
            }
        } catch (error) {
            // Silently fail if element is not available (e.g., component unmounted)
            logger.warn('Could not scroll to bottom:', error);
        }
    };

    const fetchMessages = useCallback(async (caseId: string) => {
        const { data, error } = await supabase
            .from('messages')
            .select('*')
            .eq('case_id', caseId)
            .order('created_at', { ascending: true });

        if (error) {
            logger.error('Error fetching messages:', error);
        } else {
            setMessages(data as Message[]);
        }
    }, [selectedCase?.id]);

    const fetchDocuments = useCallback(async (caseId: string) => {
        setLoadingDocuments(true);
        const { data, error } = await supabase
            .from('documents')
            .select('*')
            .eq('case_id', caseId)
            .order('uploaded_at', { ascending: false });

        if (error) {
            logger.error('Error fetching documents:', error);
            // Provide more specific error feedback
            if (error.code === 'PGRST116' || error.message?.includes('does not exist')) {
                logger.warn('Documents table does not exist. Please run database migrations in Supabase SQL Editor.');
            } else if (error.code === '42501' || error.message?.includes('row-level security')) {
                logger.warn('RLS policies not configured. Please run database migrations in Supabase SQL Editor.');
            }
            setDocuments([]);
        } else {
            setDocuments(data as Document[] || []);
        }
        setLoadingDocuments(false);
    }, []);

    // 1. Real-time subscription logic (optional - works without Realtime enabled)
    const [, setRealtimeEnabled] = useState(false);

    useEffect(() => {
        if (!selectedCase?.id) return;

        // Mark component as mounted
        isMountedRef.current = true;

        let channel: ReturnType<typeof supabase.channel> | null = null;

        // Try to set up the real-time channel (if Realtime is enabled)
        try {
            channel = supabase
                .channel(`case_chat_${selectedCase.id}`)
                .on(
                    'postgres_changes',
                    {
                        event: 'INSERT',
                        schema: 'public',
                        table: 'messages',
                        filter: `case_id=eq.${selectedCase.id}`
                    },
                    (payload) => {
                        try {
                            // Validate payload data
                            if (!payload || !payload.new) {
                                logger.warn('Invalid payload received from realtime:', payload);
                                return;
                            }

                            const newMessage = payload.new as Message;

                            // Validate message structure
                            if (!newMessage || !newMessage.id || !newMessage.content) {
                                logger.warn('Invalid message structure from realtime:', newMessage);
                                return;
                            }

                            setMessages(prev => {
                                // Prevent duplicates if the message was already added locally (optimistic update)
                                if (prev.some(msg => msg.id === newMessage.id)) {
                                    return prev;
                                }
                                // Also check for temp messages with same content/user_id/case_id to avoid duplicates
                                // This handles the case where optimistic update has temp ID but same content
                                const isDuplicate = prev.some(msg =>
                                    msg.id === newMessage.id ||
                                    (msg.id && msg.id.startsWith('temp-') &&
                                        msg.content === newMessage.content &&
                                        msg.user_id === newMessage.user_id &&
                                        msg.case_id === newMessage.case_id &&
                                        msg.created_at && newMessage.created_at &&
                                        Math.abs(new Date(msg.created_at).getTime() - new Date(newMessage.created_at).getTime()) < 5000) // Within 5 seconds
                                );
                                if (isDuplicate) {
                                    // If it's a duplicate of a temp message, replace the temp with real
                                    return prev.map(msg =>
                                        (msg.id && msg.id.startsWith('temp-') &&
                                            msg.content === newMessage.content &&
                                            msg.user_id === newMessage.user_id &&
                                            msg.case_id === newMessage.case_id) ? newMessage : msg
                                    );
                                }
                                // If AI responds, stop waiting
                                if (newMessage.sender === 'ai') {
                                    setIsWaitingForAI(false);
                                }
                                return [...prev, newMessage];
                            });
                            // Scroll to bottom after a brief delay to ensure DOM is updated
                            // Clear any existing timeout
                            if (scrollTimeoutRef.current) {
                                clearTimeout(scrollTimeoutRef.current);
                            }
                            scrollTimeoutRef.current = setTimeout(() => {
                                // Check if component is still mounted before accessing DOM
                                if (!isMountedRef.current) return;
                                try {
                                    if (messagesEndRef.current) {
                                        messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
                                    }
                                } catch (error) {
                                    // Silently fail if element is not available
                                    logger.warn('Could not scroll to bottom:', error);
                                }
                            }, 100);
                        } catch (error) {
                            logger.error('Error processing realtime message:', error);
                        }
                    }
                )
                .subscribe((status) => {
                    // Track if Realtime is working
                    if (status === 'SUBSCRIBED') {
                        setRealtimeEnabled(true);
                        logger.info('Realtime subscribed - using instant updates');
                    } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
                        setRealtimeEnabled(false);
                        logger.info('Realtime not available - using polling fallback');
                    }
                });
        } catch (error) {
            // Handle WebSocket errors gracefully (e.g., insecure connection on HTTP)
            setRealtimeEnabled(false);
            logger.warn('Realtime subscription failed, using polling fallback:', error);
        }

        // Clean up the subscription when the component unmounts or selectedCase changes
        return () => {
            // Mark component as unmounted
            isMountedRef.current = false;

            // Clear any pending scroll timeouts
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
                scrollTimeoutRef.current = null;
            }

            if (channel) {
                try {
                    supabase.removeChannel(channel);
                } catch (error) {
                    // Ignore cleanup errors
                    console.debug('Error removing channel:', error);
                }
            }
        };
    }, [selectedCase?.id]);

    // Polling fallback: Refresh messages every 2 seconds when waiting for AI
    // This ensures we pick up the AI response even if Realtime has issues
    useEffect(() => {
        if (!selectedCase?.id || !isWaitingForAI) return;

        let pollCount = 0;
        const maxPolls = 20; // Stop after 40 seconds (20 polls * 2 seconds)

        const pollInterval = setInterval(async () => {
            pollCount++;

            // Fetch messages and check if AI responded
            const { data: currentMessages } = await supabase
                .from('messages')
                .select('*')
                .eq('case_id', selectedCase.id)
                .order('created_at', { ascending: true });

            if (currentMessages) {
                // Update messages state
                setMessages(currentMessages as Message[]);

                // Check if latest message is from AI and recent
                const latestMessage = currentMessages[currentMessages.length - 1];
                if (latestMessage && latestMessage.sender === 'ai') {
                    const messageAge = Date.now() - new Date(latestMessage.created_at).getTime();
                    // If AI message is very recent (within last 15 seconds), we got the response
                    if (messageAge < 15000) {
                        setIsWaitingForAI(false);
                        clearInterval(pollInterval);
                        scrollToBottom();
                        return;
                    }
                }
            }

            // Stop polling after max attempts
            if (pollCount >= maxPolls) {
                setIsWaitingForAI(false);
                clearInterval(pollInterval);
            }
        }, 2000);

        return () => clearInterval(pollInterval);
    }, [selectedCase?.id, isWaitingForAI]);

    // 2. Initial fetch and scroll
    useEffect(() => {
        if (selectedCase) {
            fetchMessages(selectedCase.id);
            fetchDocuments(selectedCase.id);
        }
    }, [selectedCase, fetchMessages, fetchDocuments]);

    useEffect(() => {
        // Scroll to bottom when messages change, with error handling
        try {
            if (messagesEndRef.current) {
                messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
            }
        } catch (error) {
            // Silently fail if element is not available
            logger.warn('Could not scroll to bottom:', error);
        }
    }, [messages]);

    const handleSendMessage = async (e: React.FormEvent | React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        // Prevent duplicate submissions
        if (isWaitingForAI || input.trim() === '' || !selectedCase || !session.user) return;

        // Check subscription limits before sending message
        const planName = getUserPlanName(profile);
        const limitCheck = await canSendChatMessage(session.user.id, planName);
        if (!limitCheck.allowed) {
            toast.error(limitCheck.reason || 'You have reached your chat limit. Please upgrade your plan.');
            // Show upgrade prompt
            setTimeout(() => {
                showUpgradeToast(
                    'Upgrade to continue chatting without limits',
                    () => { window.location.href = '/pricing'; },
                    'Unlimited Chat Messages'
                );
            }, 1000);
            return;
        }

        const userMessage = input.trim();
        const newUserMessage: Omit<Message, 'id' | 'created_at'> = {
            case_id: selectedCase.id,
            user_id: session.user.id,
            content: userMessage,
            sender: 'user',
        };

        setInput('');

        // Show loading state immediately - set this FIRST to prevent duplicate calls
        setIsWaitingForAI(true);

        // Optimistically add user message to UI (will be confirmed when saved to DB)
        const tempUserMessage: Message = {
            ...newUserMessage,
            id: `temp-${Date.now()}`,
            created_at: new Date().toISOString(),
        };
        setMessages(prev => [...prev, tempUserMessage]);
        scrollToBottom();

        // Save user message to database
        const { data, error } = await supabase.from('messages').insert(newUserMessage).select();

        if (error) {
            logger.error('Error sending message:', error);
            setIsWaitingForAI(false);
            // Remove optimistic message on error
            setMessages(prev => prev.filter(msg => msg.id !== tempUserMessage.id));
            toast.error('Failed to send message. Please try again.');
            return;
        }

        // Update the temporary message with real data
        // If realtime already added the message, just remove the temp one
        if (data && data[0]) {
            setMessages(prev => {
                // Check if real message already exists (from realtime subscription)
                const realMessageExists = prev.some(msg => msg.id === data[0].id);
                if (realMessageExists) {
                    // Real message already added by realtime, just remove temp
                    return prev.filter(msg => msg.id !== tempUserMessage.id);
                }
                // Real message not yet added, update temp to real
                return prev.map(msg =>
                    msg.id === tempUserMessage.id ? data[0] : msg
                );
            });
        }

        // Call AI Edge Function for response
        try {
            const { data: { session: currentSession } } = await supabase.auth.getSession();
            if (!currentSession) {
                throw new Error('Not authenticated');
            }

            // Enhanced error handling for Edge Function invocation
            let response;
            try {
                response = await supabase.functions.invoke('chat-ai', {
                    body: {
                        message: userMessage,
                        caseId: selectedCase.id,
                        userId: session.user.id,
                    },
                });
            } catch (invokeError) {
                logger.error('Error invoking Edge Function:', invokeError);
                // Check if function is deployed
                if (invokeError instanceof Error && (
                    invokeError.message.includes('404') ||
                    invokeError.message.includes('not found') ||
                    invokeError.message.includes('Function not found')
                )) {
                    throw new Error('Chat AI function is not deployed. Please deploy the "chat-ai" Edge Function in Supabase Dashboard.');
                }
                throw invokeError;
            }

            if (response.error) {
                logger.error('Edge Function error:', response.error);
                // Extract detailed error information
                let errorMessage = response.error.message || 'Unknown error';
                let errorDetails = '';

                // Try to get error details from response
                if (response.data) {
                    if (typeof response.data === 'object' && 'error' in response.data) {
                        errorMessage = String(response.data.error);
                        if ('details' in response.data) {
                            errorDetails = String(response.data.details);
                        }
                    } else if (typeof response.data === 'string') {
                        try {
                            const parsed = JSON.parse(response.data);
                            if (parsed.error) errorMessage = String(parsed.error);
                            if (parsed.details) errorDetails = String(parsed.details);
                        } catch {
                            errorMessage = response.data;
                        }
                    }
                }

                // Provide specific guidance based on error type
                if (errorMessage.includes('Gemini API key') || errorMessage.includes('GEMINI_API_KEY')) {
                    throw new Error('AI service is not configured. Please set GEMINI_API_KEY secret in Supabase Edge Functions settings.');
                } else if (errorMessage.includes('Case not found') || errorMessage.includes('access denied')) {
                    throw new Error('Authorization error: You may not have access to this case. Please refresh the page.');
                } else if (errorMessage.includes('service unavailable') || errorMessage.includes('503')) {
                    throw new Error('AI service is temporarily unavailable. Please try again in a moment.');
                } else {
                    throw new Error(`AI Function Error: ${errorMessage}${errorDetails ? ` - ${errorDetails}` : ''}`);
                }
            }

            // Check if response has error in data
            if (response.data && typeof response.data === 'object' && 'error' in response.data) {
                logger.error('Edge Function returned error:', response.data.error);
                const errorMsg = String(response.data.error);
                const errorDet = response.data.details ? String(response.data.details) : '';
                throw new Error(errorDet ? `${errorMsg}: ${errorDet}` : errorMsg);
            }

            // The Edge Function already saves the AI response to the database
            // Wait a moment then refresh to ensure we get the response
            // (Realtime should pick it up, but this ensures it works either way)
            setTimeout(async () => {
                await fetchMessages(selectedCase.id);
                setIsWaitingForAI(false);
                scrollToBottom();
            }, 2000); // Give Edge Function time to save to DB, then refresh

        } catch (error) {
            setIsWaitingForAI(false);
            logger.error('Error calling AI function:', error);
            const errorMsg = error instanceof Error ? error.message : String(error);

            // Show user-friendly error based on error type
            let fallbackContent = "I apologize, but I'm currently unable to process your request. ";
            if (errorMsg.includes('Gemini API key') || errorMsg.includes('GEMINI_API_KEY')) {
                fallbackContent += "The AI service is not configured. Please check your Supabase Edge Function secrets.";
            } else if (errorMsg.includes('Case not found') || errorMsg.includes('access denied')) {
                fallbackContent += "There was an authorization issue. Please try refreshing the page.";
            } else if (errorMsg.includes('service unavailable') || errorMsg.includes('503')) {
                fallbackContent += "The AI service is temporarily unavailable. Please try again in a moment.";
            } else {
                fallbackContent += "Please check the browser console for details or try again.";
            }

            // Fallback: Show a message that AI is unavailable
            const fallbackMessage: Omit<Message, 'id' | 'created_at'> = {
                case_id: selectedCase.id,
                user_id: session.user.id,
                content: fallbackContent,
                sender: 'ai',
            };
            const { error: fallbackError } = await supabase.from('messages').insert(fallbackMessage);
            if (fallbackError) {
                logger.error('Error saving fallback message:', fallbackError);
            }
        }
    };

    const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file || !selectedCase || !session.user) return;

        // Validate file type and size
        const maxFileSize = 10 * 1024 * 1024; // 10MB
        const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'];

        if (!allowedTypes.includes(file.type)) {
            toast.error('Invalid file type. Please upload PDF, DOC, DOCX, or TXT files.');
            if (event.target) event.target.value = '';
            return;
        }

        if (file.size > maxFileSize) {
            toast.error('File size exceeds 10MB limit.');
            if (event.target) event.target.value = '';
            return;
        }

        // Check subscription limits before allowing file upload (prevents bypassing limits)
        const planName = getUserPlanName(profile);
        const limitCheck = await canUploadDocument(file, planName);

        if (!limitCheck.allowed) {
            toast.error(limitCheck.reason || 'This document exceeds your plan limits. Please upgrade.');
            if (event.target) event.target.value = '';
            // Show upgrade prompt
            setTimeout(() => {
                showUpgradeToast(
                    'Upgrade to upload larger documents',
                    () => { window.location.href = '/pricing'; },
                    'Larger Document Upload'
                );
            }, 1000);
            return;
        }

        try {
            // Upload to Supabase Storage
            const fileName = `${session.user.id}/${selectedCase.id}/${Date.now()}_${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;

            const { error: uploadError } = await supabase.storage
                .from('documents')
                .upload(fileName, file, {
                    cacheControl: '3600',
                    upsert: false
                });

            if (uploadError) {
                logger.error('Upload error:', uploadError);
                // Provide more specific error messages
                let errorMessage = 'Failed to upload file. ';

                if (uploadError.message?.includes('Bucket not found') || uploadError.message?.includes('storage') || uploadError.message?.includes('bucket')) {
                    errorMessage = 'Storage bucket "documents" not found. Please create it in Supabase Dashboard → Storage.';
                } else if (uploadError.message?.includes('new row violates row-level security') || uploadError.message?.includes('RLS')) {
                    errorMessage = 'Permission denied: Storage policies may not be configured. Please run the database migrations (0006_create_storage_bucket_and_policies.sql) in Supabase SQL Editor.';
                } else if (uploadError.message?.includes('JWT')) {
                    errorMessage = 'Authentication error: Please refresh the page and try again.';
                } else {
                    errorMessage = `Upload failed: ${uploadError.message || 'Unknown error'}. See browser console for details.`;
                }

                toast.error(errorMessage);
                if (event.target) event.target.value = '';
                return;
            }

            // Save document record to database
            // Verify authentication first
            const { data: { user: currentUser } } = await supabase.auth.getUser();

            if (!currentUser || currentUser.id !== session.user.id) {
                toast.error('Authentication error: Please refresh and try again.');
                if (event.target) event.target.value = '';
                return;
            }

            const { error: docError } = await supabase
                .from('documents')
                .insert({
                    case_id: selectedCase.id,
                    user_id: session.user.id,
                    name: file.name,
                    file_path: fileName,
                    file_size: file.size,
                    file_type: file.type,
                })
                .select()
                .single();

            if (docError) {
                logger.error('Error saving document record:', docError);
                // Provide specific error message for RLS issues
                if (docError.code === '42501' || docError.message?.includes('row-level security')) {
                    toast.error(
                        'Permission denied: Documents table RLS policies may not be configured. Please run the database migrations in Supabase SQL Editor.',
                        { duration: 8000 }
                    );
                } else {
                    toast.error(`Failed to save document: ${docError.message}`);
                }
                if (event.target) event.target.value = '';
                // Do not create chat message if document save failed - return early
                return;
            }

            // Refresh documents list
            if (selectedCase) {
                fetchDocuments(selectedCase.id);
            }

            // Create user message about the file upload - only after successful document save
            const userFileMessage: Omit<Message, 'id' | 'created_at'> = {
                case_id: selectedCase.id,
                user_id: session.user.id,
                content: `Attached file: ${file.name}`,
                sender: 'user',
            };
            const { error: userMsgError } = await supabase.from('messages').insert(userFileMessage);
            if (userMsgError) {
                logger.error("Error saving user file message:", userMsgError);
            }
            // No need to manually setMessages here, Realtime handles it.

            // Simulate AI response (will be replaced with real AI integration)
            setTimeout(async () => {
                const aiAnalyzingMessage: Omit<Message, 'id' | 'created_at'> = {
                    case_id: selectedCase.id,
                    user_id: session.user.id,
                    content: `Thank you for uploading "${file.name}". I am now performing a deep analysis...`,
                    sender: 'ai',
                };
                const { error: aiMsgError } = await supabase.from('messages').insert(aiAnalyzingMessage);
                if (aiMsgError) {
                    logger.error("Error saving AI analyzing message:", aiMsgError);
                }
            }, 1000);
        } catch (error) {
            logger.error('Unexpected error during file upload:', error);
            toast.error('An unexpected error occurred. Please try again.');
        } finally {
            if (event.target) {
                event.target.value = '';
            }
        }
    };

    const handleAttachmentClick = () => {
        fileInputRef.current?.click();
    };

    const displayName = profile ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || 'User' : 'User';
    const displayFirm = profile?.firm || 'Legal Professional';

    if (!selectedCase) {
        return (
            <div className="flex flex-col flex-1 items-center justify-center p-4 bg-luna-bg-light dark:bg-luna-bg-dark">
                <p className="text-xl text-luna-text-light/70 dark:text-luna-text-muted-dark">Select a case from the sidebar to view its chat.</p>
            </div>
        );
    }

    return (
        <div className="flex-1 grid grid-cols-1 xl:grid-cols-3 gap-0 xl:gap-4 p-0 xl:p-4 overflow-hidden">
            <div className="xl:col-span-2 flex flex-col bg-white dark:bg-luna-primary xl:rounded-xl xl:border xl:border-luna-text-light/10 xl:dark:border-luna-text-dark/20 xl:shadow-sm overflow-hidden order-1 xl:order-1 min-h-0 h-full">
                {/* Mobile-optimized header */}
                <div className="p-4 xl:p-4 border-b border-luna-text-light/10 dark:border-luna-text-dark/20 shrink-0 bg-white dark:bg-luna-primary sticky top-0 z-10">
                    <h2 className="text-lg xl:text-lg font-bold truncate">{selectedCase.title}</h2>
                    <p className="text-xs xl:text-sm text-luna-text-light/70 dark:text-luna-text-muted-dark mt-0.5">Case #{selectedCase.id.substring(0, 8)}</p>
                </div>
                {/* Messages area - optimized for mobile */}
                <div className="flex-1 p-4 xl:p-4 overflow-y-auto space-y-3 xl:space-y-6 min-h-0">
                    {messages.length === 0 && !isWaitingForAI ? (
                        <EmptyMessages />
                    ) : (
                        <>
                            {messages.map((msg) => (
                                <div key={msg.id} className={`flex ${msg.sender === 'user' ? 'flex-row-reverse' : 'items-start'} gap-2.5 xl:gap-3`}>
                                    {msg.sender === 'ai' ? (
                                        <div className="flex-shrink-0 size-9 xl:size-10 bg-luna-secondary/20 text-luna-secondary rounded-full flex items-center justify-center shadow-sm">
                                            <span className="material-symbols-outlined text-base xl:text-base">gavel</span>
                                        </div>
                                    ) : (
                                        <img
                                            alt={getAvatarAlt(getProfileFullName(profile))}
                                            className="size-9 xl:size-10 rounded-full object-cover flex-shrink-0 shadow-sm ring-2 ring-luna-secondary/20"
                                            src={profile?.avatar_url || "https://lh3.googleusercontent.com/aida-public/AB6AXuC6oq33dNp6A-9NrO3YTfoAiNzhgWchFlJuFX0rTR88Ia5vLnjO8OS23dxBfNeB2iFSmP6TfzlTtpIZJPYNQ3aD2g5i5Td_5WlHtD9f8MXuQWh5pUE_Lp26Ayht0Pu3FHP34E3pEN8WfbEA5P2GZzoXMyfkpTBcDhl3WQ56kuDkn-RAOr2xCQpVII4BEDRoLmjtNSthqKVF4uReXtrxQlDVWbQZG_0U2MY9G0Z5smvb9KvpYrbwQhi4-G6kFaqf-OskjR16cIS0_ZaS"}
                                            onError={handleImageError}
                                        />
                                    )}
                                    <div className={`rounded-2xl xl:rounded-lg px-4 py-3 xl:px-4 xl:py-4 max-w-[85%] xl:max-w-lg shadow-sm ${msg.sender === 'user' ? 'bg-luna-secondary text-luna-primary rounded-br-sm xl:rounded-br-lg' : 'bg-luna-bg-light dark:bg-luna-bg-dark rounded-bl-sm xl:rounded-bl-lg border border-luna-text-light/5 dark:border-luna-text-dark/10'}`}>
                                        <p className="text-[15px] xl:text-base leading-relaxed whitespace-pre-wrap break-words">{msg.content}</p>
                                        {msg.citations && msg.citations.length > 0 && (
                                            <div className="mt-2 flex flex-wrap gap-2 text-xs">
                                                {msg.citations.map((citation, index) => (
                                                    <span key={index} className="px-2 py-1 rounded-full bg-luna-text-dark/10 dark:bg-luna-text-light/10 text-luna-text-muted-dark dark:text-luna-text-light/70 hover:underline cursor-pointer touch-manipulation" aria-label={`Citation: ${citation}`}>
                                                        {citation}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </>
                    )}
                    {isWaitingForAI && (
                        <div className="flex items-start gap-2.5 xl:gap-3">
                            <div className="flex-shrink-0 size-9 xl:size-10 bg-luna-secondary/20 text-luna-secondary rounded-full flex items-center justify-center shadow-sm">
                                <span className="material-symbols-outlined text-base xl:text-base">gavel</span>
                            </div>
                            <div className="rounded-2xl xl:rounded-lg rounded-bl-sm xl:rounded-bl-lg px-4 py-3 xl:px-4 xl:py-4 bg-luna-bg-light dark:bg-luna-bg-dark border border-luna-text-light/5 dark:border-luna-text-dark/10 shadow-sm">
                                <div className="flex items-center gap-2.5">
                                    <span className="text-sm xl:text-sm text-luna-text-light/70 dark:text-luna-text-muted-dark font-medium">LRDEnE is thinking</span>
                                    <div className="flex gap-1.5">
                                        <span className="w-2 h-2 bg-luna-secondary rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                                        <span className="w-2 h-2 bg-luna-secondary rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                                        <span className="w-2 h-2 bg-luna-secondary rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    <div ref={messagesEndRef} />
                </div>
                {/* Mobile-optimized input area - fixed at bottom */}
                <div className="p-4 xl:p-4 border-t border-luna-text-light/10 dark:border-luna-text-dark/20 mt-auto shrink-0 bg-white dark:bg-luna-primary sticky bottom-0 z-10">
                    {/* Legal Disclaimer for AI Chat - GDPR and Legal Services Act compliant */}
                    <div className="mb-3 xl:mb-3 p-3 xl:p-3 bg-red-50 dark:bg-red-900/30 border-2 border-red-300 dark:border-red-700 rounded-lg">
                        <p className="text-[11px] xl:text-xs text-red-900 dark:text-red-200 leading-relaxed font-medium">
                            <strong>⚠️ WICHTIGER RECHTSHINWEIS:</strong> LRDEnE ist KEINE Anwaltskanzlei und bietet KEINE Rechtsberatung im Sinne des Rechtsdienstleistungsgesetzes (RDG).
                            Die AI-generierten Antworten, Analysen und Dokumente dienen ausschließlich zu Informations- und Unterstützungszwecken und stellen keine Rechtsberatung dar.
                            Es besteht KEIN Anwaltsmandatsverhältnis. Für verbindliche Rechtsberatung konsultieren Sie bitte einen qualifizierten Rechtsanwalt.
                            <a href="/terms.html" target="_blank" rel="noopener noreferrer" className="underline hover:text-red-700 dark:hover:text-red-300 ml-1">AGB</a> |
                            <a href="/legal" className="underline hover:text-red-700 dark:hover:text-red-300 ml-1">Haftungsausschluss</a>
                        </p>
                    </div>
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileSelect}
                        className="hidden"
                    />
                    <div className="relative">
                        <textarea
                            className="w-full bg-luna-bg-light dark:bg-luna-bg-dark border-2 border-luna-text-light/20 dark:border-luna-text-dark/30 rounded-2xl xl:rounded-lg p-4 xl:p-4 pr-16 xl:pr-24 focus:ring-2 focus:ring-luna-secondary focus:border-luna-secondary transition-all text-[15px] xl:text-sm leading-relaxed min-h-[52px] xl:min-h-[80px] max-h-[120px] xl:max-h-none resize-none shadow-inner"
                            placeholder="Type your message here..."
                            rows={2}
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    handleSendMessage(e);
                                }
                            }}
                        ></textarea>
                        <div className="absolute right-2.5 xl:right-2 bottom-2.5 xl:bottom-2 flex items-center gap-2 xl:gap-1">
                            <button
                                onClick={handleAttachmentClick}
                                className="p-2.5 xl:p-2 rounded-xl xl:rounded-lg hover:bg-luna-text-light/10 dark:hover:bg-luna-text-dark/20 active:bg-luna-text-light/20 dark:active:bg-luna-text-dark/30 transition-colors min-h-[44px] xl:min-h-[40px] min-w-[44px] xl:min-w-[40px] flex items-center justify-center touch-manipulation"
                                aria-label="Attach file"
                            >
                                <span className="material-symbols-outlined text-xl xl:text-base">attach_file</span>
                            </button>
                            <button
                                type="button"
                                className="flex items-center justify-center gap-1.5 xl:gap-2 h-11 xl:h-10 px-4 xl:px-4 bg-luna-secondary text-luna-primary text-sm font-bold rounded-xl xl:rounded-lg hover:opacity-90 active:opacity-80 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] xl:min-h-[40px] shadow-md xl:shadow-sm touch-manipulation"
                                onClick={handleSendMessage}
                                disabled={isWaitingForAI || input.trim() === ''}
                                aria-label="Send message"
                            >
                                {isWaitingForAI ? (
                                    <>
                                        <svg className="animate-spin h-5 w-5 xl:h-4 xl:w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        <span className="hidden xl:inline">Processing...</span>
                                    </>
                                ) : (
                                    <>
                                        <span className="hidden xl:inline">Send</span>
                                        <span className="material-symbols-outlined text-xl xl:text-base">send</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            {/* Right-hand side panel for profile and documents */}
            <div className="hidden xl:flex xl:col-span-1 flex-col gap-3 sm:gap-4 order-2 xl:order-2">
                <div className="bg-white dark:bg-luna-primary rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 shadow-sm p-4 sm:p-6 shrink-0">
                    <h3 className="text-base sm:text-lg font-bold">Your Profile</h3>
                    <div className="mt-3 sm:mt-4 flex flex-col items-center">
                        <img
                            alt={getAvatarAlt(getProfileFullName(profile))}
                            className="size-20 sm:size-24 rounded-full object-cover ring-2 sm:ring-4 ring-luna-secondary/20"
                            src={profile?.avatar_url || "https://lh3.googleusercontent.com/aida-public/AB6AXuC6oq33dNp6A-9NrO3YTfoAiNzhgWchFlJuFX0rTR88Ia5vLnjO8OS23dxBfNeB2iFSmP6TfzlTtpIZJPYNQ3aD2g5i5Td_5WlHtD9f8MXuQWh5pUE_Lp26Ayht0Pu3FHP34E3pEN8WfbEA5P2GZzoXMyfkpTBcDhl3WQ56kuDkn-RAOr2xCQpVII4BEDRoLmjtNSthqKVF4uReXtrxQlDVWbQZG_0U2MY9G0Z5smvb9KvpYrbwQhi4-G6kFaqf-OskjR16cIS0_ZaS"}
                            onError={handleImageError}
                        />
                        <h4 className="mt-3 sm:mt-4 text-lg sm:text-xl font-bold">{displayName}</h4>
                        <p className="text-xs sm:text-sm text-luna-text-light/70 dark:text-luna-text-muted-dark">{displayFirm}</p>
                        <button className="mt-3 sm:mt-4 flex min-w-[84px] max-w-full cursor-pointer items-center justify-center overflow-hidden rounded-lg h-11 sm:h-10 px-4 bg-luna-bg-light/10 dark:bg-luna-text-dark/10 text-sm font-bold leading-normal tracking-[0.015em] hover:bg-luna-bg-light/20 dark:hover:bg-luna-text-dark/20 active:bg-luna-bg-light/30 dark:active:bg-luna-text-dark/30 transition-colors w-full min-h-[44px] sm:min-h-[40px] touch-manipulation">
                            <span className="truncate">Edit Profile</span>
                        </button>
                    </div>
                </div>
                <div className="bg-white dark:bg-luna-primary rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 shadow-sm p-4 sm:p-6 flex-1">
                    <div className="flex justify-between items-center">
                        <h3 className="text-base sm:text-lg font-bold">Uploaded Documents</h3>
                        <button className="p-2 rounded-full hover:bg-luna-text-light/10 dark:hover:bg-luna-text-dark/20 active:bg-luna-text-light/20 dark:active:bg-luna-text-dark/30 transition-colors min-h-[44px] sm:min-h-[40px] min-w-[44px] sm:min-w-[40px] flex items-center justify-center touch-manipulation" aria-label="Add new document">
                            <span className="material-symbols-outlined text-lg sm:text-base">add</span>
                        </button>
                    </div>
                    <div className="mt-3 sm:mt-4 space-y-2 sm:space-y-3">
                        {loadingDocuments ? (
                            <div className="flex items-center justify-center py-6 sm:py-8">
                                <span className="text-xs sm:text-sm text-luna-text-light/60 dark:text-luna-text-muted-dark">Loading documents...</span>
                            </div>
                        ) : documents.length === 0 ? (
                            <EmptyDocuments onUpload={() => fileInputRef.current?.click()} />
                        ) : (
                            documents.map((doc) => {
                                const uploadedDate = new Date(doc.uploaded_at);
                                const now = new Date();
                                const daysDiff = Math.floor((now.getTime() - uploadedDate.getTime()) / (1000 * 60 * 60 * 24));
                                let timeAgo = '';
                                if (daysDiff === 0) timeAgo = 'Today';
                                else if (daysDiff === 1) timeAgo = '1 day ago';
                                else if (daysDiff < 7) timeAgo = `${daysDiff} days ago`;
                                else if (daysDiff < 30) timeAgo = `${Math.floor(daysDiff / 7)} weeks ago`;
                                else timeAgo = `${Math.floor(daysDiff / 30)} months ago`;

                                const fileSizeKB = Math.round(doc.file_size / 1024);
                                const fileSizeMB = Math.round((doc.file_size / 1024 / 1024) * 10) / 10;
                                const fileSizeDisplay = doc.file_size > 1024 * 1024 ? `${fileSizeMB} MB` : `${fileSizeKB} KB`;

                                return (
                                    <div key={doc.id} className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg bg-luna-bg-light dark:bg-luna-bg-dark hover:bg-luna-text-dark/5 dark:hover:bg-luna-text-dark/10 active:bg-luna-text-dark/10 dark:active:bg-luna-text-dark/20 transition-colors touch-manipulation">
                                        <span className="material-symbols-outlined text-luna-secondary text-lg sm:text-base flex-shrink-0">description</span>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-xs sm:text-sm font-medium truncate">{doc.name}</p>
                                            <p className="text-xs text-luna-text-light/70 dark:text-luna-text-muted-dark">
                                                {timeAgo} • {fileSizeDisplay}
                                            </p>
                                        </div>
                                        <button
                                            className="p-2 sm:p-1 rounded-full hover:bg-luna-text-light/10 dark:hover:bg-luna-text-dark/20 active:bg-luna-text-light/20 dark:active:bg-luna-text-dark/30 transition-colors min-h-[44px] sm:min-h-[32px] min-w-[44px] sm:min-w-[32px] flex items-center justify-center touch-manipulation flex-shrink-0"
                                            aria-label={`Download ${doc.name}`}
                                            onClick={async () => {
                                                try {
                                                    // For private buckets, use createSignedUrl for download
                                                    const { data: signedUrlData, error: signedUrlError } = await supabase.storage
                                                        .from('documents')
                                                        .createSignedUrl(doc.file_path || '', 3600); // 1 hour expiry

                                                    if (signedUrlError) {
                                                        logger.error('Error creating signed URL:', signedUrlError);
                                                        // Fallback: try public URL (might not work for private bucket)
                                                        const { data: publicUrlData } = supabase.storage.from('documents').getPublicUrl(doc.file_path || '');
                                                        if (publicUrlData?.publicUrl) {
                                                            window.open(publicUrlData.publicUrl, '_blank');
                                                        } else {
                                                            toast.error('Unable to download file. Please try again.');
                                                        }
                                                    } else if (signedUrlData?.signedUrl) {
                                                        window.open(signedUrlData.signedUrl, '_blank');
                                                    }
                                                } catch (error) {
                                                    logger.error('Error downloading file:', error);
                                                    toast.error('Unable to download file. Please try again.');
                                                }
                                            }}
                                        >
                                            <span className="material-symbols-outlined text-lg sm:text-base">download</span>
                                        </button>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
});

CaseChatView.displayName = 'CaseChatView';

const DocumentsPageView: React.FC<{ profile?: Profile | null; session: Session }> = ({ profile, session: _session }) => {
    const { t } = useTranslation();
    const [analysisState, setAnalysisState] = useState<'idle' | 'analyzing' | 'complete'>('idle');
    const [file, setFile] = useState<File | null>(null);
    const [fileUrl, setFileUrl] = useState<string | null>(null);
    const [numPages, setNumPages] = useState<number | null>(null);
    const [currentAnalysis, setCurrentAnalysis] = useState<DocumentAnalysisState | null>(null);
    const [analysisSuggestions, setAnalysisSuggestions] = useState<string[]>([]);
    const [selectedFinding, setSelectedFinding] = useState<string | number | null>(null);
    const pdfContainerRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const handleKeyboardActivation = (event: React.KeyboardEvent, callback: () => void) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            callback();
        }
    };

    useEffect(() => {
        if (!file) {
            return;
        }

        const url = URL.createObjectURL(file);
        setFileUrl(url);

        return () => {
            URL.revokeObjectURL(url);
        };
    }, [file]);

    // Helper function to check subscription limits before file upload
    // Used by both handleFileChange and handleDrop to prevent bypassing limits via drag-and-drop
    const checkFileUploadLimits = async (file: File): Promise<boolean> => {
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        if (!currentSession?.user) {
            return true; // Allow if not authenticated (will be checked elsewhere)
        }

        // Fetch profile with subscription if not already available
        let planName = 'free';
        if (profile?.subscription?.plan_name) {
            planName = profile.subscription.plan_name;
        } else {
            // Fallback: fetch subscription if profile doesn't have it
            const { data: profileData } = await supabase
                .from('profiles')
                .select('subscription_id')
                .eq('id', currentSession.user.id)
                .single();

            if (profileData?.subscription_id) {
                const { data: subData } = await supabase
                    .from('subscriptions')
                    .select('plan_name, stripe_price_id')
                    .eq('id', profileData.subscription_id)
                    .single();
                planName = normalizePlanName(subData?.plan_name, subData?.stripe_price_id);
            }
        }

        const limitCheck = await canUploadDocument(file, planName);

        if (!limitCheck.allowed) {
            toast.error(limitCheck.reason || 'This document exceeds your plan limits. Please upgrade.');
            // Show upgrade prompt
            setTimeout(() => {
                showUpgradeToast(
                    'Upgrade to analyze larger documents',
                    () => { window.location.href = '/pricing'; },
                    'Larger Document Analysis'
                );
            }, 1000);
            return false;
        }

        return true;
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files && files[0]) {
            const file = files[0];

            try {
                // Check subscription limits before allowing file selection
                const allowed = await checkFileUploadLimits(file);
                if (!allowed) {
                    // Reset file input if limit check failed
                    event.target.value = '';
                    return;
                }

                setFile(file);
            } catch (error) {
                console.error('Error checking file upload limits:', error);
                toast.error('An error occurred while checking file limits. Please try again.');
                // Reset file input on error
                event.target.value = '';
            }
        }
    };

    const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        const files = event.dataTransfer.files;

        if (!files || !files[0]) {
            return;
        }

        const file = files[0];

        // Validate file type
        if (file.type !== 'application/pdf') {
            toast.warning("Please drop a PDF file.");
            return;
        }

        try {
            // Check subscription limits before allowing file drop (prevents bypassing limits)
            const allowed = await checkFileUploadLimits(file);
            if (!allowed) {
                return; // Error already shown by checkFileUploadLimits
            }

            // All checks passed, set the file
            setFile(file);
        } catch (error) {
            console.error('Error checking file upload limits:', error);
            toast.error('An error occurred while checking file limits. Please try again.');
        }
    };

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
    };

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileAnalysis = async () => {
        if (!file) {
            toast.warning("Please select a file first.");
            return;
        }

        setAnalysisState('analyzing');

        try {
            // First, ensure the file is uploaded to Supabase Storage
            const { data: { session } } = await supabase.auth.getSession();
            if (!session?.user) {
                throw new Error('Not authenticated');
            }

            // Upload file if not already uploaded (check if we have fileUrl from upload)
            let documentRecord = null;
            let fileStoragePath = fileUrl?.replace(window.location.origin, '');

            // If file is not in storage, upload it
            if (!fileUrl || !fileUrl.includes('supabase')) {
                const fileName = `${session.user.id}/documents/${Date.now()}_${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;

                const { error: uploadError } = await supabase.storage
                    .from('documents')
                    .upload(fileName, file, {
                        cacheControl: '3600',
                        upsert: false
                    });

                if (uploadError) {
                    // Check if storage bucket doesn't exist
                    if (uploadError.message?.includes('Bucket not found') || uploadError.message?.includes('storage')) {
                        throw new Error('Storage bucket "documents" not found. Please create it in Supabase Dashboard → Storage, or document upload is unavailable.');
                    }
                    throw new Error(`Upload failed: ${uploadError.message}`);
                }

                // For private buckets, use storage path instead of public URL
                // The Edge Function can access private files using storageBucket + storagePath
                fileStoragePath = fileName; // Store path for storage method

                // Save document record
                // First verify the user is authenticated
                const { data: { user: currentUser } } = await supabase.auth.getUser();

                if (!currentUser || currentUser.id !== session.user.id) {
                    throw new Error('Authentication error: Please refresh and try again.');
                }

                const { data: docData, error: docError } = await supabase
                    .from('documents')
                    .insert({
                        user_id: session.user.id,
                        name: file.name,
                        file_path: fileName,
                        file_size: file.size,
                        file_type: file.type,
                    })
                    .select()
                    .single();

                if (docError) {
                    logger.error('Error saving document:', docError);
                    // Provide more specific error message
                    if (docError.code === '42501' || docError.message?.includes('row-level security')) {
                        throw new Error(
                            'Permission denied: Documents table RLS policies may not be configured. ' +
                            'Please run the database migrations in Supabase SQL Editor. ' +
                            'See QUICK_DATABASE_SETUP.md for instructions.'
                        );
                    }
                    throw new Error(`Failed to save document: ${docError.message}`);
                } else {
                    documentRecord = docData;
                }
            }

            // Call AI analysis Edge Function
            // Use fetch directly to get better error details from response body
            const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
            const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

            let analysisResponse: AnalyzeDocumentResult = null;
            let analysisError: Error | null = null;
            let actualError = 'Unknown error';
            let errorDetails = '';

            const buildRequestBody = (): AnalyzeDocumentRequest => {
                const shouldUseStoragePath = (!fileUrl || !fileUrl.includes('supabase.co')) && Boolean(fileStoragePath);
                if (shouldUseStoragePath && fileStoragePath) {
                    return {
                        documentId: documentRecord?.id || 'temp',
                        userId: session.user.id,
                        fileName: file.name,
                        storageBucket: 'documents',
                        storagePath: fileStoragePath,
                    };
                }

                return {
                    documentId: documentRecord?.id || 'temp',
                    userId: session.user.id,
                    fileName: file.name,
                    fileUrl: fileStoragePath || fileUrl,
                };
            };

            const requestBody = buildRequestBody();

            try {
                // Enhanced error handling for Edge Function invocation
                let result;
                try {
                    result = await supabase.functions.invoke<DocumentAnalysisResponse | string>('analyze-document', {
                        body: requestBody,
                    });
                } catch (invokeError) {
                    logger.error('Error invoking Edge Function:', invokeError);
                    // Check if function is deployed
                    if (invokeError instanceof Error && (
                        invokeError.message.includes('404') ||
                        invokeError.message.includes('not found') ||
                        invokeError.message.includes('Function not found')
                    )) {
                        throw new Error('Document analysis function is not deployed. Please deploy the "analyze-document" Edge Function in Supabase Dashboard.');
                    }
                    throw invokeError;
                }

                analysisResponse = result.data;
                analysisError = result.error;

                // If there's an error, try to fetch the raw response to get error body
                if (analysisError) {
                    logger.error('Edge Function error:', analysisError);
                    logger.error('Full error details:', JSON.stringify(analysisError, null, 2));

                    // Try to get the raw response body using fetch
                    try {
                        const { data: { session: currentSession } } = await supabase.auth.getSession();
                        const rawResponse = await fetch(`${supabaseUrl}/functions/v1/analyze-document`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${currentSession?.access_token || supabaseAnonKey}`,
                                'apikey': supabaseAnonKey,
                            },
                            body: JSON.stringify(requestBody),
                        });

                        // Read the error response body
                        const errorBody = await rawResponse.text();
                        console.log('Raw error response body:', errorBody);

                        try {
                            const parsedError = JSON.parse(errorBody) as { error?: unknown; details?: unknown };
                            if (parsedError.error) actualError = String(parsedError.error);
                            if (parsedError.details) errorDetails = String(parsedError.details);
                        } catch {
                            // Not JSON, might be plain text
                            actualError = errorBody || analysisError.message || 'Unknown error';
                        }
                    } catch (fetchError) {
                        console.warn('Failed to fetch raw error response:', fetchError);
                        // Fall back to extracting from Supabase error object
                        actualError = analysisError.message || 'Unknown error';
                    }
                }
            } catch (invokeError) {
                logger.error('Error invoking Edge Function:', invokeError);
                const normalizedError = toError(invokeError);
                analysisError = normalizedError;
                actualError = normalizedError.message;
            }

            if (analysisError || !analysisResponse) {
                // Try to extract error from response data if available
                if (analysisResponse) {
                    if (isDocumentAnalysisResponse(analysisResponse)) {
                        // Handle object response
                        if ('error' in analysisResponse) {
                            actualError = String(analysisResponse.error);
                        }
                        if ('details' in analysisResponse) {
                            errorDetails = String(analysisResponse.details);
                        }
                    } else if (typeof analysisResponse === 'string' && analysisResponse.length > 0) {
                        // Handle string response - try to parse as JSON
                        try {
                            const parsed = JSON.parse(analysisResponse) as { error?: unknown; details?: unknown };
                            if (parsed.error) actualError = String(parsed.error);
                            if (parsed.details) errorDetails = String(parsed.details);
                        } catch {
                            // Not JSON, use string directly
                            actualError = analysisResponse;
                        }
                    }
                }

                // Check error context for more details
                if (hasFunctionContext(analysisError) && analysisError.context) {
                    const { context } = analysisError;
                    if (context?.message) actualError = context.message;
                    if (context?.body) {
                        try {
                            const bodyError = typeof context.body === 'string' ? JSON.parse(context.body) : context.body;
                            if (typeof bodyError === 'object' && bodyError !== null) {
                                const parsedBody = bodyError as { error?: unknown; details?: unknown };
                                if (parsedBody.error) actualError = String(parsedBody.error);
                                if (parsedBody.details) errorDetails = String(parsedBody.details);
                            }
                        } catch {
                            // Ignore parsing errors
                        }
                    }
                }

                // Provide specific error messages based on common issues
                if (actualError.includes('not found') || actualError.includes('404')) {
                    throw new Error(
                        'Document analysis function not deployed. ' +
                        'Please deploy the "analyze-document" Edge Function in Supabase Dashboard → Edge Functions. ' +
                        'See FIX_ANALYSIS_ERROR.md for step-by-step instructions.'
                    );
                }

                if (actualError.includes('Failed to send a request') || actualError.includes('fetch')) {
                    throw new Error(
                        'Cannot reach analysis service. The Edge Function may not be deployed or the URL is incorrect. ' +
                        'Please check: Supabase Dashboard → Edge Functions → analyze-document'
                    );
                }

                if (actualError.includes('Gemini API key not configured') || errorDetails.includes('GEMINI_API_KEY')) {
                    throw new Error(
                        'Gemini API key not configured. ' +
                        'Please set GEMINI_API_KEY secret in Supabase Dashboard → Edge Functions → Settings → Secrets. ' +
                        'See FIX_ANALYSIS_ERROR.md for instructions.'
                    );
                }

                if (actualError.includes('Document not found') || actualError.includes('access denied')) {
                    throw new Error(
                        'Document not found in database. Make sure you upload the file first before analyzing. ' +
                        'The document record may not have been created properly.'
                    );
                }

                if (actualError.includes('Failed to download document')) {
                    throw new Error(
                        'Failed to download document from storage. ' +
                        'Please verify the file exists in the storage bucket and try uploading again.'
                    );
                }

                if (actualError.includes('AI analysis service unavailable') || errorDetails.includes('Gemini')) {
                    throw new Error(
                        'AI service temporarily unavailable. ' +
                        'Please check the Edge Function logs and verify GEMINI_API_KEY is correct. ' +
                        'See: Supabase Dashboard → Edge Functions → analyze-document → Logs'
                    );
                }

                // If we still don't have a meaningful error, provide guidance
                if (actualError === 'Unknown error' || actualError.includes('non-2xx status code')) {
                    throw new Error(
                        'Edge Function returned an error. The actual error message is not available in the response. ' +
                        'Please check the Edge Function logs in Supabase Dashboard to see the exact error:\n\n' +
                        '1. Go to: https://app.supabase.com/project/naognwyxpinkrwtpodix/functions/analyze-document/logs\n' +
                        '2. Look for the most recent error log entry\n' +
                        '3. The log will show the actual error message\n\n' +
                        'Common issues:\n' +
                        '- Missing GEMINI_API_KEY secret\n' +
                        '- Document not found in database\n' +
                        '- Storage bucket or file not accessible\n' +
                        '- Invalid API keys or configuration\n\n' +
                        'See CHECK_EDGE_FUNCTION_LOGS.md for detailed troubleshooting steps.'
                    );
                }

                // Show the actual error with details if available
                const fullError = errorDetails ? `${actualError}. Details: ${errorDetails}` : actualError;
                throw new Error(`Analysis failed: ${fullError}`);
            }

            if (!analysisResponse) {
                throw new Error('Analysis service returned no response. The Edge Function may not be deployed.');
            }

            if (!isDocumentAnalysisResponse(analysisResponse)) {
                throw new Error('Analysis service returned an invalid response format. Please try again.');
            }

            // Check if response contains an error
            if (analysisResponse.error) {
                logger.error('Edge Function returned error:', analysisResponse.error);

                if (analysisResponse.error.includes('Gemini API key not configured')) {
                    throw new Error(
                        'Gemini API key not configured. ' +
                        'Please set GEMINI_API_KEY secret in Supabase Dashboard → Edge Functions → Settings → Secrets. ' +
                        'See FIX_ANALYSIS_ERROR.md for instructions.'
                    );
                }

                throw new Error(`Analysis failed: ${analysisResponse.error}`);
            }

            if (!analysisResponse.analysis) {
                throw new Error('Analysis service returned incomplete data. Please try again.');
            }

            // Parse the enhanced analysis response
            const analysisData = analysisResponse.analysis;

            // Convert analysis to the expected format with enhanced features
            const formattedAnalysis: DocumentAnalysisState = {
                summary: analysisData.summary || 'Analysis completed.',
                overallRisk: normalizeRiskLevel(analysisData.overallRisk),
                riskHeatmap: {
                    low: typeof analysisData.riskHeatmap?.low === 'number' ? analysisData.riskHeatmap.low : 0,
                    medium: typeof analysisData.riskHeatmap?.medium === 'number' ? analysisData.riskHeatmap.medium : 0,
                    high: typeof analysisData.riskHeatmap?.high === 'number' ? analysisData.riskHeatmap.high : 0,
                },
                findings: (analysisData.findings ?? []).map((finding, index) => {
                    const applicableLaws = finding?.applicableLaws;
                    return {
                        id: finding?.id ?? `finding-${index}-${Date.now()}`,
                        clause: finding?.clause || 'Unknown clause',
                        page: typeof finding?.page === 'number' ? finding.page : 1,
                        risk: normalizeRiskLevel(finding?.risk),
                        finding: finding?.finding || finding?.summary || '',
                        suggestion: finding?.suggestion || '',
                        applicableLaws: Array.isArray(applicableLaws) ? applicableLaws : [],
                        priority: normalizePriorityLevel(finding?.priority),
                        summary: finding?.summary,
                    };
                }),
                compliance: {
                    gdpr: normalizeComplianceStatus(analysisData.compliance?.gdpr),
                    euAIAct: normalizeComplianceStatus(analysisData.compliance?.euAIAct),
                    germanCivilCode: normalizeComplianceStatus(analysisData.compliance?.germanCivilCode),
                }
            };

            setCurrentAnalysis(formattedAnalysis);

            // Store suggestions if available
            if (isDocumentAnalysisResponse(analysisResponse) && analysisResponse.suggestions && Array.isArray(analysisResponse.suggestions)) {
                setAnalysisSuggestions(analysisResponse.suggestions);
            } else {
                setAnalysisSuggestions([]);
            }

            setAnalysisState('complete');

        } catch (error) {
            logger.error('Analysis error:', error);
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            toast.error(`Analysis failed: ${errorMessage}`);
            setAnalysisState('idle');
            // DO NOT show fallback preset - let user know it's a real error
            // The Edge Function is real and will work when properly deployed and configured
            // Only show preset analysis if explicitly requested by user
        }
    };

    const handleNewAnalysis = () => {
        setAnalysisState('idle');
        setFile(null);
        setFileUrl(null);
        setNumPages(null);
    };

    function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
        setNumPages(numPages);
    }

    const handleFindingClick = (finding: DocumentAnalysisFinding) => {
        setSelectedFinding(finding.id);
        try {
            if (finding.page && pdfContainerRef.current) {
                const pageElement = pdfContainerRef.current.querySelector(`.react-pdf__Page[data-page-number="${finding.page}"]`);
                if (pageElement) {
                    pageElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }
        } catch (error) {
            logger.warn('Could not scroll to finding page:', error);
        }
    };

    const riskColorMap: Record<RiskLevel, string> = {
        low: 'border-green-500 bg-green-50 dark:bg-green-950/20',
        medium: 'border-yellow-500 bg-yellow-50 dark:bg-yellow-950/20',
        high: 'border-red-500 bg-red-50 dark:bg-red-950/20',
    };

    const priorityColorMap: Record<PriorityLevel, string> = {
        critical: 'text-red-700 dark:text-red-400 bg-red-100 dark:bg-red-950/30',
        high: 'text-orange-700 dark:text-orange-400 bg-orange-100 dark:bg-orange-950/30',
        medium: 'text-yellow-700 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-950/30',
        low: 'text-blue-700 dark:text-blue-400 bg-blue-100 dark:bg-blue-950/30',
    };

    const overallRiskColorMap: Record<RiskLevel, string> = {
        low: 'bg-green-500 text-white',
        medium: 'bg-yellow-500 text-white',
        high: 'bg-red-500 text-white',
    };

    if (analysisState === 'idle') {
        return (
            <div className="flex flex-col flex-1 p-4 bg-luna-bg-light dark:bg-luna-bg-dark items-center justify-center">
                <div className="w-full max-w-2xl text-center">
                    <h2 className="text-3xl md:text-4xl font-black leading-tight tracking-[-0.033em]">{t('documents.documentAnalysis')}</h2>
                    <p className="mt-4 text-base font-normal leading-normal text-luna-text-light/70 dark:text-luna-text-muted-dark">{t('documents.documentDescription')}</p>
                    <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/30 border-2 border-red-300 dark:border-red-700 rounded-lg">
                        <p className="text-sm text-red-900 dark:text-red-200 leading-relaxed font-medium">
                            <strong>⚠️ WICHTIGER RECHTSHINWEIS:</strong> LRDEnE ist KEINE Anwaltskanzlei und bietet KEINE Rechtsberatung im Sinne des Rechtsdienstleistungsgesetzes (RDG).
                            Die AI-Dokumentenanalyse dient ausschließlich zu Informationszwecken und stellt keine Rechtsberatung dar.
                            Es besteht KEIN Anwaltsmandatsverhältnis. Für verbindliche Rechtsberatung konsultieren Sie bitte einen qualifizierten Rechtsanwalt.
                            <a href="/terms.html" target="_blank" rel="noopener noreferrer" className="underline hover:text-red-700 dark:hover:text-red-300 ml-1">AGB</a> |
                            <a href="/legal" className="underline hover:text-red-700 dark:hover:text-red-300 ml-1">Haftungsausschluss</a>
                        </p>
                    </div>
                    <div
                        className="mt-8 w-full max-w-lg mx-auto p-6 rounded-xl bg-white dark:bg-luna-primary shadow-sm border-2 border-dashed border-luna-text-light/20 dark:border-luna-text-dark/20 text-center cursor-pointer"
                        onClick={handleUploadClick}
                        onDrop={handleDrop}
                        onDragOver={handleDragOver}
                        role="button"
                        tabIndex={0}
                        aria-label={file ? `Selected file ${file.name}` : 'Upload PDF document'}
                        onKeyDown={(event) => handleKeyboardActivation(event, handleUploadClick)}
                    >
                        <span className="material-symbols-outlined text-5xl text-luna-secondary">upload_file</span>
                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileChange}
                            className="hidden"
                            accept="application/pdf"
                        />
                        {file ? (
                            <div>
                                <p className="text-lg font-semibold mt-4">{file.name}</p>
                                <p className="text-xs text-luna-text-light/70 dark:text-luna-text-muted-dark">{(file.size / 1024).toFixed(2)} KB</p>
                            </div>
                        ) : (
                            <div>
                                <h3 className="text-xl font-bold mt-4">{t('documents.uploadDocument')}</h3>
                                <p className="text-sm text-luna-text-light/70 dark:text-luna-text-muted-dark mt-1">{t('documents.selectFile')} (PDF)</p>
                            </div>
                        )}
                    </div>
                    <button
                        className="mt-6 bg-luna-secondary text-luna-primary px-6 py-2 rounded-lg text-base font-bold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={handleFileAnalysis}
                        disabled={!file}
                    >
                        {t('documents.analyzeDocument')}
                    </button>
                </div>
            </div>
        );
    }

    if (analysisState === 'analyzing') {
        return (
            <div className="flex flex-col flex-1 items-center justify-center bg-luna-bg-light dark:bg-luna-bg-dark">
                <div className="flex items-center gap-4 text-xl font-semibold">
                    <svg className="animate-spin h-8 w-8 text-luna-secondary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>{t('documents.analyzing')}</span>
                </div>
                <p className="mt-4 text-sm text-luna-text-light/70 dark:text-luna-text-muted-dark">Cross-referencing with the latest EU directives and regulations.</p>
            </div>
        );
    }

    return (
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-4 p-4 overflow-hidden">
            {/* PDF Viewer Panel */}
            <div className="flex flex-col bg-luna-bg-light dark:bg-luna-bg-dark rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 shadow-sm overflow-hidden">
                <div className="p-3 border-b border-luna-text-light/10 dark:border-luna-text-dark/20 shrink-0 flex items-center justify-between">
                    <h2 className="text-lg font-bold truncate">{file?.name || 'Document'}</h2>
                    <button onClick={handleNewAnalysis} className="flex items-center gap-2 text-sm font-medium text-luna-text-light/80 dark:text-luna-text-dark/80 hover:text-luna-secondary transition-colors">
                        <span className="material-symbols-outlined">add</span>
                        New Analysis
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto p-4" ref={pdfContainerRef}>
                    <Document file={fileUrl} onLoadSuccess={onDocumentLoadSuccess}>
                        {Array.from({ length: numPages ?? 0 }, (_, index) => (
                            <React.Fragment key={`page_${index + 1}`}>
                                <Page pageNumber={index + 1} width={pdfContainerRef.current?.clientWidth ? pdfContainerRef.current.clientWidth - 32 : undefined} />
                            </React.Fragment>
                        ))}
                    </Document>
                </div>
            </div>

            {/* Analysis Panel */}
            <div className="flex flex-col bg-white dark:bg-luna-primary rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 shadow-sm overflow-hidden">
                <div className="p-3 border-b border-luna-text-light/10 dark:border-luna-text-dark/20 shrink-0">
                    <h2 className="text-lg font-bold">AI Analysis</h2>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-6">
                    {currentAnalysis ? (
                        <>
                            <div>
                                <h3 className="font-bold mb-2 text-sm uppercase tracking-wider text-luna-text-light/70 dark:text-luna-text-muted-dark">Overall Summary</h3>
                                <p className="text-sm p-3 bg-luna-bg-light dark:bg-luna-bg-dark rounded-lg">{currentAnalysis.summary}</p>
                            </div>
                            {/* Overall Risk Badge */}
                            {currentAnalysis.overallRisk && (
                                <div className="mb-4">
                                    <h3 className="font-bold mb-2 text-sm uppercase tracking-wider text-luna-text-light/70 dark:text-luna-text-muted-dark">Overall Risk Level</h3>
                                    <div className={`inline-block px-4 py-2 rounded-lg font-bold text-sm ${overallRiskColorMap[currentAnalysis.overallRisk as keyof typeof overallRiskColorMap] || overallRiskColorMap.medium}`}>
                                        {currentAnalysis.overallRisk.toUpperCase()} RISK
                                    </div>
                                </div>
                            )}

                            {/* Enhanced Risk Heatmap Visualization */}
                            <div>
                                <h3 className="font-bold mb-3 text-sm uppercase tracking-wider text-luna-text-light/70 dark:text-luna-text-muted-dark">Risk Heatmap</h3>
                                <div className="space-y-2">
                                    <div className="flex items-center gap-2">
                                        <div className="w-3 h-3 rounded-full bg-green-500"></div>
                                        <span className="text-xs font-medium text-luna-text-light/70 dark:text-luna-text-muted-dark">Low Risk</span>
                                        <div className="flex-1 bg-luna-bg-light dark:bg-luna-bg-dark rounded-full h-2 overflow-hidden">
                                            <div
                                                className="bg-green-500 h-full transition-all duration-500"
                                                style={{
                                                    width: `${currentAnalysis.riskHeatmap.low + currentAnalysis.riskHeatmap.medium + currentAnalysis.riskHeatmap.high > 0
                                                        ? (currentAnalysis.riskHeatmap.low / (currentAnalysis.riskHeatmap.low + currentAnalysis.riskHeatmap.medium + currentAnalysis.riskHeatmap.high)) * 100
                                                        : 0}%`
                                                }}
                                            ></div>
                                        </div>
                                        <span className="text-xs font-bold text-green-700 dark:text-green-400">{currentAnalysis.riskHeatmap.low}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                                        <span className="text-xs font-medium text-luna-text-light/70 dark:text-luna-text-muted-dark">Medium Risk</span>
                                        <div className="flex-1 bg-luna-bg-light dark:bg-luna-bg-dark rounded-full h-2 overflow-hidden">
                                            <div
                                                className="bg-yellow-500 h-full transition-all duration-500"
                                                style={{
                                                    width: `${currentAnalysis.riskHeatmap.low + currentAnalysis.riskHeatmap.medium + currentAnalysis.riskHeatmap.high > 0
                                                        ? (currentAnalysis.riskHeatmap.medium / (currentAnalysis.riskHeatmap.low + currentAnalysis.riskHeatmap.medium + currentAnalysis.riskHeatmap.high)) * 100
                                                        : 0}%`
                                                }}
                                            ></div>
                                        </div>
                                        <span className="text-xs font-bold text-yellow-700 dark:text-yellow-400">{currentAnalysis.riskHeatmap.medium}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-3 h-3 rounded-full bg-red-500"></div>
                                        <span className="text-xs font-medium text-luna-text-light/70 dark:text-luna-text-muted-dark">High Risk</span>
                                        <div className="flex-1 bg-luna-bg-light dark:bg-luna-bg-dark rounded-full h-2 overflow-hidden">
                                            <div
                                                className="bg-red-500 h-full transition-all duration-500"
                                                style={{
                                                    width: `${currentAnalysis.riskHeatmap.low + currentAnalysis.riskHeatmap.medium + currentAnalysis.riskHeatmap.high > 0
                                                        ? (currentAnalysis.riskHeatmap.high / (currentAnalysis.riskHeatmap.low + currentAnalysis.riskHeatmap.medium + currentAnalysis.riskHeatmap.high)) * 100
                                                        : 0}%`
                                                }}
                                            ></div>
                                        </div>
                                        <span className="text-xs font-bold text-red-700 dark:text-red-400">{currentAnalysis.riskHeatmap.high}</span>
                                    </div>
                                </div>
                            </div>

                            {/* EU Law Compliance Indicators */}
                            {currentAnalysis.compliance && (
                                <div>
                                    <h3 className="font-bold mb-3 text-sm uppercase tracking-wider text-luna-text-light/70 dark:text-luna-text-muted-dark">EU Law Compliance</h3>
                                    <div className="space-y-2">
                                        <div className={`flex items-center justify-between p-2 rounded ${currentAnalysis.compliance.gdpr.compliant ? 'bg-green-50 dark:bg-green-950/20' : 'bg-red-50 dark:bg-red-950/20'}`}>
                                            <span className="text-sm font-medium">GDPR</span>
                                            <span className={`text-xs font-bold px-2 py-1 rounded ${currentAnalysis.compliance.gdpr.compliant ? 'text-green-700 dark:text-green-400 bg-green-100 dark:bg-green-950/30' : 'text-red-700 dark:text-red-400 bg-red-100 dark:bg-red-950/30'}`}>
                                                {currentAnalysis.compliance.gdpr.compliant ? '✓ Compliant' : '✗ Issues Found'}
                                            </span>
                                        </div>
                                        <div className={`flex items-center justify-between p-2 rounded ${currentAnalysis.compliance.euAIAct.compliant ? 'bg-green-50 dark:bg-green-950/20' : 'bg-red-50 dark:bg-red-950/20'}`}>
                                            <span className="text-sm font-medium">EU AI Act</span>
                                            <span className={`text-xs font-bold px-2 py-1 rounded ${currentAnalysis.compliance.euAIAct.compliant ? 'text-green-700 dark:text-green-400 bg-green-100 dark:bg-green-950/30' : 'text-red-700 dark:text-red-400 bg-red-100 dark:bg-red-950/30'}`}>
                                                {currentAnalysis.compliance.euAIAct.compliant ? '✓ Compliant' : '✗ Issues Found'}
                                            </span>
                                        </div>
                                        <div className={`flex items-center justify-between p-2 rounded ${currentAnalysis.compliance.germanCivilCode.compliant ? 'bg-green-50 dark:bg-green-950/20' : 'bg-red-50 dark:bg-red-950/20'}`}>
                                            <span className="text-sm font-medium">German Civil Code (BGB)</span>
                                            <span className={`text-xs font-bold px-2 py-1 rounded ${currentAnalysis.compliance.germanCivilCode.compliant ? 'text-green-700 dark:text-green-400 bg-green-100 dark:bg-green-950/30' : 'text-red-700 dark:text-red-400 bg-red-100 dark:bg-red-950/30'}`}>
                                                {currentAnalysis.compliance.germanCivilCode.compliant ? '✓ Compliant' : '✗ Issues Found'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Actionable Suggestions Section */}
                            {analysisSuggestions.length > 0 && (
                                <div>
                                    <h3 className="font-bold mb-3 text-sm uppercase tracking-wider text-luna-text-light/70 dark:text-luna-text-muted-dark flex items-center gap-2">
                                        <span className="material-symbols-outlined text-lg">tips_and_updates</span>
                                        Actionable Suggestions
                                    </h3>
                                    <div className="space-y-3">
                                        {analysisSuggestions.map((suggestion, index) => (
                                            <div
                                                key={index}
                                                className="p-4 rounded-lg bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800"
                                            >
                                                <p className="text-sm leading-relaxed text-gray-800 dark:text-gray-200">
                                                    {suggestion}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div>
                                <h3 className="font-bold mb-3 text-sm uppercase tracking-wider text-luna-text-light/70 dark:text-luna-text-muted-dark">Key Findings & Suggestions</h3>
                                <div className="space-y-4">
                                    {currentAnalysis.findings.length > 0 ? (
                                        currentAnalysis.findings.map((item) => (
                                            <div
                                                key={item.id}
                                                onClick={() => handleFindingClick(item)}
                                                onKeyDown={(event) => handleKeyboardActivation(event, () => handleFindingClick(item))}
                                                role="button"
                                                tabIndex={0}
                                                aria-pressed={selectedFinding === item.id}
                                                className={`p-4 rounded-lg border-l-4 cursor-pointer transition-all hover:shadow-md ${riskColorMap[item.risk] ?? riskColorMap.medium
                                                    } ${selectedFinding === item.id ? 'ring-2 ring-luna-secondary shadow-md' : ''}`}
                                            >
                                                <div className="flex items-start justify-between gap-2 mb-2">
                                                    <p className="font-semibold text-sm flex-1">{item.clause}</p>
                                                    {item.priority && (
                                                        <span className={`text-xs font-bold px-2 py-1 rounded ${priorityColorMap[item.priority as keyof typeof priorityColorMap] || priorityColorMap.medium}`}>
                                                            {item.priority.toUpperCase()}
                                                        </span>
                                                    )}
                                                </div>

                                                {item.page && (
                                                    <p className="text-xs text-luna-text-light/60 dark:text-luna-text-muted-dark mb-2">
                                                        <span className="material-symbols-outlined inline text-xs align-middle">description</span> Page {item.page}
                                                    </p>
                                                )}

                                                <div className="mt-3 pl-4 border-l-2 border-luna-text-light/10 dark:border-luna-text-dark/20">
                                                    <p className="text-xs font-medium text-luna-text-light/70 dark:text-luna-text-muted-dark mb-1">AI Finding:</p>
                                                    <p className="text-sm mt-1 leading-relaxed">{item.finding || item.summary}</p>

                                                    {item.applicableLaws && item.applicableLaws.length > 0 && (
                                                        <div className="mt-3">
                                                            <p className="text-xs font-medium text-luna-text-light/70 dark:text-luna-text-muted-dark mb-2">Applicable Laws:</p>
                                                            <div className="flex flex-wrap gap-1">
                                                                {item.applicableLaws.map((law: string, idx: number) => (
                                                                    <span
                                                                        key={idx}
                                                                        className="text-xs px-2 py-1 rounded-full bg-luna-secondary/20 text-luna-primary font-medium"
                                                                    >
                                                                        {law}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {item.suggestion && (
                                                        <div className="mt-3 pt-3 border-t border-luna-text-light/10 dark:border-luna-text-dark/20">
                                                            <p className="text-xs font-medium text-luna-text-light/70 dark:text-luna-text-muted-dark mb-1 flex items-center gap-1">
                                                                <span className="material-symbols-outlined text-sm">lightbulb</span> Actionable Suggestion:
                                                            </p>
                                                            <p className="text-sm mt-1 leading-relaxed text-luna-primary dark:text-luna-secondary font-medium">{item.suggestion}</p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="text-center py-8 text-luna-text-light/50 dark:text-luna-text-muted-dark">
                                            <span className="material-symbols-outlined text-4xl mb-2 block">check_circle</span>
                                            <p className="text-sm">No findings identified. Document appears compliant.</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="text-center py-12 text-luna-text-light/50 dark:text-luna-text-muted-dark">
                            <span className="material-symbols-outlined text-6xl mb-4 block">description</span>
                            <p className="text-lg font-semibold mb-2">No Analysis Yet</p>
                            <p className="text-sm mb-4">Upload a document and click &quot;Analyze Document&quot; to get AI-powered legal analysis.</p>
                            <div className="mt-6 p-4 bg-luna-bg-light dark:bg-luna-bg-dark rounded-lg text-left max-w-md mx-auto border border-luna-secondary/20">
                                <p className="text-xs font-bold mb-2 text-luna-secondary flex items-center gap-2">
                                    <span className="material-symbols-outlined text-sm">auto_awesome</span> Real AI Analysis
                                </p>
                                <p className="text-xs text-luna-text-light/70 dark:text-luna-text-muted-dark leading-relaxed">
                                    This feature uses <strong>Google Gemini AI</strong> to analyze your actual documents.
                                    It checks compliance with <strong>GDPR</strong>, <strong>EU AI Act</strong>, <strong>German Civil Code (BGB)</strong>, and other EU regulations.
                                </p>
                                <p className="text-xs text-luna-text-light/60 dark:text-luna-text-muted-dark mt-3 pt-3 border-t border-luna-text-light/10 dark:border-luna-text-dark/20">
                                    The analysis is generated by AI, not a simulator. Your document is sent to Gemini AI for real legal compliance checking.
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// Template form data interface for wizard
interface TemplateFormData {
    [key: string]: string | number | Date;
}

const TemplateWizard: React.FC<{
    template: Template;
    profile: Profile | null;
    onGenerate: (formData: TemplateFormData) => Promise<string>;
    onBack: () => void;
    session: Session | null;
    onSaveTemplate?: (content: string, template: Template) => Promise<void>;
}> = ({ template, profile, onGenerate, onBack, session, onSaveTemplate }) => {
    const [formData, setFormData] = useState<TemplateFormData>({});
    const [generatedContent, setGeneratedContent] = useState<string | null>(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Auto-fill form with profile data when component mounts
    useEffect(() => {
        const autoFill: TemplateFormData = {};
        if (profile) {
            autoFill['Ihr Name'] = profile.first_name && profile.last_name
                ? `${profile.first_name} ${profile.last_name}`
                : profile.first_name || profile.last_name || '';
            autoFill['Ihre Firma'] = profile.firm || '';
            // Note: Address fields would need to be added to Profile interface
        }
        autoFill['Datum'] = new Date().toLocaleDateString('de-DE');
        setFormData(autoFill);
    }, [profile]);

    const handleInputChange = (field: string, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleGenerate = async () => {
        setIsGenerating(true);
        setError(null);
        try {
            const content = await onGenerate(formData);
            setGeneratedContent(content);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to generate template');
        } finally {
            setIsGenerating(false);
        }
    };

    // Define required fields per template
    const getTemplateFields = (slug: string): string[] => {
        const fieldMap: { [key: string]: string[] } = {
            'dsgvo-auskunft': ['Ihr Name', 'Ihre Adresse', 'Name des Unternehmens', 'Adresse des Unternehmens'],
            'dsgvo-loeschung': ['Ihr Name', 'Ihre Adresse', 'Name des Unternehmens', 'Adresse des Unternehmens'],
            'miet-maengelanzeige': ['Ihr Name', 'Ihre Adresse', 'Name des Vermieters/der Hausverwaltung', 'Adresse des Vermieters/der Hausverwaltung', 'Beschreibung der Mängel'],
            'miet-minderung': ['Ihr Name', 'Ihre Adresse', 'Name des Vermieters/der Hausverwaltung', 'Adresse des Vermieters/der Hausverwaltung', 'Prozentsatz', 'Datum der Mängelanzeige'],
            'abmahnung-widerspruch': ['Ihr Name', 'Ihre Adresse', 'Name der abmahnenden Kanzlei/des Unternehmens', 'Adresse', 'Datum der Abmahnung'],
            'schufa-bestreitung': ['Ihr Name', 'Ihre Adresse', 'Genaue Beschreibung des falschen Eintrags'],
            'schufa-loesung': ['Ihr Name', 'Ihre Adresse', 'Name des Gläubigers', 'Datum'],
        };
        return fieldMap[slug] || [];
    };

    const fields = getTemplateFields(template.slug);

    if (generatedContent) {
        return (
            <TemplateView
                template={template}
                content={generatedContent}
                onBack={() => {
                    setGeneratedContent(null);
                    setError(null);
                }}
                onSave={onSaveTemplate ? async (content) => {
                    await onSaveTemplate(content, template);
                } : undefined}
                profile={profile}
                session={session}
                caseId={null}
            />
        );
    }

    return (
        <div className="flex flex-col flex-1 p-4 bg-luna-bg-light dark:bg-luna-bg-dark">
            <div className="flex items-center mb-6">
                <button onClick={onBack} className="flex items-center gap-2 text-sm font-medium text-luna-text-light/80 dark:text-luna-text-dark/80 hover:text-luna-secondary transition-colors">
                    <span className="material-symbols-outlined">arrow_back</span>
                    Back to Templates
                </button>
            </div>
            <div className="bg-white dark:bg-luna-primary rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 shadow-sm p-6 max-w-3xl mx-auto">
                <h2 className="text-2xl font-bold mb-2">{template.title}</h2>
                <p className="text-sm text-luna-text-light/70 dark:text-luna-text-muted-dark mb-6">
                    Fill in the form below to generate a draft {template.title} document for you to review with a lawyer / adjust yourself.
                </p>

                <div className="space-y-4 mb-6">
                    {fields.map((field) => (
                        <div key={field}>
                            <label className="block text-sm font-medium mb-2 text-luna-text-light dark:text-luna-text-dark">
                                {field}
                                {field === 'Datum' && ' (DD.MM.YYYY)'}
                            </label>
                            {field === 'Beschreibung der Mängel' || field === 'Genaue Beschreibung des falschen Eintrags' ? (
                                <textarea
                                    value={formData[field] as string || ''}
                                    onChange={(e) => handleInputChange(field, e.target.value)}
                                    className="w-full px-4 py-2 border border-luna-text-light/20 dark:border-luna-text-dark/20 rounded-lg bg-white dark:bg-luna-bg-dark text-luna-text-light dark:text-luna-text-dark focus:outline-none focus:ring-2 focus:ring-luna-secondary"
                                    rows={4}
                                    placeholder={`Enter ${field.toLowerCase()}`}
                                />
                            ) : (
                                <input
                                    type={field === 'Datum' ? 'date' : 'text'}
                                    value={formData[field] as string || ''}
                                    onChange={(e) => handleInputChange(field, e.target.value)}
                                    className="w-full px-4 py-2 border border-luna-text-light/20 dark:border-luna-text-dark/20 rounded-lg bg-white dark:bg-luna-bg-dark text-luna-text-light dark:text-luna-text-dark focus:outline-none focus:ring-2 focus:ring-luna-secondary"
                                    placeholder={`Enter ${field.toLowerCase()}`}
                                />
                            )}
                        </div>
                    ))}
                </div>

                {error && (
                    <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                    </div>
                )}

                <button
                    onClick={handleGenerate}
                    title="This generates a draft template. Always have a qualified lawyer review before sending."
                    disabled={isGenerating}
                    className="w-full bg-luna-secondary text-luna-primary px-6 py-3 rounded-lg font-bold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                    {isGenerating ? (
                        <>
                            <span className="material-symbols-outlined animate-spin">sync</span>
                            Generating...
                        </>
                    ) : (
                        <>
                            <span className="material-symbols-outlined">auto_awesome</span>
                            Generate Draft Document
                        </>
                    )}
                </button>
                <p className="text-xs text-luna-text-light/60 dark:text-luna-text-dark/60 mt-3 text-center">
                    ⚠️ This generates a draft template. Always have a qualified lawyer review before sending.
                </p>
            </div>
        </div>
    );
};

interface ChatMessage {
    id: string;
    role: 'user' | 'ai';
    content: string;
    timestamp: Date;
}

const TemplateView: React.FC<{
    template: Template;
    content: string;
    onBack: () => void;
    onSave?: (content: string) => Promise<void>;
    profile?: Profile | null;
    session?: Session | null;
    caseId?: string | null;
}> = ({ template, content, onBack, onSave, profile, session, caseId }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editedContent, setEditedContent] = useState(content);
    const [isSaving, setIsSaving] = useState(false);
    const [showAIChat, setShowAIChat] = useState(false);
    const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
    const [chatInput, setChatInput] = useState('');
    const [isAITyping, setIsAITyping] = useState(false);
    const chatEndRef = useRef<HTMLDivElement>(null);
    const chatInputRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
        setEditedContent(content);
    }, [content]);

    useEffect(() => {
        // Scroll chat to bottom when new messages arrive
        try {
            if (chatEndRef.current) {
                chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
            }
        } catch (error) {
            // Silently fail if element is not available
            logger.warn('Could not scroll chat to bottom:', error);
        }
    }, [chatMessages]);

    // Initialize chat with a helpful message
    useEffect(() => {
        if (showAIChat && chatMessages.length === 0) {
            setChatMessages([{
                id: 'welcome',
                role: 'ai',
                content: `Hallo! Ich bin LRDEnE, Ihr KI-Assistent für rechtliche Dokumente. Ich kann Ihnen helfen:

• Den Dokumentinhalt zu vervollständigen
• Rechtliche Formulierungen zu verbessern
• Fehlende Abschnitte hinzuzufügen
• Rechtschreibung und Grammatik zu prüfen
• Das Dokument professioneller zu gestalten

Wie kann ich Ihnen heute helfen?

---

Hello! I'm LRDEnE, your AI assistant for legal documents. I can help you:

• Complete the document content
• Improve legal formulations
• Add missing sections
• Check spelling and grammar
• Make the document more professional

How can I help you today?`,
                timestamp: new Date()
            }]);
        }
    }, [showAIChat, chatMessages.length]);

    const handleAIChat = async () => {
        // Prevent duplicate submissions - check guard FIRST
        if (!chatInput.trim() || !session?.user.id || isAITyping) return;

        const userMessage: ChatMessage = {
            id: `user-${Date.now()}`,
            role: 'user',
            content: chatInput.trim(),
            timestamp: new Date()
        };

        // Set loading state IMMEDIATELY to prevent duplicate calls
        setIsAITyping(true);
        setChatMessages(prev => [...prev, userMessage]);
        setChatInput('');

        try {
            // Use a temporary case or the provided caseId
            let tempCaseId = caseId;

            // If no caseId provided, create a temporary case for template chat
            if (!tempCaseId && session?.user.id) {
                const { data: tempCase, error: caseError } = await supabase
                    .from('cases')
                    .insert({
                        user_id: session.user.id,
                        title: `Template Chat: ${template.title}`,
                        type: 'Template',
                        status: 'Active',
                    })
                    .select()
                    .single();

                if (!caseError && tempCase) {
                    tempCaseId = tempCase.id;
                }
            }

            if (!tempCaseId) {
                throw new Error('Could not create or find case for AI chat');
            }

            // Build enhanced prompt with document context
            const enhancedPrompt = `I'm working on a German legal document (${template.title}). Current document content:\n\n${editedContent}\n\nUser request: ${chatInput.trim()}\n\nPlease help me improve, complete, or refine this document. Respond in the same language as the user's request (German or English).`;

            // Call chat-ai Edge Function
            const { data, error } = await supabase.functions.invoke('chat-ai', {
                body: {
                    message: enhancedPrompt,
                    caseId: tempCaseId,
                    userId: session.user.id,
                    conversationHistory: chatMessages.slice(-5).map(msg => ({
                        role: msg.role,
                        content: msg.content
                    }))
                },
            });

            if (error) {
                throw error;
            }

            const aiResponse: ChatMessage = {
                id: `ai-${Date.now()}`,
                role: 'ai',
                content: data?.message || data?.response || 'I apologize, but I could not generate a response. Please try again.',
                timestamp: new Date()
            };

            setChatMessages(prev => [...prev, aiResponse]);
        } catch (err) {
            console.error('AI chat error:', err);
            const errorMessage: ChatMessage = {
                id: `error-${Date.now()}`,
                role: 'ai',
                content: `Fehler: ${err instanceof Error ? err.message : 'Could not connect to AI service. Please check your connection and try again.'}\n\nError: ${err instanceof Error ? err.message : 'Could not connect to AI service. Please check your connection and try again.'}`,
                timestamp: new Date()
            };
            setChatMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsAITyping(false);
            if (chatInputRef.current) {
                chatInputRef.current.focus();
            }
        }
    };

    const handleApplyAISuggestion = (suggestion: string) => {
        // Extract the suggested content from AI response
        // This is a simple implementation - can be enhanced with better parsing
        setEditedContent(prev => {
            // Try to extract code blocks or specific sections from AI response
            const codeBlockMatch = suggestion.match(/```[\s\S]*?```/);
            if (codeBlockMatch) {
                return codeBlockMatch[0].replace(/```/g, '').trim();
            }
            // Otherwise, append the suggestion
            return prev + '\n\n' + suggestion;
        });
        setIsEditing(true);
    };

    const handleSave = async () => {
        if (!onSave) return;
        setIsSaving(true);
        try {
            await onSave(editedContent);
            setIsEditing(false);
        } catch (err) {
            console.error('Failed to save template:', err);
        } finally {
            setIsSaving(false);
        }
    };

    const handleExportPDF = () => {
        // Create a print-friendly version that hides UI elements
        const printWindow = window.open('', '_blank');
        if (!printWindow) {
            toast.warning('Please allow popups to export PDF');
            return;
        }

        // Get user's plan name for watermarking
        const planName = profile?.subscription?.plan_name || 'free';

        // Create HTML content with only the document
        let printContent = `
<!DOCTYPE html>
<html lang="de">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${template.title}</title>
    <style>
        @media print {
            @page {
                margin: 2cm 2cm 3cm 2.5cm; /* top right bottom left - extra left for staple, extra bottom for footer */
                size: A4;
            }
            body {
                margin: 0;
                padding: 20px;
                font-family: 'Times New Roman', serif;
                font-size: 12pt;
                line-height: 1.6;
                color: #000;
                background: #fff;
            }
        }
        body {
            margin: 0;
            padding: 20px;
            font-family: 'Times New Roman', serif;
            font-size: 12pt;
            line-height: 1.6;
            color: #000;
            background: #fff;
        }
        .document-header {
            margin-bottom: 30px;
            border-bottom: 2px solid #000;
            padding-bottom: 15px;
        }
        .document-title {
            font-size: 16pt;
            font-weight: bold;
            margin-bottom: 10px;
        }
        .document-content {
            white-space: pre-wrap;
            font-family: 'Times New Roman', serif;
            font-size: 12pt;
            line-height: 1.8;
            text-align: justify;
        }
        .document-meta {
            font-size: 10pt;
            color: #666;
            margin-bottom: 20px;
            padding-bottom: 10px;
            border-bottom: 1px solid #ddd;
        }
        .signature-section {
            margin-top: 60px;
            padding-top: 20px;
            page-break-inside: avoid;
        }
        .signature-line {
            border-top: 1px solid #000;
            width: 300px;
            margin-top: 40px;
            margin-bottom: 5px;
        }
        .signature-label {
            font-size: 10pt;
            margin-top: 5px;
            font-weight: normal;
        }
        .signature-info {
            margin-top: 10px;
            font-size: 10pt;
            color: #333;
        }
        .legal-disclaimer {
            margin-top: 40px;
            padding-top: 20px;
            border-top: 1px solid #ddd;
            font-size: 9pt;
            color: #666;
            font-style: italic;
            text-align: left;
        }
        .staple-indicator {
            position: absolute;
            top: 0;
            left: -12px;
            width: 8px;
            height: 100%;
            border-left: 2px dashed #999;
            opacity: 0.4;
            pointer-events: none;
        }
        .page-footer {
            position: fixed;
            bottom: 0;
            left: 0;
            right: 0;
            padding: 10px 20px;
            font-size: 9pt;
            color: #666;
            border-top: 1px solid #ddd;
            background: #fff;
            text-align: center;
            display: none; /* Hidden in screen, visible in print */
        }
        @media print {
            .staple-indicator {
                display: block;
            }
            .page-footer {
                display: block;
            }
            @page {
                margin: 2cm 2cm 3cm 2.5cm; /* Extra left margin for staple, extra bottom for footer */
            }
        }
        @media print {
            .no-print {
                display: none !important;
            }
        }
    </style>
</head>
<body>
    <div class="document-header">
        <div class="document-title">${template.title}</div>
        <div class="document-meta">
            Erstellt am / Created on: ${new Date().toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })} • 
            Dokumenttyp / Document Type: ${template.slug.replace(/-/g, ' ')}
        </div>
    </div>
    <div class="document-content">
        ${editedContent.replace(/\n/g, '<br>').replace(/\[([^\]]+)\]/g, '<strong>[$1]</strong>')}
        <div class="signature-section">
            <div class="signature-line"></div>
            <div class="signature-label">Unterschrift / Signature</div>
            <div class="signature-info">
                <div>${profile ? (profile.first_name && profile.last_name ? `${profile.first_name} ${profile.last_name}` : profile.first_name || profile.last_name || '[Ihr Name]') : '[Ihr Name]'}</div>
                ${profile?.firm ? `<div style="margin-top: 3px;">${profile.firm}</div>` : ''}
                <div style="margin-top: 3px;">Datum / Date: ${new Date().toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}</div>
            </div>
        </div>
        <div class="legal-disclaimer">
            <strong>⚠️ Hinweis / Disclaimer:</strong> Dieses Dokument wurde als Entwurf generiert und dient nur zu Informationszwecken. Es ersetzt keine Rechtsberatung durch einen Anwalt und begründet kein Anwaltsmandatsverhältnis. Bitte überprüfen Sie den Inhalt sorgfältig und konsultieren Sie einen qualifizierten Rechtsanwalt, bevor Sie das Dokument versenden. LRDEnE übernimmt keine Haftung für die Richtigkeit oder Vollständigkeit des Inhalts.<br>
            <em>⚠️ This document was generated as a draft template for informational purposes only. It does not replace legal advice from a lawyer and does not establish an attorney-client relationship. Please review the content carefully and consult with a qualified lawyer before sending. LRDEnE assumes no liability for the accuracy or completeness of the content.</em>
        </div>
    </div>
    <div class="staple-indicator"></div>
    <div class="page-footer">
        <div>LRDEnE - AI Legal Assistant | www.lrdene.de</div>
        <div style="margin-top: 3px; font-size: 8pt;">Dokument generiert am / Document generated on: ${new Date().toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}</div>
    </div>
</body>
</html>
        `;

        // Add watermark for free plan users
        printContent = addWatermarkToHTML(printContent, planName);

        // Show upgrade message for free plan users
        if (planName === 'free') {
            toast.info('Free plan: PDFs include a watermark. Upgrade to remove it.', { duration: 4000 });
        }

        printWindow.document.write(printContent);
        printWindow.document.close();

        // Wait for content to load, then print
        setTimeout(() => {
            printWindow.print();
            // Optionally close after printing (user can cancel)
            // printWindow.close();
        }, 250);
    };

    return (
        <div className="flex flex-col flex-1 p-4 bg-luna-bg-light dark:bg-luna-bg-dark">
            <div className="flex items-center justify-between mb-6">
                <button onClick={onBack} className="flex items-center gap-2 text-sm font-medium text-luna-text-light/80 dark:text-luna-text-dark/80 hover:text-luna-secondary transition-colors">
                    <span className="material-symbols-outlined">arrow_back</span>
                    Back {isEditing ? 'to Form' : 'to Templates'}
                </button>
                <div className="flex gap-2">
                    <button
                        onClick={() => setShowAIChat(!showAIChat)}
                        className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${showAIChat
                            ? 'bg-luna-secondary text-luna-primary'
                            : 'bg-luna-text-light/10 dark:bg-luna-text-dark/10 hover:bg-luna-text-light/20 dark:hover:bg-luna-text-dark/20'
                            }`}
                    >
                        <span className="material-symbols-outlined">smart_toy</span>
                        {showAIChat ? 'Hide AI' : 'AI Assistant'}
                    </button>
                    <button
                        onClick={() => setIsEditing(!isEditing)}
                        className="px-4 py-2 text-sm font-medium bg-luna-text-light/10 dark:bg-luna-text-dark/10 rounded-lg hover:bg-luna-text-light/20 dark:hover:bg-luna-text-dark/20 transition-colors"
                    >
                        {isEditing ? 'Preview' : 'Edit'}
                    </button>
                    {onSave && (
                        <button
                            onClick={handleSave}
                            disabled={isSaving}
                            className="px-4 py-2 text-sm font-medium bg-luna-secondary text-luna-primary rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
                        >
                            {isSaving ? 'Saving...' : 'Save'}
                        </button>
                    )}
                    <button
                        onClick={handleExportPDF}
                        className="px-4 py-2 text-sm font-medium bg-luna-secondary text-luna-primary rounded-lg hover:opacity-90 transition-opacity flex items-center gap-2"
                    >
                        <span className="material-symbols-outlined">download</span>
                        Export PDF
                    </button>
                </div>
            </div>
            <div className={`flex gap-4 flex-1 ${showAIChat ? 'grid grid-cols-1 lg:grid-cols-3' : ''}`}>
                {/* Document Editor */}
                <div className={`bg-white dark:bg-luna-primary rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 shadow-sm p-6 flex flex-col ${showAIChat ? 'lg:col-span-2' : 'flex-1'}`}>
                    <h2 className="text-2xl font-bold mb-4">{template.title}</h2>
                    <div className="flex-1 overflow-y-auto">
                        {isEditing ? (
                            <textarea
                                value={editedContent}
                                onChange={(e) => setEditedContent(e.target.value)}
                                className="w-full h-full min-h-[500px] px-4 py-2 border border-luna-text-light/20 dark:border-luna-text-dark/20 rounded-lg bg-white dark:bg-luna-bg-dark text-luna-text-light dark:text-luna-text-dark focus:outline-none focus:ring-2 focus:ring-luna-secondary font-mono text-sm whitespace-pre-wrap"
                                placeholder="Enter your document content here..."
                            />
                        ) : (
                            <div className="prose prose-sm dark:prose-invert max-w-none whitespace-pre-wrap font-mono text-xs">
                                {editedContent || <span className="text-luna-text-light/50 dark:text-luna-text-dark/50 italic">No content yet. Click Edit to add content.</span>}
                            </div>
                        )}
                    </div>
                </div>

                {/* AI Chat Assistant */}
                {showAIChat && (
                    <div className="flex flex-col bg-white dark:bg-luna-primary rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 shadow-sm overflow-hidden lg:col-span-1">
                        <div className="p-4 border-b border-luna-text-light/10 dark:border-luna-text-dark/20 shrink-0">
                            <div className="flex items-center gap-2">
                                <div className="flex-shrink-0 size-8 bg-luna-secondary/20 text-luna-secondary rounded-full flex items-center justify-center">
                                    <span className="material-symbols-outlined text-base">smart_toy</span>
                                </div>
                                <div>
                                    <h3 className="text-sm font-bold">LRDEnE AI</h3>
                                    <p className="text-xs text-luna-text-light/70 dark:text-luna-text-dark/70">Document Assistant</p>
                                </div>
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto p-4 space-y-4">
                            {chatMessages.map((msg) => (
                                <div key={msg.id} className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : 'items-start'}`}>
                                    {msg.role === 'ai' ? (
                                        <div className="flex-shrink-0 size-8 bg-luna-secondary/20 text-luna-secondary rounded-full flex items-center justify-center">
                                            <span className="material-symbols-outlined text-base">gavel</span>
                                        </div>
                                    ) : (
                                        <img
                                            alt="User profile"
                                            className="size-8 rounded-full object-cover flex-shrink-0"
                                            src={profile?.avatar_url || "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Ccircle cx='50' cy='50' r='50' fill='%23d1d5db'/%3E%3Ccircle cx='50' cy='35' r='12' fill='%239ca3af'/%3E%3Cpath d='M30 75 Q30 60 50 60 Q70 60 70 75' stroke='%239ca3af' stroke-width='8' fill='none' stroke-linecap='round'/%3E%3C/svg%3E"}
                                            onError={handleImageError}
                                        />
                                    )}
                                    <div className={`flex-1 ${msg.role === 'user' ? 'text-right' : ''}`}>
                                        <div className={`inline-block px-4 py-2 rounded-lg ${msg.role === 'user'
                                            ? 'bg-luna-secondary text-luna-primary'
                                            : 'bg-luna-text-light/10 dark:bg-luna-text-dark/10 text-luna-text-light dark:text-luna-text-dark'
                                            }`}>
                                            <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                                        </div>
                                        {msg.role === 'ai' && (
                                            <button
                                                onClick={() => handleApplyAISuggestion(msg.content)}
                                                className="mt-2 text-xs text-luna-secondary hover:underline"
                                            >
                                                Apply suggestion
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                            {isAITyping && (
                                <div className="flex gap-3 items-start">
                                    <div className="flex-shrink-0 size-8 bg-luna-secondary/20 text-luna-secondary rounded-full flex items-center justify-center">
                                        <span className="material-symbols-outlined text-base">gavel</span>
                                    </div>
                                    <div className="bg-luna-text-light/10 dark:bg-luna-text-dark/10 rounded-lg px-4 py-2">
                                        <div className="flex gap-1">
                                            <div className="w-2 h-2 bg-luna-text-light/50 dark:bg-luna-text-dark/50 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                                            <div className="w-2 h-2 bg-luna-text-light/50 dark:bg-luna-text-dark/50 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                                            <div className="w-2 h-2 bg-luna-text-light/50 dark:bg-luna-text-dark/50 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div ref={chatEndRef} />
                        </div>
                        <div className="p-4 border-t border-luna-text-light/10 dark:border-luna-text-dark/20 shrink-0">
                            <div className="flex gap-2">
                                <textarea
                                    ref={chatInputRef}
                                    value={chatInput}
                                    onChange={(e) => setChatInput(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                            e.preventDefault();
                                            handleAIChat();
                                        }
                                    }}
                                    placeholder="Ask AI to help improve your document..."
                                    className="flex-1 px-4 py-2 border border-luna-text-light/20 dark:border-luna-text-dark/20 rounded-lg bg-white dark:bg-luna-bg-dark text-luna-text-light dark:text-luna-text-dark focus:outline-none focus:ring-2 focus:ring-luna-secondary resize-none text-sm"
                                    rows={2}
                                />
                                <button
                                    type="button"
                                    onClick={handleAIChat}
                                    disabled={!chatInput.trim() || isAITyping || !session?.user.id}
                                    className="px-4 py-2 bg-luna-secondary text-luna-primary rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center"
                                >
                                    <span className="material-symbols-outlined">send</span>
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};


const TemplatesPage: React.FC<{ onGenerate: (template: Template) => void }> = ({ onGenerate }) => {
    const templates: Template[] = [
        { id: '1', icon: '📝', title: 'DSGVO Auskunft', description: 'Request personal data information under GDPR.', slug: 'dsgvo-auskunft' },
        { id: '2', icon: '🗑️', title: 'DSGVO Löschung', description: 'Request deletion of personal data under GDPR.', slug: 'dsgvo-loeschung' },
        { id: '3', icon: '🏠', title: 'Mängelanzeige (Mietrecht)', description: 'Notify landlord of apartment defects.', slug: 'miet-maengelanzeige' },
        { id: '4', icon: '📉', title: 'Mietminderung (Mietrecht)', description: 'Demand rent reduction due to defects.', slug: 'miet-minderung' },
        { id: '5', icon: '⚠️', title: 'Abmahnung Widerspruch', description: 'Draft a response to a warning letter.', slug: 'abmahnung-widerspruch' },
        { id: '6', icon: '💳', title: 'SCHUFA Bestreitung', description: 'Dispute incorrect SCHUFA entries.', slug: 'schufa-bestreitung' },
        { id: '7', icon: '✅', title: 'SCHUFA Lösung', description: 'Request resolution for SCHUFA issues.', slug: 'schufa-loesung' },
    ];

    const headingRef = useAOS('fade-in-up', {}, 0);

    // Global scroll observer for all animate-on-scroll elements
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('in-view');
                        observer.unobserve(entry.target);
                    }
                });
            },
            {
                threshold: 0.1,
                rootMargin: '0px 0px -50px 0px'
            }
        );

        const observeElements = () => {
            const animatedElements = document.querySelectorAll('.animate-on-scroll:not(.in-view)');
            animatedElements.forEach((el) => {
                const rect = el.getBoundingClientRect();
                const isInViewport = rect.top < window.innerHeight && rect.bottom > 0;

                if (isInViewport) {
                    el.classList.add('in-view');
                } else {
                    observer.observe(el);
                }
            });
        };

        const timeout = setTimeout(() => {
            observeElements();
        }, 100);

        const interval = setInterval(observeElements, 1000);

        return () => {
            clearTimeout(timeout);
            clearInterval(interval);
            const animatedElements = document.querySelectorAll('.animate-on-scroll');
            animatedElements.forEach((el) => {
                observer.unobserve(el);
            });
        };
    }, []);

    // Debug: Log templates
    console.log('[TemplatesPage] Rendering with templates:', templates.length, templates);

    return (
        <div className="flex flex-col flex-1 p-4 bg-luna-bg-light dark:bg-luna-bg-dark">
            <div className="flex flex-col gap-4 text-center max-w-3xl mx-auto mb-8 animate-on-scroll fade-in-up" ref={headingRef as React.RefObject<HTMLDivElement>}>
                <h2 className="text-3xl md:text-4xl font-black leading-tight tracking-[-0.033em]">Legal Template Library</h2>
                <p className="text-base font-normal leading-normal text-luna-text-light/70 dark:text-luna-text-muted-dark">Quickly generate common legal documents with guided wizards and embedded citations.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 min-h-[400px]">
                {templates.map((template) => (
                    <div
                        key={template.id}
                        className="template-card flex flex-col gap-4 p-6 rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 bg-white dark:bg-luna-primary shadow-sm hover:translate-y-[-5px] transition-transform duration-200 animate-on-scroll fade-in-up"
                    >
                        <div className="template-icon text-4xl mb-2">{template.icon}</div>
                        <h3 className="text-lg font-bold leading-tight">{template.title}</h3>
                        <p className="text-sm font-normal leading-normal text-luna-text-light/70 dark:text-luna-text-muted-dark flex-1">{template.description}</p>
                        <button
                            className="mt-4 bg-luna-secondary text-luna-primary px-4 py-2 rounded-lg text-sm font-bold hover:opacity-90 transition-opacity self-start"
                            onClick={() => onGenerate(template)}
                            aria-label={`Generate draft for ${template.title}`}
                        >
                            Generate Draft
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
};

const LegalPage: React.FC = () => {
    const headingRef = useAOS('fade-in-up', {}, 0);
    const impressumRef = useAOS('fade-in-up', {}, 100);
    const datenschutzRef = useAOS('fade-in-up', {}, 200);
    const agbRef = useAOS('fade-in-up', {}, 300);
    const disclaimerRef = useAOS('fade-in-up', {}, 400);

    // Global scroll observer for all animate-on-scroll elements
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('in-view');
                        observer.unobserve(entry.target);
                    }
                });
            },
            {
                threshold: 0.1,
                rootMargin: '0px 0px -50px 0px'
            }
        );

        const observeElements = () => {
            const animatedElements = document.querySelectorAll('.animate-on-scroll:not(.in-view)');
            animatedElements.forEach((el) => {
                const rect = el.getBoundingClientRect();
                const isInViewport = rect.top < window.innerHeight && rect.bottom > 0;

                if (isInViewport) {
                    el.classList.add('in-view');
                } else {
                    observer.observe(el);
                }
            });
        };

        const timeout = setTimeout(() => {
            observeElements();
        }, 100);

        const interval = setInterval(observeElements, 1000);

        return () => {
            clearTimeout(timeout);
            clearInterval(interval);
            const animatedElements = document.querySelectorAll('.animate-on-scroll');
            animatedElements.forEach((el) => {
                observer.unobserve(el);
            });
        };
    }, []);

    return (
        <div className="flex flex-col flex-1">
            {/* Hero Section */}
            <section className="py-20 md:py-32 bg-gradient-to-br from-luna-primary via-luna-primary to-luna-bg-dark border-b border-luna-text-light/10 dark:border-luna-text-dark/10">
                <div className="container mx-auto px-4">
                    <div className="flex flex-col gap-4 text-center max-w-4xl mx-auto animate-on-scroll fade-in-up" ref={headingRef as React.RefObject<HTMLDivElement>}>
                        <h1 className="text-4xl md:text-6xl font-black leading-tight tracking-[-0.033em] text-white">Legal Information</h1>
                        <p className="text-lg md:text-xl font-normal leading-normal text-white/80 max-w-2xl mx-auto">
                            Transparency and compliance are at the core of LRDEnE operations. Find all legal information and policies here.
                        </p>
                    </div>
                </div>
            </section>

            <div className="p-4 bg-luna-bg-light dark:bg-luna-bg-dark">
                <div className="container mx-auto max-w-5xl">
                    <div className="flex flex-col gap-8 w-full max-w-4xl mx-auto mt-12">
                        {/* Impressum */}
                        <div className="p-8 rounded-xl bg-white dark:bg-luna-primary shadow-lg border border-luna-text-light/10 dark:border-luna-text-dark/20 animate-on-scroll fade-in-up" ref={impressumRef as React.RefObject<HTMLDivElement>}>
                            <div className="flex items-center gap-3 mb-4">
                                <span className="material-symbols-outlined text-luna-secondary text-2xl">business</span>
                                <h3 className="text-2xl font-bold text-luna-text-light dark:text-luna-text-dark">Impressum</h3>
                            </div>
                            <div className="space-y-2 text-sm leading-relaxed text-luna-text-light/80 dark:text-luna-text-muted-dark">
                                <p><strong>Firmenname:</strong> LRDEnE UG (haftungsbeschränkt)</p>
                                <p><strong>Anschrift:</strong><br />
                                    [Bitte tragen Sie hier Ihre vollständige Adresse ein]<br />
                                    [Straße und Hausnummer]<br />
                                    [Postleitzahl und Ort]<br />
                                    Deutschland</p>
                                <p><strong>Vertreten durch:</strong> [Bitte tragen Sie hier den Namen des Geschäftsführers/der Geschäftsführerin ein]</p>
                                <p><strong>E-Mail:</strong> info@lrdene.de</p>
                                <p><strong>Telefon:</strong> [Bitte tragen Sie hier Ihre Telefonnummer ein]</p>
                                <p><strong>Handelsregister:</strong> Amtsgericht [Ort]<br />HRB [Nummer]</p>
                                <p><strong>Umsatzsteuer-ID:</strong> [Bitte tragen Sie hier Ihre USt-IdNr. ein, falls vorhanden]</p>
                                <p><strong>Aufsichtsbehörde:</strong> [Bitte tragen Sie hier die zuständige Aufsichtsbehörde ein, falls zutreffend]</p>
                                <p className="mt-4 text-xs text-luna-text-light/60 dark:text-luna-text-muted-dark italic">
                                    <strong>Hinweis:</strong> Bitte ersetzen Sie alle Platzhalter in eckigen Klammern durch Ihre tatsächlichen Firmendaten gemäß &sect;5 Telemediengesetz (TMG).
                                </p>
                            </div>
                        </div>

                        {/* Datenschutz */}
                        <div className="p-8 rounded-xl bg-white dark:bg-luna-primary shadow-lg border border-luna-text-light/10 dark:border-luna-text-dark/20 animate-on-scroll fade-in-up" ref={datenschutzRef as React.RefObject<HTMLDivElement>}>
                            <div className="flex items-center gap-3 mb-4">
                                <span className="material-symbols-outlined text-luna-secondary text-2xl">privacy</span>
                                <h3 className="text-2xl font-bold text-luna-text-light dark:text-luna-text-dark">Datenschutzerklärung</h3>
                            </div>
                            <div className="space-y-4 text-sm leading-relaxed text-luna-text-light/80 dark:text-luna-text-muted-dark">
                                <p>
                                    <strong>1. Verantwortlicher:</strong><br />
                                    LRDEnE UG (haftungsbeschränkt)<br />
                                    E-Mail: info@lrdene.de<br />
                                    Support: support@lrdene.de
                                </p>
                                <p>
                                    <strong>2. Rechtsgrundlage:</strong><br />
                                    Wir verarbeiten Ihre personenbezogenen Daten gemäß der Datenschutz-Grundverordnung (DSGVO) und dem Bundesdatenschutzgesetz (BDSG). Die Verarbeitung erfolgt auf Grundlage von Vertragserfüllung (Art. 6(1)(b) DSGVO), berechtigtem Interesse (Art. 6(1)(f) DSGVO), Einwilligung (Art. 6(1)(a) DSGVO) oder gesetzlicher Verpflichtung (Art. 6(1)(c) DSGVO).
                                </p>
                                <p>
                                    <strong>3. Zwecke der Datenverarbeitung:</strong><br />
                                    Wir erheben und verarbeiten personenbezogene Daten zur Bereitstellung unserer Dienste, zur Vertragsabwicklung, zur Kommunikation, zur Verbesserung unserer Services und zur Erfüllung gesetzlicher Verpflichtungen.
                                </p>
                                <p>
                                    <strong>4. Kategorien personenbezogener Daten:</strong><br />
                                    Wir verarbeiten Kontaktdaten (E-Mail, Name), Nutzungsdaten (IP-Adresse, Browser-Informationen), Inhaltsdaten (hochgeladene Dokumente, Chat-Nachrichten), Zahlungsdaten (über sichere Zahlungsdienstleister) und Kommunikationsdaten.
                                </p>
                                <p>
                                    <strong>5. Datenweitergabe:</strong><br />
                                    Wir nutzen vertrauenswürdige Dienstleister (Supabase, Google Gemini AI, Zahlungsdienstleister) zur Bereitstellung unserer Services. Datenübertragungen außerhalb der EU erfolgen mit angemessenen Garantien (Standardvertragsklauseln, EU-US Data Privacy Framework).
                                </p>
                                <p>
                                    <strong>6. Speicherdauer:</strong><br />
                                    Wir speichern Ihre Daten nur so lange, wie es für die Vertragserfüllung, gesetzliche Aufbewahrungspflichten oder zur Beilegung von Streitigkeiten erforderlich ist. Bei Löschung Ihres Kontos werden personenbezogene Daten innerhalb von 30 Tagen gelöscht oder anonymisiert.
                                </p>
                                <p>
                                    <strong>7. Ihre Rechte (DSGVO):</strong><br />
                                    Sie haben das Recht auf Auskunft (Art. 15 DSGVO), Berichtigung (Art. 16 DSGVO), Löschung (Art. 17 DSGVO), Einschränkung der Verarbeitung (Art. 18 DSGVO), Datenübertragbarkeit (Art. 20 DSGVO), Widerspruch (Art. 21 DSGVO) und Widerruf Ihrer Einwilligung. Sie können sich auch bei der zuständigen Aufsichtsbehörde beschweren.
                                </p>
                                <p>
                                    <strong>8. Kontakt für Datenschutzanfragen:</strong><br />
                                    Bei Fragen zum Datenschutz kontaktieren Sie uns bitte unter: info@lrdene.de
                                </p>
                                <p className="mt-4">
                                    <a href="/privacy.html" target="_blank" rel="noopener noreferrer" className="text-luna-secondary hover:underline font-medium">Vollständige Datenschutzerklärung lesen →</a>
                                </p>
                            </div>
                        </div>

                        {/* AGB */}
                        <div className="p-8 rounded-xl bg-white dark:bg-luna-primary shadow-lg border border-luna-text-light/10 dark:border-luna-text-dark/20 animate-on-scroll fade-in-up" ref={agbRef as React.RefObject<HTMLDivElement>}>
                            <div className="flex items-center gap-3 mb-4">
                                <span className="material-symbols-outlined text-luna-secondary text-2xl">description</span>
                                <h3 className="text-2xl font-bold text-luna-text-light dark:text-luna-text-dark">Allgemeine Geschäftsbedingungen (AGB)</h3>
                            </div>
                            <div className="space-y-4 text-sm leading-relaxed text-luna-text-light/80 dark:text-luna-text-muted-dark">
                                <p>
                                    <strong>1. Geltungsbereich:</strong><br />
                                    Unsere Allgemeinen Geschäftsbedingungen regeln die vertraglichen Beziehungen zwischen LRDEnE und unseren Nutzern. Sie umfassen Bestimmungen zu Leistungsumfang, Preisen, Zahlungsmodalitäten, Haftung und Datenschutz.
                                </p>
                                <p>
                                    <strong>2. Vertragsabschluss:</strong><br />
                                    Mit der Nutzung unserer Dienste erklären Sie sich mit der Geltung unserer AGB einverstanden. Die vollständigen AGB können Sie jederzeit auf unserer Webseite einsehen.
                                </p>
                                <p>
                                    <strong>3. Leistungen:</strong><br />
                                    LRDEnE bietet AI-gestützte Rechtsanalyse, Dokumentenerstellung und rechtliche Beratungsdienstleistungen im Rahmen der verfügbaren Pläne an. Die genauen Leistungen richten sich nach dem gewählten Tarif.
                                </p>
                                <p>
                                    <strong>4. Zahlungsbedingungen:</strong><br />
                                    Abonnementgebühren werden im Voraus fällig. Zahlungen erfolgen über sichere Zahlungsdienstleister. Kündigungen können jederzeit mit Wirkung zum Ende der Laufzeit erfolgen.
                                </p>
                                <p>
                                    <strong>5. Kündigung:</strong><br />
                                    Beide Vertragsparteien können den Vertrag jederzeit kündigen. Bereits gezahlte Gebühren werden anteilig erstattet, sofern nicht gesetzlich ausgeschlossen.
                                </p>
                                <p className="mt-4">
                                    <a href="/terms.html" target="_blank" rel="noopener noreferrer" className="text-luna-secondary hover:underline font-medium">Vollständige AGB lesen →</a>
                                </p>
                            </div>
                        </div>

                        {/* Disclaimer */}
                        <div className="p-8 rounded-xl bg-red-100 dark:bg-red-900/20 shadow-lg border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200 animate-on-scroll fade-in-up" role="alert" ref={disclaimerRef as React.RefObject<HTMLDivElement>}>
                            <div className="flex items-center gap-3 mb-4">
                                <span className="material-symbols-outlined text-2xl">warning</span>
                                <h3 className="text-2xl font-bold">Wichtiger Haftungsausschluss</h3>
                            </div>
                            <div className="space-y-4 text-sm leading-relaxed">
                                <p>
                                    <strong>Keine Rechtsberatung gemäß Rechtsdienstleistungsgesetz (RDG):</strong><br />
                                    LRDEnE ist KEINE Anwaltskanzlei, KEIN Rechtsanwalt und bietet KEINE Rechtsberatung im Sinne des Rechtsdienstleistungsgesetzes (RDG).
                                    Die Inhalte und Dienste von LRDEnE dienen ausschließlich Informations- und Unterstützungszwecken und stellen keine Rechtsberatung dar.
                                    Es besteht KEIN Anwaltsmandatsverhältnis zwischen Ihnen und LRDEnE. Für verbindliche Rechtsberatung wenden Sie sich bitte an einen qualifizierten Rechtsanwalt.
                                </p>
                                <p>
                                    <strong>Keine Haftung:</strong><br />
                                    Die Nutzung unserer Dienste ersetzt keine individuelle juristische Prüfung. LRDEnE übernimmt keine Haftung für die Richtigkeit, Vollständigkeit oder Aktualität der bereitgestellten Informationen, AI-generierten Inhalte oder für daraus resultierende Schäden.
                                    AI-generierte Inhalte können Fehler, Ungenauigkeiten oder Auslassungen enthalten.
                                </p>
                                <p>
                                    <strong>Eigenverantwortung:</strong><br />
                                    Nutzer sind für die ordnungsgemäße Verwendung der bereitgestellten Informationen selbst verantwortlich. Sie müssen alle AI-generierten Inhalte unabhängig überprüfen.
                                    Wir empfehlen dringend, bei wichtigen rechtlichen Angelegenheiten einen qualifizierten Rechtsanwalt zu konsultieren.
                                </p>
                                <p>
                                    <strong>Rechtliche Grundlage:</strong><br />
                                    Gemäß dem Rechtsdienstleistungsgesetz (RDG) und den Richtlinien der Bundesrechtsanwaltskammer (BRAK) müssen AI-Tools die Rechtsberatung nicht ersetzen;
                                    die endgültige rechtliche Bewertung muss stets von einem menschlichen Rechtsanwalt vorgenommen werden.
                                </p>
                                <p className="mt-4">
                                    <a href="/terms.html" target="_blank" rel="noopener noreferrer" className="text-luna-secondary hover:underline font-medium">Vollständige AGB lesen →</a>
                                </p>
                            </div>
                        </div>

                        {/* Cookie Policy */}
                        <div className="p-8 rounded-xl bg-white dark:bg-luna-primary shadow-lg border border-luna-text-light/10 dark:border-luna-text-dark/20 animate-on-scroll fade-in-up">
                            <div className="flex items-center gap-3 mb-4">
                                <span className="material-symbols-outlined text-luna-secondary text-2xl">cookie</span>
                                <h3 className="text-2xl font-bold text-luna-text-light dark:text-luna-text-dark">Cookie-Richtlinie</h3>
                            </div>
                            <div className="space-y-3 text-sm leading-relaxed text-luna-text-light/80 dark:text-luna-text-muted-dark">
                                <p>
                                    Wir verwenden Cookies, um Ihnen die bestmögliche Erfahrung auf unserer Website zu bieten. Cookies sind kleine Textdateien, die auf Ihrem Gerät gespeichert werden.
                                </p>
                                <p>
                                    <strong>Notwendige Cookies:</strong> Diese sind für das Funktionieren der Website unerlässlich.<br />
                                    <strong>Funktionale Cookies:</strong> Diese ermöglichen es der Website, erweiterte Funktionalität und Personalisierung bereitzustellen.<br />
                                    <strong>Analyse-Cookies:</strong> Diese helfen uns zu verstehen, wie Besucher mit unserer Website interagieren.
                                </p>
                                <p>
                                    Sie können Ihre Cookie-Einstellungen jederzeit in Ihrem Browser ändern. Bitte beachten Sie, dass einige Funktionen möglicherweise nicht mehr verfügbar sind, wenn Cookies deaktiviert werden.
                                </p>
                                <p className="mt-4">
                                    <a href="/cookies.html" target="_blank" rel="noopener noreferrer" className="text-luna-secondary hover:underline font-medium">Vollständige Cookie-Richtlinie lesen →</a>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const Footer: React.FC<{ onNavigate?: (page: string) => void }> = ({ onNavigate }) => {
    const handleNavClick = (page: string, e: React.MouseEvent) => {
        e.preventDefault();
        if (onNavigate) {
            onNavigate(page);
        } else {
            // Fallback: update URL directly if no navigation handler
            const url = generatePermalink(page);
            window.location.href = url;
        }
    };

    return (
        <footer className="flex flex-col gap-6 px-5 py-10 text-center border-t border-solid border-luna-text-light/10 dark:border-luna-text-dark/10 shrink-0 bg-luna-bg-light dark:bg-luna-bg-dark">
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3">
                <a className="text-sm font-medium leading-normal min-w-24 hover:text-luna-secondary transition-colors" href="/features" onClick={(e) => handleNavClick('Features', e)}>Features</a>
                <a className="text-sm font-medium leading-normal min-w-24 hover:text-luna-secondary transition-colors" href="/pricing" onClick={(e) => handleNavClick('Pricing', e)}>Pricing</a>
                <a className="text-sm font-medium leading-normal min-w-24 hover:text-luna-secondary transition-colors" href="/collaboration" onClick={(e) => handleNavClick('Collaboration', e)}>Collaboration</a>
                <a className="text-sm font-medium leading-normal min-w-24 hover:text-luna-secondary transition-colors" href="/legal-updates" onClick={(e) => handleNavClick('LegalUpdates', e)}>Legal Updates</a>
                <a className="text-sm font-medium leading-normal min-w-24 hover:text-luna-secondary transition-colors" href="/legal-insurance" onClick={(e) => handleNavClick('LegalInsurance', e)}>Rechtsschutz</a>
                <a className="text-sm font-medium leading-normal min-w-24 hover:text-luna-secondary transition-colors" href="/about" onClick={(e) => handleNavClick('AboutUs', e)}>About Us</a>
                <a className="text-sm font-medium leading-normal min-w-24 hover:text-luna-secondary transition-colors" href="/contact" onClick={(e) => handleNavClick('Contact', e)}>Contact</a>
                <a className="text-sm font-medium leading-normal min-w-24 hover:text-luna-secondary transition-colors" href="/terms.html" target="_blank" rel="noopener noreferrer">Terms of Service</a>
                <a className="text-sm font-medium leading-normal min-w-24 hover:text-luna-secondary transition-colors" href="/privacy.html" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                <a className="text-sm font-medium leading-normal min-w-24 hover:text-luna-secondary transition-colors" href="/cookies.html" target="_blank" rel="noopener noreferrer">Cookie Policy</a>
            </div>
            {/* Social Media Links - Update with your actual social media URLs */}
            {/* Uncomment and update URLs when social media accounts are set up */}
            {false && (
                <div className="flex flex-wrap justify-center gap-6">
                    <a className="hover:text-luna-secondary transition-colors" href="https://linkedin.com/company/lrdene" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                        <svg className="feather feather-linkedin" fill="none" height="24" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path><rect height="12" width="4" x="2" y="9"></rect><circle cx="4" cy="4" r="2"></circle></svg>
                    </a>
                    <a className="hover:text-luna-secondary transition-colors" href="https://twitter.com/lrdene" target="_blank" rel="noopener noreferrer" aria-label="Twitter">
                        <svg className="feather feather-twitter" fill="none" height="24" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg"><path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z"></path></svg>
                    </a>
                    <a className="hover:text-luna-secondary transition-colors" href="https://facebook.com/lrdene" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                        <svg className="feather feather-facebook" fill="none" height="24" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path></svg>
                    </a>
                </div>
            )}
            <p className="text-sm font-normal leading-normal text-luna-text-light/70 dark:text-luna-text-muted-dark">© 2024 LRDEnE. All rights reserved.</p>
        </footer>
    );
};

// --- NEW FEATURE PAGE ---
const FeaturesPage: React.FC = () => {
    // AOS refs for features section
    const featuresHeadingRef = useAOS('fade-in-up', {}, 0);
    const featuresParagraphRef = useAOS('fade-in-up', {}, 100);
    const whyChooseParagraphRef = useAOS('fade-in-up', {}, 100);

    // Global scroll observer for all animate-on-scroll elements
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('in-view');
                        observer.unobserve(entry.target);
                    }
                });
            },
            {
                threshold: 0.1,
                rootMargin: '0px 0px -50px 0px'
            }
        );

        const observeElements = () => {
            const animatedElements = document.querySelectorAll('.animate-on-scroll:not(.in-view)');
            animatedElements.forEach((el) => {
                const rect = el.getBoundingClientRect();
                const isInViewport = rect.top < window.innerHeight && rect.bottom > 0;

                if (isInViewport) {
                    el.classList.add('in-view');
                } else {
                    observer.observe(el);
                }
            });
        };

        const timeout = setTimeout(() => {
            observeElements();
        }, 100);

        const interval = setInterval(observeElements, 1000);

        return () => {
            clearTimeout(timeout);
            clearInterval(interval);
            const animatedElements = document.querySelectorAll('.animate-on-scroll');
            animatedElements.forEach((el) => {
                observer.unobserve(el);
            });
        };
    }, []);

    return (
        <div className="flex flex-col flex-1">
            {/* Hero Section */}
            <section className="py-20 md:py-32 bg-gradient-to-br from-luna-primary via-luna-primary to-luna-bg-dark border-b border-luna-text-light/10 dark:border-luna-text-dark/10">
                <div className="container mx-auto px-4">
                    <div className="flex flex-col gap-4 text-center max-w-4xl mx-auto animate-on-scroll fade-in-up" ref={featuresHeadingRef as React.RefObject<HTMLDivElement>}>
                        <h1 className="text-4xl md:text-6xl font-black leading-tight tracking-[-0.033em] text-white">Your AI-Powered Legal Co-Pilot</h1>
                        <p className="text-lg md:text-xl font-normal leading-normal text-white/80 max-w-2xl mx-auto" ref={featuresParagraphRef as React.RefObject<HTMLParagraphElement>}>
                            LRDEnE streamlines complex legal tasks with a suite of intelligent tools designed for precision, efficiency, and unparalleled legal accuracy.
                        </p>
                    </div>
                </div>
            </section>

            {/* Core Features Grid */}
            <section className="py-20 md:py-28 bg-white dark:bg-luna-primary">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-16 animate-on-scroll fade-in-up">
                        <h2 className="text-3xl md:text-5xl font-black leading-tight tracking-[-0.033em] mb-4 text-luna-text-light dark:text-luna-text-dark">
                            Powerful Features for Modern Legal Practice
                        </h2>
                        <p className="text-lg text-luna-text-light/70 dark:text-luna-text-dark/70 max-w-2xl mx-auto">
                            Everything you need to streamline your legal workflow, powered by advanced AI
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
                        {[
                            {
                                icon: 'description',
                                title: 'AI Document Analysis',
                                description: 'Upload legal documents and get instant AI-powered analysis with risk assessment, compliance checks, and actionable suggestions based on EU and German law.',
                                features: ['GDPR & EU AI Act compliance', 'Risk heatmaps & prioritized findings', 'Legal citations with article numbers'],
                                color: 'from-luna-secondary/20 to-luna-secondary/5',
                                iconColor: 'text-luna-secondary'
                            },
                            {
                                icon: 'article',
                                title: 'AI Template Generator',
                                description: 'Generate professional German legal documents instantly with our AI-powered template library. 7+ document types including GDPR requests, rent reduction notices, and SCHUFA disputes.',
                                features: ['Interactive wizard forms', 'Auto-fill from profile', 'Export to PDF & save to cases'],
                                color: 'from-purple-500/20 to-purple-500/5',
                                iconColor: 'text-purple-500'
                            },
                            {
                                icon: 'chat_bubble',
                                title: 'AI Legal Assistant',
                                description: 'Chat with LRDEnE, your AI-powered legal research assistant. Get information and analysis on German and EU law with proper citations, context-aware responses, and comprehensive guidance. Note: This is not legal advice - always consult a qualified lawyer for important legal matters.',
                                features: ['Real-time chat with context', 'Precise legal citations', 'Case-specific guidance'],
                                color: 'from-blue-500/20 to-blue-500/5',
                                iconColor: 'text-blue-500'
                            },
                            {
                                icon: 'folder_managed',
                                title: 'Smart Case Management',
                                description: 'Organize all your legal cases in one place. Track status, deadlines, documents, and AI conversations for each case with ease.',
                                features: ['Create unlimited cases', 'Track status & deadlines', 'Link documents & templates'],
                                color: 'from-green-500/20 to-green-500/5',
                                iconColor: 'text-green-500'
                            },
                            {
                                icon: 'cloud_upload',
                                title: 'Secure Document Storage',
                                description: 'Upload, store, and manage all your legal documents securely. Support for PDF, DOCX, and TXT files with automatic organization by case.',
                                features: ['Secure cloud storage', 'PDF preview & download', 'Case-based organization'],
                                color: 'from-orange-500/20 to-orange-500/5',
                                iconColor: 'text-orange-500'
                            },
                            {
                                icon: 'gavel',
                                title: 'EU & German Law Expertise',
                                description: 'Built specifically for German and EU legal professionals. Expert knowledge of GDPR, BGB, EU AI Act, DSA, DMA, and more.',
                                features: ['GDPR compliance checks', 'German Civil Code (BGB)', 'Latest EU regulations'],
                                color: 'from-red-500/20 to-red-500/5',
                                iconColor: 'text-red-500'
                            },
                        ].map((feature) => (
                            <div key={feature.title} className="feature-card group relative bg-white dark:bg-luna-primary rounded-2xl border border-luna-text-light/10 dark:border-luna-text-dark/20 p-8 shadow-sm hover:shadow-xl animate-on-scroll fade-in-up">
                                <div className={`flex items-center justify-center size-16 rounded-2xl bg-gradient-to-br ${feature.color} mb-6 group-hover:scale-110 transition-transform duration-300`}>
                                    <span className={`material-symbols-outlined text-4xl ${feature.iconColor}`}>{feature.icon}</span>
                                </div>
                                <h3 className="text-xl font-bold mb-3 text-luna-text-light dark:text-luna-text-dark">{feature.title}</h3>
                                <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 leading-relaxed mb-4">
                                    {feature.description}
                                </p>
                                <ul className="space-y-2 text-xs text-luna-text-light/60 dark:text-luna-text-muted-dark">
                                    {feature.features.map((item, i) => (
                                        <li key={i} className="flex items-center gap-2">
                                            <span className={`material-symbols-outlined text-base ${feature.iconColor}`}>check_circle</span>
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Feature Comparison */}
            <section className="py-20 md:py-28 bg-luna-bg-light dark:bg-luna-bg-dark">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-16 animate-on-scroll fade-in-up">
                        <h2 className="text-3xl md:text-5xl font-black leading-tight tracking-[-0.033em] mb-4 text-luna-text-light dark:text-luna-text-dark">
                            Why Legal Professionals Choose LRDEnE
                        </h2>
                        <p className="text-lg text-luna-text-light/70 dark:text-luna-text-dark/70 max-w-2xl mx-auto" ref={whyChooseParagraphRef as React.RefObject<HTMLParagraphElement>}>
                            Our advanced AI provides unparalleled insights and clarity into complex legal matters across Germany and the European Union.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
                        {[
                            { icon: 'database', title: 'Comprehensive Database', description: 'Access a vast and up-to-date repository of legal documents, statutes, and case law from EU and Germany.' },
                            { icon: 'format_quote', title: 'AI-Powered Citations', description: 'Receive accurate, verifiable citations with every analysis to support your legal arguments.' },
                            { icon: 'description', title: 'Instant Summaries', description: 'Quickly grasp the key points of any case with AI-generated comprehensive summaries.' },
                            { icon: 'security', title: 'Enterprise Security', description: 'Bank-level encryption and GDPR-compliant data storage to protect your sensitive legal information.' },
                        ].map((feature) => (
                            <div key={feature.title} className="flex flex-col gap-3 rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/10 bg-white dark:bg-luna-primary p-6 shadow-sm hover:shadow-lg transition-all animate-on-scroll fade-in-up">
                                <div className="text-luna-secondary">
                                    <span className="material-symbols-outlined !text-3xl">{feature.icon}</span>
                                </div>
                                <div className="flex flex-col gap-1">
                                    <h3 className="text-lg font-bold leading-tight text-luna-text-light dark:text-luna-text-dark">{feature.title}</h3>
                                    <p className="text-sm font-normal leading-normal text-luna-text-light/70 dark:text-luna-text-muted-dark">{feature.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Integration & Workflow Section */}
            <section className="py-20 md:py-28 bg-white dark:bg-luna-primary border-y border-luna-text-light/10 dark:border-luna-text-dark/10">
                <div className="container mx-auto px-4">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
                        <div className="animate-on-scroll fade-in-up">
                            <h2 className="text-3xl md:text-5xl font-black leading-tight tracking-[-0.033em] mb-6 text-luna-text-light dark:text-luna-text-dark">
                                Seamless Integration with Your Workflow
                            </h2>
                            <p className="text-base text-luna-text-light/70 dark:text-luna-text-dark/70 mb-6 leading-relaxed">
                                LRDEnE integrates seamlessly into your existing legal workflow. Whether you&apos;re a solo practitioner or part of a large firm, our tools are designed to enhance your productivity without disrupting your established processes.
                            </p>
                            <div className="space-y-4">
                                {[
                                    { icon: 'sync', text: 'API access for custom integrations' },
                                    { icon: 'cloud_sync', text: 'Real-time document synchronization' },
                                    { icon: 'workspaces', text: 'Team collaboration features' },
                                    { icon: 'security', text: 'Enterprise SSO/SAML support' },
                                ].map((item, i) => (
                                    <div key={i} className="flex items-center gap-3 animate-on-scroll fade-in-up">
                                        <span className="material-symbols-outlined text-luna-secondary text-xl">{item.icon}</span>
                                        <span className="text-sm text-luna-text-light/80 dark:text-luna-text-dark/80">{item.text}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="animate-on-scroll fade-in-up">
                            <div className="bg-gradient-to-br from-luna-secondary/10 via-luna-secondary/5 to-transparent rounded-2xl p-8 border border-luna-secondary/20">
                                <h3 className="text-2xl font-bold mb-6 text-luna-text-light dark:text-luna-text-dark">
                                    Platform Capabilities
                                </h3>
                                <dl className="space-y-4">
                                    {[
                                        { metric: 'Scalable', label: 'For Legal Teams' },
                                        { metric: 'High Volume', label: 'Document Processing' },
                                        { metric: '99.9%', label: 'Target Uptime' },
                                        { metric: '24/7', label: 'AI Assistant Availability' },
                                    ].map((stat, index) => (
                                        <div key={index} className="flex items-center justify-between py-3 border-b border-luna-text-light/10 dark:border-luna-text-dark/10">
                                            <dt className="text-sm font-medium text-luna-text-light/80 dark:text-luna-text-dark/80">{stat.label}</dt>
                                            <dd className="text-lg font-bold text-luna-secondary">{stat.metric}</dd>
                                        </div>
                                    ))}
                                </dl>
                                <p className="mt-4 text-xs text-luna-text-light/60 dark:text-luna-text-muted-dark italic">
                                    * Metrics represent platform capabilities and targets. Actual usage may vary.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 md:py-32 bg-gradient-to-br from-luna-secondary/20 via-luna-secondary/10 to-luna-secondary/5">
                <div className="container mx-auto px-4">
                    <div className="max-w-4xl mx-auto text-center animate-on-scroll fade-in-up">
                        <h2 className="text-4xl md:text-6xl font-black leading-tight tracking-[-0.033em] mb-6 text-luna-text-light dark:text-luna-text-dark">
                            Ready to Transform Your Legal Practice?
                        </h2>
                        <p className="text-lg md:text-xl text-luna-text-light/80 dark:text-luna-text-dark/80 mb-8 max-w-2xl mx-auto leading-relaxed">
                            Join legal professionals across Germany and the EU who trust LRDEnE for accurate, AI-powered legal assistance.
                        </p>
                        <button className="flex items-center justify-center gap-2 h-14 px-8 bg-luna-secondary text-luna-primary text-base font-bold rounded-lg hover:opacity-90 transition-opacity shadow-lg shadow-luna-secondary/30 mx-auto">
                            <span>Get Started Free</span>
                            <span className="material-symbols-outlined">arrow_forward</span>
                        </button>
                    </div>
                </div>
            </section>
        </div>
    );
};

// --- NEW ABOUT US PAGE ---
const AboutUsPage: React.FC = () => {
    const testimonialsHeadingRef = useAOS('fade-in-up', {}, 0);
    const testimonialsParagraphRef = useAOS('fade-in-up', {}, 100);
    const missionRef = useAOS('fade-in-up', {}, 0);

    // Global scroll observer for all animate-on-scroll elements
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('in-view');
                        observer.unobserve(entry.target);
                    }
                });
            },
            {
                threshold: 0.1,
                rootMargin: '0px 0px -50px 0px'
            }
        );

        const observeElements = () => {
            const animatedElements = document.querySelectorAll('.animate-on-scroll:not(.in-view)');
            animatedElements.forEach((el) => {
                const rect = el.getBoundingClientRect();
                const isInViewport = rect.top < window.innerHeight && rect.bottom > 0;

                if (isInViewport) {
                    el.classList.add('in-view');
                } else {
                    observer.observe(el);
                }
            });
        };

        const timeout = setTimeout(() => {
            observeElements();
        }, 100);

        const interval = setInterval(observeElements, 1000);

        return () => {
            clearTimeout(timeout);
            clearInterval(interval);
            const animatedElements = document.querySelectorAll('.animate-on-scroll');
            animatedElements.forEach((el) => {
                observer.unobserve(el);
            });
        };
    }, []);

    const testimonials = [
        {
            img: "https://lh3.googleusercontent.com/aida-public/AB6AXuC6oq33dNp6A-9NrO3YTfoAiNzhgWchFlJuFX0rTR88Ia5vLnjO8OS23dxBfNeB2iFSmP6TfzlTtpIZJPYNQ3aD2g5i5Td_5WlHtD9f8MXuQWh5pUE_Lp26Ayht0Pu3FHP34E3pEN8WfbEA5P2GZzoXMyfkpTBcDhl3WQ56kuDkn-RAOr2xCQpVII4BEDRoLmjtNSthqKVF4uReXtrxQlDVWbQZG_0U2MY9G0Z5smvb9KvpYrbwQhi4-G6kFaqf-OskjR16cIS0_ZaS",
            name: "Dr. Anke Schmidt",
            title: "Partner, Schmidt & Meier Rechtsanwälte",
            quote: "LRDEnE has become an indispensable tool in our firm. Its ability to cross-reference German and EU law with such speed and accuracy has cut our research time in half.",
        },
        {
            img: "https://lh3.googleusercontent.com/aida-public/AB6AXuAqByYWhqenqZy3P1S-knNZuc7IU0sFAXCbjzpXr-eXrYT7kd2q9A2zlAv54wEqRiVeo5hXeiLm4MlMCjux8aa3gL_G1OUiyXCzIQbtUMJxH8v1AujPdjjmamaK9YFLrpzS6ZtT-PFrd9HmhdGwh07p83l5jNnjMxoo49Qt4fmbeZbPEbHfnrl5RghUQt3kQLYmmLAhP93C7Hf1RxkmlqoFRzTgxUMWDU1QJ2gx_H9B_E7RuKby-3foRmDv8nhYclgppTXovL5x9P1tyhpEqUFndQjxNAaSK",
            name: "Jean-Luc Dubois",
            title: "Corporate Counsel, EuroTech Innovations",
            quote: "The citation analysis feature is a game-changer. It gives us immense confidence in our legal positions, knowing that every source is thoroughly vetted. LRDEnE is simply brilliant.",
        },
        {
            img: "https://lh3.googleusercontent.com/aida-public/AB6AXuCldMAjJQpyX4b7kFOPZbYY-SaD-0yaDUhmujuknT6XeFTAWkqTH5f6MHmOewJ0Tc3LSByHXowbygEDLyqP3AIU7Aw0C5ujIwn06gDHWr3ClS1w_y2VNfWnCGr3oT104IGzYIaH3ltbcd-HzBe1wAANdx8iDT_QZ1BPnGTa3r4jQE7BGXNC91AJStxu_oSljDEkORJD_dD29AIvUcVzRcPHJzfF6GwbJcW272JzvHKhy3PS3kqnZmz9xNA6wFe75m5qag0Up4EVFNbP",
            name: "Sofia Rossi",
            title: "Legal Analyst, EU Policy Watch",
            quote: "As a legal analyst focusing on EU directives, navigating the complexities is my daily challenge. LRDEnE provides the clarity I need with its intuitive interface and powerful AI.",
        },
    ];

    return (
        <div className="flex flex-col flex-1">
            {/* Hero Section */}
            <section className="py-20 md:py-32 bg-gradient-to-br from-luna-primary via-luna-primary to-luna-bg-dark border-b border-luna-text-light/10 dark:border-luna-text-dark/10">
                <div className="container mx-auto px-4">
                    <div className="flex flex-col gap-4 text-center max-w-4xl mx-auto animate-on-scroll fade-in-up" ref={missionRef as React.RefObject<HTMLDivElement>}>
                        <h1 className="text-4xl md:text-6xl font-black leading-tight tracking-[-0.033em] text-white">About LRDEnE</h1>
                        <p className="text-lg md:text-xl font-normal leading-normal text-white/80 max-w-2xl mx-auto">
                            We&apos;re on a mission to democratize legal clarity and make expert legal assistance accessible to everyone.
                        </p>
                    </div>
                </div>
            </section>

            {/* Mission Section */}
            <section className="py-20 md:py-28 bg-white dark:bg-luna-primary">
                <div className="container mx-auto px-4 max-w-4xl">
                    <div className="text-center mb-12 animate-on-scroll fade-in-up">
                        <h2 className="text-3xl md:text-5xl font-black leading-tight tracking-[-0.033em] mb-6 text-luna-text-light dark:text-luna-text-dark">
                            Our Mission: Democratizing Legal Clarity
                        </h2>
                        <p className="text-lg text-luna-text-light/70 dark:text-luna-text-dark/70 leading-relaxed">
                            Founded by a team of legal tech experts and European lawyers, LRDEnE is dedicated to bridging the gap between complex legislation and actionable insights. We believe that access to accurate, verifiable legal information should be fast, affordable, and available to everyone, from individual citizens to large law firms.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
                        {[
                            { icon: 'lightbulb', title: 'Innovation', description: 'Pioneering AI technology tailored specifically for German and EU legal frameworks.' },
                            { icon: 'balance', title: 'Accuracy', description: 'Precision-focused with verifiable citations and expert legal knowledge.' },
                            { icon: 'people', title: 'Accessibility', description: 'Making professional legal assistance available to everyone, not just large firms.' },
                        ].map((value, index) => (
                            <div key={index} className="text-center p-6 rounded-xl bg-luna-bg-light dark:bg-luna-bg-dark border border-luna-text-light/10 dark:border-luna-text-dark/20 animate-on-scroll fade-in-up">
                                <div className="flex items-center justify-center size-16 rounded-full bg-luna-secondary/10 mx-auto mb-4">
                                    <span className="material-symbols-outlined text-4xl text-luna-secondary">{value.icon}</span>
                                </div>
                                <h3 className="text-xl font-bold mb-2 text-luna-text-light dark:text-luna-text-dark">{value.title}</h3>
                                <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">{value.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Stats Section */}
            <section className="py-16 md:py-24 bg-luna-bg-light dark:bg-luna-bg-dark border-y border-luna-text-light/10 dark:border-luna-text-dark/10" aria-labelledby="platform-stats-heading">
                <div className="container mx-auto px-4">
                    <h2 id="platform-stats-heading" className="sr-only">Platform Statistics</h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto" role="list">
                        {[
                            { number: '7+', label: 'Legal Templates', icon: 'article' },
                            { number: '24/7', label: 'AI Assistant', icon: 'smart_toy' },
                            { number: 'GDPR', label: 'Compliant', icon: 'verified' },
                            { number: '99.9%', label: 'Target Uptime', icon: 'check_circle' },
                        ].map((stat, index) => (
                            <div key={index} className="flex flex-col items-center text-center animate-on-scroll fade-in-up" role="listitem">
                                <div className="flex items-center justify-center size-16 rounded-2xl bg-gradient-to-br from-luna-secondary/10 to-luna-secondary/5 mb-4" aria-hidden="true">
                                    <span className="material-symbols-outlined text-3xl text-luna-secondary" aria-hidden="true">{stat.icon}</span>
                                </div>
                                <div className="text-3xl md:text-4xl font-black text-luna-secondary mb-2" aria-label={`${stat.number} ${stat.label}`}>{stat.number}</div>
                                <div className="text-xs md:text-sm font-medium text-luna-text-light/70 dark:text-luna-text-dark/70">{stat.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Team Section */}
            <section className="py-20 md:py-28 bg-white dark:bg-luna-primary">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-12 animate-on-scroll fade-in-up">
                        <h2 className="text-3xl md:text-5xl font-black leading-tight tracking-[-0.033em] mb-4 text-luna-text-light dark:text-luna-text-dark">
                            Our Team
                        </h2>
                        <p className="text-lg text-luna-text-light/70 dark:text-luna-text-dark/70">
                            Experts in law, technology, and AI working together to transform legal practice
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
                        {[
                            { name: 'Dr. Elena Müller', role: 'Lead Legal AI Researcher', expertise: 'Expert in German Civil Code (BGB) and EU regulations' },
                            { name: 'Marcus Weber', role: 'Chief Technology Officer', expertise: 'AI/ML systems architect with 15+ years experience' },
                            { name: 'Sophie Dubois', role: 'Head of Product', expertise: 'Legal tech product design and user experience' },
                        ].map((member, index) => (
                            <div key={index} className="text-center p-6 rounded-xl bg-luna-bg-light dark:bg-luna-bg-dark border border-luna-text-light/10 dark:border-luna-text-dark/20 animate-on-scroll fade-in-up">
                                <div className="flex items-center justify-center size-20 rounded-full bg-gradient-to-br from-luna-secondary/20 to-luna-secondary/5 mx-auto mb-4">
                                    <span className="material-symbols-outlined text-5xl text-luna-secondary">account_circle</span>
                                </div>
                                <h3 className="text-lg font-bold mb-1 text-luna-text-light dark:text-luna-text-dark">{member.name}</h3>
                                <p className="text-sm font-medium text-luna-secondary mb-2">{member.role}</p>
                                <p className="text-xs text-luna-text-light/70 dark:text-luna-text-dark/70">{member.expertise}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Testimonials Section */}
            <section className="py-20 md:py-28 border-t border-luna-text-light/10 dark:border-luna-text-dark/10 bg-luna-bg-light dark:bg-luna-bg-dark">
                <div className="container mx-auto px-4">
                    <div className="flex flex-col gap-4 text-center max-w-3xl mx-auto mb-12 animate-on-scroll fade-in-up" ref={testimonialsHeadingRef as React.RefObject<HTMLDivElement>}>
                        <h2 className="text-3xl md:text-5xl font-black leading-tight tracking-[-0.033em] text-luna-text-light dark:text-luna-text-dark">Hear From Our Clients</h2>
                        <p className="text-base font-normal leading-normal text-luna-text-light/70 dark:text-luna-text-dark/70" ref={testimonialsParagraphRef as React.RefObject<HTMLParagraphElement>}>Discover how LRDEnE is transforming legal research for professionals across Europe.</p>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
                        {testimonials.map((testimonial) => (
                            <div key={testimonial.name} className="flex flex-col gap-6 rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 bg-white dark:bg-luna-primary p-6 shadow-sm hover:shadow-lg transition-shadow animate-on-scroll fade-in-up">
                                <div className="flex items-center gap-4">
                                    <img alt={`${testimonial.name}, ${testimonial.title}`} className="size-12 rounded-full object-cover" src={testimonial.img} onError={handleImageError} />
                                    <div>
                                        <h4 className="text-base font-bold leading-tight text-luna-text-light dark:text-luna-text-dark">{testimonial.name}</h4>
                                        <p className="text-sm font-normal text-luna-text-light/70 dark:text-luna-text-muted-dark">{testimonial.title}</p>
                                    </div>
                                </div>
                                <blockquote className="text-base italic text-luna-text-light/80 dark:text-luna-text-dark/90 leading-relaxed flex-1">
                                    &ldquo;{testimonial.quote}&rdquo;
                                </blockquote>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 md:py-32 bg-gradient-to-br from-luna-secondary/20 via-luna-secondary/10 to-luna-secondary/5">
                <div className="container mx-auto px-4">
                    <div className="max-w-4xl mx-auto text-center animate-on-scroll fade-in-up">
                        <h2 className="text-4xl md:text-6xl font-black leading-tight tracking-[-0.033em] mb-6 text-luna-text-light dark:text-luna-text-dark">
                            Join Our Mission
                        </h2>
                        <p className="text-lg md:text-xl text-luna-text-light/80 dark:text-luna-text-dark/80 mb-8 max-w-2xl mx-auto leading-relaxed">
                            Be part of the transformation in legal technology. Experience the future of legal practice with LRDEnE.
                        </p>
                        <button className="flex items-center justify-center gap-2 h-14 px-8 bg-luna-secondary text-luna-primary text-base font-bold rounded-lg hover:opacity-90 transition-opacity shadow-lg shadow-luna-secondary/30 mx-auto">
                            <span>Get Started Free</span>
                            <span className="material-symbols-outlined">arrow_forward</span>
                        </button>
                    </div>
                </div>
            </section>
        </div>
    );
};

// --- COLLABORATION PAGE ---
const CollaborationPage: React.FC<{ onNavigateToAuth?: (mode: 'signup' | 'login') => void; onNavigate?: (page: string) => void }> = ({ onNavigateToAuth, onNavigate }) => {
    const heroRef = useAOS('fade-in-up', {}, 0);

    // Global scroll observer for all animate-on-scroll elements
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('in-view');
                        observer.unobserve(entry.target);
                    }
                });
            },
            {
                threshold: 0.1,
                rootMargin: '0px 0px -50px 0px'
            }
        );

        const observeElements = () => {
            const animatedElements = document.querySelectorAll('.animate-on-scroll:not(.in-view)');
            animatedElements.forEach((el) => {
                const rect = el.getBoundingClientRect();
                const isInViewport = rect.top < window.innerHeight && rect.bottom > 0;

                if (isInViewport) {
                    el.classList.add('in-view');
                } else {
                    observer.observe(el);
                }
            });
        };

        const timeout = setTimeout(() => {
            observeElements();
        }, 100);

        const interval = setInterval(observeElements, 1000);

        return () => {
            clearTimeout(timeout);
            clearInterval(interval);
            const animatedElements = document.querySelectorAll('.animate-on-scroll');
            animatedElements.forEach((el) => {
                observer.unobserve(el);
            });
        };
    }, []);

    return (
        <div className="flex flex-col flex-1">
            {/* Hero Section */}
            <section className="py-20 md:py-32 bg-gradient-to-br from-luna-primary via-luna-primary to-luna-bg-dark border-b border-luna-text-light/10 dark:border-luna-text-dark/10">
                <div className="container mx-auto px-4">
                    <div className="flex flex-col gap-4 text-center max-w-4xl mx-auto animate-on-scroll fade-in-up" ref={heroRef as React.RefObject<HTMLDivElement>}>
                        <div className="inline-flex items-center justify-center size-20 rounded-2xl bg-gradient-to-br from-luna-secondary/20 to-luna-secondary/5 mb-6 mx-auto">
                            <span className="material-symbols-outlined text-5xl text-luna-secondary">handshake</span>
                        </div>
                        <h1 className="text-4xl md:text-6xl font-black leading-tight tracking-[-0.033em] text-white">Lawyer Partnership Program</h1>
                        <p className="text-lg md:text-xl font-normal leading-normal text-white/80 max-w-2xl mx-auto">
                            Partner with LRDEnE to grow your practice, receive qualified referrals, and access powerful AI tools for your legal work.
                        </p>
                    </div>
                </div>
            </section>

            {/* Lawyer Partners Section */}
            <section className="py-20 md:py-28 bg-white dark:bg-luna-primary">
                <div className="container mx-auto px-4">
                    <div className="max-w-6xl mx-auto">
                        <div className="text-center mb-16 animate-on-scroll fade-in-up">
                            <div className="inline-flex items-center justify-center size-16 rounded-xl bg-gradient-to-br from-blue-500/20 to-blue-500/5 mb-6">
                                <span className="material-symbols-outlined text-4xl text-blue-500">handshake</span>
                            </div>
                            <h2 className="text-3xl md:text-5xl font-black leading-tight tracking-[-0.033em] mb-4 text-luna-text-light dark:text-luna-text-dark">
                                Lawyer Partnership Program
                            </h2>
                            <p className="text-lg text-luna-text-light/70 dark:text-luna-text-dark/70 max-w-2xl mx-auto">
                                Partner with LRDEnE to grow your practice, receive qualified referrals, and access powerful AI tools
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
                            <div className="bg-luna-bg-light dark:bg-luna-bg-dark rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 p-8 hover:shadow-lg transition-all duration-300 animate-on-scroll fade-in-up">
                                <div className="flex items-center justify-center size-14 rounded-xl bg-gradient-to-br from-blue-500/20 to-blue-500/5 mb-4">
                                    <span className="material-symbols-outlined text-3xl text-blue-500">speed</span>
                                </div>
                                <h3 className="text-xl font-bold mb-3 text-luna-text-light dark:text-luna-text-dark">AI-Powered Research</h3>
                                <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 leading-relaxed mb-4">
                                    Reduce research time by up to 80% with instant AI analysis. Get precise legal citations, risk assessments, and compliance checks for German and EU law.
                                </p>
                                <ul className="space-y-2 text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">
                                    <li className="flex items-start gap-2">
                                        <span className="material-symbols-outlined text-blue-500 text-lg mt-0.5">check_circle</span>
                                        Instant document analysis with legal citations
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="material-symbols-outlined text-blue-500 text-lg mt-0.5">check_circle</span>
                                        GDPR, BGB, EU AI Act compliance checks
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="material-symbols-outlined text-blue-500 text-lg mt-0.5">check_circle</span>
                                        Risk assessment with prioritized findings
                                    </li>
                                </ul>
                            </div>

                            <div className="bg-luna-bg-light dark:bg-luna-bg-dark rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 p-8 hover:shadow-lg transition-all duration-300 animate-on-scroll fade-in-up">
                                <div className="flex items-center justify-center size-14 rounded-xl bg-gradient-to-br from-purple-500/20 to-purple-500/5 mb-4">
                                    <span className="material-symbols-outlined text-3xl text-purple-500">groups</span>
                                </div>
                                <h3 className="text-xl font-bold mb-3 text-luna-text-light dark:text-luna-text-dark">Client Collaboration</h3>
                                <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 leading-relaxed mb-4">
                                    Seamlessly collaborate with clients through our platform. Share documents, communicate securely, and manage cases efficiently.
                                </p>
                                <ul className="space-y-2 text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">
                                    <li className="flex items-start gap-2">
                                        <span className="material-symbols-outlined text-purple-500 text-lg mt-0.5">check_circle</span>
                                        Secure document sharing and storage
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="material-symbols-outlined text-purple-500 text-lg mt-0.5">check_circle</span>
                                        Real-time chat and communication
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="material-symbols-outlined text-purple-500 text-lg mt-0.5">check_circle</span>
                                        Case management and organization
                                    </li>
                                </ul>
                            </div>

                            <div className="bg-luna-bg-light dark:bg-luna-bg-dark rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 p-8 hover:shadow-lg transition-all duration-300 animate-on-scroll fade-in-up">
                                <div className="flex items-center justify-center size-14 rounded-xl bg-gradient-to-br from-green-500/20 to-green-500/5 mb-4">
                                    <span className="material-symbols-outlined text-3xl text-green-500">description</span>
                                </div>
                                <h3 className="text-xl font-bold mb-3 text-luna-text-light dark:text-luna-text-dark">Template Library</h3>
                                <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 leading-relaxed mb-4">
                                    Access professional German legal document templates. Generate GDPR requests, SCHUFA disputes, rental notices, and more in minutes.
                                </p>
                                <ul className="space-y-2 text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">
                                    <li className="flex items-start gap-2">
                                        <span className="material-symbols-outlined text-green-500 text-lg mt-0.5">check_circle</span>
                                        7+ German legal document types
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="material-symbols-outlined text-green-500 text-lg mt-0.5">check_circle</span>
                                        AI-powered document generation
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="material-symbols-outlined text-green-500 text-lg mt-0.5">check_circle</span>
                                        PDF export and customization
                                    </li>
                                </ul>
                            </div>

                            <div className="bg-luna-bg-light dark:bg-luna-bg-dark rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 p-8 hover:shadow-lg transition-all duration-300 animate-on-scroll fade-in-up">
                                <div className="flex items-center justify-center size-14 rounded-xl bg-gradient-to-br from-orange-500/20 to-orange-500/5 mb-4">
                                    <span className="material-symbols-outlined text-3xl text-orange-500">trending_up</span>
                                </div>
                                <h3 className="text-xl font-bold mb-3 text-luna-text-light dark:text-luna-text-dark">Scale Your Practice</h3>
                                <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 leading-relaxed mb-4">
                                    Handle more cases without increasing costs. Automate routine tasks and focus on high-value legal work.
                                </p>
                                <ul className="space-y-2 text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">
                                    <li className="flex items-start gap-2">
                                        <span className="material-symbols-outlined text-orange-500 text-lg mt-0.5">check_circle</span>
                                        Bulk document analysis
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="material-symbols-outlined text-orange-500 text-lg mt-0.5">check_circle</span>
                                        Team collaboration features
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="material-symbols-outlined text-orange-500 text-lg mt-0.5">check_circle</span>
                                        Cost-effective pricing plans
                                    </li>
                                </ul>
                            </div>
                        </div>

                        {/* Partnership Benefits */}
                        <div className="bg-gradient-to-r from-blue-500/10 via-luna-secondary/10 to-blue-500/10 dark:from-blue-500/5 dark:via-luna-secondary/5 dark:to-blue-500/5 rounded-xl border border-blue-200 dark:border-blue-900/30 p-8 mb-8 animate-on-scroll fade-in-up">
                            <h3 className="text-2xl font-bold text-center mb-6 text-luna-text-light dark:text-luna-text-dark">
                                Partnership Benefits
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="flex items-start gap-4">
                                    <div className="flex items-center justify-center size-12 rounded-lg bg-blue-500/10 flex-shrink-0">
                                        <span className="material-symbols-outlined text-2xl text-blue-500">trending_up</span>
                                    </div>
                                    <div>
                                        <h4 className="text-lg font-bold mb-2 text-luna-text-light dark:text-luna-text-dark">Qualified Client Referrals</h4>
                                        <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">
                                            Receive pre-qualified client referrals from our platform. Clients who need expert legal representation are matched with partner lawyers.
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4">
                                    <div className="flex items-center justify-center size-12 rounded-lg bg-green-500/10 flex-shrink-0">
                                        <span className="material-symbols-outlined text-2xl text-green-500">savings</span>
                                    </div>
                                    <div>
                                        <h4 className="text-lg font-bold mb-2 text-luna-text-light dark:text-luna-text-dark">Revenue Sharing</h4>
                                        <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">
                                            Earn revenue from referrals and platform usage. Transparent commission structure with competitive rates for partner lawyers.
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4">
                                    <div className="flex items-center justify-center size-12 rounded-lg bg-purple-500/10 flex-shrink-0">
                                        <span className="material-symbols-outlined text-2xl text-purple-500">verified</span>
                                    </div>
                                    <div>
                                        <h4 className="text-lg font-bold mb-2 text-luna-text-light dark:text-luna-text-dark">Verified Partner Badge</h4>
                                        <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">
                                            Get verified partner status with a badge on your profile. Build trust and credibility with potential clients.
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4">
                                    <div className="flex items-center justify-center size-12 rounded-lg bg-orange-500/10 flex-shrink-0">
                                        <span className="material-symbols-outlined text-2xl text-orange-500">support_agent</span>
                                    </div>
                                    <div>
                                        <h4 className="text-lg font-bold mb-2 text-luna-text-light dark:text-luna-text-dark">Priority Support</h4>
                                        <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">
                                            Access dedicated partner support team. Get help with platform features, client management, and technical issues.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="text-center animate-on-scroll fade-in-up">
                            <button
                                onClick={() => onNavigate?.('Contact')}
                                className="flex items-center justify-center gap-2 h-12 px-8 bg-blue-500 text-white text-base font-bold rounded-lg hover:bg-blue-600 transition-colors shadow-lg shadow-blue-500/30 mx-auto"
                            >
                                <span>Become a Partner</span>
                                <span className="material-symbols-outlined">arrow_forward</span>
                            </button>
                            <p className="text-xs text-luna-text-light/60 dark:text-luna-text-dark/60 mt-4">
                                Or <button onClick={() => onNavigateToAuth?.('signup')} className="text-blue-500 hover:underline font-semibold">sign up</button> to get started with our tools
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Partnership Requirements Section */}
            <section className="py-20 md:py-28 bg-luna-bg-light dark:bg-luna-bg-dark border-y border-luna-text-light/10 dark:border-luna-text-dark/10">
                <div className="container mx-auto px-4">
                    <div className="max-w-6xl mx-auto">
                        <div className="text-center mb-16 animate-on-scroll fade-in-up">
                            <h2 className="text-3xl md:text-5xl font-black leading-tight tracking-[-0.033em] mb-4 text-luna-text-light dark:text-luna-text-dark">
                                Partnership Requirements
                            </h2>
                            <p className="text-lg text-luna-text-light/70 dark:text-luna-text-dark/70 max-w-2xl mx-auto">
                                What we look for in our partner lawyers
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
                            {[
                                {
                                    icon: 'school',
                                    iconColor: 'text-blue-500',
                                    color: 'from-blue-500/20 to-blue-500/5',
                                    title: 'Legal Qualifications',
                                    description: 'Valid license to practice law in Germany or EU. Minimum 3 years of professional experience preferred.'
                                },
                                {
                                    icon: 'verified',
                                    iconColor: 'text-green-500',
                                    color: 'from-green-500/20 to-green-500/5',
                                    title: 'Professional Standing',
                                    description: 'Good standing with bar association. No disciplinary actions or malpractice claims.'
                                },
                                {
                                    icon: 'workspace_premium',
                                    iconColor: 'text-purple-500',
                                    color: 'from-purple-500/20 to-purple-500/5',
                                    title: 'Specialization Areas',
                                    description: 'Expertise in German/EU law areas: GDPR, contract law, employment law, real estate, or consumer protection.'
                                },
                                {
                                    icon: 'language',
                                    iconColor: 'text-orange-500',
                                    color: 'from-orange-500/20 to-orange-500/5',
                                    title: 'Language Skills',
                                    description: 'Fluent in German and English. Ability to communicate clearly with clients and handle documentation.'
                                },
                                {
                                    icon: 'schedule',
                                    iconColor: 'text-red-500',
                                    color: 'from-red-500/20 to-red-500/5',
                                    title: 'Availability',
                                    description: 'Commitment to respond to client inquiries within 24-48 hours. Dedicated time for partner clients.'
                                },
                                {
                                    icon: 'security',
                                    iconColor: 'text-luna-secondary',
                                    color: 'from-luna-secondary/20 to-luna-secondary/5',
                                    title: 'Professional Insurance',
                                    description: 'Valid professional liability insurance (Berufshaftpflichtversicherung) as required by German law.'
                                }
                            ].map((requirement, index) => (
                                <div key={index} className="bg-white dark:bg-luna-primary rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 p-6 hover:shadow-lg transition-all duration-300 animate-on-scroll fade-in-up">
                                    <div className={`flex items-center justify-center size-12 rounded-xl bg-gradient-to-br ${requirement.color} mb-4`}>
                                        <span className={`material-symbols-outlined text-2xl ${requirement.iconColor}`}>{requirement.icon}</span>
                                    </div>
                                    <h3 className="text-lg font-bold mb-2 text-luna-text-light dark:text-luna-text-dark">{requirement.title}</h3>
                                    <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 leading-relaxed">{requirement.description}</p>
                                </div>
                            ))}
                        </div>

                        <div className="bg-gradient-to-r from-blue-500/10 via-luna-secondary/10 to-blue-500/10 dark:from-blue-500/5 dark:via-luna-secondary/5 dark:to-blue-500/5 rounded-xl border border-blue-200 dark:border-blue-900/30 p-8 text-center animate-on-scroll fade-in-up">
                            <h3 className="text-xl font-bold mb-3 text-luna-text-light dark:text-luna-text-dark">
                                Application Process
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
                                {[
                                    { step: '1', text: 'Submit Application' },
                                    { step: '2', text: 'Credential Review' },
                                    { step: '3', text: 'Interview & Verification' },
                                    { step: '4', text: 'Partnership Approval' }
                                ].map((item, idx) => (
                                    <div key={idx} className="flex flex-col items-center">
                                        <div className="flex items-center justify-center size-12 rounded-full bg-blue-500 text-white text-lg font-bold mb-2">
                                            {item.step}
                                        </div>
                                        <p className="text-xs text-luna-text-light/70 dark:text-luna-text-dark/70 text-center">{item.text}</p>
                                    </div>
                                ))}
                            </div>
                            <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 mt-6">
                                Typical processing time: 5-7 business days
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Partner Success Stories Section */}
            <section className="py-20 md:py-28 bg-white dark:bg-luna-primary">
                <div className="container mx-auto px-4">
                    <div className="max-w-6xl mx-auto">
                        <div className="text-center mb-16 animate-on-scroll fade-in-up">
                            <h2 className="text-3xl md:text-5xl font-black leading-tight tracking-[-0.033em] mb-4 text-luna-text-light dark:text-luna-text-dark">
                                Partner Success Stories
                            </h2>
                            <p className="text-lg text-luna-text-light/70 dark:text-luna-text-dark/70 max-w-2xl mx-auto">
                                Hear from lawyers who have grown their practice through our partnership program
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {[
                                {
                                    name: 'Dr. Michael Weber',
                                    firm: 'Weber & Partners Rechtsanwälte',
                                    location: 'Berlin',
                                    image: 'https://lh3.googleusercontent.com/aida-public/AB6AXuC6oq33dNp6A-9NrO3YTfoAiNzhgWchFlJuFX0rTR88Ia5vLnjO8OS23dxBfNeB2iFSmP6TfzlTtpIZJPYNQ3aD2g5i5Td_5WlHtD9f8MXuQWh5pUE_Lp26Ayht0Pu3FHP34E3pEN8WfbEA5P2GZzoXMyfkpTBcDhl3WQ56kuDkn-RAOr2xCQpVII4BEDRoLmjtNSthqKVF4uReXtrxQlDVWbQZG_0U2MY9G0Z5smvb9KvpYrbwQhi4-G6kFaqf-OskjR16cIS0_ZaS',
                                    quote: 'Since becoming a partner, I\'ve received 15+ qualified referrals in just 3 months. The AI tools have also cut my research time in half.',
                                    metric: '15+ referrals',
                                    metricLabel: 'in 3 months'
                                },
                                {
                                    name: 'Anna Müller',
                                    firm: 'Müller Legal Services',
                                    location: 'Munich',
                                    image: 'https://lh3.googleusercontent.com/aida-public/AB6AXuAqByYWhqenqZy3P1S-knNZuc7IU0sFAXCbjzpXr-eXrYT7kd2q9A2zlAv54wEqRiVeo5hXeiLm4MlMCjux8aa3gL_G1OUiyXCzIQbtUMJxH8v1AujPdjjmamaK9YFLrpzS6ZtT-PFrd9HmhdGwh07p83l5jNnjMxoo49Qt4fmbeZbPEbHfnrl5RghUQt3kQLYmmLAhP93C7Hf1RxkmlqoFRzTgxUMWDU1QJ2gx_H9B_E7RuKby-3foRmDv8nhYclgppTXovL5x9P1tyhpEqUFndQjxNAaSK',
                                    quote: 'The partnership program has been a game-changer. Clients come pre-qualified with case details, saving me hours of initial consultations.',
                                    metric: '80%',
                                    metricLabel: 'time saved'
                                },
                                {
                                    name: 'Thomas Schmidt',
                                    firm: 'Schmidt Rechtsanwaltskanzlei',
                                    location: 'Hamburg',
                                    image: 'https://lh3.googleusercontent.com/aida-public/AB6AXuCldMAjJQpyX4b7kFOPZbYY-SaD-0yaDUhmujuknT6XeFTAWkqTH5f6MHmOewJ0Tc3LSByHXowbygEDLyqP3AIU7Aw0C5ujIwn06gDHWr3ClS1w_y2VNfWnCGr3oT104IGzYIaH3ltbcd-HzBe1wAANdx8iDT_QZ1BPnGTa3r4jQE7BGXNC91AJStxu_oSljDEkORJD_dD29AIvUcVzRcPHJzfF6GwbJcW272JzvHKhy3PS3kqnZmz9xNA6wFe75m5qag0Up4EVFNbP',
                                    quote: 'Revenue sharing from referrals has added a significant income stream. Plus, the verified partner badge builds trust with new clients.',
                                    metric: '30%',
                                    metricLabel: 'revenue increase'
                                }
                            ].map((story, index) => (
                                <div key={index} className="bg-luna-bg-light dark:bg-luna-bg-dark rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 p-6 hover:shadow-lg transition-all duration-300 animate-on-scroll fade-in-up">
                                    <div className="flex items-center gap-3 mb-4">
                                        <img
                                            src={story.image}
                                            alt={story.name}
                                            className="size-12 rounded-full object-cover"
                                            onError={handleImageError}
                                        />
                                        <div>
                                            <p className="text-sm font-bold text-luna-text-light dark:text-luna-text-dark">{story.name}</p>
                                            <p className="text-xs text-luna-text-light/60 dark:text-luna-text-dark/60">{story.firm}</p>
                                            <p className="text-xs text-luna-text-light/50 dark:text-luna-text-dark/50">{story.location}</p>
                                        </div>
                                    </div>
                                    <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 leading-relaxed mb-4 italic">
                                        &ldquo;{story.quote}&rdquo;
                                    </p>
                                    <div className="pt-4 border-t border-luna-text-light/10 dark:border-luna-text-dark/10">
                                        <div className="text-center">
                                            <p className="text-2xl font-bold text-luna-secondary">{story.metric}</p>
                                            <p className="text-xs text-luna-text-light/60 dark:text-luna-text-dark/60">{story.metricLabel}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ Section for Partners */}
            <section className="py-20 md:py-28 bg-luna-bg-light dark:bg-luna-bg-dark border-y border-luna-text-light/10 dark:border-luna-text-dark/10">
                <div className="container mx-auto px-4">
                    <div className="max-w-4xl mx-auto">
                        <div className="text-center mb-16 animate-on-scroll fade-in-up">
                            <h2 className="text-3xl md:text-5xl font-black leading-tight tracking-[-0.033em] mb-4 text-luna-text-light dark:text-luna-text-dark">
                                Partnership FAQ
                            </h2>
                            <p className="text-lg text-luna-text-light/70 dark:text-luna-text-dark/70 max-w-2xl mx-auto">
                                Common questions about becoming a partner lawyer
                            </p>
                        </div>

                        <div className="space-y-4">
                            {[
                                {
                                    question: 'How do I become a partner lawyer?',
                                    answer: 'Submit an application through our Contact page or sign up and indicate your interest in partnership. We\'ll review your credentials, conduct an interview, and verify your professional standing. The process typically takes 5-7 business days.'
                                },
                                {
                                    question: 'What are the costs to become a partner?',
                                    answer: 'There are no upfront costs to become a partner. We operate on a revenue-sharing model where partners earn commissions from referrals and platform usage. You only pay standard platform subscription fees if you choose premium features.'
                                },
                                {
                                    question: 'How are clients referred to me?',
                                    answer: 'Clients who need expert legal representation are matched with partner lawyers based on specialization, location, availability, and client preferences. You\'ll receive case details and can accept or decline referrals based on your capacity.'
                                },
                                {
                                    question: 'What is the revenue sharing structure?',
                                    answer: 'Partners earn competitive commissions on referred cases and platform usage. The exact percentage depends on partnership tier and case type. All details are provided during the partnership onboarding process.'
                                },
                                {
                                    question: 'Do I need to use LRDEnE\'s platform tools?',
                                    answer: 'While not mandatory, using our AI tools enhances your efficiency and can increase your referral priority. Many partners find the document analysis and template generation features significantly improve their workflow.'
                                },
                                {
                                    question: 'Can I partner if I\'m part of a law firm?',
                                    answer: 'Yes! Both individual lawyers and law firms can become partners. We work with solo practitioners, small firms, and larger practices. Partnership benefits apply to the entire firm when applicable.'
                                },
                                {
                                    question: 'What happens if I can\'t take a referral?',
                                    answer: 'You can decline referrals at any time without penalty. We understand that lawyers have capacity limits. Declining a referral simply means it goes to another qualified partner lawyer.'
                                },
                                {
                                    question: 'Is there a minimum commitment?',
                                    answer: 'No, there\'s no minimum number of cases you must accept. Partners can participate as much or as little as fits their practice. However, active partners who accept more referrals may receive priority matching.'
                                }
                            ].map((faq, index) => (
                                <div key={index} className="bg-white dark:bg-luna-primary rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 p-6 hover:shadow-lg transition-all duration-300 animate-on-scroll fade-in-up">
                                    <h3 className="text-lg font-bold mb-3 text-luna-text-light dark:text-luna-text-dark flex items-start gap-3">
                                        <span className="material-symbols-outlined text-luna-secondary text-xl mt-0.5">help</span>
                                        {faq.question}
                                    </h3>
                                    <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 leading-relaxed pl-9">
                                        {faq.answer}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Partnership Statistics Section */}
            <section className="py-20 md:py-28 bg-gradient-to-br from-blue-50 via-white to-blue-50/30 dark:from-blue-950/20 dark:via-luna-primary dark:to-blue-950/10 border-y border-blue-200/20 dark:border-blue-900/20">
                <div className="container mx-auto px-4">
                    <div className="max-w-6xl mx-auto">
                        <div className="text-center mb-16 animate-on-scroll fade-in-up">
                            <h2 className="text-3xl md:text-5xl font-black leading-tight tracking-[-0.033em] mb-4 text-luna-text-light dark:text-luna-text-dark">
                                Partnership Network by the Numbers
                            </h2>
                            <p className="text-lg text-luna-text-light/70 dark:text-luna-text-dark/70 max-w-2xl mx-auto">
                                Growing network of verified partner lawyers across Germany and the EU
                            </p>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                            {[
                                { number: '150+', label: 'Partner Lawyers', icon: 'gavel', color: 'text-blue-500' },
                                { number: '500+', label: 'Cases Referred', icon: 'description', color: 'text-green-500' },
                                { number: '95%', label: 'Client Satisfaction', icon: 'star', color: 'text-yellow-500' },
                                { number: '24h', label: 'Avg. Response Time', icon: 'schedule', color: 'text-purple-500' }
                            ].map((stat, index) => (
                                <div key={index} className="bg-white dark:bg-luna-primary rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 p-6 text-center hover:shadow-lg transition-all duration-300 animate-on-scroll fade-in-up">
                                    <div className="flex items-center justify-center mb-3">
                                        <span className={`material-symbols-outlined text-3xl ${stat.color}`}>{stat.icon}</span>
                                    </div>
                                    <p className="text-3xl font-black text-luna-secondary mb-2">{stat.number}</p>
                                    <p className="text-xs text-luna-text-light/70 dark:text-luna-text-dark/70">{stat.label}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Referral Matching Process Section */}
            <section className="py-20 md:py-28 bg-white dark:bg-luna-primary">
                <div className="container mx-auto px-4">
                    <div className="max-w-6xl mx-auto">
                        <div className="text-center mb-16 animate-on-scroll fade-in-up">
                            <h2 className="text-3xl md:text-5xl font-black leading-tight tracking-[-0.033em] mb-4 text-luna-text-light dark:text-luna-text-dark">
                                How Referral Matching Works
                            </h2>
                            <p className="text-lg text-luna-text-light/70 dark:text-luna-text-dark/70 max-w-2xl mx-auto">
                                Smart matching algorithm connects clients with the right partner lawyers
                            </p>
                        </div>

                        <div className="bg-luna-bg-light dark:bg-luna-bg-dark rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 p-8 mb-8 animate-on-scroll fade-in-up">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div>
                                    <h3 className="text-xl font-bold mb-4 text-luna-text-light dark:text-luna-text-dark">Matching Criteria</h3>
                                    <ul className="space-y-3">
                                        {[
                                            'Legal specialization and expertise area',
                                            'Geographic location and service area',
                                            'Lawyer availability and capacity',
                                            'Client case complexity and requirements',
                                            'Language preferences',
                                            'Previous client ratings and reviews'
                                        ].map((item, idx) => (
                                            <li key={idx} className="flex items-start gap-2 text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">
                                                <span className="material-symbols-outlined text-luna-secondary text-lg mt-0.5">check_circle</span>
                                                {item}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold mb-4 text-luna-text-light dark:text-luna-text-dark">What You Receive</h3>
                                    <ul className="space-y-3">
                                        {[
                                            'Complete case summary and initial documentation',
                                            'Client contact information and communication history',
                                            'Case priority level and urgency indicator',
                                            'Estimated case value and complexity',
                                            'Client expectations and timeline',
                                            'All relevant documents pre-analyzed by AI'
                                        ].map((item, idx) => (
                                            <li key={idx} className="flex items-start gap-2 text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">
                                                <span className="material-symbols-outlined text-green-500 text-lg mt-0.5">description</span>
                                                {item}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <div className="bg-gradient-to-r from-blue-500/10 via-luna-secondary/10 to-blue-500/10 dark:from-blue-500/5 dark:via-luna-secondary/5 dark:to-blue-500/5 rounded-xl border border-blue-200 dark:border-blue-900/30 p-6 animate-on-scroll fade-in-up">
                            <div className="flex items-start gap-4">
                                <div className="flex items-center justify-center size-12 rounded-lg bg-blue-500/10 flex-shrink-0">
                                    <span className="material-symbols-outlined text-2xl text-blue-500">lightbulb</span>
                                </div>
                                <div>
                                    <h4 className="text-lg font-bold mb-2 text-luna-text-light dark:text-luna-text-dark">Smart Matching Benefits</h4>
                                    <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 leading-relaxed">
                                        Our AI-powered matching system ensures clients are connected with lawyers who have the right expertise and availability.
                                        This means higher case acceptance rates, better client outcomes, and more efficient use of your time.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Partnership Tiers Section */}
            <section className="py-20 md:py-28 bg-luna-bg-light dark:bg-luna-bg-dark border-y border-luna-text-light/10 dark:border-luna-text-dark/10">
                <div className="container mx-auto px-4">
                    <div className="max-w-6xl mx-auto">
                        <div className="text-center mb-16 animate-on-scroll fade-in-up">
                            <h2 className="text-3xl md:text-5xl font-black leading-tight tracking-[-0.033em] mb-4 text-luna-text-light dark:text-luna-text-dark">
                                Partnership Tiers
                            </h2>
                            <p className="text-lg text-luna-text-light/70 dark:text-luna-text-dark/70 max-w-2xl mx-auto">
                                Choose the partnership level that fits your practice
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {[
                                {
                                    tier: 'Standard Partner',
                                    tierSlug: 'standard',
                                    icon: 'person',
                                    color: 'from-blue-500/20 to-blue-500/5',
                                    iconColor: 'text-blue-500',
                                    features: [
                                        'Qualified client referrals',
                                        'Standard commission rates',
                                        'Verified partner badge',
                                        'Platform access',
                                        'Email support'
                                    ],
                                    bestFor: 'Solo practitioners and small firms'
                                },
                                {
                                    tier: 'Premium Partner',
                                    tierSlug: 'premium',
                                    icon: 'workspace_premium',
                                    color: 'from-purple-500/20 to-purple-500/5',
                                    iconColor: 'text-purple-500',
                                    features: [
                                        'Priority referral matching',
                                        'Higher commission rates',
                                        'Premium partner badge',
                                        'Advanced platform features',
                                        'Priority support',
                                        'Marketing support'
                                    ],
                                    bestFor: 'Established firms and specialists',
                                    popular: true
                                },
                                {
                                    tier: 'Enterprise Partner',
                                    tierSlug: 'enterprise',
                                    icon: 'business',
                                    color: 'from-luna-secondary/20 to-luna-secondary/5',
                                    iconColor: 'text-luna-secondary',
                                    features: [
                                        'Dedicated account manager',
                                        'Custom commission structure',
                                        'Enterprise partner badge',
                                        'White-label options',
                                        'API access',
                                        'Custom integrations',
                                        'Dedicated support team'
                                    ],
                                    bestFor: 'Large firms and legal networks'
                                }
                            ].map((tier, index) => (
                                <div key={index} className={`bg-white dark:bg-luna-primary rounded-xl border-2 ${tier.popular ? 'border-purple-500 dark:border-purple-500' : 'border-luna-text-light/10 dark:border-luna-text-dark/20'} p-8 hover:shadow-lg transition-all duration-300 animate-on-scroll fade-in-up relative`}>
                                    {tier.popular && (
                                        <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                                            <span className="bg-purple-500 text-white text-xs font-bold px-4 py-1 rounded-full">Most Popular</span>
                                        </div>
                                    )}
                                    <div className={`flex items-center justify-center size-16 rounded-xl bg-gradient-to-br ${tier.color} mb-6`}>
                                        <span className={`material-symbols-outlined text-4xl ${tier.iconColor}`}>{tier.icon}</span>
                                    </div>
                                    <h3 className="text-2xl font-bold mb-2 text-luna-text-light dark:text-luna-text-dark">{tier.tier}</h3>
                                    <p className="text-sm text-luna-text-light/60 dark:text-luna-text-dark/60 mb-6">{tier.bestFor}</p>
                                    <ul className="space-y-3 mb-6">
                                        {tier.features.map((feature, idx) => (
                                            <li key={idx} className="flex items-start gap-2 text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">
                                                <span className={`material-symbols-outlined text-lg mt-0.5 ${tier.iconColor}`}>check_circle</span>
                                                {feature}
                                            </li>
                                        ))}
                                    </ul>
                                    <button
                                        onClick={() => {
                                            window.location.href = `/partnership-tier/${tier.tierSlug}`;
                                        }}
                                        className={`w-full flex items-center justify-center gap-2 h-12 px-6 rounded-lg font-bold transition-colors ${tier.popular
                                            ? 'bg-purple-500 text-white hover:bg-purple-600 shadow-lg shadow-purple-500/30'
                                            : 'bg-luna-bg-light dark:bg-luna-bg-dark border border-luna-text-light/20 dark:border-luna-text-dark/30 text-luna-text-light dark:text-luna-text-dark hover:bg-luna-text-light/5 dark:hover:bg-luna-text-dark/5'
                                            }`}
                                    >
                                        <span>Learn More</span>
                                        <span className="material-symbols-outlined">arrow_forward</span>
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* For Legal Insurances Section */}
            <section className="py-20 md:py-28 bg-white dark:bg-luna-primary border-y border-luna-text-light/10 dark:border-luna-text-dark/10">
                <div className="container mx-auto px-4">
                    <div className="max-w-6xl mx-auto">
                        <div className="text-center mb-16 animate-on-scroll fade-in-up">
                            <div className="inline-flex items-center justify-center size-16 rounded-xl bg-gradient-to-br from-green-500/20 to-green-500/5 mb-6">
                                <span className="material-symbols-outlined text-4xl text-green-500">shield</span>
                            </div>
                            <h2 className="text-3xl md:text-5xl font-black leading-tight tracking-[-0.033em] mb-4 text-luna-text-light dark:text-luna-text-dark">
                                For Legal Insurances
                            </h2>
                            <p className="text-lg text-luna-text-light/70 dark:text-luna-text-dark/70 max-w-2xl mx-auto">
                                Streamline claims processing and provide better service to your policyholders
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                            {[
                                {
                                    icon: 'assessment',
                                    iconColor: 'text-blue-500',
                                    color: 'from-blue-500/20 to-blue-500/5',
                                    title: 'Faster Claims Processing',
                                    description: 'Automate initial case assessment with AI-powered document analysis. Reduce processing time and improve accuracy.'
                                },
                                {
                                    icon: 'savings',
                                    iconColor: 'text-green-500',
                                    color: 'from-green-500/20 to-green-500/5',
                                    title: 'Cost Reduction',
                                    description: 'Lower operational costs by automating routine legal document reviews and compliance checks.'
                                },
                                {
                                    icon: 'verified',
                                    iconColor: 'text-purple-500',
                                    color: 'from-purple-500/20 to-purple-500/5',
                                    title: 'Quality Assurance',
                                    description: 'Ensure consistent quality in legal assessments with AI-powered compliance verification and risk analysis.'
                                },
                                {
                                    icon: 'connect_without_contact',
                                    iconColor: 'text-orange-500',
                                    color: 'from-orange-500/20 to-orange-500/5',
                                    title: 'Policyholder Portal',
                                    description: 'Provide your policyholders with direct access to legal document generation and AI assistance.'
                                },
                                {
                                    icon: 'analytics',
                                    iconColor: 'text-red-500',
                                    color: 'from-red-500/20 to-red-500/5',
                                    title: 'Claims Analytics',
                                    description: 'Get insights into claim patterns, common legal issues, and processing efficiency metrics.'
                                },
                                {
                                    icon: 'handshake',
                                    iconColor: 'text-luna-secondary',
                                    color: 'from-luna-secondary/20 to-luna-secondary/5',
                                    title: 'Lawyer Network',
                                    description: 'Connect policyholders with qualified lawyers from our partner network when needed.'
                                }
                            ].map((feature, index) => (
                                <div key={index} className="bg-white dark:bg-luna-primary rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 p-6 hover:shadow-lg transition-all duration-300 animate-on-scroll fade-in-up">
                                    <div className={`flex items-center justify-center size-12 rounded-xl bg-gradient-to-br ${feature.color} mb-4`}>
                                        <span className={`material-symbols-outlined text-2xl ${feature.iconColor}`}>{feature.icon}</span>
                                    </div>
                                    <h3 className="text-lg font-bold mb-2 text-luna-text-light dark:text-luna-text-dark">{feature.title}</h3>
                                    <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 leading-relaxed">{feature.description}</p>
                                </div>
                            ))}
                        </div>

                        <div className="bg-gradient-to-r from-green-500/10 via-luna-secondary/10 to-green-500/10 dark:from-green-500/5 dark:via-luna-secondary/5 dark:to-green-500/5 rounded-xl border border-green-200 dark:border-green-900/30 p-8 text-center animate-on-scroll fade-in-up">
                            <h3 className="text-2xl font-bold mb-4 text-luna-text-light dark:text-luna-text-dark">
                                Partnership Opportunities
                            </h3>
                            <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 mb-6 max-w-2xl mx-auto">
                                We offer custom integration solutions for legal insurances. White-label options, API access, and dedicated support available.
                            </p>
                            <button
                                onClick={() => onNavigate?.('LegalInsurance')}
                                className="flex items-center justify-center gap-2 h-12 px-8 bg-green-500 text-white text-base font-bold rounded-lg hover:bg-green-600 transition-colors shadow-lg shadow-green-500/30 mx-auto"
                            >
                                <span>Learn More About Partnership</span>
                                <span className="material-symbols-outlined">arrow_forward</span>
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* For People Section */}
            <section className="py-20 md:py-28 bg-white dark:bg-luna-primary">
                <div className="container mx-auto px-4">
                    <div className="max-w-6xl mx-auto">
                        <div className="text-center mb-16 animate-on-scroll fade-in-up">
                            <div className="inline-flex items-center justify-center size-16 rounded-xl bg-gradient-to-br from-purple-500/20 to-purple-500/5 mb-6">
                                <span className="material-symbols-outlined text-4xl text-purple-500">people</span>
                            </div>
                            <h2 className="text-3xl md:text-5xl font-black leading-tight tracking-[-0.033em] mb-4 text-luna-text-light dark:text-luna-text-dark">
                                For People & Citizens
                            </h2>
                            <p className="text-lg text-luna-text-light/70 dark:text-luna-text-dark/70 max-w-2xl mx-auto">
                                Get professional legal assistance without expensive lawyer fees. Connect with qualified lawyers when you need expert help.
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
                            <div className="bg-luna-bg-light dark:bg-luna-bg-dark rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 p-8 hover:shadow-lg transition-all duration-300 animate-on-scroll fade-in-up">
                                <div className="flex items-center justify-center size-14 rounded-xl bg-gradient-to-br from-purple-500/20 to-purple-500/5 mb-4">
                                    <span className="material-symbols-outlined text-3xl text-purple-500">description</span>
                                </div>
                                <h3 className="text-xl font-bold mb-3 text-luna-text-light dark:text-luna-text-dark">Self-Service Legal Documents</h3>
                                <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 leading-relaxed mb-4">
                                    Generate professional legal documents yourself. GDPR requests, SCHUFA disputes, rental notices, and more—all without expensive lawyer fees.
                                </p>
                                <ul className="space-y-2 text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">
                                    <li className="flex items-start gap-2">
                                        <span className="material-symbols-outlined text-purple-500 text-lg mt-0.5">check_circle</span>
                                        AI-powered document generation
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="material-symbols-outlined text-purple-500 text-lg mt-0.5">check_circle</span>
                                        Professional legal language
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="material-symbols-outlined text-purple-500 text-lg mt-0.5">check_circle</span>
                                        PDF export ready for submission
                                    </li>
                                </ul>
                            </div>

                            <div className="bg-luna-bg-light dark:bg-luna-bg-dark rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 p-8 hover:shadow-lg transition-all duration-300 animate-on-scroll fade-in-up">
                                <div className="flex items-center justify-center size-14 rounded-xl bg-gradient-to-br from-blue-500/20 to-blue-500/5 mb-4">
                                    <span className="material-symbols-outlined text-3xl text-blue-500">support_agent</span>
                                </div>
                                <h3 className="text-xl font-bold mb-3 text-luna-text-light dark:text-luna-text-dark">AI Legal Assistant</h3>
                                <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 leading-relaxed mb-4">
                                    Chat with our AI legal assistant for instant answers to legal questions. Get expert guidance with proper citations and actionable advice.
                                </p>
                                <ul className="space-y-2 text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">
                                    <li className="flex items-start gap-2">
                                        <span className="material-symbols-outlined text-blue-500 text-lg mt-0.5">check_circle</span>
                                        24/7 availability
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="material-symbols-outlined text-blue-500 text-lg mt-0.5">check_circle</span>
                                        Precise legal citations
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="material-symbols-outlined text-blue-500 text-lg mt-0.5">check_circle</span>
                                        Context-aware responses
                                    </li>
                                </ul>
                            </div>

                            <div className="bg-luna-bg-light dark:bg-luna-bg-dark rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 p-8 hover:shadow-lg transition-all duration-300 animate-on-scroll fade-in-up">
                                <div className="flex items-center justify-center size-14 rounded-xl bg-gradient-to-br from-green-500/20 to-green-500/5 mb-4">
                                    <span className="material-symbols-outlined text-3xl text-green-500">gavel</span>
                                </div>
                                <h3 className="text-xl font-bold mb-3 text-luna-text-light dark:text-luna-text-dark">Connect with Lawyers</h3>
                                <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 leading-relaxed mb-4">
                                    When you need expert legal representation, easily connect with qualified lawyers from our partner network. Share documents securely and collaborate efficiently.
                                </p>
                                <ul className="space-y-2 text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">
                                    <li className="flex items-start gap-2">
                                        <span className="material-symbols-outlined text-green-500 text-lg mt-0.5">check_circle</span>
                                        Find qualified lawyers
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="material-symbols-outlined text-green-500 text-lg mt-0.5">check_circle</span>
                                        Secure document sharing
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="material-symbols-outlined text-green-500 text-lg mt-0.5">check_circle</span>
                                        Transparent communication
                                    </li>
                                </ul>
                            </div>

                            <div className="bg-luna-bg-light dark:bg-luna-bg-dark rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 p-8 hover:shadow-lg transition-all duration-300 animate-on-scroll fade-in-up">
                                <div className="flex items-center justify-center size-14 rounded-xl bg-gradient-to-br from-orange-500/20 to-orange-500/5 mb-4">
                                    <span className="material-symbols-outlined text-3xl text-orange-500">security</span>
                                </div>
                                <h3 className="text-xl font-bold mb-3 text-luna-text-light dark:text-luna-text-dark">Legal Insurance Integration</h3>
                                <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 leading-relaxed mb-4">
                                    If you have legal insurance, use our platform through your insurance provider. Access legal services seamlessly through your policy.
                                </p>
                                <ul className="space-y-2 text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">
                                    <li className="flex items-start gap-2">
                                        <span className="material-symbols-outlined text-orange-500 text-lg mt-0.5">check_circle</span>
                                        Insurance provider integration
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="material-symbols-outlined text-orange-500 text-lg mt-0.5">check_circle</span>
                                        Streamlined claims process
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="material-symbols-outlined text-orange-500 text-lg mt-0.5">check_circle</span>
                                        Direct access to legal services
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <div className="text-center animate-on-scroll fade-in-up">
                            <button
                                onClick={() => onNavigateToAuth?.('signup')}
                                className="flex items-center justify-center gap-2 h-12 px-8 bg-purple-500 text-white text-base font-bold rounded-lg hover:bg-purple-600 transition-colors shadow-lg shadow-purple-500/30 mx-auto"
                            >
                                <span>Get Started Free</span>
                                <span className="material-symbols-outlined">arrow_forward</span>
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* How It Works Together Section */}
            <section className="py-20 md:py-28 bg-luna-bg-light dark:bg-luna-bg-dark border-y border-luna-text-light/10 dark:border-luna-text-dark/10">
                <div className="container mx-auto px-4">
                    <div className="max-w-6xl mx-auto">
                        <div className="text-center mb-16 animate-on-scroll fade-in-up">
                            <h2 className="text-3xl md:text-5xl font-black leading-tight tracking-[-0.033em] mb-4 text-luna-text-light dark:text-luna-text-dark">
                                How Collaboration Works
                            </h2>
                            <p className="text-lg text-luna-text-light/70 dark:text-luna-text-dark/70 max-w-2xl mx-auto">
                                A seamless ecosystem connecting people, lawyers, and legal insurances
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            {[
                                {
                                    step: '1',
                                    title: 'People Need Legal Help',
                                    description: 'Citizens use LRDEnE to generate legal documents or get AI assistance. When expert help is needed, they can connect with lawyers.',
                                    icon: 'person',
                                    color: 'from-purple-500/20 to-purple-500/5',
                                    iconColor: 'text-purple-500'
                                },
                                {
                                    step: '2',
                                    title: 'Lawyers Provide Expertise',
                                    description: 'Lawyers use AI tools to work more efficiently, then collaborate with clients through our secure platform. Legal insurances can refer cases.',
                                    icon: 'gavel',
                                    color: 'from-blue-500/20 to-blue-500/5',
                                    iconColor: 'text-blue-500'
                                },
                                {
                                    step: '3',
                                    title: 'Legal Insurances Streamline',
                                    description: 'Insurance companies integrate LRDEnE to automate claims processing, reduce costs, and provide better service to policyholders.',
                                    icon: 'shield',
                                    color: 'from-green-500/20 to-green-500/5',
                                    iconColor: 'text-green-500'
                                }
                            ].map((step, index) => (
                                <div key={index} className="bg-white dark:bg-luna-primary rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 p-8 text-center hover:shadow-lg transition-all duration-300 animate-on-scroll fade-in-up">
                                    <div className={`inline-flex items-center justify-center size-16 rounded-full bg-gradient-to-br ${step.color} mb-4`}>
                                        <span className={`material-symbols-outlined text-3xl ${step.iconColor}`}>{step.icon}</span>
                                    </div>
                                    <div className="inline-flex items-center justify-center size-10 rounded-full bg-luna-secondary text-luna-primary text-lg font-bold mb-4">
                                        {step.step}
                                    </div>
                                    <h3 className="text-xl font-bold mb-3 text-luna-text-light dark:text-luna-text-dark">{step.title}</h3>
                                    <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 leading-relaxed">{step.description}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 md:py-28 bg-gradient-to-br from-luna-secondary/10 via-luna-primary to-luna-secondary/10 dark:from-luna-secondary/5 dark:via-luna-bg-dark dark:to-luna-secondary/5">
                <div className="container mx-auto px-4">
                    <div className="max-w-4xl mx-auto text-center animate-on-scroll fade-in-up">
                        <h2 className="text-3xl md:text-5xl font-black leading-tight tracking-[-0.033em] mb-6 text-luna-text-light dark:text-luna-text-dark">
                            Ready to Become a Partner?
                        </h2>
                        <p className="text-lg text-luna-text-light/70 dark:text-luna-text-dark/70 mb-8 max-w-2xl mx-auto leading-relaxed">
                            Join our network of verified partner lawyers. Grow your practice with qualified referrals and powerful AI tools.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <button
                                onClick={() => onNavigateToAuth?.('signup')}
                                className="flex items-center justify-center gap-2 h-14 px-8 bg-luna-secondary text-luna-primary text-base font-bold rounded-lg hover:opacity-90 transition-opacity shadow-lg shadow-luna-secondary/30"
                            >
                                <span>Get Started Free</span>
                                <span className="material-symbols-outlined">arrow_forward</span>
                            </button>
                            <button
                                onClick={() => onNavigate?.('Contact')}
                                className="flex items-center justify-center gap-2 h-14 px-8 bg-white/80 dark:bg-luna-primary/80 backdrop-blur-md border border-luna-text-light/20 dark:border-luna-text-dark/30 text-luna-text-light dark:text-luna-text-dark text-base font-bold rounded-lg hover:bg-white dark:hover:bg-luna-primary transition-all"
                            >
                                <span>Contact Us</span>
                                <span className="material-symbols-outlined">mail</span>
                            </button>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

// --- NEW CONTACT PAGE ---
const NotFoundPage: React.FC<{ onNavigate?: (page: string) => void }> = ({ onNavigate }) => {
    return (
        <div className="flex flex-col items-center justify-center min-h-[60vh] px-4 py-20">
            <div className="text-center max-w-2xl mx-auto">
                {/* 404 Number */}
                <div className="mb-8">
                    <h1 className="text-9xl md:text-[12rem] font-black leading-none text-luna-secondary/20 dark:text-luna-secondary/10">
                        404
                    </h1>
                </div>

                {/* Error Message */}
                <div className="mb-8">
                    <h2 className="text-3xl md:text-5xl font-black leading-tight tracking-[-0.033em] mb-4 text-luna-text-light dark:text-luna-text-dark">
                        Page Not Found
                    </h2>
                    <p className="text-lg md:text-xl text-luna-text-light/70 dark:text-luna-text-dark/70 leading-relaxed">
                        The page you&apos;re looking for doesn&apos;t exist or has been moved.
                        It might have been deleted, or the URL might be incorrect.
                    </p>
                </div>

                {/* Helpful Links */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
                    {onNavigate ? (
                        <>
                            <button
                                onClick={() => onNavigate('Dashboard')}
                                className="flex items-center justify-center gap-2 h-12 px-6 bg-luna-secondary text-luna-primary text-base font-bold rounded-lg hover:opacity-90 transition-opacity"
                            >
                                <span className="material-symbols-outlined">dashboard</span>
                                <span>Go to Dashboard</span>
                            </button>
                            <button
                                onClick={() => onNavigate('Landing')}
                                className="flex items-center justify-center gap-2 h-12 px-6 border-2 border-luna-text-light/20 dark:border-luna-text-dark/20 text-luna-text-light dark:text-luna-text-dark text-base font-bold rounded-lg hover:bg-luna-bg-light dark:hover:bg-luna-bg-dark/50 transition-colors"
                            >
                                <span className="material-symbols-outlined">home</span>
                                <span>Go to Home</span>
                            </button>
                        </>
                    ) : (
                        <>
                            <a
                                href="/"
                                className="flex items-center justify-center gap-2 h-12 px-6 bg-luna-secondary text-luna-primary text-base font-bold rounded-lg hover:opacity-90 transition-opacity"
                            >
                                <span className="material-symbols-outlined">home</span>
                                <span>Go to Home</span>
                            </a>
                            <a
                                href="/dashboard"
                                className="flex items-center justify-center gap-2 h-12 px-6 border-2 border-luna-text-light/20 dark:border-luna-text-dark/20 text-luna-text-light dark:text-luna-text-dark text-base font-bold rounded-lg hover:bg-luna-bg-light dark:hover:bg-luna-bg-dark/50 transition-colors"
                            >
                                <span className="material-symbols-outlined">dashboard</span>
                                <span>Go to Dashboard</span>
                            </a>
                        </>
                    )}
                </div>

                {/* Popular Links */}
                <div className="mt-12 pt-8 border-t border-luna-text-light/10 dark:border-luna-text-dark/10">
                    <p className="text-sm font-medium text-luna-text-light/60 dark:text-luna-text-dark/60 mb-4">
                        Popular Pages:
                    </p>
                    <div className="flex flex-wrap justify-center gap-4">
                        {['Features', 'Pricing', 'About', 'Contact'].map((page) => (
                            onNavigate ? (
                                <button
                                    key={page}
                                    onClick={() => onNavigate(page === 'About' ? 'AboutUs' : page)}
                                    className="text-sm text-luna-secondary hover:underline font-medium"
                                >
                                    {page}
                                </button>
                            ) : (
                                <a
                                    key={page}
                                    href={`/${page.toLowerCase()}`}
                                    className="text-sm text-luna-secondary hover:underline font-medium"
                                >
                                    {page}
                                </a>
                            )
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

const ContactPage: React.FC = () => {
    const [formData, setFormData] = useState({ name: '', email: '', message: '' });
    const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
    const [errorMessage, setErrorMessage] = useState('');

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { id, value } = e.target;
        // Apply character limits
        let newValue = value;
        if (id === 'name' && value.length > 100) {
            newValue = value.substring(0, 100);
        } else if (id === 'email' && value.length > 255) {
            newValue = value.substring(0, 255);
        } else if (id === 'message' && value.length > 2000) {
            newValue = value.substring(0, 2000);
        }
        setFormData({ ...formData, [id]: newValue });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        e.stopPropagation(); // Prevent event from bubbling up
        setStatus('submitting');
        setErrorMessage('');

        // Validate form data
        if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
            setErrorMessage('All fields are required.');
            setStatus('error');
            return;
        }

        // Character limit validation
        if (formData.name.trim().length > 100) {
            setErrorMessage('Name must be 100 characters or less.');
            setStatus('error');
            return;
        }

        if (formData.email.trim().length > 255) {
            setErrorMessage('Email must be 255 characters or less.');
            setStatus('error');
            return;
        }

        if (formData.message.trim().length > 2000) {
            setErrorMessage('Message must be 2000 characters or less.');
            setStatus('error');
            return;
        }

        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            setErrorMessage('Please enter a valid email address.');
            setStatus('error');
            return;
        }

        try {
            const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
            const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

            if (!supabaseUrl || !supabaseAnonKey) {
                throw new Error('Missing Supabase configuration. Please check your environment variables.');
            }

            console.log('Submitting contact form to:', `${supabaseUrl}/functions/v1/send-contact-email`);

            const response = await fetch(`${supabaseUrl}/functions/v1/send-contact-email`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${supabaseAnonKey}`,
                    'apikey': supabaseAnonKey,
                },
                body: JSON.stringify({
                    name: formData.name,
                    email: formData.email,
                    message: formData.message,
                }),
            });

            console.log('Contact form response status:', response.status);

            const data = await response.json();

            console.log('Contact form response data:', data);

            if (!response.ok) {
                const errorMsg = data.error || 'Failed to send message. Please try again.';
                throw new Error(errorMsg);
            }

            setStatus('success');
            setFormData({ name: '', email: '', message: '' });
        } catch (error) {
            console.error('Error submitting contact form:', error);
            // More descriptive error handling
            if (error instanceof Error) {
                console.error('Error name:', error.name);
                console.error('Error message:', error.message);
                console.error('Error stack:', error.stack);
                setErrorMessage(error.message || 'An unexpected error occurred. Please try again.');
            } else {
                setErrorMessage('An unexpected error occurred. Please try again.');
            }
            setStatus('error');
        }
    };

    const headingRef = useAOS('fade-in-up', {}, 0);
    const formRef = useAOS('fade-in-up', {}, 100);

    // Global scroll observer for all animate-on-scroll elements
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('in-view');
                        observer.unobserve(entry.target);
                    }
                });
            },
            {
                threshold: 0.1,
                rootMargin: '0px 0px -50px 0px'
            }
        );

        const observeElements = () => {
            const animatedElements = document.querySelectorAll('.animate-on-scroll:not(.in-view)');
            animatedElements.forEach((el) => {
                const rect = el.getBoundingClientRect();
                const isInViewport = rect.top < window.innerHeight && rect.bottom > 0;

                if (isInViewport) {
                    el.classList.add('in-view');
                } else {
                    observer.observe(el);
                }
            });
        };

        const timeout = setTimeout(() => {
            observeElements();
        }, 100);

        const interval = setInterval(observeElements, 1000);

        return () => {
            clearTimeout(timeout);
            clearInterval(interval);
            const animatedElements = document.querySelectorAll('.animate-on-scroll');
            animatedElements.forEach((el) => {
                observer.unobserve(el);
            });
        };
    }, []);

    return (
        <div className="flex flex-col flex-1">
            {/* Hero Section */}
            <section className="py-20 md:py-32 bg-gradient-to-br from-luna-primary via-luna-primary to-luna-bg-dark border-b border-luna-text-light/10 dark:border-luna-text-dark/10">
                <div className="container mx-auto px-4">
                    <div className="flex flex-col gap-4 text-center max-w-4xl mx-auto animate-on-scroll fade-in-up" ref={headingRef as React.RefObject<HTMLDivElement>}>
                        <h1 className="text-4xl md:text-6xl font-black leading-tight tracking-[-0.033em] text-white">Get in Touch</h1>
                        <p className="text-lg md:text-xl font-normal leading-normal text-white/80 max-w-2xl mx-auto">
                            We&apos;re here to answer your questions about our AI legal tools, partnerships, or support needs. Reach out anytime!
                        </p>
                    </div>
                </div>
            </section>

            <div className="p-4 bg-luna-bg-light dark:bg-luna-bg-dark">
                <div className="container mx-auto max-w-7xl">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl mx-auto w-full mt-12 mb-16">
                        {/* Contact Info */}
                        <div className="lg:col-span-1 space-y-6 p-8 rounded-xl bg-white dark:bg-luna-primary shadow-lg border border-luna-text-light/10 dark:border-luna-text-dark/20 animate-on-scroll fade-in-up">
                            <div className="flex items-start gap-4">
                                <span className="material-symbols-outlined text-luna-secondary text-2xl">mail</span>
                                <div>
                                    <h3 className="font-bold text-luna-text-light dark:text-luna-text-dark mb-1">Email Support</h3>
                                    <p className="text-sm text-luna-text-light/70 dark:text-luna-text-muted-dark">info@lrdene.de</p>
                                    <p className="text-xs text-luna-text-light/60 dark:text-luna-text-muted-dark mt-1">support@lrdene.de</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-4">
                                <span className="material-symbols-outlined text-luna-secondary text-2xl">location_on</span>
                                <div>
                                    <h3 className="font-bold text-luna-text-light dark:text-luna-text-dark mb-1">Office Address</h3>
                                    <p className="text-sm text-luna-text-light/70 dark:text-luna-text-muted-dark">
                                        LRDEnE UG (haftungsbeschränkt)<br />
                                        [Bitte tragen Sie hier Ihre vollständige Adresse ein]<br />
                                        [Straße und Hausnummer]<br />
                                        [Postleitzahl und Ort], Germany
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-start gap-4">
                                <span className="material-symbols-outlined text-luna-secondary text-2xl">schedule</span>
                                <div>
                                    <h3 className="font-bold text-luna-text-light dark:text-luna-text-dark mb-1">Business Hours</h3>
                                    <p className="text-sm text-luna-text-light/70 dark:text-luna-text-muted-dark">Mon - Fri: 9:00 AM - 5:00 PM CET<br />Sat - Sun: Closed</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-4">
                                <span className="material-symbols-outlined text-luna-secondary text-2xl">phone</span>
                                <div>
                                    <h3 className="font-bold text-luna-text-light dark:text-luna-text-dark mb-1">Phone Support</h3>
                                    <p className="text-sm text-luna-text-light/70 dark:text-luna-text-muted-dark">[Bitte tragen Sie hier Ihre Telefonnummer ein]</p>
                                    <p className="text-xs text-luna-text-light/60 dark:text-luna-text-muted-dark mt-1">Available during business hours</p>
                                </div>
                            </div>
                        </div>

                        {/* Contact Form */}
                        <div className="lg:col-span-2 p-6 rounded-xl bg-white dark:bg-luna-primary shadow-sm border border-luna-text-light/10 dark:border-luna-text-dark/20 animate-on-scroll fade-in-up" ref={formRef as React.RefObject<HTMLDivElement>}>
                            <h3 className="text-xl font-bold mb-4">Send us a message</h3>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Name</label>
                                    <input
                                        type="text"
                                        id="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        required
                                        className="mt-1 block w-full rounded-lg border-luna-text-light/20 bg-luna-bg-light/50 dark:border-luna-text-dark/20 dark:bg-luna-bg-dark/50 focus:border-luna-secondary focus:ring-luna-secondary transition duration-200 p-2 text-sm"
                                        maxLength={100}
                                    />
                                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right">
                                        {formData.name.length}/100
                                    </div>
                                </div>
                                <div>
                                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email</label>
                                    <input
                                        type="email"
                                        id="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        required
                                        className="mt-1 block w-full rounded-lg border-luna-text-light/20 bg-luna-bg-light/50 dark:border-luna-text-dark/20 dark:bg-luna-bg-dark/50 focus:border-luna-secondary focus:ring-luna-secondary transition duration-200 p-2 text-sm"
                                        maxLength={255}
                                    />
                                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right">
                                        {formData.email.length}/255
                                    </div>
                                </div>
                                <div>
                                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Message</label>
                                    <textarea
                                        id="message"
                                        rows={4}
                                        value={formData.message}
                                        onChange={handleChange}
                                        required
                                        className="mt-1 block w-full rounded-lg border-luna-text-light/20 bg-luna-bg-light/50 dark:border-luna-text-dark/20 dark:bg-luna-bg-dark/50 focus:border-luna-secondary focus:ring-luna-secondary transition duration-200 p-2 text-sm"
                                        maxLength={2000}
                                    ></textarea>
                                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right">
                                        {formData.message.length}/2000
                                    </div>
                                </div>
                                <button
                                    type="submit"
                                    disabled={status === 'submitting'}
                                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-lg shadow-sm text-sm font-bold text-white bg-luna-secondary hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-luna-secondary disabled:opacity-50"
                                >
                                    {status === 'submitting' ? 'Sending...' : 'Submit Message'}
                                </button>
                                {status === 'success' && (
                                    <p className="mt-3 text-sm text-center text-green-600 dark:text-green-400">Thank you! Your message has been sent successfully. We will get back to you soon.</p>
                                )}
                                {status === 'error' && (
                                    <p className="mt-3 text-sm text-center text-red-600 dark:text-red-400">
                                        {errorMessage || 'Error sending message. Please check your internet connection and try again. If the problem persists, contact us at info@lrdene.de.'}
                                    </p>
                                )}
                            </form>
                        </div>
                    </div>

                    {/* FAQ Section */}
                    <section className="py-20 md:py-28 bg-white dark:bg-luna-primary border-y border-luna-text-light/10 dark:border-luna-text-dark/10">
                        <div className="container mx-auto px-4 max-w-4xl">
                            <div className="text-center mb-12 animate-on-scroll fade-in-up">
                                <h2 className="text-3xl md:text-5xl font-black leading-tight tracking-[-0.033em] mb-4 text-luna-text-light dark:text-luna-text-dark">
                                    Frequently Asked Questions
                                </h2>
                                <p className="text-lg text-luna-text-light/70 dark:text-luna-text-dark/70">
                                    Common questions about contacting us and getting support
                                </p>
                            </div>
                            <div className="space-y-4">
                                {[
                                    {
                                        question: 'How quickly will I receive a response?',
                                        answer: 'We typically respond to all inquiries within 24 hours during business days. For urgent matters, please call us during business hours for immediate assistance.'
                                    },
                                    {
                                        question: 'Can I schedule a demo or consultation?',
                                        answer: 'Absolutely! Please send us a message with your preferred date and time, and we\'ll arrange a personalized demo or consultation call to discuss your specific needs.'
                                    },
                                    {
                                        question: 'Do you offer enterprise support?',
                                        answer: 'Yes, we provide dedicated enterprise support for firms and organizations. Contact us to discuss custom support packages, SLAs, and dedicated account management.'
                                    },
                                    {
                                        question: 'What languages do you support?',
                                        answer: 'Our primary support languages are English and German. We also provide documentation in both languages to serve our international client base.'
                                    },
                                    {
                                        question: 'How can I report a bug or technical issue?',
                                        answer: 'Please send detailed information about the issue to support@lrdene.de. Include steps to reproduce, screenshots if applicable, and your account details for faster resolution.'
                                    },
                                ].map((faq, index) => (
                                    <details key={index} className="bg-luna-bg-light dark:bg-luna-bg-dark rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 p-6 cursor-pointer hover:shadow-md transition-shadow animate-on-scroll fade-in-up">
                                        <summary className="font-bold text-luna-text-light dark:text-luna-text-dark cursor-pointer flex items-center gap-3">
                                            <span className="material-symbols-outlined text-luna-secondary flex-shrink-0">expand_more</span>
                                            {faq.question}
                                        </summary>
                                        <p className="mt-4 text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 leading-relaxed pl-9">
                                            {faq.answer}
                                        </p>
                                    </details>
                                ))}
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
};

// --- SIMPLIFIED LANDING PAGE ---
const LandingPage: React.FC<{ onNavigateToAuth: (mode: 'signup' | 'login') => void; onNavigate?: (page: string) => void; currentPage?: string; hideHeader?: boolean }> = ({ onNavigateToAuth, onNavigate, currentPage = 'Landing', hideHeader = false }) => {
    const { t } = useTranslation();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const mobileMenuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
                setIsMobileMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleMobileNavClick = (page: string) => {
        if (onNavigate) {
            onNavigate(page);
        }
        setIsMobileMenuOpen(false);
    };
    // AOS refs for hero section
    const heroTitleRef = useAOS('fade-in-up', {}, 0);
    const heroSubtitleRef = useAOS('fade-in-up', {}, 100);
    const heroButtonRef = useAOS('fade-in-up', {}, 200);
    const trustLogosRef = useAOS('fade-in-up', {}, 0);

    // Scroll to top button state
    const [showScrollTop, setShowScrollTop] = useState(false);

    // Global scroll observer for all animate-on-scroll elements
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('in-view');
                        // Stop observing once visible for performance
                        observer.unobserve(entry.target);
                    }
                });
            },
            {
                threshold: 0.1,
                rootMargin: '0px 0px -50px 0px'
            }
        );

        const observeElements = () => {
            const animatedElements = document.querySelectorAll('.animate-on-scroll:not(.in-view)');
            animatedElements.forEach((el) => {
                // Check if element is already in viewport
                const rect = el.getBoundingClientRect();
                const isInViewport = rect.top < window.innerHeight && rect.bottom > 0;

                if (isInViewport) {
                    // Element is already visible, add in-view class immediately
                    el.classList.add('in-view');
                } else {
                    // Element not yet visible, observe for when it scrolls into view
                    observer.observe(el);
                }
            });
        };

        // Initial observation after a brief delay to ensure DOM is ready
        const timeout = setTimeout(() => {
            observeElements();
        }, 100);

        // Also check periodically for dynamically added elements
        const interval = setInterval(observeElements, 1000);

        return () => {
            clearTimeout(timeout);
            clearInterval(interval);
            const animatedElements = document.querySelectorAll('.animate-on-scroll');
            animatedElements.forEach((el) => {
                observer.unobserve(el);
            });
        };
    }, []);

    // Handle scroll to show/hide scroll-to-top button
    useEffect(() => {
        const handleScroll = () => {
            setShowScrollTop(window.scrollY > 500);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Scroll to top function
    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    };

    return (
        <div className="w-full min-h-screen flex flex-col">
            {/* Glass morphism header with navigation - shown when Navbar is hidden */}
            {!hideHeader && (
                <header className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-4 py-3
                bg-white/10 dark:bg-luna-primary/20 
                backdrop-blur-xl backdrop-saturate-150
                border-b border-solid border-white/20 dark:border-white/10
                shadow-lg shadow-black/10
                before:absolute before:inset-0 before:bg-gradient-to-b before:from-white/20 before:to-transparent before:dark:from-luna-secondary/10 before:dark:to-transparent before:pointer-events-none">
                    {/* Logo */}
                    <button
                        className="relative flex items-center gap-4 cursor-pointer hover:opacity-80 z-10
                        px-3 py-1.5 rounded-lg 
                        hover:bg-white/20 dark:hover:bg-white/10 
                        backdrop-blur-sm 
                        transition-all border border-transparent hover:border-white/20 dark:hover:border-white/10"
                        onClick={(e) => {
                            e.preventDefault();
                            if (onNavigate) {
                                onNavigate('Landing');
                            }
                        }}
                        aria-label="LRDEnE Logo"
                        type="button"
                    >
                        <div className="size-6 text-white drop-shadow-lg">
                            <svg fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93s3.05-7.44 7-7.93v15.86zm2-15.86c1.03.13 2 .45 2.87.93L15.87 5c-1.03-.13-2-.45-2.87-.93zM12 5.95c.57.06 1.13.2 1.66.42l.66-.66C13.66 5.3 12.85 5.06 12 5V5.95zm5.5 1.05c-1.3-1.3-3.11-2.05-5-2.05s-3.7.75-5 2.05l-1.41-1.41C7.89 3.84 9.87 3 12 3s4.11.84 5.91 2.59l-1.41 1.41z"></path></svg>
                        </div>
                        <h2 className="text-xl font-bold leading-tight tracking-[-0.015em] text-white drop-shadow-lg">LRDEnE</h2>
                    </button>

                    {/* Navigation Links and Buttons */}
                    <div className="hidden md:flex flex-1 justify-end items-center gap-8 relative z-10">
                        <div className="flex items-center gap-6">
                            <a
                                className={`text-sm font-medium leading-normal text-white/90 hover:text-white transition-all relative px-3 py-1.5 rounded-lg backdrop-blur-sm ${currentPage === 'Features' ? 'bg-white/30 text-white font-semibold' : 'hover:bg-white/20'}`}
                                href="/features"
                                onClick={(e) => {
                                    e.preventDefault();
                                    if (onNavigate) {
                                        onNavigate('Features');
                                    }
                                }}
                                aria-current={currentPage === 'Features' ? 'page' : undefined}
                            >
                                Features
                            </a>
                            <a
                                className={`text-sm font-medium leading-normal text-white/90 hover:text-white transition-all relative px-3 py-1.5 rounded-lg backdrop-blur-sm ${currentPage === 'Pricing' ? 'bg-white/30 text-white font-semibold' : 'hover:bg-white/20'}`}
                                href="/pricing"
                                onClick={(e) => {
                                    e.preventDefault();
                                    if (onNavigate) {
                                        onNavigate('Pricing');
                                    }
                                }}
                                aria-current={currentPage === 'Pricing' ? 'page' : undefined}
                            >
                                Pricing
                            </a>
                            <a
                                className={`text-sm font-medium leading-normal text-white/90 hover:text-white transition-all relative px-3 py-1.5 rounded-lg backdrop-blur-sm ${currentPage === 'AboutUs' ? 'bg-white/30 text-white font-semibold' : 'hover:bg-white/20'}`}
                                href="/about"
                                onClick={(e) => {
                                    e.preventDefault();
                                    if (onNavigate) {
                                        onNavigate('AboutUs');
                                    }
                                }}
                                aria-current={currentPage === 'AboutUs' ? 'page' : undefined}
                            >
                                About Us
                            </a>
                            <a
                                className={`text-sm font-medium leading-normal text-white/90 hover:text-white transition-all relative px-3 py-1.5 rounded-lg backdrop-blur-sm ${currentPage === 'Contact' ? 'bg-white/30 text-white font-semibold' : 'hover:bg-white/20'}`}
                                href="/contact"
                                onClick={(e) => {
                                    e.preventDefault();
                                    if (onNavigate) {
                                        onNavigate('Contact');
                                    }
                                }}
                                aria-current={currentPage === 'Contact' ? 'page' : undefined}
                            >
                                Contact
                            </a>
                            <a
                                className={`text-sm font-medium leading-normal text-white/90 hover:text-white transition-all relative px-3 py-1.5 rounded-lg backdrop-blur-sm ${currentPage === 'Legal' ? 'bg-white/30 text-white font-semibold' : 'hover:bg-white/20'}`}
                                href="/legal"
                                onClick={(e) => {
                                    e.preventDefault();
                                    if (onNavigate) {
                                        onNavigate('Legal');
                                    }
                                }}
                                aria-current={currentPage === 'Legal' ? 'page' : undefined}
                            >
                                Legal
                            </a>
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => onNavigateToAuth('login')}
                                className="flex min-w-[84px] max-w-[480px] cursor-pointer items-center justify-center overflow-hidden rounded-lg h-10 px-4 bg-white/20 backdrop-blur-md border border-white/30 text-sm font-bold leading-normal tracking-[0.015em] text-white hover:bg-white/30 transition-all shadow-sm"
                            >
                                <span className="truncate">Log In</span>
                            </button>
                            <button
                                onClick={() => onNavigateToAuth('signup')}
                                className="flex min-w-[84px] max-w-[480px] cursor-pointer items-center justify-center overflow-hidden rounded-lg h-10 px-4 bg-luna-secondary/90 backdrop-blur-md border border-luna-secondary/50 text-luna-primary text-sm font-bold leading-normal tracking-[0.015em] hover:bg-luna-secondary transition-all shadow-lg shadow-luna-secondary/20"
                            >
                                <span className="truncate">Sign Up Free</span>
                            </button>
                        </div>
                    </div>

                    {/* Mobile Menu Button - Landing Page */}
                    <div className="flex md:hidden items-center gap-2 relative z-[60] ml-auto">
                        <button
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setIsMobileMenuOpen(!isMobileMenuOpen);
                            }}
                            className="p-3 rounded-lg bg-luna-secondary text-white hover:bg-luna-secondary/90 transition-all border-2 border-luna-secondary shadow-lg min-w-[48px] min-h-[48px] flex items-center justify-center"
                            type="button"
                            aria-label="Toggle mobile menu"
                            aria-expanded={isMobileMenuOpen}
                        >
                            <span className="material-symbols-outlined text-white text-2xl leading-none font-bold">
                                {isMobileMenuOpen ? 'close' : 'menu'}
                            </span>
                        </button>
                    </div>

                    {/* Mobile Menu Overlay - Landing Page */}
                    {isMobileMenuOpen && (
                        <button
                            type="button"
                            className="fixed inset-0 bg-black/50 dark:bg-black/70 z-40 md:hidden focus:outline-none"
                            onClick={() => setIsMobileMenuOpen(false)}
                            aria-label="Close mobile navigation overlay"
                        >
                            <span className="sr-only">Close mobile menu</span>
                        </button>
                    )}

                    {/* Mobile Menu Panel - Landing Page */}
                    {isMobileMenuOpen && (
                        <div
                            ref={mobileMenuRef}
                            className="fixed top-0 right-0 h-screen w-80 max-w-[85vw] bg-white/95 dark:bg-luna-primary/95 backdrop-blur-xl z-[100] md:hidden transform transition-transform duration-300 ease-in-out translate-x-0 shadow-2xl border-l border-white/20 dark:border-white/10 overflow-y-auto"
                            style={{ height: '100vh', top: 0 }}
                        >
                            <div className="flex flex-col h-full">
                                {/* Mobile Menu Header */}
                                <div className="flex items-center justify-between p-4 border-b border-white/20 dark:border-white/10">
                                    <h3 className="text-lg font-bold text-luna-text-light dark:text-luna-text-dark">Menu</h3>
                                    <button
                                        onClick={() => setIsMobileMenuOpen(false)}
                                        className="p-2 rounded-lg hover:bg-white/20 dark:hover:bg-white/10 transition-colors"
                                        type="button"
                                        aria-label="Close menu"
                                    >
                                        <span className="material-symbols-outlined text-luna-text-light dark:text-luna-text-dark">close</span>
                                    </button>
                                </div>

                                {/* Mobile Menu Content */}
                                <div className="flex-1 flex flex-col p-4 gap-2">
                                    {/* Navigation Items */}
                                    <nav className="flex flex-col gap-1" role="navigation" aria-label="Mobile navigation">
                                        <button
                                            onClick={() => handleMobileNavClick('Features')}
                                            className={`text-left px-4 py-3 rounded-lg transition-all ${currentPage === 'Features'
                                                ? 'bg-luna-secondary/20 text-luna-secondary font-semibold'
                                                : 'text-luna-text-light dark:text-luna-text-dark hover:bg-white/20 dark:hover:bg-white/10'
                                                }`}
                                            type="button"
                                        >
                                            Features
                                        </button>
                                        <button
                                            onClick={() => handleMobileNavClick('Pricing')}
                                            className={`text-left px-4 py-3 rounded-lg transition-all ${currentPage === 'Pricing'
                                                ? 'bg-luna-secondary/20 text-luna-secondary font-semibold'
                                                : 'text-luna-text-light dark:text-luna-text-dark hover:bg-white/20 dark:hover:bg-white/10'
                                                }`}
                                            type="button"
                                        >
                                            Pricing
                                        </button>
                                        <button
                                            onClick={() => handleMobileNavClick('AboutUs')}
                                            className={`text-left px-4 py-3 rounded-lg transition-all ${currentPage === 'AboutUs'
                                                ? 'bg-luna-secondary/20 text-luna-secondary font-semibold'
                                                : 'text-luna-text-light dark:text-luna-text-dark hover:bg-white/20 dark:hover:bg-white/10'
                                                }`}
                                            type="button"
                                        >
                                            About Us
                                        </button>
                                        <button
                                            onClick={() => handleMobileNavClick('Contact')}
                                            className={`text-left px-4 py-3 rounded-lg transition-all ${currentPage === 'Contact'
                                                ? 'bg-luna-secondary/20 text-luna-secondary font-semibold'
                                                : 'text-luna-text-light dark:text-luna-text-dark hover:bg-white/20 dark:hover:bg-white/10'
                                                }`}
                                            type="button"
                                        >
                                            Contact
                                        </button>
                                        <button
                                            onClick={() => handleMobileNavClick('Legal')}
                                            className={`text-left px-4 py-3 rounded-lg transition-all ${currentPage === 'Legal'
                                                ? 'bg-luna-secondary/20 text-luna-secondary font-semibold'
                                                : 'text-luna-text-light dark:text-luna-text-dark hover:bg-white/20 dark:hover:bg-white/10'
                                                }`}
                                            type="button"
                                        >
                                            Legal
                                        </button>
                                    </nav>

                                    {/* Divider */}
                                    <div className="border-t border-white/20 dark:border-white/10 my-2"></div>

                                    {/* Auth Buttons */}
                                    <div className="flex flex-col gap-2">
                                        <button
                                            onClick={() => {
                                                onNavigateToAuth('login');
                                                setIsMobileMenuOpen(false);
                                            }}
                                            className="w-full px-4 py-3 rounded-lg bg-white/30 dark:bg-white/10 backdrop-blur-md border border-white/30 dark:border-white/20 text-sm font-bold text-luna-text-light dark:text-luna-text-dark hover:bg-white/40 dark:hover:bg-white/15 transition-all"
                                            type="button"
                                        >
                                            Log In
                                        </button>
                                        <button
                                            onClick={() => {
                                                onNavigateToAuth('signup');
                                                setIsMobileMenuOpen(false);
                                            }}
                                            className="w-full px-4 py-3 rounded-lg bg-luna-secondary/90 backdrop-blur-md border border-luna-secondary/50 text-luna-primary text-sm font-bold hover:bg-luna-secondary transition-all shadow-lg shadow-luna-secondary/20"
                                            type="button"
                                        >
                                            Sign Up Free
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </header>
            )}
            <main className="w-full">
                <section className="min-h-screen text-center relative overflow-hidden flex flex-col items-center justify-center p-4 pt-20">
                    <img
                        alt="Conceptual art of three distinct, glowing AI figures or busts representing legal analysis."
                        className="absolute inset-0 w-full h-full object-cover -z-10"
                        src="https://lh3.googleusercontent.com/aida-public/AB6AXuDtYZZMfUn-l1YY1uNrosV6EQUrqEeQ0PFXbmxw6_-d_A5fU6YCFAlkob40WeoIBKn2Bi7AcUzfCoFfg6VzvLpznCrJk5Cc7B0v5i3jA6nv20j4wnFkkszpMDms-pTkaa3bxgdPgAT1NUGjXYQkEYP_AinuWLp7-9tYI-CwPod16OwcuaOKjYSI0KKSiB58Ly15EzD_F_k3uAkGcOrAbOjyxPYGBZ-d_WiTT9kYSgsPQNDRd-sDUBNq6GK0HzGcr9X1tuTtyP6HkQUf"
                    />
                    <div className="absolute inset-0 bg-luna-bg-dark/50 -z-10"></div>

                    <div className="relative z-10 flex flex-col items-center justify-center bg-black/20 backdrop-blur-xl border border-white/10 rounded-3xl p-8 md:p-12 shadow-2xl max-w-3xl">
                        <h1 className="text-4xl md:text-6xl font-black leading-tight tracking-[-0.033em] text-white animate-on-scroll fade-in-up" ref={heroTitleRef as React.RefObject<HTMLHeadingElement>}>{t('landing.heroTitle')}</h1>
                        <h2 className="mt-4 text-lg md:text-xl font-normal leading-normal text-white/80 max-w-2xl mx-auto animate-on-scroll fade-in-up" ref={heroSubtitleRef as React.RefObject<HTMLHeadingElement>}>{t('landing.heroSubtitle')}</h2>
                        <div className="mt-8 animate-on-scroll fade-in-up" ref={heroButtonRef as React.RefObject<HTMLDivElement>}>
                            <button
                                className="flex mx-auto min-w-[84px] max-w-[480px] cursor-pointer items-center justify-center overflow-hidden rounded-lg h-12 px-5 bg-luna-secondary text-luna-primary text-base font-bold leading-normal tracking-[0.015em] hover:opacity-90 transition-opacity shadow-lg shadow-luna-secondary/20"
                                onClick={() => onNavigateToAuth('signup')}
                            >
                                <span className="truncate">{t('landing.analyzeFirstCaseFree')}</span>
                            </button>
                        </div>
                    </div>
                </section>

                {/* SCHUFA Solution Section */}
                <section className="py-20 md:py-28 bg-gradient-to-br from-orange-50 via-white to-orange-50/30 dark:from-orange-950/20 dark:via-luna-primary dark:to-orange-950/10 border-y border-orange-200/20 dark:border-orange-900/20">
                    <div className="container mx-auto px-4">
                        <div className="max-w-6xl mx-auto">
                            <div className="text-center mb-16 animate-on-scroll fade-in-up">
                                <div className="inline-flex items-center justify-center size-20 rounded-2xl bg-gradient-to-br from-orange-500/20 to-orange-500/5 mb-6">
                                    <span className="material-symbols-outlined text-5xl text-orange-500">credit_card</span>
                                </div>
                                <h2 className="text-3xl md:text-5xl font-black leading-tight tracking-[-0.033em] mb-4 text-luna-text-light dark:text-luna-text-dark">
                                    SCHUFA Lösungen: Protect Your Credit Score
                                </h2>
                                <p className="text-lg text-luna-text-light/70 dark:text-luna-text-dark/70 max-w-3xl mx-auto">
                                    Incorrect SCHUFA entries can destroy your financial future. LRDEnE helps you dispute errors and protect your creditworthiness with professional legal documents.
                                </p>
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
                                {/* Problem Side */}
                                <div className="bg-white dark:bg-luna-primary rounded-xl border border-red-200 dark:border-red-900/30 p-8 shadow-lg animate-on-scroll fade-in-up">
                                    <div className="flex items-center gap-4 mb-6">
                                        <div className="flex items-center justify-center size-16 rounded-xl bg-red-500/10">
                                            <span className="material-symbols-outlined text-4xl text-red-500">warning</span>
                                        </div>
                                        <div>
                                            <h3 className="text-2xl font-bold text-luna-text-light dark:text-luna-text-dark">The SCHUFA Problem</h3>
                                            <p className="text-sm text-red-600 dark:text-red-400 font-semibold">Why It Matters</p>
                                        </div>
                                    </div>
                                    <div className="space-y-4">
                                        <div>
                                            <p className="text-sm font-semibold text-luna-text-light dark:text-luna-text-dark mb-2">What is SCHUFA?</p>
                                            <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 leading-relaxed">
                                                SCHUFA is Germany&apos;s largest credit bureau, tracking the creditworthiness of millions of Germans. Banks, landlords, and employers use SCHUFA scores to make critical decisions.
                                            </p>
                                        </div>
                                        <div className="pt-4 border-t border-red-200 dark:border-red-900/30">
                                            <p className="text-sm font-semibold text-red-600 dark:text-red-400 mb-3">Common SCHUFA Problems:</p>
                                            <ul className="space-y-2">
                                                {[
                                                    'Incorrect payment defaults recorded',
                                                    'Outdated information not removed',
                                                    'Identity theft or fraud entries',
                                                    'Disputed debts still showing',
                                                    'Wrong personal information',
                                                    'Negative entries affecting credit score'
                                                ].map((problem, idx) => (
                                                    <li key={idx} className="flex items-start gap-2 text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">
                                                        <span className="material-symbols-outlined text-red-500 text-lg mt-0.5">close</span>
                                                        {problem}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                        <div className="bg-red-50 dark:bg-red-950/20 rounded-lg p-4 mt-4">
                                            <p className="text-xs font-semibold text-red-700 dark:text-red-400 mb-1">Impact:</p>
                                            <p className="text-xs text-red-600 dark:text-red-300 leading-relaxed">
                                                A single incorrect SCHUFA entry can prevent you from getting loans, renting apartments, or even landing jobs. The longer it stays, the more damage it causes.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Solution Side */}
                                <div className="bg-gradient-to-br from-green-50 to-white dark:from-green-950/20 dark:to-luna-primary rounded-xl border border-green-200 dark:border-green-900/30 p-8 shadow-lg animate-on-scroll fade-in-up">
                                    <div className="flex items-center gap-4 mb-6">
                                        <div className="flex items-center justify-center size-16 rounded-xl bg-green-500/10">
                                            <span className="material-symbols-outlined text-4xl text-green-500">shield</span>
                                        </div>
                                        <div>
                                            <h3 className="text-2xl font-bold text-luna-text-light dark:text-luna-text-dark">LRDEnE&apos;s SCHUFA Solution</h3>
                                            <p className="text-sm text-green-600 dark:text-green-400 font-semibold">How We Help</p>
                                        </div>
                                    </div>
                                    <div className="space-y-4">
                                        <div>
                                            <p className="text-sm font-semibold text-luna-text-light dark:text-luna-text-dark mb-2">Professional Dispute Documents</p>
                                            <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 leading-relaxed">
                                                Generate legally sound SCHUFA dispute letters (Bestreitung) and resolution requests (Lösung) in minutes. Our AI ensures proper legal language and includes all required information.
                                            </p>
                                        </div>
                                        <div className="pt-4 border-t border-green-200 dark:border-green-900/30">
                                            <p className="text-sm font-semibold text-green-600 dark:text-green-400 mb-3">What You Get:</p>
                                            <ul className="space-y-2">
                                                {[
                                                    'SCHUFA Bestreitung (Dispute Letter) - Challenge incorrect entries',
                                                    'SCHUFA Lösung (Resolution Request) - Request entry removal',
                                                    'Proper legal citations and BDSG references',
                                                    'Professional formatting ready for submission',
                                                    'PDF export for official documentation',
                                                    'Step-by-step guidance throughout the process'
                                                ].map((benefit, idx) => (
                                                    <li key={idx} className="flex items-start gap-2 text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">
                                                        <span className="material-symbols-outlined text-green-500 text-lg mt-0.5">check_circle</span>
                                                        {benefit}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                        <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-400 rounded-lg p-4 mt-4">
                                            <p className="text-xs font-semibold text-blue-800 dark:text-blue-200 mb-1">
                                                <strong>Success Cannot Be Guaranteed:</strong>
                                            </p>
                                            <p className="text-xs text-blue-700 dark:text-blue-300 leading-relaxed">
                                                While many users have successfully used our tools, we cannot guarantee specific outcomes.
                                                Legal processes vary, and results depend on individual circumstances.
                                                Always consult with a qualified lawyer for your specific case.
                                            </p>
                                        </div>
                                        <div className="bg-green-50 dark:bg-green-950/20 rounded-lg p-4 mt-4">
                                            <p className="text-xs font-semibold text-green-700 dark:text-green-400 mb-1">Best Practices:</p>
                                            <p className="text-xs text-green-600 dark:text-green-300 leading-relaxed">
                                                Professional dispute letters with proper legal language and complete information follow best practices for SCHUFA correction requests. Results may vary and are not guaranteed. Always consult with a qualified lawyer for important legal matters.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* How It Works */}
                            <div className="bg-white dark:bg-luna-primary rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 p-8 shadow-lg mb-8 animate-on-scroll fade-in-up">
                                <h3 className="text-2xl font-bold text-center mb-8 text-luna-text-light dark:text-luna-text-dark">How LRDEnE Helps with SCHUFA Issues</h3>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    {[
                                        {
                                            step: '1',
                                            title: 'Identify the Problem',
                                            description: 'Review your SCHUFA report and identify incorrect or outdated entries. Common issues include wrong payment defaults, outdated information, or identity theft.',
                                            icon: 'search'
                                        },
                                        {
                                            step: '2',
                                            title: 'Generate Dispute Document',
                                            description: 'Use our SCHUFA Bestreitung template. Fill in your details and describe the incorrect entry. Our AI generates a professional dispute letter with proper legal language.',
                                            icon: 'description'
                                        },
                                        {
                                            step: '3',
                                            title: 'Submit & Follow Up',
                                            description: 'Export your document as PDF and send it to SCHUFA. Track your submission and follow up if needed. Our SCHUFA Lösung template helps request resolution.',
                                            icon: 'send'
                                        }
                                    ].map((step, index) => (
                                        <div key={index} className="text-center">
                                            <div className="inline-flex items-center justify-center size-16 rounded-full bg-gradient-to-br from-orange-500/20 to-orange-500/5 mb-4">
                                                <span className="material-symbols-outlined text-3xl text-orange-500">{step.icon}</span>
                                            </div>
                                            <div className="inline-flex items-center justify-center size-8 rounded-full bg-orange-500 text-white text-sm font-bold mb-3">
                                                {step.step}
                                            </div>
                                            <h4 className="text-lg font-bold mb-2 text-luna-text-light dark:text-luna-text-dark">{step.title}</h4>
                                            <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 leading-relaxed">{step.description}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Real-World Benefits */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                                {[
                                    {
                                        title: 'Save Money',
                                        description: 'Avoid expensive lawyer fees. Generate professional SCHUFA dispute documents yourself for a fraction of the cost.',
                                        icon: 'savings',
                                        color: 'from-green-500/20 to-green-500/5',
                                        iconColor: 'text-green-500'
                                    },
                                    {
                                        title: 'Save Time',
                                        description: 'Generate dispute letters in minutes instead of days. No need to research legal requirements or format documents manually.',
                                        icon: 'schedule',
                                        color: 'from-blue-500/20 to-blue-500/5',
                                        iconColor: 'text-blue-500'
                                    },
                                    {
                                        title: 'Follow Best Practices',
                                        description: 'Professional legal language and proper citations follow best practices for SCHUFA correction requests. Results may vary.',
                                        icon: 'trending_up',
                                        color: 'from-purple-500/20 to-purple-500/5',
                                        iconColor: 'text-purple-500'
                                    },
                                    {
                                        title: 'Protect Your Future',
                                        description: 'Correct SCHUFA entries protect your ability to get loans, rent apartments, and secure employment opportunities.',
                                        icon: 'security',
                                        color: 'from-red-500/20 to-red-500/5',
                                        iconColor: 'text-red-500'
                                    }
                                ].map((benefit, index) => (
                                    <div key={index} className="bg-white dark:bg-luna-primary rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 p-6 hover:shadow-lg transition-all duration-300 animate-on-scroll fade-in-up">
                                        <div className={`flex items-center justify-center size-14 rounded-xl bg-gradient-to-br ${benefit.color} mb-4`}>
                                            <span className={`material-symbols-outlined text-3xl ${benefit.iconColor}`}>{benefit.icon}</span>
                                        </div>
                                        <h4 className="text-lg font-bold mb-2 text-luna-text-light dark:text-luna-text-dark">{benefit.title}</h4>
                                        <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 leading-relaxed">{benefit.description}</p>
                                    </div>
                                ))}
                            </div>

                            {/* CTA */}
                            <div className="text-center bg-gradient-to-r from-orange-500/10 via-luna-secondary/10 to-orange-500/10 dark:from-orange-500/5 dark:via-luna-secondary/5 dark:to-orange-500/5 rounded-xl border border-orange-200 dark:border-orange-900/30 p-8 animate-on-scroll fade-in-up">
                                <h3 className="text-2xl font-bold mb-4 text-luna-text-light dark:text-luna-text-dark">
                                    Ready to Fix Your SCHUFA Report?
                                </h3>
                                <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 mb-6 max-w-2xl mx-auto">
                                    Don&apos;t let incorrect SCHUFA entries damage your financial future. Generate professional dispute documents in minutes and protect your creditworthiness.
                                </p>
                                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                    <button
                                        onClick={() => onNavigateToAuth('signup')}
                                        className="flex items-center justify-center gap-2 h-12 px-6 bg-orange-500 text-white text-base font-bold rounded-lg hover:bg-orange-600 transition-colors shadow-lg shadow-orange-500/30"
                                    >
                                        <span>Get Started Free</span>
                                        <span className="material-symbols-outlined">arrow_forward</span>
                                    </button>
                                    <button
                                        onClick={() => onNavigate?.('Templates')}
                                        className="flex items-center justify-center gap-2 h-12 px-6 bg-white dark:bg-luna-primary border border-orange-200 dark:border-orange-900/30 text-luna-text-light dark:text-luna-text-dark text-base font-bold rounded-lg hover:bg-orange-50 dark:hover:bg-orange-950/20 transition-colors"
                                    >
                                        <span>View SCHUFA Templates</span>
                                        <span className="material-symbols-outlined">description</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Features Showcase Section */}
                <section className="py-20 md:py-28 bg-white dark:bg-luna-primary border-y border-luna-text-light/10 dark:border-luna-text-dark/10 section-divider">
                    <div className="container mx-auto px-4">
                        <div className="text-center mb-16 animate-on-scroll fade-in-up">
                            <h2 className="text-3xl md:text-5xl font-black leading-tight tracking-[-0.033em] mb-4 text-luna-text-light dark:text-luna-text-dark">
                                Powerful Features for Modern Legal Practice
                            </h2>
                            <p className="text-lg text-luna-text-light/70 dark:text-luna-text-dark/70 max-w-2xl mx-auto">
                                Everything you need to streamline your legal workflow, powered by advanced AI
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
                            {/* Feature 1: AI-Powered Document Analysis */}
                            <div className="feature-card group relative bg-white dark:bg-luna-primary rounded-2xl border border-luna-text-light/10 dark:border-luna-text-dark/20 p-8 shadow-sm hover:shadow-xl animate-on-scroll fade-in-up">
                                <div className="flex items-center justify-center size-16 rounded-2xl bg-gradient-to-br from-luna-secondary/20 to-luna-secondary/5 mb-6 group-hover:scale-110 transition-transform duration-300">
                                    <span className="material-symbols-outlined text-4xl text-luna-secondary">description</span>
                                </div>
                                <h3 className="text-xl font-bold mb-3 text-luna-text-light dark:text-luna-text-dark">AI Document Analysis</h3>
                                <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 leading-relaxed mb-4">
                                    Upload legal documents and get instant AI-powered analysis with risk assessment, compliance checks, and actionable suggestions based on EU and German law.
                                </p>
                                <ul className="space-y-2 text-xs text-luna-text-light/60 dark:text-luna-text-muted-dark">
                                    <li className="flex items-center gap-2">
                                        <span className="material-symbols-outlined text-base text-luna-secondary">check_circle</span>
                                        GDPR & EU AI Act compliance
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <span className="material-symbols-outlined text-base text-luna-secondary">check_circle</span>
                                        Risk heatmaps & prioritized findings
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <span className="material-symbols-outlined text-base text-luna-secondary">check_circle</span>
                                        Legal citations with article numbers
                                    </li>
                                </ul>
                            </div>

                            {/* Feature 2: Interactive Template Library */}
                            <div className="feature-card group relative bg-white dark:bg-luna-primary rounded-2xl border border-luna-text-light/10 dark:border-luna-text-dark/20 p-8 shadow-sm hover:shadow-xl animate-on-scroll fade-in-up">
                                <div className="flex items-center justify-center size-16 rounded-2xl bg-gradient-to-br from-purple-500/20 to-purple-500/5 mb-6 group-hover:scale-110 transition-transform duration-300">
                                    <span className="material-symbols-outlined text-4xl text-purple-500">article</span>
                                </div>
                                <h3 className="text-xl font-bold mb-3 text-luna-text-light dark:text-luna-text-dark">AI Template Generator</h3>
                                <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 leading-relaxed mb-4">
                                    Generate professional German legal documents instantly with our AI-powered template library. 7+ document types including GDPR requests, rent reduction notices, and SCHUFA disputes.
                                </p>
                                <ul className="space-y-2 text-xs text-luna-text-light/60 dark:text-luna-text-muted-dark">
                                    <li className="flex items-center gap-2">
                                        <span className="material-symbols-outlined text-base text-purple-500">check_circle</span>
                                        Interactive wizard forms
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <span className="material-symbols-outlined text-base text-purple-500">check_circle</span>
                                        Auto-fill from profile
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <span className="material-symbols-outlined text-base text-purple-500">check_circle</span>
                                        Export to PDF & save to cases
                                    </li>
                                </ul>
                            </div>

                            {/* Feature 3: AI Legal Chat */}
                            <div className="feature-card group relative bg-white dark:bg-luna-primary rounded-2xl border border-luna-text-light/10 dark:border-luna-text-dark/20 p-8 shadow-sm hover:shadow-xl animate-on-scroll fade-in-up">
                                <div className="flex items-center justify-center size-16 rounded-2xl bg-gradient-to-br from-blue-500/20 to-blue-500/5 mb-6 group-hover:scale-110 transition-transform duration-300">
                                    <span className="material-symbols-outlined text-4xl text-blue-500">chat_bubble</span>
                                </div>
                                <h3 className="text-xl font-bold mb-3 text-luna-text-light dark:text-luna-text-dark">AI Legal Assistant</h3>
                                <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 leading-relaxed mb-4">
                                    Chat with LRDEnE, your AI-powered legal research assistant. Get information and analysis on German and EU law with proper citations, context-aware responses, and comprehensive guidance. Note: This is not legal advice - always consult a qualified lawyer for important legal matters.
                                </p>
                                <ul className="space-y-2 text-xs text-luna-text-light/60 dark:text-luna-text-muted-dark">
                                    <li className="flex items-center gap-2">
                                        <span className="material-symbols-outlined text-base text-blue-500">check_circle</span>
                                        Real-time chat with context
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <span className="material-symbols-outlined text-base text-blue-500">check_circle</span>
                                        Precise legal citations
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <span className="material-symbols-outlined text-base text-blue-500">check_circle</span>
                                        Case-specific guidance
                                    </li>
                                </ul>
                            </div>

                            {/* Feature 4: Case Management */}
                            <div className="feature-card group relative bg-white dark:bg-luna-primary rounded-2xl border border-luna-text-light/10 dark:border-luna-text-dark/20 p-8 shadow-sm hover:shadow-xl animate-on-scroll fade-in-up">
                                <div className="flex items-center justify-center size-16 rounded-2xl bg-gradient-to-br from-green-500/20 to-green-500/5 mb-6 group-hover:scale-110 transition-transform duration-300">
                                    <span className="material-symbols-outlined text-4xl text-green-500">folder_managed</span>
                                </div>
                                <h3 className="text-xl font-bold mb-3 text-luna-text-light dark:text-luna-text-dark">Smart Case Management</h3>
                                <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 leading-relaxed mb-4">
                                    Organize all your legal cases in one place. Track status, deadlines, documents, and AI conversations for each case with ease.
                                </p>
                                <ul className="space-y-2 text-xs text-luna-text-light/60 dark:text-luna-text-muted-dark">
                                    <li className="flex items-center gap-2">
                                        <span className="material-symbols-outlined text-base text-green-500">check_circle</span>
                                        Create unlimited cases
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <span className="material-symbols-outlined text-base text-green-500">check_circle</span>
                                        Track status & deadlines
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <span className="material-symbols-outlined text-base text-green-500">check_circle</span>
                                        Link documents & templates
                                    </li>
                                </ul>
                            </div>

                            {/* Feature 5: Document Storage */}
                            <div className="feature-card group relative bg-white dark:bg-luna-primary rounded-2xl border border-luna-text-light/10 dark:border-luna-text-dark/20 p-8 shadow-sm hover:shadow-xl animate-on-scroll fade-in-up">
                                <div className="flex items-center justify-center size-16 rounded-2xl bg-gradient-to-br from-orange-500/20 to-orange-500/5 mb-6 group-hover:scale-110 transition-transform duration-300">
                                    <span className="material-symbols-outlined text-4xl text-orange-500">cloud_upload</span>
                                </div>
                                <h3 className="text-xl font-bold mb-3 text-luna-text-light dark:text-luna-text-dark">Secure Document Storage</h3>
                                <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 leading-relaxed mb-4">
                                    Upload, store, and manage all your legal documents securely. Support for PDF, DOCX, and TXT files with automatic organization by case.
                                </p>
                                <ul className="space-y-2 text-xs text-luna-text-light/60 dark:text-luna-text-muted-dark">
                                    <li className="flex items-center gap-2">
                                        <span className="material-symbols-outlined text-base text-orange-500">check_circle</span>
                                        Secure cloud storage
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <span className="material-symbols-outlined text-base text-orange-500">check_circle</span>
                                        PDF preview & download
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <span className="material-symbols-outlined text-base text-orange-500">check_circle</span>
                                        Case-based organization
                                    </li>
                                </ul>
                            </div>

                            {/* Feature 6: EU/German Law Focus */}
                            <div className="feature-card group relative bg-white dark:bg-luna-primary rounded-2xl border border-luna-text-light/10 dark:border-luna-text-dark/20 p-8 shadow-sm hover:shadow-xl animate-on-scroll fade-in-up">
                                <div className="flex items-center justify-center size-16 rounded-2xl bg-gradient-to-br from-red-500/20 to-red-500/5 mb-6 group-hover:scale-110 transition-transform duration-300">
                                    <span className="material-symbols-outlined text-4xl text-red-500">gavel</span>
                                </div>
                                <h3 className="text-xl font-bold mb-3 text-luna-text-light dark:text-luna-text-dark">EU & German Law Expertise</h3>
                                <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 leading-relaxed mb-4">
                                    Built specifically for German and EU legal professionals. Expert knowledge of GDPR, BGB, EU AI Act, DSA, DMA, and more.
                                </p>
                                <ul className="space-y-2 text-xs text-luna-text-light/60 dark:text-luna-text-muted-dark">
                                    <li className="flex items-center gap-2">
                                        <span className="material-symbols-outlined text-base text-red-500">check_circle</span>
                                        GDPR compliance checks
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <span className="material-symbols-outlined text-base text-red-500">check_circle</span>
                                        German Civil Code (BGB)
                                    </li>
                                    <li className="flex items-center gap-2">
                                        <span className="material-symbols-outlined text-base text-red-500">check_circle</span>
                                        Latest EU regulations
                                    </li>
                                </ul>
                            </div>
                        </div>

                        {/* CTA Section */}
                        <div className="mt-16 text-center animate-on-scroll fade-in-up">
                            <div className="inline-flex flex-col items-center gap-4 p-8 rounded-2xl bg-gradient-to-br from-luna-secondary/10 via-luna-secondary/5 to-transparent border border-luna-secondary/20">
                                <h3 className="text-2xl md:text-3xl font-bold text-luna-text-light dark:text-luna-text-dark">
                                    Ready to Transform Your Legal Practice?
                                </h3>
                                <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 max-w-xl">
                                    Join legal professionals across Germany and the EU who trust LRDEnE for accurate, AI-powered legal assistance.
                                </p>
                                <button
                                    onClick={() => onNavigateToAuth('signup')}
                                    className="flex items-center justify-center gap-2 h-12 px-8 bg-luna-secondary text-luna-primary text-base font-bold rounded-lg hover:opacity-90 transition-opacity shadow-lg shadow-luna-secondary/20 mt-2"
                                >
                                    <span>Get Started Free</span>
                                    <span className="material-symbols-outlined">arrow_forward</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </section>

                {/* How It Works Section */}
                <section className="py-20 md:py-28 bg-luna-bg-light dark:bg-luna-bg-dark">
                    <div className="container mx-auto px-4">
                        <div className="text-center mb-16 animate-on-scroll fade-in-up">
                            <h2 className="text-3xl md:text-5xl font-black leading-tight tracking-[-0.033em] mb-4 text-luna-text-light dark:text-luna-text-dark">
                                How LRDEnE Works
                            </h2>
                            <p className="text-lg text-luna-text-light/70 dark:text-luna-text-dark/70 max-w-2xl mx-auto">
                                Get started in minutes and transform your legal workflow
                            </p>
                        </div>

                        <div className="max-w-5xl mx-auto">
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                                {/* Step 1 */}
                                <div className="flex flex-col items-center text-center animate-on-scroll fade-in-up">
                                    <div className="flex items-center justify-center size-20 rounded-full bg-gradient-to-br from-luna-secondary/20 to-luna-secondary/5 mb-6 border-4 border-luna-secondary/30">
                                        <span className="text-3xl font-black text-luna-secondary">1</span>
                                    </div>
                                    <h3 className="text-lg font-bold mb-3 text-luna-text-light dark:text-luna-text-dark">Sign Up Free</h3>
                                    <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 leading-relaxed">
                                        Create your account in seconds. No credit card required. Start with our free plan.
                                    </p>
                                </div>

                                {/* Arrow */}
                                <div className="hidden md:flex items-center justify-center animate-on-scroll fade-in-up">
                                    <span className="material-symbols-outlined text-4xl text-luna-secondary/30">arrow_forward</span>
                                </div>

                                {/* Step 2 */}
                                <div className="flex flex-col items-center text-center animate-on-scroll fade-in-up">
                                    <div className="flex items-center justify-center size-20 rounded-full bg-gradient-to-br from-blue-500/20 to-blue-500/5 mb-6 border-4 border-blue-500/30">
                                        <span className="text-3xl font-black text-blue-500">2</span>
                                    </div>
                                    <h3 className="text-lg font-bold mb-3 text-luna-text-light dark:text-luna-text-dark">Create Your First Case</h3>
                                    <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 leading-relaxed">
                                        Organize your legal matters. Add documents, set deadlines, and track progress.
                                    </p>
                                </div>

                                {/* Arrow */}
                                <div className="hidden md:flex items-center justify-center animate-on-scroll fade-in-up">
                                    <span className="material-symbols-outlined text-4xl text-luna-secondary/30">arrow_forward</span>
                                </div>

                                {/* Step 3 */}
                                <div className="flex flex-col items-center text-center animate-on-scroll fade-in-up">
                                    <div className="flex items-center justify-center size-20 rounded-full bg-gradient-to-br from-green-500/20 to-green-500/5 mb-6 border-4 border-green-500/30">
                                        <span className="text-3xl font-black text-green-500">3</span>
                                    </div>
                                    <h3 className="text-lg font-bold mb-3 text-luna-text-light dark:text-luna-text-dark">Analyze & Chat</h3>
                                    <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 leading-relaxed">
                                        Upload documents for AI analysis or chat with LRDEnE for instant legal guidance with citations.
                                    </p>
                                </div>
                            </div>

                            {/* Step 4 (Full Width) */}
                            <div className="mt-12 flex flex-col items-center text-center animate-on-scroll fade-in-up">
                                <div className="flex items-center justify-center size-20 rounded-full bg-gradient-to-br from-purple-500/20 to-purple-500/5 mb-6 border-4 border-purple-500/30">
                                    <span className="text-3xl font-black text-purple-500">4</span>
                                </div>
                                <h3 className="text-xl font-bold mb-3 text-luna-text-light dark:text-luna-text-dark">Generate Legal Documents</h3>
                                <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 leading-relaxed max-w-2xl">
                                    Use our AI-powered template library to generate GDPR requests, rent reduction notices, SCHUFA disputes, and more in minutes.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Statistics Section */}
                <section className="py-16 md:py-24 bg-white dark:bg-luna-primary border-y border-luna-text-light/10 dark:border-luna-text-dark/10">
                    <div className="container mx-auto px-4">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
                            {[
                                { number: '100%', label: 'Complete & Ready', icon: 'check_circle' },
                                { number: '7+', label: 'Legal Templates', icon: 'article' },
                                { number: '50+', label: 'Documentation Guides', icon: 'menu_book' },
                                { number: '24/7', label: 'AI Available', icon: 'schedule' },
                            ].map((stat, index) => (
                                <div key={index} className="flex flex-col items-center text-center animate-on-scroll fade-in-up">
                                    <div className="flex items-center justify-center size-16 rounded-2xl bg-gradient-to-br from-luna-secondary/10 to-luna-secondary/5 mb-4">
                                        <span className="material-symbols-outlined text-3xl text-luna-secondary">{stat.icon}</span>
                                    </div>
                                    <div className="text-3xl md:text-4xl font-black text-luna-secondary mb-2">{stat.number}</div>
                                    <div className="text-xs md:text-sm font-medium text-luna-text-light/70 dark:text-luna-text-dark/70">{stat.label}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Use Cases Section */}
                <section className="py-20 md:py-28 bg-luna-bg-light dark:bg-luna-bg-dark">
                    <div className="container mx-auto px-4">
                        <div className="text-center mb-16 animate-on-scroll fade-in-up">
                            <h2 className="text-3xl md:text-5xl font-black leading-tight tracking-[-0.033em] mb-4 text-luna-text-light dark:text-luna-text-dark">
                                Perfect For
                            </h2>
                            <p className="text-lg text-luna-text-light/70 dark:text-luna-text-dark/70 max-w-2xl mx-auto">
                                Whether you&apos;re a solo practitioner or part of a large firm
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
                            {[
                                {
                                    title: 'Law Firms',
                                    description: 'Streamline document review, compliance checks, and case research with AI-powered analysis.',
                                    icon: 'account_balance',
                                    color: 'from-blue-500/20 to-blue-500/5',
                                    iconColor: 'text-blue-500'
                                },
                                {
                                    title: 'Corporate Legal Teams',
                                    description: 'Ensure contracts and policies comply with GDPR, EU AI Act, and German regulations.',
                                    icon: 'business',
                                    color: 'from-purple-500/20 to-purple-500/5',
                                    iconColor: 'text-purple-500'
                                },
                                {
                                    title: 'Legal Consultants',
                                    description: 'Generate professional legal documents quickly and provide clients with accurate advice.',
                                    icon: 'badge',
                                    color: 'from-green-500/20 to-green-500/5',
                                    iconColor: 'text-green-500'
                                },
                                {
                                    title: 'Compliance Officers',
                                    description: 'Perform comprehensive compliance audits and identify risks in legal documents.',
                                    icon: 'verified_user',
                                    color: 'from-orange-500/20 to-orange-500/5',
                                    iconColor: 'text-orange-500'
                                },
                                {
                                    title: 'Law Students',
                                    description: 'Learn from expert AI analysis and understand complex German and EU legal frameworks.',
                                    icon: 'school',
                                    color: 'from-red-500/20 to-red-500/5',
                                    iconColor: 'text-red-500'
                                },
                                {
                                    title: 'Legal Tech Startups',
                                    description: 'Integrate LRDEnE API capabilities into your own legal technology solutions.',
                                    icon: 'rocket_launch',
                                    color: 'from-luna-secondary/20 to-luna-secondary/5',
                                    iconColor: 'text-luna-secondary'
                                },
                            ].map((useCase, index) => (
                                <div key={index} className="bg-white dark:bg-luna-primary rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 p-6 hover:shadow-lg transition-all duration-300 animate-on-scroll fade-in-up">
                                    <div className={`flex items-center justify-center size-14 rounded-xl bg-gradient-to-br ${useCase.color} mb-4`}>
                                        <span className={`material-symbols-outlined text-3xl ${useCase.iconColor}`}>{useCase.icon}</span>
                                    </div>
                                    <h3 className="text-lg font-bold mb-2 text-luna-text-light dark:text-luna-text-dark">{useCase.title}</h3>
                                    <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 leading-relaxed">{useCase.description}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Who We Help Section */}
                <section className="py-20 md:py-28 bg-white dark:bg-luna-primary border-y border-luna-text-light/10 dark:border-luna-text-dark/10">
                    <div className="container mx-auto px-4">
                        <div className="text-center mb-16 animate-on-scroll fade-in-up">
                            <h2 className="text-3xl md:text-5xl font-black leading-tight tracking-[-0.033em] mb-4 text-luna-text-light dark:text-luna-text-dark">
                                Who Can Benefit from LRDEnE?
                            </h2>
                            <p className="text-lg text-luna-text-light/70 dark:text-luna-text-dark/70 max-w-2xl mx-auto">
                                LRDEnE empowers legal professionals and individuals across Germany and the EU
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
                            {[
                                {
                                    title: 'Individual Citizens',
                                    description: 'Protect your rights with professional legal documents. Generate GDPR requests, dispute SCHUFA entries, handle rental issues, and more—all without expensive legal fees.',
                                    icon: 'person',
                                    color: 'from-blue-500/20 to-blue-500/5',
                                    iconColor: 'text-blue-500',
                                    useCases: ['GDPR data requests', 'Rental disputes', 'SCHUFA corrections', 'Consumer rights protection']
                                },
                                {
                                    title: 'Solo Practitioners',
                                    description: 'Level the playing field with AI-powered research and document generation. Handle more cases efficiently while maintaining the highest quality standards.',
                                    icon: 'account_circle',
                                    color: 'from-purple-500/20 to-purple-500/5',
                                    iconColor: 'text-purple-500',
                                    useCases: ['Case research acceleration', 'Document drafting', 'Compliance verification', 'Client communication']
                                },
                                {
                                    title: 'Small to Medium Law Firms',
                                    description: 'Scale your practice without scaling costs. Automate routine compliance checks and document reviews, allowing your team to focus on high-value legal work.',
                                    icon: 'groups',
                                    color: 'from-green-500/20 to-green-500/5',
                                    iconColor: 'text-green-500',
                                    useCases: ['Bulk document analysis', 'Team collaboration', 'Standardized workflows', 'Cost reduction']
                                },
                                {
                                    title: 'Corporate Legal Departments',
                                    description: 'Ensure compliance across all contracts and policies. Stay ahead of regulatory changes with automated monitoring and risk assessment.',
                                    icon: 'business_center',
                                    color: 'from-orange-500/20 to-orange-500/5',
                                    iconColor: 'text-orange-500',
                                    useCases: ['Contract compliance', 'Policy review', 'Risk management', 'Regulatory updates']
                                },
                                {
                                    title: 'Compliance & Risk Officers',
                                    description: 'Identify and mitigate legal risks proactively. Get comprehensive compliance audits with prioritized action items and legal citations.',
                                    icon: 'shield',
                                    color: 'from-red-500/20 to-red-500/5',
                                    iconColor: 'text-red-500',
                                    useCases: ['Compliance audits', 'Risk assessment', 'Regulatory reporting', 'Documentation']
                                },
                                {
                                    title: 'Legal Tech Companies',
                                    description: 'Integrate powerful legal AI capabilities into your platform. Use our API to enhance your own legal technology solutions.',
                                    icon: 'integration_instructions',
                                    color: 'from-luna-secondary/20 to-luna-secondary/5',
                                    iconColor: 'text-luna-secondary',
                                    useCases: ['API integration', 'White-label solutions', 'Custom workflows', 'Platform enhancement']
                                },
                            ].map((persona, index) => (
                                <div key={index} className="bg-luna-bg-light dark:bg-luna-bg-dark rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 p-6 hover:shadow-lg transition-all duration-300 animate-on-scroll fade-in-up">
                                    <div className={`flex items-center justify-center size-16 rounded-xl bg-gradient-to-br ${persona.color} mb-4`}>
                                        <span className={`material-symbols-outlined text-4xl ${persona.iconColor}`}>{persona.icon}</span>
                                    </div>
                                    <h3 className="text-xl font-bold mb-3 text-luna-text-light dark:text-luna-text-dark">{persona.title}</h3>
                                    <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 leading-relaxed mb-4">{persona.description}</p>
                                    <div className="space-y-2">
                                        <p className="text-xs font-semibold text-luna-text-light/60 dark:text-luna-text-dark/60 uppercase tracking-wide mb-2">Common Use Cases:</p>
                                        <ul className="space-y-1.5">
                                            {persona.useCases.map((useCase, idx) => (
                                                <li key={idx} className="flex items-center gap-2 text-xs text-luna-text-light/70 dark:text-luna-text-dark/70">
                                                    <span className={`material-symbols-outlined text-sm ${persona.iconColor}`}>check_circle</span>
                                                    {useCase}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Problem/Solution Section */}
                <section className="py-20 md:py-28 bg-luna-bg-light dark:bg-luna-bg-dark">
                    <div className="container mx-auto px-4">
                        <div className="text-center mb-16 animate-on-scroll fade-in-up">
                            <h2 className="text-3xl md:text-5xl font-black leading-tight tracking-[-0.033em] mb-4 text-luna-text-light dark:text-luna-text-dark">
                                The Challenges We Solve
                            </h2>
                            <p className="text-lg text-luna-text-light/70 dark:text-luna-text-dark/70 max-w-2xl mx-auto">
                                Common pain points in legal practice—and how LRDEnE addresses them
                            </p>
                        </div>

                        <div className="max-w-5xl mx-auto space-y-8">
                            {[
                                {
                                    problem: 'Time-Consuming Document Review',
                                    problemIcon: 'schedule',
                                    solution: 'AI-Powered Instant Analysis',
                                    solutionIcon: 'flash_on',
                                    description: 'What used to take hours now takes minutes. Upload documents and receive comprehensive analysis with risk assessment, compliance checks, and actionable recommendations instantly.',
                                    benefits: ['80% time reduction', '24/7 availability', 'Consistent quality', 'Detailed citations']
                                },
                                {
                                    problem: 'Keeping Up with Regulatory Changes',
                                    problemIcon: 'update',
                                    solution: 'Always Current AI Knowledge',
                                    solutionIcon: 'sync',
                                    description: 'Our AI is continuously updated with the latest EU directives, German case law, and regulatory changes. Never miss an important legal update again.',
                                    benefits: ['Real-time updates', 'EU & German focus', 'Case law integration', 'Regulatory monitoring']
                                },
                                {
                                    problem: 'High Legal Document Costs',
                                    problemIcon: 'payments',
                                    solution: 'Affordable Template Library',
                                    solutionIcon: 'savings',
                                    description: 'Generate professional legal documents without expensive lawyer fees. Our AI-powered templates ensure accuracy while dramatically reducing costs.',
                                    benefits: ['90% cost savings', 'Professional quality', 'Instant generation', 'PDF export']
                                },
                                {
                                    problem: 'Complex Legal Research',
                                    problemIcon: 'search',
                                    solution: 'AI Legal Research Assistant',
                                    solutionIcon: 'support_agent',
                                    description: 'Chat with LRDEnE, your AI-powered legal research assistant. Get information and analysis with proper citations, context-aware responses, and comprehensive guidance. Note: This is not legal advice - always consult a qualified lawyer for important legal matters.',
                                    benefits: ['Legal information access', 'Precise citations', 'Context awareness', '24/7 availability']
                                },
                            ].map((item, index) => (
                                <div key={index} className="bg-white dark:bg-luna-primary rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 p-8 hover:shadow-lg transition-all duration-300 animate-on-scroll fade-in-up">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                                        <div className="flex flex-col gap-4">
                                            <div className="flex items-center gap-4">
                                                <div className="flex items-center justify-center size-14 rounded-lg bg-red-500/10">
                                                    <span className="material-symbols-outlined text-3xl text-red-500">{item.problemIcon}</span>
                                                </div>
                                                <div>
                                                    <p className="text-xs font-semibold text-red-600 dark:text-red-400 uppercase tracking-wide mb-1">The Problem</p>
                                                    <h3 className="text-lg font-bold text-luna-text-light dark:text-luna-text-dark">{item.problem}</h3>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex flex-col gap-4">
                                            <div className="flex items-center gap-4">
                                                <div className="flex items-center justify-center size-14 rounded-lg bg-green-500/10">
                                                    <span className="material-symbols-outlined text-3xl text-green-500">{item.solutionIcon}</span>
                                                </div>
                                                <div>
                                                    <p className="text-xs font-semibold text-green-600 dark:text-green-400 uppercase tracking-wide mb-1">Our Solution</p>
                                                    <h3 className="text-lg font-bold text-luna-text-light dark:text-luna-text-dark">{item.solution}</h3>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <p className="mt-6 text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 leading-relaxed">{item.description}</p>
                                    <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                                        {item.benefits.map((benefit, idx) => (
                                            <div key={idx} className="flex items-center gap-2 text-xs text-luna-text-light/70 dark:text-luna-text-dark/70">
                                                <span className="material-symbols-outlined text-sm text-green-500">check</span>
                                                {benefit}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Real-World Scenarios Section */}
                <section className="py-20 md:py-28 bg-white dark:bg-luna-primary border-y border-luna-text-light/10 dark:border-luna-text-dark/10">
                    <div className="container mx-auto px-4">
                        <div className="text-center mb-16 animate-on-scroll fade-in-up">
                            <h2 className="text-3xl md:text-5xl font-black leading-tight tracking-[-0.033em] mb-4 text-luna-text-light dark:text-luna-text-dark">
                                Real-World Scenarios
                            </h2>
                            <p className="text-lg text-luna-text-light/70 dark:text-luna-text-dark/70 max-w-2xl mx-auto">
                                See how LRDEnE helps solve actual legal challenges
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
                            {[
                                {
                                    scenario: 'GDPR Data Request',
                                    icon: 'privacy_tip',
                                    color: 'from-blue-500/20 to-blue-500/5',
                                    iconColor: 'text-blue-500',
                                    situation: 'A citizen wants to request their personal data from a company.',
                                    solution: 'Generate a professional DSGVO Auskunft document in minutes. The AI ensures all required elements are included per GDPR Article 15.',
                                    outcome: 'Professional document ready in 5 minutes, properly formatted with legal citations.'
                                },
                                {
                                    scenario: 'Rental Dispute',
                                    icon: 'home',
                                    color: 'from-green-500/20 to-green-500/5',
                                    iconColor: 'text-green-500',
                                    situation: 'Tenant discovers defects in rental property and wants to reduce rent.',
                                    solution: 'Create a Mängelanzeige and Mietminderung notice with proper German legal language and BGB citations.',
                                    outcome: 'Legally sound documents that protect tenant rights and can be used in court if needed.'
                                },
                                {
                                    scenario: 'Contract Compliance Review',
                                    icon: 'description',
                                    color: 'from-purple-500/20 to-purple-500/5',
                                    iconColor: 'text-purple-500',
                                    situation: 'A law firm needs to review 50 contracts for GDPR compliance.',
                                    solution: 'Upload all contracts for batch analysis. AI identifies non-compliant clauses with specific GDPR article references.',
                                    outcome: 'Complete compliance report in hours instead of weeks, with prioritized action items.'
                                },
                                {
                                    scenario: 'SCHUFA Dispute',
                                    icon: 'credit_card',
                                    color: 'from-orange-500/20 to-orange-500/5',
                                    iconColor: 'text-orange-500',
                                    situation: 'Individual finds incorrect information in their SCHUFA report.',
                                    solution: 'Generate SCHUFA Bestreitung document with proper legal framework and required information.',
                                    outcome: 'Professional dispute letter following best practices for correction requests. Results may vary and are not guaranteed.'
                                },
                                {
                                    scenario: 'Legal Research',
                                    icon: 'gavel',
                                    color: 'from-red-500/20 to-red-500/5',
                                    iconColor: 'text-red-500',
                                    situation: 'Lawyer needs quick research on EU AI Act compliance requirements.',
                                    solution: 'Chat with LRDEnE AI assistant for instant expert guidance with precise article citations.',
                                    outcome: 'Comprehensive answer with citations in minutes, saving hours of research time.'
                                },
                                {
                                    scenario: 'Policy Compliance Audit',
                                    icon: 'verified',
                                    color: 'from-luna-secondary/20 to-luna-secondary/5',
                                    iconColor: 'text-luna-secondary',
                                    situation: 'Corporate legal team needs to audit privacy policy for GDPR compliance.',
                                    solution: 'Upload policy document for AI analysis. Receive detailed compliance report with risk assessment.',
                                    outcome: 'Complete audit report identifying all compliance gaps with specific legal requirements and recommendations.'
                                },
                            ].map((caseStudy, index) => (
                                <div key={index} className="bg-luna-bg-light dark:bg-luna-bg-dark rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 p-6 hover:shadow-lg transition-all duration-300 animate-on-scroll fade-in-up">
                                    <div className={`flex items-center justify-center size-14 rounded-xl bg-gradient-to-br ${caseStudy.color} mb-4`}>
                                        <span className={`material-symbols-outlined text-3xl ${caseStudy.iconColor}`}>{caseStudy.icon}</span>
                                    </div>
                                    <h3 className="text-lg font-bold mb-3 text-luna-text-light dark:text-luna-text-dark">{caseStudy.scenario}</h3>
                                    <div className="space-y-3 text-sm">
                                        <div>
                                            <p className="font-semibold text-luna-text-light/80 dark:text-luna-text-dark/80 mb-1">Situation:</p>
                                            <p className="text-luna-text-light/70 dark:text-luna-text-dark/70 leading-relaxed">{caseStudy.situation}</p>
                                        </div>
                                        <div>
                                            <p className="font-semibold text-luna-text-light/80 dark:text-luna-text-dark/80 mb-1">Solution:</p>
                                            <p className="text-luna-text-light/70 dark:text-luna-text-dark/70 leading-relaxed">{caseStudy.solution}</p>
                                        </div>
                                        <div className="pt-2 border-t border-luna-text-light/10 dark:border-luna-text-dark/10">
                                            <p className="font-semibold text-green-600 dark:text-green-400 mb-1">Outcome:</p>
                                            <p className="text-luna-text-light/70 dark:text-luna-text-dark/70 leading-relaxed">{caseStudy.outcome}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Testimonials Section */}
                <section className="py-20 md:py-28 bg-luna-bg-light dark:bg-luna-bg-dark">
                    <div className="container mx-auto px-4">
                        <div className="text-center mb-16 animate-on-scroll fade-in-up">
                            <h2 className="text-3xl md:text-5xl font-black leading-tight tracking-[-0.033em] mb-4 text-luna-text-light dark:text-luna-text-dark">
                                What Legal Professionals Say
                            </h2>
                            <p className="text-lg text-luna-text-light/70 dark:text-luna-text-dark/70 max-w-2xl mx-auto">
                                Trusted by lawyers, compliance officers, and legal professionals across Germany and the EU
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
                            {[
                                {
                                    name: 'Dr. Anke Schmidt',
                                    role: 'Partner, Schmidt & Meier Rechtsanwälte',
                                    image: 'https://lh3.googleusercontent.com/aida-public/AB6AXuC6oq33dNp6A-9NrO3YTfoAiNzhgWchFlJuFX0rTR88Ia5vLnjO8OS23dxBfNeB2iFSmP6TfzlTtpIZJPYNQ3aD2g5i5Td_5WlHtD9f8MXuQWh5pUE_Lp26Ayht0Pu3FHP34E3pEN8WfbEA5P2GZzoXMyfkpTBcDhl3WQ56kuDkn-RAOr2xCQpVII4BEDRoLmjtNSthqKVF4uReXtrxQlDVWbQZG_0U2MY9G0Z5smvb9KvpYrbwQhi4-G6kFaqf-OskjR16cIS0_ZaS',
                                    quote: 'LRDEnE has become an indispensable tool in our firm. Its ability to cross-reference German and EU law with such speed and accuracy has cut our research time in half.',
                                    rating: 5
                                },
                                {
                                    name: 'Jean-Luc Dubois',
                                    role: 'Corporate Counsel, EuroTech Innovations',
                                    image: 'https://lh3.googleusercontent.com/aida-public/AB6AXuAqByYWhqenqZy3P1S-knNZuc7IU0sFAXCbjzpXr-eXrYT7kd2q9A2zlAv54wEqRiVeo5hXeiLm4MlMCjux8aa3gL_G1OUiyXCzIQbtUMJxH8v1AujPdjjmamaK9YFLrpzS6ZtT-PFrd9HmhdGwh07p83l5jNnjMxoo49Qt4fmbeZbPEbHfnrl5RghUQt3kQLYmmLAhP93C7Hf1RxkmlqoFRzTgxUMWDU1QJ2gx_H9B_E7RuKby-3foRmDv8nhYclgppTXovL5x9P1tyhpEqUFndQjxNAaSK',
                                    quote: 'The citation analysis feature is a game-changer. It gives us immense confidence in our legal positions, knowing that every source is thoroughly vetted. LRDEnE is simply brilliant.',
                                    rating: 5
                                },
                                {
                                    name: 'Sofia Rossi',
                                    role: 'Legal Analyst, EU Policy Watch',
                                    image: 'https://lh3.googleusercontent.com/aida-public/AB6AXuCldMAjJQpyX4b7kFOPZbYY-SaD-0yaDUhmujuknT6XeFTAWkqTH5f6MHmOewJ0Tc3LSByHXowbygEDLyqP3AIU7Aw0C5ujIwn06gDHWr3ClS1w_y2VNfWnCGr3oT104IGzYIaH3ltbcd-HzBe1wAANdx8iDT_QZ1BPnGTa3r4jQE7BGXNC91AJStxu_oSljDEkORJD_dD29AIvUcVzRcPHJzfF6GwbJcW272JzvHKhy3PS3kqnZmz9xNA6wFe75m5qag0Up4EVFNbP',
                                    quote: 'As a legal analyst focusing on EU directives, navigating the complexities is my daily challenge. LRDEnE provides the clarity I need with its intuitive interface and powerful AI.',
                                    rating: 5
                                },
                            ].map((testimonial, index) => (
                                <div key={index} className="bg-white dark:bg-luna-primary rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 p-6 hover:shadow-lg transition-all duration-300 animate-on-scroll fade-in-up">
                                    <div className="flex items-center gap-1 mb-4">
                                        {[...Array(testimonial.rating)].map((_, i) => (
                                            <span key={i} className="material-symbols-outlined text-yellow-500 text-lg">star</span>
                                        ))}
                                    </div>
                                    <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 leading-relaxed mb-6 italic">
                                        &ldquo;{testimonial.quote}&rdquo;
                                    </p>
                                    <div className="flex items-center gap-3 pt-4 border-t border-luna-text-light/10 dark:border-luna-text-dark/10">
                                        <img
                                            src={testimonial.image}
                                            alt={testimonial.name}
                                            className="size-12 rounded-full object-cover"
                                            onError={handleImageError}
                                        />
                                        <div>
                                            <p className="text-sm font-bold text-luna-text-light dark:text-luna-text-dark">{testimonial.name}</p>
                                            <p className="text-xs text-luna-text-light/60 dark:text-luna-text-dark/60">{testimonial.role}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Benefits Section */}
                <section className="py-20 md:py-28 bg-white dark:bg-luna-primary">
                    <div className="container mx-auto px-4">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
                            <div className="animate-on-scroll fade-in-up">
                                <h2 className="text-3xl md:text-5xl font-black leading-tight tracking-[-0.033em] mb-6 text-luna-text-light dark:text-luna-text-dark">
                                    Why Choose LRDEnE?
                                </h2>
                                <div className="space-y-6">
                                    {[
                                        {
                                            icon: 'speed',
                                            title: 'Save Time',
                                            description: 'Reduce document review time by up to 80% with instant AI analysis and automated compliance checks.'
                                        },
                                        {
                                            icon: 'verified',
                                            title: 'Legal Accuracy',
                                            description: 'Built specifically for German and EU law with expert knowledge of GDPR, BGB, and latest regulations.'
                                        },
                                        {
                                            icon: 'security',
                                            title: 'Secure & Private',
                                            description: 'Your documents and data are encrypted and stored securely. We never share your information.'
                                        },
                                        {
                                            icon: 'trending_up',
                                            title: 'Stay Current',
                                            description: 'AI continuously updated with latest EU directives, German case law, and regulatory changes.'
                                        },
                                    ].map((benefit, index) => (
                                        <div key={index} className="flex gap-4 animate-on-scroll fade-in-up">
                                            <div className="flex-shrink-0 flex items-center justify-center size-12 rounded-lg bg-luna-secondary/10">
                                                <span className="material-symbols-outlined text-2xl text-luna-secondary">{benefit.icon}</span>
                                            </div>
                                            <div>
                                                <h3 className="text-lg font-bold mb-1 text-luna-text-light dark:text-luna-text-dark">{benefit.title}</h3>
                                                <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 leading-relaxed">{benefit.description}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="animate-on-scroll fade-in-up">
                                <div className="bg-gradient-to-br from-luna-secondary/10 via-luna-secondary/5 to-transparent rounded-2xl p-8 border border-luna-secondary/20">
                                    <h3 className="text-2xl font-bold mb-6 text-luna-text-light dark:text-luna-text-dark">
                                        Trusted by Legal Professionals
                                    </h3>
                                    <div className="space-y-4">
                                        {[
                                            { metric: '3,000+', label: 'Lines of Production Code' },
                                            { metric: '7', label: 'German Legal Document Templates' },
                                            { metric: '24-hour', label: 'AI Response Caching' },
                                            { metric: '100%', label: 'GDPR Compliant' },
                                        ].map((stat, index) => (
                                            <div key={index} className="flex items-center justify-between py-3 border-b border-luna-text-light/10 dark:border-luna-text-dark/10">
                                                <span className="text-sm font-medium text-luna-text-light/80 dark:text-luna-text-dark/80">{stat.label}</span>
                                                <span className="text-lg font-bold text-luna-secondary">{stat.metric}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* FAQ Section */}
                <section className="py-20 md:py-28 bg-luna-bg-light dark:bg-luna-bg-dark">
                    <div className="container mx-auto px-4">
                        <div className="text-center mb-16 animate-on-scroll fade-in-up">
                            <h2 className="text-3xl md:text-5xl font-black leading-tight tracking-[-0.033em] mb-4 text-luna-text-light dark:text-luna-text-dark">
                                Frequently Asked Questions
                            </h2>
                            <p className="text-lg text-luna-text-light/70 dark:text-luna-text-dark/70 max-w-2xl mx-auto">
                                Everything you need to know about LRDEnE
                            </p>
                        </div>

                        <div className="max-w-3xl mx-auto space-y-4">
                            {[
                                {
                                    question: 'What legal jurisdictions does LRDEnE support?',
                                    answer: 'LRDEnE specializes in German law (BGB, Bürgerliches Gesetzbuch) and EU regulations including GDPR, EU AI Act, Digital Services Act (DSA), and Digital Markets Act (DMA). Our AI has deep expertise in these specific legal frameworks.'
                                },
                                {
                                    question: 'How accurate is the AI analysis?',
                                    answer: 'Our AI is fine-tuned specifically for German and EU law and provides citations with article numbers, actionable suggestions, and risk assessments. All responses include proper legal citations you can verify. However, AI-generated content may contain errors and should not replace professional legal advice. Always consult with a qualified lawyer for important legal matters.'
                                },
                                {
                                    question: 'Is my data secure and private?',
                                    answer: 'Yes. LRDEnE uses industry-standard security measures including encrypted data storage via Supabase, Row-Level Security (RLS) policies ensuring only you can access your data, and secure third-party service providers (Supabase, Google Gemini AI, payment processors) that are contractually bound to protect your data. We do not sell your data to third parties for marketing purposes. See our Privacy Policy for complete details on data sharing and security.'
                                },
                                {
                                    question: 'What document types can I analyze?',
                                    answer: 'You can upload PDF, TXT, and DOCX files for analysis. Our AI can extract text, analyze compliance, identify risks, and provide actionable suggestions based on EU and German law requirements.'
                                },
                                {
                                    question: 'Can I generate legal documents with LRDEnE?',
                                    answer: 'Yes! Our AI-powered Template Library includes 7+ German legal document types including GDPR requests (DSGVO Auskunft/Löschung), rent reduction notices (Mietminderung), defect notices (Mängelanzeige), and SCHUFA disputes. All templates can be customized and exported to PDF.'
                                },
                                {
                                    question: 'Do I need coding knowledge to use LRDEnE?',
                                    answer: 'No coding required! LRDEnE is designed for legal professionals, not developers. Simply sign up, create a case, upload documents or chat with our AI assistant. Everything is intuitive and user-friendly.'
                                },
                            ].map((faq, index) => (
                                <details key={index} className="bg-white dark:bg-luna-primary rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 p-6 cursor-pointer hover:shadow-md transition-shadow animate-on-scroll fade-in-up">
                                    <summary className="font-bold text-luna-text-light dark:text-luna-text-dark cursor-pointer flex items-center gap-3">
                                        <span className="material-symbols-outlined text-luna-secondary flex-shrink-0">expand_more</span>
                                        {faq.question}
                                    </summary>
                                    <p className="mt-4 text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 leading-relaxed pl-9">
                                        {faq.answer}
                                    </p>
                                </details>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Final CTA Section */}
                <section className="py-20 md:py-32 bg-gradient-to-br from-luna-secondary/20 via-luna-secondary/10 to-luna-secondary/5 border-y border-luna-secondary/20">
                    <div className="container mx-auto px-4">
                        <div className="max-w-4xl mx-auto text-center animate-on-scroll fade-in-up">
                            <h2 className="text-4xl md:text-6xl font-black leading-tight tracking-[-0.033em] mb-6 text-luna-text-light dark:text-luna-text-dark">
                                Ready to Transform Your Legal Practice?
                            </h2>
                            <p className="text-lg md:text-xl text-luna-text-light/80 dark:text-luna-text-dark/80 mb-8 max-w-2xl mx-auto leading-relaxed">
                                Join legal professionals across Germany and the EU who trust LRDEnE for accurate, AI-powered legal assistance. Start analyzing your first case for free.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                                <button
                                    onClick={() => onNavigateToAuth('signup')}
                                    className="flex items-center justify-center gap-2 h-14 px-8 bg-luna-secondary text-luna-primary text-base font-bold rounded-lg hover:opacity-90 transition-opacity shadow-lg shadow-luna-secondary/30"
                                >
                                    <span>Get Started Free</span>
                                    <span className="material-symbols-outlined">arrow_forward</span>
                                </button>
                                <button
                                    onClick={() => onNavigate?.('Features')}
                                    className="flex items-center justify-center gap-2 h-14 px-8 bg-white/80 dark:bg-luna-primary/80 backdrop-blur-md border border-luna-text-light/20 dark:border-luna-text-dark/30 text-luna-text-light dark:text-luna-text-dark text-base font-bold rounded-lg hover:bg-white dark:hover:bg-luna-primary transition-all"
                                >
                                    <span>View Features</span>
                                    <span className="material-symbols-outlined">info</span>
                                </button>
                            </div>
                            <p className="mt-6 text-sm text-luna-text-light/60 dark:text-luna-text-dark/60">
                                No credit card required • Free plan available • Cancel anytime
                            </p>
                        </div>
                    </div>
                </section>

                <div className="py-8 border-b border-luna-text-light/10 dark:border-luna-text-dark/10 animate-on-scroll fade-in-up" ref={trustLogosRef as React.RefObject<HTMLDivElement>}>
                    <div className="container mx-auto px-4">
                        <h3 className="text-center text-sm font-semibold text-luna-text-light/50 dark:text-luna-text-dark/50 uppercase tracking-widest mb-6">Trusted by leading firms &amp; legal innovators</h3>
                        <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-4 opacity-50">
                            {[
                                "https://lh3.googleusercontent.com/aida-public/AB6AXuCXlt4gLdV7liz0Mx3aRy6KpqufMD6bk5XRWyMMCr6pWGe-J3uV2v-hclnxQjr0vdXeF3A5RBvHFkPdj166FTYTWinmWFbqw3-d62-Weui4igN434mh3BOQoPgVpMjxwmaJDm8My1P2YuM-__QdqvFokhUaXNz51P0srKnxzipKo8EEQgQkHUGVcMzb4qMtew-xUXyBK6VU3hLK2rLBETHie8E8mKVrOit83hZ1sEQzvI6zlaquXmEYvqwk-vO-1S3KzHRZATwmZpwq",
                                "https://lh3.googleusercontent.com/aida-public/AB6AXuDUNljWV4SMGnt1MAABTAVblgrHL68PioxT5bqxoLezYVYTYjEgiIHwbKkVRpjOoFEOqUt34Fv9W58ZC363-SOSn5rRESykdP2g9VGUhOtN9TqFKgvdh1oMxs3CkfZK42azwTEMfHep_R2lpgeCIOnq94IRqB52or6wjpbykDfT5b11_YgLRpvIsvk_PpMWWxAgGT_vKCgQTzgMSw7YW9d9DiVQg9312-kpDiausSfHzK04cHPjNwpqOc-JHnucg9HTJxctOu62tEEs",
                                "https://lh3.googleusercontent.com/aida-public/AB6AXuB4VQFo3nN2RgYdhzvdrl3A-jbXxO1PKXgG4vkfHvREIEUfECylie7kIO9PGZnVEIwsZxvPFbx_VF4fI5qbfbn-CWZ5qFNFKphMdMTQMsWz9tg-w5dSwvQuuWuTxW7jZLIlfDVZN-N8kNEhrwYrIbm8JqpG8wPpp7Qa4_6V1wN9ZqijaKRK_ES9N-fKy0TEbB08wapLzhnATLcVsCcyhzKqtZciCUijYoFbxU9QDYn4Ezwdvzc3Hh-9Y6-0NR76ipC4YMMO_tzoeEFgz",
                                "https://lh3.googleusercontent.com/aida-public/AB6AXuAL7r5_10GqM2GbOCIcYfGFemgSGjHSu1hvH_Eey1mSWuygSzf8Kp4oD2u9DruShIXsqAISxgR7mj4XyI_ji7raKqHa3cL56W-0OBxC9KZ8z8CayQ-UUcCU2rrndlPxoSPVCQS9tH3pQ-KSb4ZIjuAu4o4oW_LFjIwYcAgUTe5ArSFAlDdrlkV3hEMqDK6T3rx0iq9VN_UmLhrzQR15s6UbtS7N5l43ByD0fHIpoeW-K7CORyDsj-i3qL-F5MOv-G6obo6-SdgJ8",
                                "https://lh3.googleusercontent.com/aida-public/AB6AXuBkje0wQIHs_eoBQ7IZIqaPfqqxd4QHycAmPE7qJXX5znlnWavTQ15Has7A9rissW2tNArJgWOGnhreuJCcbpzKUED84p0hrWVMqWZMDlIXbDfN62T0uKeLLQMVtw_BW7V4Px3vLBLX1Q3J8MkwM0JmBNJ69L6O0GPwoFufqewTcisT_XGqp-_jdYmjZwAH1ae2Awdr-q8yicFcDGnxtKS8ZF69BLNkeY3koG7n57nCJDCuif6MDOJb4uaUpZGn0lnQkRQ_-4FAgpd",
                            ].map((src, index) => (
                                <img
                                    key={index}
                                    className="h-8"
                                    alt={`Logo for a partner company ${index + 1}`}
                                    src={src}
                                    onError={(e) => {
                                        const target = e.target as HTMLImageElement;
                                        target.style.display = 'none';
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </main>

            {/* Scroll to Top Button */}
            <button
                onClick={scrollToTop}
                className={`scroll-to-top flex items-center justify-center size-12 rounded-full bg-luna-secondary text-luna-primary shadow-lg hover:bg-luna-secondary/90 transition-all ${showScrollTop ? 'visible' : ''}`}
                aria-label="Scroll to top"
                type="button"
            >
                <span className="material-symbols-outlined text-2xl">arrow_upward</span>
            </button>
        </div>
    );
};


const Sidebar: React.FC<{
    currentPage: string;
    onNavigate: (page: string, caseId?: string) => void;
    selectedCaseId: string | null;
    isCollapsed: boolean;
    toggleSidebar: () => void;
    cases: Case[];
    onNewCase: () => void;
    profile: Profile | null;
    folders?: Folder[];
    onCreateFolder?: (name: string) => Promise<void>;
    onDeleteFolder?: (folderId: string) => Promise<void>;
    onRenameFolder?: (folderId: string, newName: string) => Promise<void>;
}> = ({ currentPage, onNavigate, selectedCaseId, isCollapsed, toggleSidebar, cases, onNewCase, profile, folders = [], onCreateFolder, onDeleteFolder, onRenameFolder }) => {
    const [showCreateFolder, setShowCreateFolder] = useState(false);
    const [newFolderName, setNewFolderName] = useState('');
    const [editingFolderId, setEditingFolderId] = useState<string | null>(null);
    const [editingFolderName, setEditingFolderName] = useState('');
    const [showFolderMenu, setShowFolderMenu] = useState<string | null>(null);
    const newFolderInputRef = useRef<HTMLInputElement | null>(null);
    const handleFolderRenameInputRef = useCallback((input: HTMLInputElement | null) => {
        if (input) {
            input.focus();
            input.select();
        }
    }, []);

    const handleCreateFolderSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (onCreateFolder && newFolderName.trim()) {
            await onCreateFolder(newFolderName);
            setNewFolderName('');
            setShowCreateFolder(false);
        }
    };

    const handleRenameFolderSubmit = async (folderId: string) => {
        if (onRenameFolder && editingFolderName.trim()) {
            await onRenameFolder(folderId, editingFolderName);
            setEditingFolderId(null);
            setEditingFolderName('');
        }
    };

    // Group cases by folder
    const casesByFolder = cases.reduce((acc, _case) => {
        const folderId = _case.folder_id || 'no-folder';
        if (!acc[folderId]) {
            acc[folderId] = [];
        }
        acc[folderId].push(_case);
        return acc;
    }, {} as Record<string, Case[]>);

    // Automatically focus the new folder input when it is shown
    useEffect(() => {
        if (showCreateFolder) {
            newFolderInputRef.current?.focus();
            newFolderInputRef.current?.select();
        }
    }, [showCreateFolder]);

    // Close folder menu when clicking outside
    useEffect(() => {
        if (!showFolderMenu) {
            return;
        }

        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (!target.closest('.relative')) {
                setShowFolderMenu(null);
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [showFolderMenu]);
    return (
        <aside className={`border-r border-solid border-luna-text-light/10 dark:border-luna-text-dark/10 p-4 flex-col hidden md:flex shrink-0 transition-all duration-300 ease-in-out ${isCollapsed ? 'w-20' : 'w-72'}`}>
            <div className="flex-grow">
                <nav className="flex flex-col gap-1">
                    <a className={`flex items-center gap-3 rounded-lg px-3 py-2 ${currentPage === 'Dashboard' && !selectedCaseId ? 'bg-luna-secondary/10 text-luna-secondary' : 'text-luna-text-light/80 dark:text-luna-text-dark/80 hover:bg-luna-text-dark/5 dark:hover:bg-luna-text-dark/10'} transition-colors ${isCollapsed ? 'justify-center' : ''}`} href="/dashboard" onClick={(e) => { e.preventDefault(); onNavigate('Dashboard'); }} aria-current={currentPage === 'Dashboard' && !selectedCaseId ? 'page' : undefined}>
                        <span className="material-symbols-outlined">dashboard</span>
                        {!isCollapsed && <span className="text-sm font-medium">Dashboard</span>}
                    </a>
                    <a className={`flex items-center gap-3 rounded-lg px-3 py-2 ${currentPage === 'Documents' ? 'bg-luna-secondary/10 text-luna-secondary' : 'text-luna-text-light/80 dark:text-luna-text-dark/80 hover:bg-luna-text-dark/5 dark:hover:bg-luna-text-dark/10'} transition-colors ${isCollapsed ? 'justify-center' : ''}`} href="/documents" onClick={(e) => { e.preventDefault(); onNavigate('Documents'); }} aria-current={currentPage === 'Documents' ? 'page' : undefined}>
                        <span className="material-symbols-outlined">description</span>
                        {!isCollapsed && <span className="text-sm font-medium">Documents</span>}
                    </a>
                    <a className={`flex items-center gap-3 rounded-lg px-3 py-2 ${currentPage === 'Templates' ? 'bg-luna-secondary/10 text-luna-secondary' : 'text-luna-text-light/80 dark:text-luna-text-dark/80 hover:bg-luna-text-dark/5 dark:hover:bg-luna-text-dark/10'} transition-colors ${isCollapsed ? 'justify-center' : ''}`} href="/templates" onClick={(e) => { e.preventDefault(); onNavigate('Templates'); }} aria-current={currentPage === 'Templates' ? 'page' : undefined}>
                        <span className="material-symbols-outlined">article</span>
                        {!isCollapsed && <span className="text-sm font-medium">Templates</span>}
                    </a>
                    <a className={`flex items-center gap-3 rounded-lg px-3 py-2 ${currentPage === 'News' ? 'bg-luna-secondary/10 text-luna-secondary' : 'text-luna-text-light/80 dark:text-luna-text-dark/80 hover:bg-luna-text-dark/5 dark:hover:bg-luna-text-dark/10'} transition-colors ${isCollapsed ? 'justify-center' : ''}`} href="/news" onClick={(e) => { e.preventDefault(); onNavigate('News'); }} aria-current={currentPage === 'News' ? 'page' : undefined}>
                        <span className="material-symbols-outlined">campaign</span>
                        {!isCollapsed && <span className="text-sm font-medium">News & Updates</span>}
                    </a>
                    <a className={`flex items-center gap-3 rounded-lg px-3 py-2 ${currentPage === 'Teams' ? 'bg-luna-secondary/10 text-luna-secondary' : 'text-luna-text-light/80 dark:text-luna-text-dark/80 hover:bg-luna-text-dark/5 dark:hover:bg-luna-text-dark/10'} transition-colors ${isCollapsed ? 'justify-center' : ''}`} href="/teams" onClick={(e) => { e.preventDefault(); onNavigate('Teams'); }} aria-current={currentPage === 'Teams' ? 'page' : undefined}>
                        <span className="material-symbols-outlined">groups</span>
                        {!isCollapsed && <span className="text-sm font-medium">Teams</span>}
                    </a>
                    <a className={`flex items-center gap-3 rounded-lg px-3 py-2 ${currentPage === 'Settings' ? 'bg-luna-secondary/10 text-luna-secondary' : 'text-luna-text-light/80 dark:text-luna-text-dark/80 hover:bg-luna-text-dark/5 dark:hover:bg-luna-text-dark/10'} transition-colors ${isCollapsed ? 'justify-center' : ''}`} href="/settings" onClick={(e) => { e.preventDefault(); onNavigate('Settings'); }} aria-current={currentPage === 'Settings' ? 'page' : undefined}>
                        <span className="material-symbols-outlined">settings</span>
                        {!isCollapsed && <span className="text-sm font-medium">Settings</span>}
                    </a>
                    {(profile?.role === 'admin' || profile?.role === 'super_admin') && (
                        <a className={`flex items-center gap-3 rounded-lg px-3 py-2 ${currentPage === 'Admin' ? 'bg-red-500/10 text-red-600 dark:text-red-400' : 'text-luna-text-light/80 dark:text-luna-text-dark/80 hover:bg-luna-text-dark/5 dark:hover:bg-luna-text-dark/10'} transition-colors ${isCollapsed ? 'justify-center' : ''}`} href="/admin" onClick={(e) => { e.preventDefault(); onNavigate('Admin'); }} aria-current={currentPage === 'Admin' ? 'page' : undefined}>
                            <span className="material-symbols-outlined">admin_panel_settings</span>
                            {!isCollapsed && <span className="text-sm font-medium">Admin</span>}
                        </a>
                    )}
                </nav>

                {!isCollapsed && (
                    <div className="mt-6 flex items-center justify-between px-2">
                        <h3 className="text-sm font-semibold text-luna-text-light/70 dark:text-luna-text-muted-dark">Folders</h3>
                        <button
                            onClick={(e) => { e.preventDefault(); setShowCreateFolder(true); }}
                            className="p-1 rounded hover:bg-luna-text-dark/5 dark:hover:bg-luna-text-dark/10 transition-colors"
                            aria-label="Create folder"
                            type="button"
                        >
                            <span className="material-symbols-outlined text-base">create_new_folder</span>
                        </button>
                    </div>
                )}

                {!isCollapsed && folders.length > 0 && (
                    <nav className="mt-2 flex flex-col gap-1" role="navigation" aria-label="Folders navigation">
                        {folders.map((folder) => {
                            const folderCases = casesByFolder[folder.id] || [];
                            return (
                                <div key={folder.id} className="group">
                                    <div className="flex items-center gap-2">
                                        {editingFolderId === folder.id ? (
                                            <div className="flex-1 flex items-center gap-2">
                                                <input
                                                    type="text"
                                                    value={editingFolderName}
                                                    onChange={(e) => setEditingFolderName(e.target.value)}
                                                    onBlur={() => handleRenameFolderSubmit(folder.id)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                            handleRenameFolderSubmit(folder.id);
                                                        } else if (e.key === 'Escape') {
                                                            setEditingFolderId(null);
                                                            setEditingFolderName('');
                                                        }
                                                    }}
                                                    className="flex-1 px-2 py-1 text-sm bg-luna-bg-light dark:bg-luna-bg-dark border border-luna-secondary rounded focus:outline-none"
                                                    ref={handleFolderRenameInputRef}
                                                />
                                            </div>
                                        ) : (
                                            <>
                                                <span className="material-symbols-outlined text-base">folder</span>
                                                <span className="flex-1 text-sm font-medium text-luna-text-light/80 dark:text-luna-text-dark/80">
                                                    {folder.name} ({folderCases.length})
                                                </span>
                                                <div className="relative">
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setShowFolderMenu(showFolderMenu === folder.id ? null : folder.id);
                                                        }}
                                                        className="p-1 rounded hover:bg-luna-text-dark/5 dark:hover:bg-luna-text-dark/10 transition-colors opacity-0 group-hover:opacity-100"
                                                        aria-label="Folder options"
                                                        type="button"
                                                    >
                                                        <span className="material-symbols-outlined text-xs">more_vert</span>
                                                    </button>
                                                    {showFolderMenu === folder.id && (
                                                        <div className="absolute right-0 mt-1 w-32 bg-luna-bg-light dark:bg-luna-bg-dark border border-luna-text-light/10 dark:border-luna-text-dark/10 rounded-lg shadow-lg z-10">
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setEditingFolderId(folder.id);
                                                                    setEditingFolderName(folder.name);
                                                                    setShowFolderMenu(null);
                                                                }}
                                                                className="w-full text-left px-3 py-2 text-sm hover:bg-luna-text-dark/5 dark:hover:bg-luna-text-dark/10 flex items-center gap-2"
                                                                type="button"
                                                            >
                                                                <span className="material-symbols-outlined text-base">edit</span>
                                                                Rename
                                                            </button>
                                                            <button
                                                                onClick={async (e) => {
                                                                    e.stopPropagation();
                                                                    if (onDeleteFolder && confirm(`Delete folder "${folder.name}"? Cases will be moved to "No Folder".`)) {
                                                                        await onDeleteFolder(folder.id);
                                                                    }
                                                                    setShowFolderMenu(null);
                                                                }}
                                                                className="w-full text-left px-3 py-2 text-sm hover:bg-red-500/10 text-red-600 dark:text-red-400 flex items-center gap-2"
                                                                type="button"
                                                            >
                                                                <span className="material-symbols-outlined text-base">delete</span>
                                                                Delete
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                    {folderCases.length > 0 && !isCollapsed && (
                                        <div className="ml-6 mt-1 flex flex-col gap-1">
                                            {folderCases.map((_case) => {
                                                const isActive = currentPage === 'Chat' && selectedCaseId === _case.id;
                                                return (
                                                    <button
                                                        key={_case.id}
                                                        className={`flex items-center gap-2 rounded-lg px-2 py-1 text-sm transition-colors
                                                            ${isActive
                                                                ? 'bg-luna-secondary/10 text-luna-secondary'
                                                                : 'text-luna-text-light/70 dark:text-luna-text-dark/70 hover:bg-luna-text-dark/5 dark:hover:bg-luna-text-dark/10'
                                                            }`}
                                                        onClick={(e) => { e.preventDefault(); onNavigate('Chat', _case.id); }}
                                                        aria-current={isActive ? 'page' : undefined}
                                                        type="button"
                                                    >
                                                        <span className="material-symbols-outlined text-sm">chat_bubble</span>
                                                        <span className="truncate">{_case.title}</span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </nav>
                )}

                {!isCollapsed && <h3 className="px-2 mt-6 text-sm font-semibold text-luna-text-light/70 dark:text-luna-text-muted-dark">Cases</h3>}

                <nav className="mt-2 flex flex-col gap-1" role="navigation" aria-label="Cases navigation">
                    {(casesByFolder['no-folder'] || []).map((_case) => {
                        const isActive = currentPage === 'Chat' && selectedCaseId === _case.id;
                        return (
                            <button
                                key={_case.id}
                                className={`flex items-center gap-3 rounded-lg px-3 py-2 transition-colors ${isCollapsed ? 'justify-center' : ''}
                                    ${isActive
                                        ? 'bg-luna-secondary/10 text-luna-secondary'
                                        : 'text-luna-text-light/80 dark:text-luna-text-dark/80 hover:bg-luna-text-dark/5 dark:hover:bg-luna-text-dark/10'
                                    }`}
                                onClick={(e) => { e.preventDefault(); onNavigate('Chat', _case.id); }}
                                aria-current={isActive ? 'page' : undefined}
                                type="button"
                            >
                                <span className="material-symbols-outlined">chat_bubble</span>
                                {!isCollapsed && <span className="text-sm font-medium">{_case.title}</span>}
                            </button>
                        );
                    })}
                    <button
                        className={`flex items-center gap-3 rounded-lg px-3 py-2 text-luna-text-light/50 dark:text-luna-text-dark/50 hover:bg-luna-text-dark/5 dark:hover:bg-luna-text-dark/10 transition-colors ${isCollapsed ? 'justify-center' : ''}`}
                        onClick={(e) => { e.preventDefault(); onNewCase(); }}
                        type="button"
                        aria-label="Start new case"
                    >
                        <span className="material-symbols-outlined">add</span>
                        {!isCollapsed && <span className="text-sm font-medium">Start New Case</span>}
                    </button>
                </nav>

                {showCreateFolder && !isCollapsed && (
                    <form onSubmit={handleCreateFolderSubmit} className="mt-2 px-2">
                        <div className="flex items-center gap-2">
                            <input
                                type="text"
                                value={newFolderName}
                                onChange={(e) => setNewFolderName(e.target.value)}
                                placeholder="Folder name"
                                className="flex-1 px-2 py-1 text-sm bg-luna-bg-light dark:bg-luna-bg-dark border border-luna-secondary rounded focus:outline-none"
                                ref={newFolderInputRef}
                            />
                            <button
                                type="submit"
                                className="p-1 rounded hover:bg-luna-secondary/10 transition-colors"
                                aria-label="Create folder"
                            >
                                <span className="material-symbols-outlined text-base">check</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setShowCreateFolder(false);
                                    setNewFolderName('');
                                }}
                                className="p-1 rounded hover:bg-luna-text-dark/5 dark:hover:bg-luna-text-dark/10 transition-colors"
                                aria-label="Cancel"
                            >
                                <span className="material-symbols-outlined text-base">close</span>
                            </button>
                        </div>
                    </form>
                )}
            </div>
            <div className="mt-auto">
                <button
                    onClick={(e) => { e.preventDefault(); toggleSidebar(); }}
                    className={`w-full flex items-center gap-3 rounded-lg px-3 py-2 text-luna-text-light/80 dark:text-luna-text-dark/80 hover:bg-luna-text-dark/5 dark:hover:bg-luna-text-dark/10 transition-colors ${isCollapsed ? 'justify-center' : ''}`}
                    type="button"
                    aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                >
                    <span className="material-symbols-outlined">{isCollapsed ? 'menu_open' : 'menu'}</span>
                    {!isCollapsed && <span className="text-sm font-medium">Collapse</span>}
                </button>
            </div>
        </aside>
    );
};

const DashboardPage: React.FC<{
    onNavigate: (page: string, caseId?: string) => void;
    cases: Case[];
    onNewCase: () => void;
    isLoading?: boolean;
    error?: string | null;
    onRetry?: () => void;
    onDeleteCase?: (caseId: string) => Promise<void>;
    onRenameCase?: (caseId: string, newTitle: string) => Promise<void>;
    onMoveToFolder?: (caseId: string, folderId: string | null) => Promise<void>;
    folders?: Folder[];
    session?: Session | null;
}> = React.memo(({ onNavigate, cases, onNewCase, isLoading = false, error = null, onRetry, onDeleteCase, onRenameCase, onMoveToFolder, folders = [], session = null }) => {
    const { t } = useTranslation();
    const [editingCaseId, setEditingCaseId] = useState<string | null>(null);
    const [editingTitle, setEditingTitle] = useState('');
    const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
    const [showFolderMenu, setShowFolderMenu] = useState<string | null>(null);
    const [showMenu, setShowMenu] = useState<string | null>(null);
    const [selectedLawyer, setSelectedLawyer] = useState<string | null>(null);
    const [selectedInsurance, setSelectedInsurance] = useState<string | null>(null);
    const [showLawyerModal, setShowLawyerModal] = useState(false);
    const [showInsuranceModal, setShowInsuranceModal] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchFilters, setSearchFilters] = useState<SearchFilters>({});
    const [selectedCases, setSelectedCases] = useState<Set<string>>(new Set());
    const [isBulkMode, setIsBulkMode] = useState(false);

    // Mock data for lawyers and insurances - in production, this would come from database
    const availableLawyers = [
        { id: '1', name: 'Dr. Michael Weber', firm: 'Weber & Partners Rechtsanwälte', location: 'Berlin', specialization: 'GDPR, Contract Law', rating: 4.9, verified: true },
        { id: '2', name: 'Anna Müller', firm: 'Müller Legal Services', location: 'Munich', specialization: 'Employment Law, Real Estate', rating: 4.8, verified: true },
        { id: '3', name: 'Thomas Schmidt', firm: 'Schmidt Rechtsanwaltskanzlei', location: 'Hamburg', specialization: 'Consumer Protection, SCHUFA', rating: 4.7, verified: true },
    ];

    const availableInsurances = [
        { id: '1', name: 'Allianz Rechtschutz', logo: '🛡️', description: 'Comprehensive legal protection insurance', coverage: 'Full coverage' },
        { id: '2', name: 'ARAG Rechtschutz', logo: '⚖️', description: 'Leading legal insurance provider in Germany', coverage: 'Full coverage' },
        { id: '3', name: 'DAS Rechtsschutz', logo: '📋', description: 'Affordable legal protection solutions', coverage: 'Standard coverage' },
    ];

    // Filter cases using search hook
    const filteredCases = useSearch({
        items: cases,
        searchQuery,
        filters: searchFilters,
        searchFields: ['title', 'type', 'status'],
    });

    // Close menus when clicking outside
    useEffect(() => {
        if (!showMenu && !showFolderMenu) {
            return;
        }

        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (!target.closest('.relative')) {
                setShowMenu(null);
                setShowFolderMenu(null);
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [showMenu, showFolderMenu]);

    // Bulk actions handlers - memoized with useCallback
    const handleSelectCase = useCallback((caseId: string) => {
        setSelectedCases((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(caseId)) {
                newSet.delete(caseId);
            } else {
                newSet.add(caseId);
            }
            return newSet;
        });
    }, []);

    const handleSelectAll = useCallback(() => {
        setSelectedCases((prev) => {
            if (prev.size === filteredCases.length) {
                return new Set();
            } else {
                return new Set(filteredCases.map((c) => c.id));
            }
        });
    }, [filteredCases]);

    const handleCaseCardKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>, caseId: string) => {
        if (event.key !== 'Enter' && event.key !== ' ') {
            return;
        }
        event.preventDefault();
        if (isBulkMode) {
            handleSelectCase(caseId);
        } else {
            onNavigate('Chat', caseId);
        }
    }, [handleSelectCase, isBulkMode, onNavigate]);

    const handleBulkDelete = useCallback(async () => {
        if (selectedCases.size === 0 || !onDeleteCase) return;

        const count = selectedCases.size;
        const confirmed = window.confirm(
            `Are you sure you want to delete ${count} case(s)? This action cannot be undone.`
        );

        if (!confirmed) return;

        try {
            const deletePromises = Array.from(selectedCases).map((id) => onDeleteCase(id));
            await Promise.all(deletePromises);
            setSelectedCases(new Set());
            setIsBulkMode(false);
            toast.success(`Successfully deleted ${count} case(s)`);
        } catch (error) {
            logger.error('Error deleting cases:', error);
            toast.error('Failed to delete some cases');
        }
    }, [selectedCases, onDeleteCase]);

    // Show loading skeletons on initial load
    if (isLoading && cases.length === 0) {
        return (
            <div className="flex flex-col flex-1 p-4 overflow-y-auto bg-luna-bg-light dark:bg-luna-bg-dark">
                <div className="flex flex-col gap-6">
                    <div className="flex items-center justify-between">
                        <div className="h-8 w-48 bg-luna-text-light/10 dark:bg-luna-text-dark/10 rounded animate-pulse" />
                        <div className="h-10 w-32 bg-luna-text-light/10 dark:bg-luna-text-dark/10 rounded animate-pulse" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="bg-white dark:bg-luna-primary rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 shadow-sm p-5">
                                <div className="h-6 w-3/4 bg-luna-text-light/10 dark:bg-luna-text-dark/10 rounded mb-2 animate-pulse" />
                                <div className="h-4 w-1/2 bg-luna-text-light/10 dark:bg-luna-text-dark/10 rounded mb-4 animate-pulse" />
                                <div className="h-4 w-full bg-luna-text-light/10 dark:bg-luna-text-dark/10 rounded mb-2 animate-pulse" />
                                <div className="h-4 w-2/3 bg-luna-text-light/10 dark:bg-luna-text-dark/10 rounded animate-pulse" />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col flex-1 p-4 overflow-y-auto bg-luna-bg-light dark:bg-luna-bg-dark">
            <div className="flex flex-col gap-6">
                {/* Lawyers & Legal Insurances Selection Section */}
                <div className="bg-white dark:bg-luna-primary rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 shadow-sm p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-xl font-bold text-luna-text-light dark:text-luna-text-dark">Connect with Partners</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Lawyers Selection */}
                        <div className="bg-luna-bg-light dark:bg-luna-bg-dark rounded-lg border border-luna-text-light/10 dark:border-luna-text-dark/20 p-4">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="flex items-center justify-center size-10 rounded-lg bg-gradient-to-br from-blue-500/20 to-blue-500/5">
                                    <span className="material-symbols-outlined text-blue-500">gavel</span>
                                </div>
                                <div>
                                    <h3 className="text-base font-bold text-luna-text-light dark:text-luna-text-dark">Select a Lawyer</h3>
                                    <p className="text-xs text-luna-text-light/60 dark:text-luna-text-dark/60">Connect with partner lawyers</p>
                                </div>
                            </div>
                            {selectedLawyer ? (
                                <div className="flex items-center justify-between p-3 bg-luna-secondary/10 rounded-lg">
                                    <div className="flex items-center gap-2">
                                        <span className="material-symbols-outlined text-luna-secondary">verified</span>
                                        <span className="text-sm font-medium text-luna-text-light dark:text-luna-text-dark">
                                            {availableLawyers.find(l => l.id === selectedLawyer)?.name}
                                        </span>
                                    </div>
                                    <button
                                        onClick={() => setShowLawyerModal(true)}
                                        className="text-xs text-luna-secondary hover:underline"
                                    >
                                        Change
                                    </button>
                                </div>
                            ) : (
                                <button
                                    onClick={() => setShowLawyerModal(true)}
                                    className="w-full flex items-center justify-center gap-2 h-10 px-4 bg-blue-500 text-white text-sm font-bold rounded-lg hover:bg-blue-600 transition-colors"
                                >
                                    <span className="material-symbols-outlined text-base">person_add</span>
                                    <span>Select Lawyer</span>
                                </button>
                            )}
                        </div>

                        {/* Legal Insurance Selection */}
                        <div className="bg-luna-bg-light dark:bg-luna-bg-dark rounded-lg border border-luna-text-light/10 dark:border-luna-text-dark/20 p-4">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="flex items-center justify-center size-10 rounded-lg bg-gradient-to-br from-green-500/20 to-green-500/5">
                                    <span className="material-symbols-outlined text-green-500">shield</span>
                                </div>
                                <div>
                                    <h3 className="text-base font-bold text-luna-text-light dark:text-luna-text-dark">Select Legal Insurance</h3>
                                    <p className="text-xs text-luna-text-light/60 dark:text-luna-text-dark/60">Connect your Rechtschutz</p>
                                </div>
                            </div>
                            {selectedInsurance ? (
                                <div className="flex items-center justify-between p-3 bg-green-500/10 rounded-lg">
                                    <div className="flex items-center gap-2">
                                        <span className="text-lg">{availableInsurances.find(i => i.id === selectedInsurance)?.logo}</span>
                                        <span className="text-sm font-medium text-luna-text-light dark:text-luna-text-dark">
                                            {availableInsurances.find(i => i.id === selectedInsurance)?.name}
                                        </span>
                                    </div>
                                    <button
                                        onClick={() => setShowInsuranceModal(true)}
                                        className="text-xs text-green-600 dark:text-green-400 hover:underline"
                                    >
                                        Change
                                    </button>
                                </div>
                            ) : (
                                <button
                                    onClick={() => setShowInsuranceModal(true)}
                                    className="w-full flex items-center justify-center gap-2 h-10 px-4 bg-green-500 text-white text-sm font-bold rounded-lg hover:bg-green-600 transition-colors"
                                >
                                    <span className="material-symbols-outlined text-base">shield</span>
                                    <span>Select Insurance</span>
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Notification Center - Only show if session exists */}
                {session && (
                    <NotificationCenter
                        session={session}
                        onNotificationClick={(resourceType, resourceId) => {
                            if (resourceType === 'case' && resourceId) {
                                onNavigate('Chat', resourceId);
                            }
                        }}
                    />
                )}

                {/* Deadline Widget - Only show if session exists */}
                {session && (
                    <DeadlineWidget
                        session={session}
                        onNavigateToCase={(caseId) => onNavigate('Chat', caseId)}
                    />
                )}

                {/* Analytics Dashboard - Only show if session exists */}
                {session && (
                    <AnalyticsDashboard
                        session={session}
                    />
                )}

                <div className="flex flex-col gap-4">
                    <div className="flex items-center justify-between">
                        <h1 className="text-2xl font-bold">{t('cases.title')}</h1>
                        <div className="flex items-center gap-2">
                            {isBulkMode && selectedCases.size > 0 && (
                                <>
                                    <button
                                        onClick={handleBulkDelete}
                                        className="flex items-center justify-center gap-2 h-10 px-4 bg-red-500 text-white text-sm font-bold rounded-lg hover:bg-red-600 transition-colors"
                                    >
                                        <span className="material-symbols-outlined text-base">delete</span>
                                        <span>Delete ({selectedCases.size})</span>
                                    </button>
                                    <button
                                        onClick={() => setIsBulkMode(false)}
                                        className="flex items-center justify-center gap-2 h-10 px-4 bg-luna-text-light/10 dark:bg-luna-text-dark/10 text-luna-text-light dark:text-luna-text-dark text-sm font-bold rounded-lg hover:opacity-90 transition-opacity"
                                    >
                                        Cancel
                                    </button>
                                </>
                            )}
                            {!isBulkMode && (
                                <>
                                    <button
                                        onClick={() => setIsBulkMode(true)}
                                        className="flex items-center justify-center gap-2 h-10 px-4 bg-luna-text-light/10 dark:bg-luna-text-dark/10 text-luna-text-light dark:text-luna-text-dark text-sm font-bold rounded-lg hover:opacity-90 transition-opacity"
                                    >
                                        <span className="material-symbols-outlined text-base">checklist</span>
                                        <span>Select</span>
                                    </button>
                                    <button
                                        className="flex items-center justify-center gap-2 h-10 px-4 bg-luna-secondary text-luna-primary text-sm font-bold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
                                        onClick={onNewCase}
                                        disabled={isLoading}
                                    >
                                        {isLoading ? (
                                            <>
                                                <LoadingSpinner size="sm" />
                                                <span>{t('common.loading')}</span>
                                            </>
                                        ) : (
                                            <>
                                                <span>{t('cases.createCase')}</span>
                                                <span className="material-symbols-outlined text-base">add</span>
                                            </>
                                        )}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                    <SearchBar
                        value={searchQuery}
                        onChange={setSearchQuery}
                        onFilterChange={setSearchFilters}
                        placeholder="Search cases by title, type, or status..."
                        showFilters={true}
                    />
                </div>

                {error && (
                    <ErrorMessage
                        error={error}
                        onRetry={onRetry}
                        variant="banner"
                    />
                )}

                {filteredCases.length === 0 && !isLoading && !error && (
                    <EmptyCases onNewCase={onNewCase} />
                )}

                {isLoading && cases.length > 0 && (
                    <div className="flex items-center gap-2 text-sm text-luna-text-light/70 dark:text-luna-text-muted-dark">
                        <LoadingSpinner size="sm" />
                        <span>Refreshing cases...</span>
                    </div>
                )}

                {isBulkMode && filteredCases.length > 0 && (
                    <div className="flex items-center justify-between p-3 bg-luna-secondary/10 dark:bg-luna-secondary/20 rounded-lg">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={selectedCases.size === filteredCases.length}
                                onChange={handleSelectAll}
                                className="w-4 h-4 rounded border-luna-text-light/30 dark:border-luna-text-dark/30 text-luna-secondary focus:ring-luna-secondary"
                            />
                            <span className="text-sm font-medium text-luna-text-light dark:text-luna-text-dark">
                                Select all ({filteredCases.length} cases)
                            </span>
                        </label>
                        {selectedCases.size > 0 && (
                            <span className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">
                                {selectedCases.size} selected
                            </span>
                        )}
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    {filteredCases.map((_case) => (
                        <div
                            key={_case.id}
                            className={`bg-white dark:bg-luna-primary rounded-xl border shadow-sm p-5 flex flex-col relative transition-all ${isBulkMode
                                ? 'cursor-pointer hover:border-luna-secondary'
                                : 'border-luna-text-light/10 dark:border-luna-text-dark/20'
                                } ${selectedCases.has(_case.id)
                                    ? 'border-luna-secondary ring-2 ring-luna-secondary/50'
                                    : ''
                                }`}
                            onClick={(e) => {
                                // Don't navigate if clicking on buttons inside the card
                                if ((e.target as HTMLElement).closest('button')) {
                                    return;
                                }
                                if (isBulkMode) {
                                    handleSelectCase(_case.id);
                                } else {
                                    onNavigate('Chat', _case.id);
                                }
                            }}
                            onKeyDown={(event) => handleCaseCardKeyDown(event, _case.id)}
                            role="button"
                            tabIndex={0}
                            aria-pressed={isBulkMode ? selectedCases.has(_case.id) : undefined}
                        >
                            {isBulkMode && (
                                <div className="absolute top-4 right-4 z-10">
                                    <input
                                        type="checkbox"
                                        checked={selectedCases.has(_case.id)}
                                        onChange={() => handleSelectCase(_case.id)}
                                        onClick={(e) => e.stopPropagation()}
                                        className="w-5 h-5 rounded border-luna-text-light/30 dark:border-luna-text-dark/30 text-luna-secondary focus:ring-luna-secondary cursor-pointer"
                                    />
                                </div>
                            )}
                            <div className="flex justify-between items-start">
                                <div className="flex-1">
                                    {editingCaseId === _case.id ? (
                                        <input
                                            type="text"
                                            value={editingTitle}
                                            onChange={(e) => setEditingTitle(e.target.value)}
                                            onBlur={async () => {
                                                if (editingTitle.trim() && editingTitle !== _case.title && onRenameCase) {
                                                    await onRenameCase(_case.id, editingTitle.trim());
                                                }
                                                setEditingCaseId(null);
                                                setEditingTitle('');
                                            }}
                                            onKeyDown={async (e) => {
                                                if (e.key === 'Enter') {
                                                    if (editingTitle.trim() && editingTitle !== _case.title && onRenameCase) {
                                                        await onRenameCase(_case.id, editingTitle.trim());
                                                    }
                                                    setEditingCaseId(null);
                                                    setEditingTitle('');
                                                } else if (e.key === 'Escape') {
                                                    setEditingCaseId(null);
                                                    setEditingTitle('');
                                                }
                                            }}
                                            className="font-bold text-lg w-full bg-transparent border-b-2 border-luna-secondary focus:outline-none"
                                            // eslint-disable-next-line jsx-a11y/no-autofocus
                                            autoFocus
                                        />
                                    ) : (
                                        <>
                                            <h3 className="font-bold text-lg">{_case.title}</h3>
                                            <p className="text-sm text-luna-text-light/70 dark:text-luna-text-muted-dark">{_case.type}</p>
                                        </>
                                    )}
                                </div>
                                <div className="flex items-start gap-2">
                                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${_case.status === 'Active' ? 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300' :
                                        _case.status === 'Pending Review' ? 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300' :
                                            'bg-gray-100 dark:bg-gray-700/50 text-gray-800 dark:text-gray-300'
                                        }`}>
                                        <span className={`w-2 h-2 mr-1.5 rounded-full ${_case.status === 'Active' ? 'bg-green-500' : _case.status === 'Pending Review' ? 'bg-yellow-500' : 'bg-gray-500'}`}></span>
                                        {_case.status}
                                    </span>
                                    <div className="relative">
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setShowMenu(showMenu === _case.id ? null : _case.id);
                                            }}
                                            className="p-1 rounded hover:bg-luna-text-dark/5 dark:hover:bg-luna-text-dark/10 transition-colors"
                                            aria-label="Case options"
                                        >
                                            <span className="material-symbols-outlined text-base">more_vert</span>
                                        </button>
                                        {showMenu === _case.id && (
                                            <div className="absolute right-0 top-8 z-50 bg-white dark:bg-luna-primary border border-luna-text-light/10 dark:border-luna-text-dark/20 rounded-lg shadow-lg min-w-[160px] py-1">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setEditingCaseId(_case.id);
                                                        setEditingTitle(_case.title);
                                                        setShowMenu(null);
                                                    }}
                                                    className="w-full text-left px-4 py-2 text-sm hover:bg-luna-text-dark/5 dark:hover:bg-luna-text-dark/10 flex items-center gap-2"
                                                >
                                                    <span className="material-symbols-outlined text-base">edit</span>
                                                    Rename
                                                </button>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setShowFolderMenu(_case.id);
                                                        setShowMenu(null);
                                                    }}
                                                    className="w-full text-left px-4 py-2 text-sm hover:bg-luna-text-dark/5 dark:hover:bg-luna-text-dark/10 flex items-center gap-2"
                                                >
                                                    <span className="material-symbols-outlined text-base">folder</span>
                                                    Move to Folder
                                                </button>
                                                <div className="border-t border-luna-text-light/10 dark:border-luna-text-dark/20 my-1"></div>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setShowDeleteConfirm(_case.id);
                                                        setShowMenu(null);
                                                    }}
                                                    className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                                                >
                                                    <span className="material-symbols-outlined text-base">delete</span>
                                                    Delete
                                                </button>
                                            </div>
                                        )}
                                        {showFolderMenu === _case.id && (
                                            <div className="absolute right-0 top-8 z-50 bg-white dark:bg-luna-primary border border-luna-text-light/10 dark:border-luna-text-dark/20 rounded-lg shadow-lg min-w-[200px] py-1 max-h-[300px] overflow-y-auto">
                                                <button
                                                    onClick={async (e) => {
                                                        e.stopPropagation();
                                                        if (onMoveToFolder) {
                                                            await onMoveToFolder(_case.id, null);
                                                        }
                                                        setShowFolderMenu(null);
                                                    }}
                                                    className={`w-full text-left px-4 py-2 text-sm hover:bg-luna-text-dark/5 dark:hover:bg-luna-text-dark/10 flex items-center gap-2 ${!_case.folder_id ? 'bg-luna-secondary/10' : ''}`}
                                                >
                                                    <span className="material-symbols-outlined text-base">folder_off</span>
                                                    No Folder
                                                </button>
                                                {folders.map((folder) => (
                                                    <button
                                                        key={folder.id}
                                                        onClick={async (e) => {
                                                            e.stopPropagation();
                                                            if (onMoveToFolder) {
                                                                await onMoveToFolder(_case.id, folder.id);
                                                            }
                                                            setShowFolderMenu(null);
                                                        }}
                                                        className={`w-full text-left px-4 py-2 text-sm hover:bg-luna-text-dark/5 dark:hover:bg-luna-text-dark/10 flex items-center gap-2 ${_case.folder_id === folder.id ? 'bg-luna-secondary/10' : ''}`}
                                                    >
                                                        <span className="material-symbols-outlined text-base">folder</span>
                                                        {folder.name}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="mt-4 space-y-3 text-sm">
                                <div className="flex items-center gap-3">
                                    <span className="material-symbols-outlined text-base text-luna-text-light/70 dark:text-luna-text-muted-dark">calendar_today</span>
                                    <p>
                                        {_case.status === 'Closed' ? 'Completed' : 'Deadline'}:{' '}
                                        <span className="font-medium text-luna-text-light dark:text-luna-text-dark">
                                            {_case.status === 'Closed' ? '' : _case.deadline ? formatDate(_case.deadline) : 'No deadline'}
                                        </span>
                                    </p>
                                </div>
                                <div className="flex items-center gap-3">
                                    <span className="material-symbols-outlined text-base text-luna-text-light/70 dark:text-luna-text-muted-dark">person</span>
                                    <p>Assigned: <span className="font-medium text-luna-text-light dark:text-luna-text-dark">You</span></p>
                                </div>
                            </div>
                            <div className="mt-auto pt-4 flex gap-2">
                                <button
                                    type="button"
                                    className="flex-1 flex items-center justify-center gap-2 h-9 px-3 bg-luna-secondary text-luna-primary text-sm font-bold rounded-lg hover:opacity-90 transition-opacity"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        console.log('[DashboardPage] Chat button clicked for case:', _case.id);
                                        onNavigate('Chat', _case.id);
                                    }}
                                >
                                    <span className="material-symbols-outlined text-base">chat_bubble</span>
                                    <span>Chat</span>
                                </button>
                                <button
                                    type="button"
                                    className={`flex-1 flex items-center justify-center gap-2 h-9 px-3 bg-luna-bg-light dark:bg-luna-bg-dark border border-luna-text-light/20 dark:border-luna-text-dark/30 text-sm font-medium rounded-lg ${_case.status === 'Closed' ? 'cursor-not-allowed opacity-70' : 'hover:bg-luna-text-dark/5 dark:hover:bg-luna-text-dark/10 transition-colors'
                                        }`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        if (_case.status === 'Closed') {
                                            return;
                                        }
                                        console.log('[DashboardPage] Documents button clicked for case:', _case.id);
                                        onNavigate('Documents', _case.id);
                                    }}
                                >
                                    <span className="material-symbols-outlined text-base">description</span>
                                    <span>Documents</span>
                                </button>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Delete Confirmation Modal */}
                {showDeleteConfirm && (
                    <div
                        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
                        role="button"
                        tabIndex={0}
                        aria-label="Close delete confirmation"
                        onClick={() => setShowDeleteConfirm(null)}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                setShowDeleteConfirm(null);
                            }
                        }}
                    >
                        <div className="bg-white dark:bg-luna-primary rounded-xl p-6 max-w-md w-full mx-4 shadow-xl" onMouseDown={(e) => e.stopPropagation()}>
                            <h3 className="text-lg font-bold mb-4">Delete Case</h3>
                            <p className="text-sm text-luna-text-light/70 dark:text-luna-text-muted-dark mb-6">
                                Are you sure you want to delete this case? This action cannot be undone.
                            </p>
                            <div className="flex gap-3 justify-end">
                                <button
                                    onClick={() => setShowDeleteConfirm(null)}
                                    className="px-4 py-2 text-sm font-medium rounded-lg border border-luna-text-light/20 dark:border-luna-text-dark/30 hover:bg-luna-text-dark/5 dark:hover:bg-luna-text-dark/10 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={async () => {
                                        if (onDeleteCase && showDeleteConfirm) {
                                            await onDeleteCase(showDeleteConfirm);
                                        }
                                        setShowDeleteConfirm(null);
                                    }}
                                    className="px-4 py-2 text-sm font-bold rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors"
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Lawyer Selection Modal */}
                {showLawyerModal && (
                    <div
                        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
                        role="button"
                        tabIndex={0}
                        aria-label="Close lawyer selection dialog"
                        onClick={() => setShowLawyerModal(false)}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                setShowLawyerModal(false);
                            }
                        }}
                    >
                        <div className="bg-white dark:bg-luna-primary rounded-xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto shadow-xl" onMouseDown={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="text-xl font-bold text-luna-text-light dark:text-luna-text-dark">Select a Lawyer</h3>
                                <button
                                    onClick={() => setShowLawyerModal(false)}
                                    className="p-1 rounded hover:bg-luna-text-dark/5 dark:hover:bg-luna-text-dark/10 transition-colors"
                                >
                                    <span className="material-symbols-outlined">close</span>
                                </button>
                            </div>
                            <div className="space-y-3">
                                {availableLawyers.map((lawyer) => (
                                    <div
                                        key={lawyer.id}
                                        onClick={() => {
                                            setSelectedLawyer(lawyer.id);
                                            setShowLawyerModal(false);
                                        }}
                                        onKeyDown={(event) => {
                                            if (event.key === 'Enter' || event.key === ' ') {
                                                event.preventDefault();
                                                setSelectedLawyer(lawyer.id);
                                                setShowLawyerModal(false);
                                            }
                                        }}
                                        className={`p-4 rounded-lg border cursor-pointer transition-all ${selectedLawyer === lawyer.id
                                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                                            : 'border-luna-text-light/10 dark:border-luna-text-dark/20 hover:border-blue-500/50 hover:bg-luna-bg-light dark:hover:bg-luna-bg-dark'
                                            }`}
                                        role="button"
                                        tabIndex={0}
                                        aria-pressed={selectedLawyer === lawyer.id}
                                    >
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <h4 className="text-base font-bold text-luna-text-light dark:text-luna-text-dark">{lawyer.name}</h4>
                                                    {lawyer.verified && (
                                                        <span className="material-symbols-outlined text-blue-500 text-sm" title="Verified Partner">verified</span>
                                                    )}
                                                </div>
                                                <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 mb-1">{lawyer.firm}</p>
                                                <div className="flex items-center gap-4 mt-2">
                                                    <div className="flex items-center gap-1 text-xs text-luna-text-light/60 dark:text-luna-text-dark/60">
                                                        <span className="material-symbols-outlined text-sm">location_on</span>
                                                        {lawyer.location}
                                                    </div>
                                                    <div className="flex items-center gap-1 text-xs text-luna-text-light/60 dark:text-luna-text-dark/60">
                                                        <span className="material-symbols-outlined text-sm">star</span>
                                                        {lawyer.rating}
                                                    </div>
                                                </div>
                                                <p className="text-xs text-luna-text-light/70 dark:text-luna-text-dark/70 mt-2">
                                                    Specialization: {lawyer.specialization}
                                                </p>
                                            </div>
                                            {selectedLawyer === lawyer.id && (
                                                <span className="material-symbols-outlined text-blue-500">check_circle</span>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-6 pt-4 border-t border-luna-text-light/10 dark:border-luna-text-dark/10">
                                <button
                                    onClick={() => onNavigate('Collaboration')}
                                    className="text-sm text-blue-500 hover:underline font-medium"
                                >
                                    View all partner lawyers →
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Legal Insurance Selection Modal */}
                {showInsuranceModal && (
                    <div
                        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
                        role="button"
                        tabIndex={0}
                        aria-label="Close insurance selection dialog"
                        onClick={() => setShowInsuranceModal(false)}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                setShowInsuranceModal(false);
                            }
                        }}
                    >
                        <div className="bg-white dark:bg-luna-primary rounded-xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto shadow-xl" onMouseDown={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="text-xl font-bold text-luna-text-light dark:text-luna-text-dark">Select Legal Insurance</h3>
                                <button
                                    onClick={() => setShowInsuranceModal(false)}
                                    className="p-1 rounded hover:bg-luna-text-dark/5 dark:hover:bg-luna-text-dark/10 transition-colors"
                                >
                                    <span className="material-symbols-outlined">close</span>
                                </button>
                            </div>
                            <div className="space-y-3">
                                {availableInsurances.map((insurance) => (
                                    <div
                                        key={insurance.id}
                                        onClick={() => {
                                            setSelectedInsurance(insurance.id);
                                            setShowInsuranceModal(false);
                                        }}
                                        onKeyDown={(event) => {
                                            if (event.key === 'Enter' || event.key === ' ') {
                                                event.preventDefault();
                                                setSelectedInsurance(insurance.id);
                                                setShowInsuranceModal(false);
                                            }
                                        }}
                                        className={`p-4 rounded-lg border cursor-pointer transition-all ${selectedInsurance === insurance.id
                                            ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                                            : 'border-luna-text-light/10 dark:border-luna-text-dark/20 hover:border-green-500/50 hover:bg-luna-bg-light dark:hover:bg-luna-bg-dark'
                                            }`}
                                        role="button"
                                        tabIndex={0}
                                        aria-pressed={selectedInsurance === insurance.id}
                                    >
                                        <div className="flex items-start justify-between">
                                            <div className="flex items-start gap-3 flex-1">
                                                <div className="flex items-center justify-center size-12 rounded-lg bg-gradient-to-br from-green-500/20 to-green-500/5">
                                                    <span className="text-2xl">{insurance.logo}</span>
                                                </div>
                                                <div className="flex-1">
                                                    <h4 className="text-base font-bold text-luna-text-light dark:text-luna-text-dark mb-1">{insurance.name}</h4>
                                                    <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 mb-2">{insurance.description}</p>
                                                    <div className="flex items-center gap-2 text-xs text-luna-text-light/60 dark:text-luna-text-dark/60">
                                                        <span className="material-symbols-outlined text-sm">check_circle</span>
                                                        {insurance.coverage}
                                                    </div>
                                                </div>
                                            </div>
                                            {selectedInsurance === insurance.id && (
                                                <span className="material-symbols-outlined text-green-500">check_circle</span>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-6 pt-4 border-t border-luna-text-light/10 dark:border-luna-text-dark/10">
                                <button
                                    onClick={() => onNavigate('LegalInsurance')}
                                    className="text-sm text-green-500 hover:underline font-medium"
                                >
                                    Learn more about insurance partnerships →
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
});

DashboardPage.displayName = 'DashboardPage';

// Benefits Page - Comprehensive overview of LRDEnE account benefits
const BenefitsPage: React.FC<{ onNavigate?: (page: string) => void; onNavigateToAuth?: (mode: 'signup' | 'login') => void }> = ({ onNavigate, onNavigateToAuth }) => {
    return (
        <div className="flex flex-col flex-1 p-4 md:p-6 lg:p-8 bg-luna-bg-light dark:bg-luna-bg-dark">
            <div className="max-w-6xl mx-auto w-full">
                {/* Hero Section */}
                <div className="text-center mb-12 py-8">
                    <h1 className="text-4xl md:text-5xl font-black mb-4 text-luna-text-light dark:text-luna-text-dark">
                        🌙 Benefits of Your LRDEnE Account
                    </h1>
                    <p className="text-lg text-luna-text-light/70 dark:text-luna-text-dark/70 max-w-3xl mx-auto">
                        Discover how LRDEnE transforms your legal practice with AI-powered tools designed specifically for German and EU law
                    </p>
                </div>

                {/* Main Benefits Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                    {/* AI Document Analysis */}
                    <div className="bg-white dark:bg-luna-primary rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 shadow-sm p-6 hover:shadow-lg transition-shadow">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="flex-shrink-0 flex items-center justify-center size-12 rounded-lg bg-luna-secondary/10">
                                <span className="material-symbols-outlined text-2xl text-luna-secondary">psychology</span>
                            </div>
                            <h3 className="text-xl font-bold text-luna-text-light dark:text-luna-text-dark">AI Document Analysis</h3>
                        </div>
                        <ul className="space-y-2 text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">
                            <li className="flex items-start gap-2">
                                <span className="text-luna-secondary mt-0.5">✓</span>
                                <span>Instant compliance checks (GDPR, BGB, EU AI Act)</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-luna-secondary mt-0.5">✓</span>
                                <span>Risk assessment with severity levels</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-luna-secondary mt-0.5">✓</span>
                                <span>Precise legal citations with article numbers</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-luna-secondary mt-0.5">✓</span>
                                <span>Actionable suggestions with specific wording</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-luna-secondary mt-0.5">✓</span>
                                <span>Expert analysis by Dr. Elena Müller persona</span>
                            </li>
                        </ul>
                        <p className="mt-4 text-xs text-luna-text-light/60 dark:text-luna-text-muted-dark font-medium">
                            Save up to 80% of document review time
                        </p>
                    </div>

                    {/* Legal Template Library */}
                    <div className="bg-white dark:bg-luna-primary rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 shadow-sm p-6 hover:shadow-lg transition-shadow">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="flex-shrink-0 flex items-center justify-center size-12 rounded-lg bg-luna-secondary/10">
                                <span className="material-symbols-outlined text-2xl text-luna-secondary">description</span>
                            </div>
                            <h3 className="text-xl font-bold text-luna-text-light dark:text-luna-text-dark">Legal Template Library</h3>
                        </div>
                        <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 mb-3">
                            7+ AI-powered German legal document templates:
                        </p>
                        <ul className="space-y-2 text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">
                            <li className="flex items-start gap-2">
                                <span className="text-luna-secondary mt-0.5">•</span>
                                <span>DSGVO Auskunft & Löschung (GDPR requests)</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-luna-secondary mt-0.5">•</span>
                                <span>Mängelanzeige & Mietminderung (Rental law)</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-luna-secondary mt-0.5">•</span>
                                <span>Abmahnung Widerspruch (Warning responses)</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-luna-secondary mt-0.5">•</span>
                                <span>SCHUFA Bestreitung & Lösung (Credit disputes)</span>
                            </li>
                        </ul>
                        <p className="mt-4 text-xs text-luna-text-light/60 dark:text-luna-text-muted-dark font-medium">
                            Generate documents in minutes, not hours
                        </p>
                    </div>

                    {/* AI Legal Chat */}
                    <div className="bg-white dark:bg-luna-primary rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 shadow-sm p-6 hover:shadow-lg transition-shadow">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="flex-shrink-0 flex items-center justify-center size-12 rounded-lg bg-luna-secondary/10">
                                <span className="material-symbols-outlined text-2xl text-luna-secondary">chat_bubble</span>
                            </div>
                            <h3 className="text-xl font-bold text-luna-text-light dark:text-luna-text-dark">AI Legal Chat</h3>
                        </div>
                        <ul className="space-y-2 text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">
                            <li className="flex items-start gap-2">
                                <span className="text-luna-secondary mt-0.5">✓</span>
                                <span>Real-time legal advice powered by Google Gemini</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-luna-secondary mt-0.5">✓</span>
                                <span>Context-aware conversations about your cases</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-luna-secondary mt-0.5">✓</span>
                                <span>Comprehensive responses with legal citations</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-luna-secondary mt-0.5">✓</span>
                                <span>Support for German and English queries</span>
                            </li>
                        </ul>
                        <p className="mt-4 text-xs text-luna-text-light/60 dark:text-luna-text-muted-dark font-medium">
                            Get answers 24/7 without waiting for appointments
                        </p>
                    </div>

                    {/* Case Management */}
                    <div className="bg-white dark:bg-luna-primary rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 shadow-sm p-6 hover:shadow-lg transition-shadow">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="flex-shrink-0 flex items-center justify-center size-12 rounded-lg bg-luna-secondary/10">
                                <span className="material-symbols-outlined text-2xl text-luna-secondary">folder</span>
                            </div>
                            <h3 className="text-xl font-bold text-luna-text-light dark:text-luna-text-dark">Case Management</h3>
                        </div>
                        <ul className="space-y-2 text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">
                            <li className="flex items-start gap-2">
                                <span className="text-luna-secondary mt-0.5">✓</span>
                                <span>Organize legal work with dedicated cases</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-luna-secondary mt-0.5">✓</span>
                                <span>Store documents securely in cloud storage</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-luna-secondary mt-0.5">✓</span>
                                <span>Track deadlines and case status</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-luna-secondary mt-0.5">✓</span>
                                <span>Maintain conversation history per case</span>
                            </li>
                        </ul>
                        <p className="mt-4 text-xs text-luna-text-light/60 dark:text-luna-text-muted-dark font-medium">
                            Everything organized in one secure place
                        </p>
                    </div>

                    {/* EU Law Notifications */}
                    <div className="bg-white dark:bg-luna-primary rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 shadow-sm p-6 hover:shadow-lg transition-shadow">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="flex-shrink-0 flex items-center justify-center size-12 rounded-lg bg-luna-secondary/10">
                                <span className="material-symbols-outlined text-2xl text-luna-secondary">notifications</span>
                            </div>
                            <h3 className="text-xl font-bold text-luna-text-light dark:text-luna-text-dark">EU Law Updates</h3>
                        </div>
                        <ul className="space-y-2 text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">
                            <li className="flex items-start gap-2">
                                <span className="text-luna-secondary mt-0.5">✓</span>
                                <span>Subscribe to legal topics you care about</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-luna-secondary mt-0.5">✓</span>
                                <span>AI-summarized email notifications for new laws</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-luna-secondary mt-0.5">✓</span>
                                <span>Topics: Digital Policy, GDPR, Environmental Law, etc.</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-luna-secondary mt-0.5">✓</span>
                                <span>Personalized summaries in plain English</span>
                            </li>
                        </ul>
                        <p className="mt-4 text-xs text-luna-text-light/60 dark:text-luna-text-muted-dark font-medium">
                            Stay current with latest EU legislation automatically
                        </p>
                    </div>

                    {/* Security & Privacy */}
                    <div className="bg-white dark:bg-luna-primary rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 shadow-sm p-6 hover:shadow-lg transition-shadow">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="flex-shrink-0 flex items-center justify-center size-12 rounded-lg bg-luna-secondary/10">
                                <span className="material-symbols-outlined text-2xl text-luna-secondary">lock</span>
                            </div>
                            <h3 className="text-xl font-bold text-luna-text-light dark:text-luna-text-dark">Security & Privacy</h3>
                        </div>
                        <ul className="space-y-2 text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">
                            <li className="flex items-start gap-2">
                                <span className="text-luna-secondary mt-0.5">✓</span>
                                <span>Encrypted data storage with Supabase</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-luna-secondary mt-0.5">✓</span>
                                <span>Row-Level Security (RLS) policies</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-luna-secondary mt-0.5">✓</span>
                                <span>100% GDPR compliant</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-luna-secondary mt-0.5">✓</span>
                                <span>No third-party data sharing</span>
                            </li>
                        </ul>
                        <p className="mt-4 text-xs text-luna-text-light/60 dark:text-luna-text-muted-dark font-medium">
                            Your data belongs to you, always
                        </p>
                    </div>
                </div>

                {/* Key Statistics */}
                <div className="bg-gradient-to-br from-luna-secondary/10 via-luna-secondary/5 to-transparent rounded-xl border border-luna-secondary/20 p-8 mb-12">
                    <h2 className="text-2xl font-bold mb-6 text-center text-luna-text-light dark:text-luna-text-dark">
                        Trusted by Legal Professionals
                    </h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                        {[
                            { metric: '80%', label: 'Time Saved' },
                            { metric: '7+', label: 'Legal Templates' },
                            { metric: '100%', label: 'GDPR Compliant' },
                            { metric: '24/7', label: 'AI Available' },
                        ].map((stat, index) => (
                            <div key={index} className="text-center">
                                <div className="text-3xl font-black text-luna-secondary mb-2">{stat.metric}</div>
                                <div className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">{stat.label}</div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* CTA Section */}
                <div className="text-center bg-white dark:bg-luna-primary rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 shadow-sm p-8">
                    <h2 className="text-3xl font-bold mb-4 text-luna-text-light dark:text-luna-text-dark">
                        Ready to Get Started?
                    </h2>
                    <p className="text-lg text-luna-text-light/70 dark:text-luna-text-dark/70 mb-6 max-w-2xl mx-auto">
                        Join thousands of legal professionals who trust LRDEnE for accurate, AI-powered legal assistance.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        {onNavigateToAuth ? (
                            <button
                                onClick={() => onNavigateToAuth('signup')}
                                className="flex items-center justify-center gap-2 h-12 px-8 bg-luna-secondary text-luna-primary text-base font-bold rounded-lg hover:opacity-90 transition-opacity"
                            >
                                <span>Create Free Account</span>
                                <span className="material-symbols-outlined">arrow_forward</span>
                            </button>
                        ) : onNavigate ? (
                            <button
                                onClick={() => onNavigate('Landing')}
                                className="flex items-center justify-center gap-2 h-12 px-8 bg-luna-secondary text-luna-primary text-base font-bold rounded-lg hover:opacity-90 transition-opacity"
                            >
                                <span>Get Started</span>
                                <span className="material-symbols-outlined">arrow_forward</span>
                            </button>
                        ) : null}
                        {onNavigate && (
                            <button
                                onClick={() => onNavigate('Pricing')}
                                className="flex items-center justify-center gap-2 h-12 px-8 border-2 border-luna-text-light/20 dark:border-luna-text-dark/20 text-luna-text-light dark:text-luna-text-dark text-base font-bold rounded-lg hover:bg-luna-bg-light dark:hover:bg-luna-bg-dark/50 transition-colors"
                            >
                                <span>View Pricing</span>
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

// Legal Insurance Partnership Page
const LegalInsurancePage: React.FC<{ onNavigate?: (page: string) => void }> = ({ onNavigate }) => {
    const heroRef = useAOS('fade-in-up', {}, 0);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('in-view');
                        observer.unobserve(entry.target);
                    }
                });
            },
            {
                threshold: 0.1,
                rootMargin: '0px 0px -50px 0px'
            }
        );

        const observeElements = () => {
            const animatedElements = document.querySelectorAll('.animate-on-scroll:not(.in-view)');
            animatedElements.forEach((el) => {
                const rect = el.getBoundingClientRect();
                const isInViewport = rect.top < window.innerHeight && rect.bottom > 0;

                if (isInViewport) {
                    el.classList.add('in-view');
                } else {
                    observer.observe(el);
                }
            });
        };

        const timeout = setTimeout(() => {
            observeElements();
        }, 100);

        const interval = setInterval(observeElements, 1000);

        return () => {
            clearTimeout(timeout);
            clearInterval(interval);
            const animatedElements = document.querySelectorAll('.animate-on-scroll');
            animatedElements.forEach((el) => {
                observer.unobserve(el);
            });
        };
    }, []);

    return (
        <div className="flex flex-col flex-1">
            {/* Hero Section */}
            <section className="py-20 md:py-32 bg-gradient-to-br from-green-600 via-green-500 to-green-700 border-b border-luna-text-light/10 dark:border-luna-text-dark/10">
                <div className="container mx-auto px-4">
                    <div className="flex flex-col gap-4 text-center max-w-4xl mx-auto animate-on-scroll fade-in-up" ref={heroRef as React.RefObject<HTMLDivElement>}>
                        <div className="inline-flex items-center justify-center size-20 rounded-2xl bg-gradient-to-br from-white/20 to-white/5 mb-6 mx-auto">
                            <span className="material-symbols-outlined text-5xl text-white">shield</span>
                        </div>
                        <h1 className="text-4xl md:text-6xl font-black leading-tight tracking-[-0.033em] text-white">Rechtschutz Versicherungen</h1>
                        <p className="text-lg md:text-xl font-normal leading-normal text-white/90 max-w-2xl mx-auto">
                            Partneren Sie mit LRDEnE, um Ihre Versicherten besser zu betreuen und Ihre Betriebskosten zu senken
                        </p>
                        <p className="text-base text-white/80 max-w-xl mx-auto">
                            Moderne Lösungen für Rechtschutzversicherungen
                        </p>
                    </div>
                </div>
            </section>

            {/* Benefits Section */}
            <section className="py-20 md:py-28 bg-white dark:bg-luna-primary">
                <div className="container mx-auto px-4">
                    <div className="max-w-6xl mx-auto">
                        <div className="text-center mb-16 animate-on-scroll fade-in-up">
                            <h2 className="text-3xl md:text-5xl font-black leading-tight tracking-[-0.033em] mb-4 text-luna-text-light dark:text-luna-text-dark">
                                Vorteile für Rechtschutzversicherungen
                            </h2>
                            <p className="text-lg text-luna-text-light/70 dark:text-luna-text-dark/70 max-w-2xl mx-auto">
                                Wie unsere Plattform Ihre Versichertenbetreuung verbessert
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                            {[
                                {
                                    icon: 'assessment',
                                    iconColor: 'text-blue-500',
                                    color: 'from-blue-500/20 to-blue-500/5',
                                    title: 'Schnellere Schadensbearbeitung',
                                    description: 'Automatisieren Sie die erste Fallbewertung mit KI-gestützter Dokumentenanalyse. Reduzieren Sie Bearbeitungszeiten und verbessern Sie die Genauigkeit.'
                                },
                                {
                                    icon: 'savings',
                                    iconColor: 'text-green-500',
                                    color: 'from-green-500/20 to-green-500/5',
                                    title: 'Kostenreduzierung',
                                    description: 'Senken Sie Betriebskosten durch Automatisierung routinemäßiger rechtlicher Dokumentenprüfungen und Compliance-Checks.'
                                },
                                {
                                    icon: 'verified',
                                    iconColor: 'text-purple-500',
                                    color: 'from-purple-500/20 to-purple-500/5',
                                    title: 'Qualitätssicherung',
                                    description: 'Gewährleisten Sie konsistente Qualität bei rechtlichen Bewertungen mit KI-gestützter Compliance-Verifizierung und Risikoanalyse.'
                                },
                                {
                                    icon: 'connect_without_contact',
                                    iconColor: 'text-orange-500',
                                    color: 'from-orange-500/20 to-orange-500/5',
                                    title: 'Versichertenportal',
                                    description: 'Bieten Sie Ihren Versicherten direkten Zugang zur Generierung rechtlicher Dokumente und KI-Unterstützung.'
                                },
                                {
                                    icon: 'analytics',
                                    iconColor: 'text-red-500',
                                    color: 'from-red-500/20 to-red-500/5',
                                    title: 'Schadensanalysen',
                                    description: 'Erhalten Sie Einblicke in Schadensmuster, häufige Rechtsprobleme und Effizienzmetriken der Bearbeitung.'
                                },
                                {
                                    icon: 'handshake',
                                    iconColor: 'text-luna-secondary',
                                    color: 'from-luna-secondary/20 to-luna-secondary/5',
                                    title: 'Anwaltsnetzwerk',
                                    description: 'Vermitteln Sie Versicherte bei Bedarf an qualifizierte Anwälte aus unserem Partnernetzwerk.'
                                }
                            ].map((feature, index) => (
                                <div key={index} className="bg-luna-bg-light dark:bg-luna-bg-dark rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 p-6 hover:shadow-lg transition-all duration-300 animate-on-scroll fade-in-up">
                                    <div className={`flex items-center justify-center size-12 rounded-xl bg-gradient-to-br ${feature.color} mb-4`}>
                                        <span className={`material-symbols-outlined text-2xl ${feature.iconColor}`}>{feature.icon}</span>
                                    </div>
                                    <h3 className="text-lg font-bold mb-2 text-luna-text-light dark:text-luna-text-dark">{feature.title}</h3>
                                    <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 leading-relaxed">{feature.description}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* How It Works Section */}
            <section className="py-20 md:py-28 bg-luna-bg-light dark:bg-luna-bg-dark border-y border-luna-text-light/10 dark:border-luna-text-dark/10">
                <div className="container mx-auto px-4">
                    <div className="max-w-6xl mx-auto">
                        <div className="text-center mb-16 animate-on-scroll fade-in-up">
                            <h2 className="text-3xl md:text-5xl font-black leading-tight tracking-[-0.033em] mb-4 text-luna-text-light dark:text-luna-text-dark">
                                Wie die Partnerschaft funktioniert
                            </h2>
                            <p className="text-lg text-luna-text-light/70 dark:text-luna-text-dark/70 max-w-2xl mx-auto">
                                Ein nahtloser Prozess für die Integration in Ihre Versicherungsprozesse
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                            {[
                                {
                                    step: '1',
                                    title: 'Integration',
                                    description: 'Wir integrieren unsere Plattform in Ihre bestehenden Systeme und Prozesse.',
                                    icon: 'settings',
                                    color: 'from-blue-500/20 to-blue-500/5',
                                    iconColor: 'text-blue-500'
                                },
                                {
                                    step: '2',
                                    title: 'Versichertenzugang',
                                    description: 'Ihre Versicherten erhalten Zugang zu unserer Plattform über Ihr Portal.',
                                    icon: 'person',
                                    color: 'from-green-500/20 to-green-500/5',
                                    iconColor: 'text-green-500'
                                },
                                {
                                    step: '3',
                                    title: 'Automatisierte Bearbeitung',
                                    description: 'KI-gestützte Dokumentenanalyse beschleunigt die Schadensbearbeitung.',
                                    icon: 'auto_awesome',
                                    color: 'from-purple-500/20 to-purple-500/5',
                                    iconColor: 'text-purple-500'
                                },
                                {
                                    step: '4',
                                    title: 'Erfolgreiche Partnerschaft',
                                    description: 'Senken Sie Kosten, verbessern Sie die Versichertenzufriedenheit und steigern Sie die Effizienz.',
                                    icon: 'trending_up',
                                    color: 'from-orange-500/20 to-orange-500/5',
                                    iconColor: 'text-orange-500'
                                }
                            ].map((step, index) => (
                                <div key={index} className="bg-white dark:bg-luna-primary rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 p-6 text-center hover:shadow-lg transition-all duration-300 animate-on-scroll fade-in-up">
                                    <div className={`inline-flex items-center justify-center size-16 rounded-full bg-gradient-to-br ${step.color} mb-4`}>
                                        <span className={`material-symbols-outlined text-3xl ${step.iconColor}`}>{step.icon}</span>
                                    </div>
                                    <div className="inline-flex items-center justify-center size-10 rounded-full bg-green-500 text-white text-lg font-bold mb-4">
                                        {step.step}
                                    </div>
                                    <h3 className="text-xl font-bold mb-3 text-luna-text-light dark:text-luna-text-dark">{step.title}</h3>
                                    <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 leading-relaxed">{step.description}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Partnership Options Section */}
            <section className="py-20 md:py-28 bg-white dark:bg-luna-primary">
                <div className="container mx-auto px-4">
                    <div className="max-w-6xl mx-auto">
                        <div className="text-center mb-16 animate-on-scroll fade-in-up">
                            <h2 className="text-3xl md:text-5xl font-black leading-tight tracking-[-0.033em] mb-4 text-luna-text-light dark:text-luna-text-dark">
                                Partnerschaftsoptionen
                            </h2>
                            <p className="text-lg text-luna-text-light/70 dark:text-luna-text-dark/70 max-w-2xl mx-auto">
                                Flexible Lösungen für Ihre Bedürfnisse
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div className="bg-luna-bg-light dark:bg-luna-bg-dark rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 p-8 hover:shadow-lg transition-all duration-300 animate-on-scroll fade-in-up">
                                <div className="flex items-center justify-center size-14 rounded-xl bg-gradient-to-br from-blue-500/20 to-blue-500/5 mb-4">
                                    <span className="material-symbols-outlined text-3xl text-blue-500">api</span>
                                </div>
                                <h3 className="text-xl font-bold mb-3 text-luna-text-light dark:text-luna-text-dark">API-Integration</h3>
                                <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 leading-relaxed mb-4">
                                    Integrieren Sie unsere Dienste direkt in Ihre bestehenden Systeme über unsere REST-API.
                                </p>
                                <ul className="space-y-2 text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">
                                    <li className="flex items-start gap-2">
                                        <span className="material-symbols-outlined text-blue-500 text-lg mt-0.5">check_circle</span>
                                        RESTful API für nahtlose Integration
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="material-symbols-outlined text-blue-500 text-lg mt-0.5">check_circle</span>
                                        Umfassende Dokumentation und Support
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="material-symbols-outlined text-blue-500 text-lg mt-0.5">check_circle</span>
                                        Sichere Authentifizierung und Autorisierung
                                    </li>
                                </ul>
                            </div>

                            <div className="bg-luna-bg-light dark:bg-luna-bg-dark rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 p-8 hover:shadow-lg transition-all duration-300 animate-on-scroll fade-in-up">
                                <div className="flex items-center justify-center size-14 rounded-xl bg-gradient-to-br from-green-500/20 to-green-500/5 mb-4">
                                    <span className="material-symbols-outlined text-3xl text-green-500">label</span>
                                </div>
                                <h3 className="text-xl font-bold mb-3 text-luna-text-light dark:text-luna-text-dark">White-Label Lösung</h3>
                                <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 leading-relaxed mb-4">
                                    Bieten Sie unsere Plattform unter Ihrer eigenen Marke an, vollständig angepasst an Ihr Corporate Design.
                                </p>
                                <ul className="space-y-2 text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">
                                    <li className="flex items-start gap-2">
                                        <span className="material-symbols-outlined text-green-500 text-lg mt-0.5">check_circle</span>
                                        Vollständige Branding-Anpassung
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="material-symbols-outlined text-green-500 text-lg mt-0.5">check_circle</span>
                                        Eigene Domain und Hosting-Optionen
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="material-symbols-outlined text-green-500 text-lg mt-0.5">check_circle</span>
                                        Angepasste Benutzeroberfläche
                                    </li>
                                </ul>
                            </div>

                            <div className="bg-luna-bg-light dark:bg-luna-bg-dark rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 p-8 hover:shadow-lg transition-all duration-300 animate-on-scroll fade-in-up">
                                <div className="flex items-center justify-center size-14 rounded-xl bg-gradient-to-br from-purple-500/20 to-purple-500/5 mb-4">
                                    <span className="material-symbols-outlined text-3xl text-purple-500">support_agent</span>
                                </div>
                                <h3 className="text-xl font-bold mb-3 text-luna-text-light dark:text-luna-text-dark">Dedizierter Support</h3>
                                <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 leading-relaxed mb-4">
                                    Erhalten Sie dedizierten Support von unserem Team für alle technischen und strategischen Fragen.
                                </p>
                                <ul className="space-y-2 text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">
                                    <li className="flex items-start gap-2">
                                        <span className="material-symbols-outlined text-purple-500 text-lg mt-0.5">check_circle</span>
                                        Dedizierter Account Manager
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="material-symbols-outlined text-purple-500 text-lg mt-0.5">check_circle</span>
                                        24/7 technischer Support
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="material-symbols-outlined text-purple-500 text-lg mt-0.5">check_circle</span>
                                        Regelmäßige Strategie-Meetings
                                    </li>
                                </ul>
                            </div>

                            <div className="bg-luna-bg-light dark:bg-luna-bg-dark rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 p-8 hover:shadow-lg transition-all duration-300 animate-on-scroll fade-in-up">
                                <div className="flex items-center justify-center size-14 rounded-xl bg-gradient-to-br from-orange-500/20 to-orange-500/5 mb-4">
                                    <span className="material-symbols-outlined text-3xl text-orange-500">analytics</span>
                                </div>
                                <h3 className="text-xl font-bold mb-3 text-luna-text-light dark:text-luna-text-dark">Analysen & Reporting</h3>
                                <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 leading-relaxed mb-4">
                                    Erhalten Sie detaillierte Einblicke in die Nutzung und Leistung der Plattform durch Ihre Versicherten.
                                </p>
                                <ul className="space-y-2 text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">
                                    <li className="flex items-start gap-2">
                                        <span className="material-symbols-outlined text-orange-500 text-lg mt-0.5">check_circle</span>
                                        Detaillierte Nutzungsstatistiken
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="material-symbols-outlined text-orange-500 text-lg mt-0.5">check_circle</span>
                                        Kosten-Nutzen-Analysen
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="material-symbols-outlined text-orange-500 text-lg mt-0.5">check_circle</span>
                                        Regelmäßige Performance-Reports
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ROI Section */}
            <section className="py-20 md:py-28 bg-gradient-to-br from-green-500/10 via-luna-primary to-green-500/10 dark:from-green-500/5 dark:via-luna-bg-dark dark:to-green-500/5 border-y border-luna-text-light/10 dark:border-luna-text-dark/10">
                <div className="container mx-auto px-4">
                    <div className="max-w-6xl mx-auto">
                        <div className="text-center mb-16 animate-on-scroll fade-in-up">
                            <h2 className="text-3xl md:text-5xl font-black leading-tight tracking-[-0.033em] mb-4 text-luna-text-light dark:text-luna-text-dark">
                                Return on Investment
                            </h2>
                            <p className="text-lg text-luna-text-light/70 dark:text-luna-text-dark/70 max-w-2xl mx-auto">
                                Messbare Ergebnisse für Ihre Versicherung
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {[
                                { number: '40%', label: 'Kostenreduzierung', icon: 'savings', color: 'text-green-500' },
                                { number: '60%', label: 'Schnellere Bearbeitung', icon: 'speed', color: 'text-blue-500' },
                                { number: '85%', label: 'Versichertenzufriedenheit', icon: 'sentiment_satisfied', color: 'text-purple-500' }
                            ].map((stat, index) => (
                                <div key={index} className="bg-white dark:bg-luna-primary rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 p-8 text-center hover:shadow-lg transition-all duration-300 animate-on-scroll fade-in-up">
                                    <div className="flex items-center justify-center mb-3">
                                        <span className={`material-symbols-outlined text-4xl ${stat.color}`}>{stat.icon}</span>
                                    </div>
                                    <p className="text-4xl font-black text-luna-secondary mb-2">{stat.number}</p>
                                    <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">{stat.label}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 md:py-28 bg-gradient-to-br from-green-600/10 via-luna-primary to-green-600/10 dark:from-green-600/5 dark:via-luna-bg-dark dark:to-green-600/5">
                <div className="container mx-auto px-4">
                    <div className="max-w-4xl mx-auto text-center animate-on-scroll fade-in-up">
                        <h2 className="text-3xl md:text-5xl font-black leading-tight tracking-[-0.033em] mb-6 text-luna-text-light dark:text-luna-text-dark">
                            Bereit für eine Partnerschaft?
                        </h2>
                        <p className="text-lg text-luna-text-light/70 dark:text-luna-text-dark/70 mb-8 max-w-2xl mx-auto leading-relaxed">
                            Kontaktieren Sie uns, um zu besprechen, wie wir Ihre Rechtschutzversicherung unterstützen können.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <button
                                onClick={() => onNavigate?.('Contact')}
                                className="flex items-center justify-center gap-2 h-14 px-8 bg-green-500 text-white text-base font-bold rounded-lg hover:bg-green-600 transition-colors shadow-lg shadow-green-500/30"
                            >
                                <span>Kontakt aufnehmen</span>
                                <span className="material-symbols-outlined">mail</span>
                            </button>
                            <button
                                onClick={() => onNavigate?.('Collaboration')}
                                className="flex items-center justify-center gap-2 h-14 px-8 bg-white/80 dark:bg-luna-primary/80 backdrop-blur-md border border-luna-text-light/20 dark:border-luna-text-dark/30 text-luna-text-light dark:text-luna-text-dark text-base font-bold rounded-lg hover:bg-white dark:hover:bg-luna-primary transition-all"
                            >
                                <span>Anwalts-Partnerschaften</span>
                                <span className="material-symbols-outlined">arrow_forward</span>
                            </button>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

// Partnership Tier Details Page
const PartnershipTierPage: React.FC<{ tierSlug?: string | null; onNavigate?: (page: string) => void; onNavigateToAuth?: (mode: 'signup' | 'login') => void }> = ({ tierSlug, onNavigate, onNavigateToAuth }) => {
    const tierData = {
        standard: {
            tier: 'Standard Partner',
            icon: 'person',
            color: 'from-blue-500/20 to-blue-500/5',
            iconColor: 'text-blue-500',
            badgeColor: 'bg-blue-500',
            description: 'Perfect for solo practitioners and small law firms looking to grow their practice with qualified referrals.',
            bestFor: 'Solo practitioners and small firms',
            features: [
                { title: 'Qualified Client Referrals', description: 'Receive pre-qualified client referrals matched to your specialization and location.', icon: 'group' },
                { title: 'Standard Commission Rates', description: 'Competitive commission structure on referred cases and platform usage.', icon: 'payments' },
                { title: 'Verified Partner Badge', description: 'Get verified partner status with a badge on your profile to build trust.', icon: 'verified' },
                { title: 'Full Platform Access', description: 'Access all core platform features including AI tools and document templates.', icon: 'apps' },
                { title: 'Email Support', description: 'Get help via email with our support team during business hours.', icon: 'support_agent' }
            ],
            pricing: 'No upfront costs. Revenue-sharing model only.',
            requirements: [
                'Valid license to practice law in Germany or EU',
                'Minimum 3 years of professional experience',
                'Good standing with bar association',
                'Professional liability insurance'
            ],
            process: [
                { step: '1', title: 'Submit Application', description: 'Fill out the partnership application form' },
                { step: '2', title: 'Credential Review', description: 'We review your qualifications and expertise' },
                { step: '3', title: 'Interview', description: 'Brief interview to discuss partnership details' },
                { step: '4', title: 'Approval', description: 'Get approved and start receiving referrals' }
            ]
        },
        premium: {
            tier: 'Premium Partner',
            icon: 'workspace_premium',
            color: 'from-purple-500/20 to-purple-500/5',
            iconColor: 'text-purple-500',
            badgeColor: 'bg-purple-500',
            description: 'Ideal for established firms and specialists who want priority matching and enhanced support.',
            bestFor: 'Established firms and specialists',
            features: [
                { title: 'Priority Referral Matching', description: 'Get first access to high-value cases before they go to other partners.', icon: 'priority_high' },
                { title: 'Higher Commission Rates', description: 'Earn more with premium commission rates on all referrals.', icon: 'trending_up' },
                { title: 'Premium Partner Badge', description: 'Exclusive premium badge to showcase your elite partner status.', icon: 'workspace_premium' },
                { title: 'Advanced Platform Features', description: 'Access to premium AI tools, analytics, and advanced features.', icon: 'star' },
                { title: 'Priority Support', description: 'Dedicated support channel with faster response times.', icon: 'support_agent' },
                { title: 'Marketing Support', description: 'Get featured in our marketing materials and receive co-marketing opportunities.', icon: 'campaign' }
            ],
            pricing: 'No upfront costs. Enhanced revenue-sharing model.',
            requirements: [
                'All Standard Partner requirements',
                'Minimum 5 years of professional experience',
                'Established practice with proven track record',
                'Specialization in high-demand legal areas'
            ],
            process: [
                { step: '1', title: 'Submit Application', description: 'Complete the premium partnership application' },
                { step: '2', title: 'Comprehensive Review', description: 'Detailed review of your practice and expertise' },
                { step: '3', title: 'Business Interview', description: 'Strategic discussion about partnership goals' },
                { step: '4', title: 'Premium Approval', description: 'Get approved and unlock premium benefits' }
            ]
        },
        enterprise: {
            tier: 'Enterprise Partner',
            icon: 'business',
            color: 'from-luna-secondary/20 to-luna-secondary/5',
            iconColor: 'text-luna-secondary',
            badgeColor: 'bg-luna-secondary',
            description: 'Designed for large firms and legal networks requiring custom solutions and dedicated support.',
            bestFor: 'Large firms and legal networks',
            features: [
                { title: 'Dedicated Account Manager', description: 'Your personal account manager for strategic partnership support.', icon: 'person' },
                { title: 'Custom Commission Structure', description: 'Negotiated commission rates tailored to your firm\'s needs.', icon: 'tune' },
                { title: 'Enterprise Partner Badge', description: 'Exclusive enterprise badge recognizing your firm\'s partnership.', icon: 'business' },
                { title: 'White-Label Options', description: 'Brand the platform with your firm\'s identity for client-facing use.', icon: 'label' },
                { title: 'API Access', description: 'Direct API access for custom integrations with your systems.', icon: 'api' },
                { title: 'Custom Integrations', description: 'Work with our team to build custom integrations.', icon: 'settings' },
                { title: 'Dedicated Support Team', description: '24/7 dedicated support team for your firm.', icon: 'groups' }
            ],
            pricing: 'Custom pricing based on volume and requirements.',
            requirements: [
                'All Premium Partner requirements',
                'Large firm with multiple lawyers',
                'Established legal network or practice group',
                'High case volume capacity'
            ],
            process: [
                { step: '1', title: 'Contact Sales', description: 'Reach out to discuss enterprise partnership' },
                { step: '2', title: 'Custom Proposal', description: 'We create a tailored partnership proposal' },
                { step: '3', title: 'Negotiation', description: 'Work together to finalize terms' },
                { step: '4', title: 'Enterprise Setup', description: 'Dedicated onboarding and integration support' }
            ]
        }
    };

    const currentTier = tierSlug && tierData[tierSlug as keyof typeof tierData]
        ? tierData[tierSlug as keyof typeof tierData]
        : tierData.standard;

    const heroRef = useAOS('fade-in-up', {}, 0);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('in-view');
                        observer.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
        );

        const observeElements = () => {
            const animatedElements = document.querySelectorAll('.animate-on-scroll:not(.in-view)');
            animatedElements.forEach((el) => {
                const rect = el.getBoundingClientRect();
                const isInViewport = rect.top < window.innerHeight && rect.bottom > 0;
                if (isInViewport) {
                    el.classList.add('in-view');
                } else {
                    observer.observe(el);
                }
            });
        };

        const timeout = setTimeout(observeElements, 100);
        const interval = setInterval(observeElements, 1000);

        return () => {
            clearTimeout(timeout);
            clearInterval(interval);
            const animatedElements = document.querySelectorAll('.animate-on-scroll');
            animatedElements.forEach((el) => observer.unobserve(el));
        };
    }, []);

    return (
        <div className="flex flex-col flex-1">
            {/* Hero Section */}
            <section className="py-20 md:py-32 bg-gradient-to-br from-luna-primary via-luna-primary to-luna-bg-dark border-b border-luna-text-light/10 dark:border-luna-text-dark/10">
                <div className="container mx-auto px-4">
                    <div className="flex flex-col gap-4 text-center max-w-4xl mx-auto animate-on-scroll fade-in-up" ref={heroRef as React.RefObject<HTMLDivElement>}>
                        <div className={`inline-flex items-center justify-center size-20 rounded-2xl bg-gradient-to-br ${currentTier.color} mb-6 mx-auto`}>
                            <span className={`material-symbols-outlined text-5xl ${currentTier.iconColor}`}>{currentTier.icon}</span>
                        </div>
                        <div className={`inline-block px-4 py-1 rounded-full ${currentTier.badgeColor} text-white text-sm font-bold mb-4`}>
                            {currentTier.tier}
                        </div>
                        <h1 className="text-4xl md:text-6xl font-black leading-tight tracking-[-0.033em] text-white">{currentTier.tier}</h1>
                        <p className="text-lg md:text-xl font-normal leading-normal text-white/80 max-w-2xl mx-auto">
                            {currentTier.description}
                        </p>
                        <p className="text-base text-white/70 max-w-xl mx-auto">
                            Best for: {currentTier.bestFor}
                        </p>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="py-20 md:py-28 bg-white dark:bg-luna-primary">
                <div className="container mx-auto px-4">
                    <div className="max-w-6xl mx-auto">
                        <div className="text-center mb-16 animate-on-scroll fade-in-up">
                            <h2 className="text-3xl md:text-5xl font-black leading-tight tracking-[-0.033em] mb-4 text-luna-text-light dark:text-luna-text-dark">
                                Partnership Benefits
                            </h2>
                            <p className="text-lg text-luna-text-light/70 dark:text-luna-text-dark/70 max-w-2xl mx-auto">
                                Everything included in {currentTier.tier}
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
                            {currentTier.features.map((feature, index) => (
                                <div key={index} className="bg-luna-bg-light dark:bg-luna-bg-dark rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 p-6 hover:shadow-lg transition-all duration-300 animate-on-scroll fade-in-up">
                                    <div className={`flex items-center justify-center size-12 rounded-xl bg-gradient-to-br ${currentTier.color} mb-4`}>
                                        <span className={`material-symbols-outlined text-2xl ${currentTier.iconColor}`}>{feature.icon}</span>
                                    </div>
                                    <h3 className="text-xl font-bold mb-2 text-luna-text-light dark:text-luna-text-dark">{feature.title}</h3>
                                    <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 leading-relaxed">{feature.description}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Requirements & Process Section */}
            <section className="py-20 md:py-28 bg-luna-bg-light dark:bg-luna-bg-dark border-y border-luna-text-light/10 dark:border-luna-text-dark/10">
                <div className="container mx-auto px-4">
                    <div className="max-w-6xl mx-auto">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            {/* Requirements */}
                            <div className="bg-white dark:bg-luna-primary rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 p-8 animate-on-scroll fade-in-up">
                                <h3 className="text-2xl font-bold mb-6 text-luna-text-light dark:text-luna-text-dark flex items-center gap-3">
                                    <span className={`material-symbols-outlined ${currentTier.iconColor}`}>checklist</span>
                                    Requirements
                                </h3>
                                <ul className="space-y-3">
                                    {currentTier.requirements.map((req, idx) => (
                                        <li key={idx} className="flex items-start gap-3 text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">
                                            <span className={`material-symbols-outlined ${currentTier.iconColor} mt-0.5`}>check_circle</span>
                                            {req}
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* Process */}
                            <div className="bg-white dark:bg-luna-primary rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 p-8 animate-on-scroll fade-in-up">
                                <h3 className="text-2xl font-bold mb-6 text-luna-text-light dark:text-luna-text-dark flex items-center gap-3">
                                    <span className={`material-symbols-outlined ${currentTier.iconColor}`}>route</span>
                                    Application Process
                                </h3>
                                <div className="space-y-4">
                                    {currentTier.process.map((step, idx) => (
                                        <div key={idx} className="flex gap-4">
                                            <div className={`flex items-center justify-center size-10 rounded-full ${currentTier.badgeColor} text-white text-sm font-bold flex-shrink-0`}>
                                                {step.step}
                                            </div>
                                            <div className="flex-1">
                                                <h4 className="text-base font-bold mb-1 text-luna-text-light dark:text-luna-text-dark">{step.title}</h4>
                                                <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">{step.description}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Pricing Info */}
                        <div className={`mt-8 bg-gradient-to-r ${currentTier.color} rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 p-6 text-center animate-on-scroll fade-in-up`}>
                            <div className="flex items-center justify-center gap-3 mb-2">
                                <span className={`material-symbols-outlined ${currentTier.iconColor}`}>payments</span>
                                <h3 className="text-xl font-bold text-luna-text-light dark:text-luna-text-dark">Pricing</h3>
                            </div>
                            <p className="text-base text-luna-text-light/80 dark:text-luna-text-dark/80">{currentTier.pricing}</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 md:py-28 bg-gradient-to-br from-luna-secondary/10 via-luna-primary to-luna-secondary/10 dark:from-luna-secondary/5 dark:via-luna-bg-dark dark:to-luna-secondary/5">
                <div className="container mx-auto px-4">
                    <div className="max-w-4xl mx-auto text-center animate-on-scroll fade-in-up">
                        <h2 className="text-3xl md:text-5xl font-black leading-tight tracking-[-0.033em] mb-6 text-luna-text-light dark:text-luna-text-dark">
                            Ready to Become a {currentTier.tier}?
                        </h2>
                        <p className="text-lg text-luna-text-light/70 dark:text-luna-text-dark/70 mb-8 max-w-2xl mx-auto leading-relaxed">
                            Join our network and start receiving qualified referrals today.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <button
                                onClick={() => onNavigate?.('Contact')}
                                className="flex items-center justify-center gap-2 h-14 px-8 bg-luna-secondary text-luna-primary text-base font-bold rounded-lg hover:opacity-90 transition-opacity shadow-lg shadow-luna-secondary/30"
                            >
                                <span>Contact Us</span>
                                <span className="material-symbols-outlined">mail</span>
                            </button>
                            <button
                                onClick={() => onNavigateToAuth?.('signup')}
                                className="flex items-center justify-center gap-2 h-14 px-8 bg-white/80 dark:bg-luna-primary/80 backdrop-blur-md border border-luna-text-light/20 dark:border-luna-text-dark/30 text-luna-text-light dark:text-luna-text-dark text-base font-bold rounded-lg hover:bg-white dark:hover:bg-luna-primary transition-all"
                            >
                                <span>Sign Up</span>
                                <span className="material-symbols-outlined">person_add</span>
                            </button>
                        </div>
                        <button
                            onClick={() => onNavigate?.('Collaboration')}
                            className="mt-6 text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 hover:text-luna-secondary transition-colors underline"
                        >
                            ← Back to Partnership Overview
                        </button>
                    </div>
                </div>
            </section>
        </div>
    );
};

type NewsHighlight = {
    icon: string;
    title: string;
    description: string;
};

type NewsItem = {
    id: string;
    type: 'features' | 'improvements' | 'announcements';
    date: string;
    version?: string | null;
    title: string;
    icon: string;
    description: string;
    highlights: NewsHighlight[];
    cta?: {
        text: string;
        action: () => void;
    };
};

type NewsFilterValue = 'all' | NewsItem['type'];

const NEWS_FILTER_OPTIONS: Array<{ id: NewsFilterValue; label: string; icon: string }> = [
    { id: 'all', label: 'All Updates', icon: 'list' },
    { id: 'features', label: 'Features', icon: 'star' },
    { id: 'improvements', label: 'Improvements', icon: 'upgrade' },
    { id: 'announcements', label: 'Announcements', icon: 'campaign' },
];

const NEWS_FILTER_STORAGE_KEY = 'lrdene_news_filter';

function NewsFilterTabs({
    filter,
    onChange,
}: {
    filter: NewsFilterValue;
    onChange: (next: NewsFilterValue) => void;
}) {
    return (
        <div className="flex flex-wrap gap-2 mb-6">
            {NEWS_FILTER_OPTIONS.map((tab) => (
                <button
                    key={tab.id}
                    type="button"
                    onClick={() => onChange(tab.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${filter === tab.id
                        ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/30'
                        : 'bg-white dark:bg-luna-primary border border-luna-text-light/10 dark:border-luna-text-dark/20 text-luna-text-light dark:text-luna-text-dark hover:bg-luna-bg-light dark:hover:bg-luna-bg-dark'
                        }`}
                >
                    <span className="material-symbols-outlined text-base">{tab.icon}</span>
                    {tab.label}
                </button>
            ))}
        </div>
    );
}

function NewsSkeleton() {
    return (
        <div className="bg-white dark:bg-luna-primary rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 shadow-sm p-6 animate-pulse">
            <div className="flex items-center gap-4 mb-4">
                <div className="size-12 rounded-xl bg-luna-text-light/10 dark:bg-luna-text-dark/10" />
                <div className="flex-1 space-y-2">
                    <div className="h-4 rounded bg-luna-text-light/20 dark:bg-luna-text-dark/20 w-40" />
                    <div className="h-3 rounded bg-luna-text-light/10 dark:bg-luna-text-dark/30 w-24" />
                </div>
            </div>
            <div className="space-y-3">
                <div className="h-3 rounded bg-luna-text-light/10 dark:bg-luna-text-dark/30" />
                <div className="h-3 rounded bg-luna-text-light/10 dark:bg-luna-text-dark/30 w-3/4" />
            </div>
        </div>
    );
}

function NewsCard({ item }: { item: NewsItem }) {
    return (
        <article
            className={`bg-white dark:bg-luna-primary rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 shadow-sm p-6 hover:shadow-md transition-shadow ${item.type === 'announcements' ? 'bg-gradient-to-br from-luna-secondary/10 to-luna-secondary/5 dark:from-luna-secondary/20 dark:to-luna-secondary/10' : ''
                }`}
        >
            <div className="flex items-start gap-4 mb-4">
                <div className="flex items-center justify-center size-12 rounded-xl bg-gradient-to-br from-blue-500/20 to-blue-500/5 flex-shrink-0">
                    <span className="text-2xl">{item.icon}</span>
                </div>
                <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                        <h2 className="text-xl font-bold text-luna-text-light dark:text-luna-text-dark">{item.title}</h2>
                        {item.version && (
                            <span className="px-2 py-0.5 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 rounded font-medium">
                                v{item.version}
                            </span>
                        )}
                    </div>
                    <p className="text-xs text-luna-text-light/70 dark:text-luna-text-dark/70">
                        {item.date}
                    </p>
                </div>
            </div>

            <div
                className="text-sm text-luna-text-light dark:text-luna-text-dark mb-4 leading-relaxed prose prose-sm dark:prose-invert max-w-none prose-headings:text-luna-text-light prose-headings:dark:text-luna-text-dark prose-p:text-luna-text-light prose-p:dark:text-luna-text-dark prose-strong:text-luna-text-light prose-strong:dark:text-luna-text-dark prose-a:text-luna-secondary prose-a:dark:text-luna-secondary"
                dangerouslySetInnerHTML={{ __html: item.description }}
            />

            {item.highlights && item.highlights.length > 0 && (
                <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 ${item.cta ? '' : 'mb-0'}`}>
                    {item.highlights.map((highlight, idx) => (
                        <div key={idx} className="bg-luna-bg-light dark:bg-luna-bg-dark rounded-lg p-4">
                            <div className="flex items-start gap-2">
                                <span className="text-lg flex-shrink-0">{highlight.icon}</span>
                                <div>
                                    <h3 className="font-bold mb-1 text-sm text-luna-text-light dark:text-luna-text-dark">{highlight.title}</h3>
                                    <p className="text-xs text-luna-text-light/70 dark:text-luna-text-dark/70 leading-relaxed">
                                        {highlight.description}
                                    </p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {item.cta && (
                <button
                    type="button"
                    onClick={item.cta.action}
                    className="mt-4 flex items-center gap-2 bg-luna-secondary text-luna-primary px-4 py-2 rounded-lg text-sm font-bold hover:opacity-90 transition-opacity"
                >
                    <span>{item.cta.text}</span>
                    <span className="material-symbols-outlined text-base">arrow_forward</span>
                </button>
            )}
        </article>
    );
}

const NewsUpdatesPage: React.FC<{ onNavigate: (page: string) => void }> = ({ onNavigate }) => {

    const [filter, setFilter] = useState<NewsFilterValue>(() => {
        if (typeof window === 'undefined') return 'all';
        const saved = window.localStorage.getItem(NEWS_FILTER_STORAGE_KEY) as NewsFilterValue | null;
        return saved && (saved === 'all' || NEWS_FILTER_OPTIONS.some((option) => option.id === saved)) ? saved : 'all';
    });
    const [newsItems, setNewsItems] = useState<NewsItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [refreshing, setRefreshing] = useState(false);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        window.localStorage.setItem(NEWS_FILTER_STORAGE_KEY, filter);
    }, [filter]);

    const filteredNews = useMemo(() => {
        return filter === 'all' ? newsItems : newsItems.filter((item) => item.type === filter);
    }, [filter, newsItems]);

    const activeFilterLabel = useMemo(() => {
        if (filter === 'all') return 'updates';
        const label = NEWS_FILTER_OPTIONS.find((option) => option.id === filter)?.label ?? 'updates';
        return label.toLowerCase();
    }, [filter]);

    // Load news items from database
    useEffect(() => {
        const loadNewsItems = async () => {
            setLoading(true);
            setError(null);
            try {
                const { data, error: fetchError } = await supabase
                    .from('news_updates')
                    .select('*')
                    .eq('is_published', true)
                    .order('published_at', { ascending: false })
                    .limit(50);

                if (fetchError) {
                    // Check if table exists
                    if (fetchError.message?.includes('does not exist') ||
                        fetchError.message?.includes('relation') ||
                        fetchError.code === '42P01') {
                        console.warn('News updates table does not exist. Migration 0024 needs to be run.');
                        setError('Migration required: Please run migration 0024_create_news_updates_table.sql in Supabase SQL Editor. See scripts/RUN_MIGRATION_0024.sql for quick setup.');
                        setNewsItems([]);
                        setLoading(false);
                        return;
                    }
                    // Check for RLS/permission errors
                    if (fetchError.message?.includes('permission') || fetchError.message?.includes('policy')) {
                        console.error('RLS policy error:', fetchError);
                        setError('Permission error: Please check Row-Level Security policies for news_updates table.');
                        setLoading(false);
                        return;
                    }
                    throw fetchError;
                }

                // Transform database records to match component structure
                const transformedItems = (data || []).map((item: any): NewsItem => {
                    // Parse highlights if it's a string (JSON)
                    let highlights: NewsHighlight[] = [];
                    if (item.highlights) {
                        if (typeof item.highlights === 'string') {
                            try {
                                const parsed = JSON.parse(item.highlights);
                                highlights = Array.isArray(parsed) ? parsed : [];
                            } catch {
                                highlights = [];
                            }
                        } else if (Array.isArray(item.highlights)) {
                            highlights = item.highlights;
                        }
                    }

                    // Format date
                    let formattedDate = item.date;
                    if (!formattedDate && item.published_at) {
                        formattedDate = new Date(item.published_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                    } else if (!formattedDate && item.created_at) {
                        formattedDate = new Date(item.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                    }

                    return {
                        id: item.id,
                        type: item.type || 'announcements',
                        date: formattedDate || 'Recent',
                        version: item.version || null,
                        title: item.title || 'Untitled Update',
                        icon: item.icon || '📢',
                        description: item.description || '',
                        highlights: highlights,
                        cta: item.cta_text && item.cta_action ? {
                            text: item.cta_text,
                            action: () => onNavigate(item.cta_action)
                        } : undefined
                    };
                });

                setNewsItems(transformedItems);
            } catch (err) {
                console.error('Error loading news items:', err);
                setError(err instanceof Error ? err.message : 'Failed to load news updates');
            } finally {
                setLoading(false);
            }
        };

        loadNewsItems();

        // Set up real-time subscription for news updates
        const channel = supabase
            .channel('public-news-updates')
            .on(
                'postgres_changes',
                {
                    event: '*',
                    schema: 'public',
                    table: 'news_updates',
                    filter: 'is_published=eq.true'
                },
                () => {
                    loadNewsItems();
                }
            )
            .subscribe();

        return () => {
            channel.unsubscribe();
        };
    }, [onNavigate]);

    const handleRefresh = async () => {
        setRefreshing(true);
        setError(null);
        try {
            const { data, error: fetchError } = await supabase
                .from('news_updates')
                .select('*')
                .eq('is_published', true)
                .order('published_at', { ascending: false })
                .limit(50);

            if (fetchError) throw fetchError;

            const transformedItems = (data || []).map((item: any): NewsItem => {
                let highlights: NewsHighlight[] = [];
                if (item.highlights) {
                    if (typeof item.highlights === 'string') {
                        try {
                            const parsed = JSON.parse(item.highlights);
                            highlights = Array.isArray(parsed) ? parsed : [];
                        } catch {
                            highlights = [];
                        }
                    } else if (Array.isArray(item.highlights)) {
                        highlights = item.highlights;
                    }
                }
                let formattedDate = item.date;
                if (!formattedDate && item.published_at) {
                    formattedDate = new Date(item.published_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                } else if (!formattedDate && item.created_at) {
                    formattedDate = new Date(item.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                }
                return {
                    id: item.id,
                    type: item.type || 'announcements',
                    date: formattedDate || 'Recent',
                    version: item.version || null,
                    title: item.title || 'Untitled Update',
                    icon: item.icon || '📢',
                    description: item.description || '',
                    highlights: highlights,
                    cta: item.cta_text && item.cta_action ? {
                        text: item.cta_text,
                        action: () => onNavigate(item.cta_action)
                    } : undefined
                };
            });
            setNewsItems(transformedItems);
        } catch (err) {
            console.error('Error refreshing news:', err);
            setError(err instanceof Error ? err.message : 'Failed to refresh news updates');
        } finally {
            setRefreshing(false);
        }
    };

    return (
        <div className="flex flex-col flex-1 p-4 md:p-6 lg:p-8 bg-luna-bg-light dark:bg-luna-bg-dark">
            <div className="max-w-5xl mx-auto w-full">
                <div className="mb-8">
                    <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-3">
                            <div className="flex items-center justify-center size-12 rounded-xl bg-gradient-to-br from-blue-500/20 to-blue-500/5">
                                <span className="material-symbols-outlined text-2xl text-blue-500">newspaper</span>
                            </div>
                            <div>
                                <h1 className="text-3xl md:text-4xl font-black text-luna-text-light dark:text-luna-text-dark">Product News & Updates</h1>
                                <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 mt-1">
                                    Stay up-to-date with the latest features, improvements, and announcements
                                </p>
                            </div>
                        </div>
                        {!loading && !error && (
                            <button
                                onClick={handleRefresh}
                                disabled={refreshing}
                                className="px-4 py-2 rounded-lg border border-luna-secondary/30 bg-white dark:bg-luna-primary text-luna-text-light dark:text-luna-text-dark hover:bg-luna-secondary/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                title="Refresh news"
                            >
                                <span className={`material-symbols-outlined ${refreshing ? 'animate-spin' : ''}`}>refresh</span>
                                <span className="hidden sm:inline">Refresh</span>
                            </button>
                        )}
                    </div>
                </div>

                {/* Filter Tabs */}
                <NewsFilterTabs filter={filter} onChange={setFilter} />

                {/* Loading State */}
                {loading && newsItems.length === 0 && (
                    <div className="space-y-4">
                        {[0, 1, 2].map((index) => (
                            <NewsSkeleton key={index} />
                        ))}
                    </div>
                )}

                {/* Error State */}
                {error && !loading && (
                    <div className="bg-white dark:bg-luna-primary rounded-xl border border-red-200 dark:border-red-800 shadow-sm p-12">
                        <div className="text-center mb-4">
                            <span className="material-symbols-outlined text-4xl text-red-500 mb-4">error</span>
                            <p className="text-sm text-red-600 dark:text-red-400 mb-2 font-medium">
                                Failed to load news updates
                            </p>
                            <p className="text-xs text-luna-text-light/70 dark:text-luna-text-dark/70 mb-4">
                                {error}
                            </p>
                        </div>
                        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-left">
                            <p className="text-xs font-bold text-red-800 dark:text-red-200 mb-2">Quick Fix:</p>
                            <ol className="text-xs text-red-700 dark:text-red-300 space-y-1 list-decimal list-inside">
                                <li>Go to Supabase Dashboard → SQL Editor</li>
                                <li>Open file: <code className="bg-red-100 dark:bg-red-900/30 px-1 rounded">scripts/RUN_MIGRATION_0024.sql</code></li>
                                <li>Copy and paste the entire contents</li>
                                <li>Click the Run button to execute the migration</li>
                                <li>Refresh this page</li>
                            </ol>
                        </div>
                        <button
                            type="button"
                            onClick={handleRefresh}
                            className="mt-6 inline-flex items-center gap-2 rounded-lg border border-red-200 dark:border-red-800 px-4 py-2 text-sm font-semibold text-red-600 dark:text-red-300 hover:bg-red-50/80 dark:hover:bg-red-900/20 transition-colors"
                        >
                            <span className="material-symbols-outlined text-base">refresh</span>
                            Try Again
                        </button>
                    </div>
                )}

                {/* News Items */}
                {!loading && !error && (
                    <div className="space-y-6">
                        {filteredNews.map((item) => (
                            <NewsCard key={item.id} item={item} />
                        ))}
                    </div>
                )}

                {!loading && !error && filteredNews.length === 0 && (
                    <div className="bg-white dark:bg-luna-primary rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 shadow-sm p-12 text-center">
                        <span className="material-symbols-outlined text-4xl text-luna-text-light/30 dark:text-luna-text-dark/30 mb-4">newspaper</span>
                        <p className="text-sm font-medium text-luna-text-light dark:text-luna-text-dark mb-2">
                            {newsItems.length === 0
                                ? 'No news updates available yet'
                                : `No ${activeFilterLabel} found`}
                        </p>
                        <p className="text-xs text-luna-text-light/70 dark:text-luna-text-dark/70">
                            {newsItems.length === 0
                                ? 'Check back soon for the latest features and announcements!'
                                : 'Try selecting a different filter'}
                        </p>
                    </div>
                )}

                {!loading && !error && (
                    <div className="mt-8 text-center text-xs text-luna-text-light/70 dark:text-luna-text-dark/70">
                        <p>Stay tuned for more updates!</p>
                    </div>
                )}
            </div>
        </div>
    );
};

interface LawTopic {
    id: string;
    name: string;
    slug: string;
    description: string | null;
    rss_feed_url: string;
    is_active: boolean;
    created_at?: string;
    updated_at?: string;
}

interface TopicSubscription {
    id: string;
    topic_id: string;
    email_enabled: boolean;
}

interface NotificationTopic {
    id: string;
    key: string;
    label: string;
    description: string | null;
}

// Admin Dashboard Component
export const AdminDashboard: React.FC<{ session: Session; profile: Profile | null; onNavigate?: (page: string) => void }> = ({ session, profile, onNavigate }) => {
    const [activeTab, setActiveTab] = useState<'overview' | 'topics' | 'users' | 'subscriptions' | 'notifications' | 'contacts' | 'news' | 'settings'>('overview');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    // Statistics
    const [stats, setStats] = useState({
        totalUsers: 0,
        totalTopics: 0,
        totalSubscriptions: 0,
        totalNotifications: 0,
        activeSubscriptions: 0,
    });

    // Law Topics Management
    const [topics, setTopics] = useState<LawTopic[]>([]);
    const [editingTopic, setEditingTopic] = useState<LawTopic | null>(null);
    const [showTopicForm, setShowTopicForm] = useState(false);
    const [newTopic, setNewTopic] = useState({
        name: '',
        slug: '',
        description: '',
        rss_feed_url: '',
        is_active: true,
    });

    // Users Management
    const [users, setUsers] = useState<any[]>([]);
    const [usersLoading, setUsersLoading] = useState(false);

    // Subscriptions Overview
    const [subscriptions, setSubscriptions] = useState<any[]>([]);

    // Notifications
    const [notifications, setNotifications] = useState<any[]>([]);

    // Contact Submissions
    const [contactSubmissions, setContactSubmissions] = useState<any[]>([]);
    const [contactSubmissionsLoading, setContactSubmissionsLoading] = useState(false);
    const [selectedContact, setSelectedContact] = useState<any | null>(null);

    // News Updates
    const [newsUpdates, setNewsUpdates] = useState<any[]>([]);
    const [newsLoading, setNewsLoading] = useState(false);
    const [editingNews, setEditingNews] = useState<any | null>(null);
    const [showNewsForm, setShowNewsForm] = useState(false);
    const [newNews, setNewNews] = useState({
        type: 'features' as 'features' | 'improvements' | 'announcements',
        title: '',
        description: '',
        icon: '📢',
        version: '',
        date: new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
        highlights: [] as any[],
        cta_text: '',
        cta_action: '',
        is_published: true,
    });
    const newsFormIdPrefix = editingNews?.id ? `news-${editingNews.id}` : 'news-create';
    const newsFormFieldIds = {
        type: `${newsFormIdPrefix}-type`,
        icon: `${newsFormIdPrefix}-icon`,
        title: `${newsFormIdPrefix}-title`,
        description: `${newsFormIdPrefix}-description`,
        version: `${newsFormIdPrefix}-version`,
        date: `${newsFormIdPrefix}-date`,
        published: `${newsFormIdPrefix}-published`,
    };

    // Auto-processing status
    const [autoProcessingStatus, setAutoProcessingStatus] = useState<{
        enabled: boolean;
        available: boolean;
        loading: boolean;
    }>({
        enabled: false,
        available: true,
        loading: true,
    });

    // Check admin access
    useEffect(() => {
        if (!session || !profile) {
            setError('You must be logged in to access the admin dashboard.');
            return;
        }

        if (profile.role !== 'admin' && profile.role !== 'super_admin') {
            setError('Access denied. Admin privileges required.');
            if (onNavigate) {
                onNavigate('Dashboard');
            }
            return;
        }
    }, [session, profile, onNavigate]);

    // Load statistics
    useEffect(() => {
        if (activeTab === 'overview' && (profile?.role === 'admin' || profile?.role === 'super_admin')) {
            loadStatistics();
        }
    }, [activeTab, profile]);

    // Load topics
    useEffect(() => {
        if (activeTab === 'topics' && (profile?.role === 'admin' || profile?.role === 'super_admin')) {
            loadTopics();
        }
    }, [activeTab, profile]);

    // Load users
    useEffect(() => {
        if (activeTab === 'users' && (profile?.role === 'admin' || profile?.role === 'super_admin')) {
            loadUsers();
        }
    }, [activeTab, profile]);

    // Load subscriptions
    useEffect(() => {
        if (activeTab === 'subscriptions' && (profile?.role === 'admin' || profile?.role === 'super_admin')) {
            loadSubscriptions();
        }
    }, [activeTab, profile]);

    // Load notifications
    useEffect(() => {
        if (activeTab === 'notifications' && (profile?.role === 'admin' || profile?.role === 'super_admin')) {
            loadNotifications();
        }
    }, [activeTab, profile]);

    // Load auto-processing status
    useEffect(() => {
        if (activeTab === 'settings' && (profile?.role === 'admin' || profile?.role === 'super_admin')) {
            checkAutoProcessingStatus();
        }
    }, [activeTab, profile]);

    // Load contact submissions
    useEffect(() => {
        if (activeTab === 'contacts' && (profile?.role === 'admin' || profile?.role === 'super_admin')) {
            loadContactSubmissions();
        }
    }, [activeTab, profile]);

    // Load news updates
    useEffect(() => {
        if (activeTab === 'news' && (profile?.role === 'admin' || profile?.role === 'super_admin')) {
            loadNewsUpdates();
        }
    }, [activeTab, profile]);

    const checkAutoProcessingStatus = async () => {
        setAutoProcessingStatus(prev => ({ ...prev, loading: true }));
        try {
            const { data, error } = await supabase.functions.invoke('setup-auto-processing', {
                body: { action: 'status' },
            });

            if (error) throw error;

            setAutoProcessingStatus({
                enabled: data?.enabled || false,
                available: data?.available !== false,
                loading: false,
            });
        } catch (err) {
            console.error('Error checking auto-processing status:', err);
            setAutoProcessingStatus(prev => ({ ...prev, loading: false }));
        }
    };

    const toggleAutoProcessing = async (enable: boolean) => {
        setAutoProcessingStatus(prev => ({ ...prev, loading: true }));
        try {
            const action = enable ? 'enable' : 'disable';
            const { data, error } = await supabase.functions.invoke('setup-auto-processing', {
                body: { action },
            });

            if (error) throw error;

            if (data?.success) {
                setSuccess(data.message || `Auto-processing ${enable ? 'enabled' : 'disabled'} successfully!`);
                await checkAutoProcessingStatus();
            } else {
                setError(data.error || `Failed to ${enable ? 'enable' : 'disable'} auto-processing`);
            }
        } catch (err: any) {
            console.error('Error toggling auto-processing:', err);
            setError(err.message || `Failed to ${enable ? 'enable' : 'disable'} auto-processing`);
        } finally {
            setAutoProcessingStatus(prev => ({ ...prev, loading: false }));
            setTimeout(() => {
                setSuccess(null);
                setError(null);
            }, 5000);
        }
    };

    const loadStatistics = async () => {
        setLoading(true);
        try {
            // Load all statistics in parallel
            const [usersRes, topicsRes, subsRes, notifsRes] = await Promise.all([
                supabase.from('profiles').select('id', { count: 'exact', head: true }),
                supabase.from('law_topics').select('id', { count: 'exact', head: true }),
                supabase.from('user_topic_subscriptions').select('id', { count: 'exact', head: true }),
                supabase.from('notifications').select('id', { count: 'exact', head: true }),
            ]);

            const [activeSubsRes] = await Promise.all([
                supabase.from('user_topic_subscriptions').select('id', { count: 'exact', head: true }).eq('email_enabled', true),
            ]);

            setStats({
                totalUsers: usersRes.count || 0,
                totalTopics: topicsRes.count || 0,
                totalSubscriptions: subsRes.count || 0,
                totalNotifications: notifsRes.count || 0,
                activeSubscriptions: activeSubsRes.count || 0,
            });
        } catch (err) {
            console.error('Error loading statistics:', err);
            setError('Failed to load statistics');
        } finally {
            setLoading(false);
        }
    };

    const loadTopics = async () => {
        setLoading(true);
        try {
            const { data, error } = await supabase
                .from('law_topics')
                .select('*')
                .order('name');

            if (error) throw error;
            setTopics(data || []);
        } catch (err) {
            console.error('Error loading topics:', err);
            setError('Failed to load topics');
        } finally {
            setLoading(false);
        }
    };

    const loadUsers = async () => {
        setUsersLoading(true);
        try {
            // Note: This requires service role or proper RLS policy
            // For now, we'll use a function that returns user count and basic info
            const { data, error } = await supabase
                .from('profiles')
                .select('id, first_name, last_name, role, created_at, updated_at')
                .order('created_at', { ascending: false })
                .limit(100);

            if (error) throw error;
            setUsers(data || []);
        } catch (err) {
            console.error('Error loading users:', err);
            setError('Failed to load users. You may need additional permissions.');
        } finally {
            setUsersLoading(false);
        }
    };

    const loadSubscriptions = async () => {
        setLoading(true);
        try {
            const { data, error } = await supabase
                .from('user_topic_subscriptions')
                .select(`
                    *,
                    law_topics:topic_id (id, name, slug),
                    profiles:user_id (id, first_name, last_name)
                `)
                .order('created_at', { ascending: false })
                .limit(100);

            if (error) throw error;
            setSubscriptions(data || []);
        } catch (err) {
            console.error('Error loading subscriptions:', err);
            setError('Failed to load subscriptions');
        } finally {
            setLoading(false);
        }
    };

    const loadNotifications = async () => {
        setLoading(true);
        try {
            const { data, error } = await supabase
                .from('notifications')
                .select(`
                    *,
                    law_topics:topic_id (id, name),
                    profiles:user_id (id, first_name, last_name)
                `)
                .order('created_at', { ascending: false })
                .limit(100);

            if (error) throw error;
            setNotifications(data || []);
        } catch (err) {
            console.error('Error loading notifications:', err);
            setError('Failed to load notifications');
        } finally {
            setLoading(false);
        }
    };

    const loadContactSubmissions = async () => {
        setContactSubmissionsLoading(true);
        setError(''); // Clear any previous errors
        try {
            const { data, error } = await supabase
                .from('contact_submissions')
                .select('*')
                .order('created_at', { ascending: false });

            if (error) {
                // Check for table not found error
                if (error.message?.includes('does not exist') || error.code === '42P01') {
                    setError('Contact submissions table does not exist. Please run migration: supabase/migrations/0023_create_contact_submissions_table.sql');
                    setContactSubmissions([]);
                    setContactSubmissionsLoading(false);
                    return;
                }
                // Check for permission/RLS errors
                if (error.message?.includes('permission') || error.message?.includes('policy') || error.code === '42501' || error.code === 'PGRST301') {
                    console.error('RLS Policy Error:', error);
                    setError('Permission denied. Your user needs admin role. Check your role with: SELECT id, first_name, last_name, role FROM profiles WHERE id = auth.uid();');
                    setContactSubmissions([]);
                    setContactSubmissionsLoading(false);
                    return;
                }
                // Generic error
                console.error('Error loading contact submissions:', error);
                setError(`Failed to load contact submissions: ${error.message || 'Unknown error'}`);
                setContactSubmissions([]);
                setContactSubmissionsLoading(false);
                return;
            }
            // Success - clear errors and set data
            setError('');
            setContactSubmissions(data || []);
        } catch (err: any) {
            console.error('Error loading contact submissions:', err);
            setError(`Failed to load contact submissions: ${err?.message || 'Unknown error'}`);
            setContactSubmissions([]);
        } finally {
            setContactSubmissionsLoading(false);
        }
    };

    const updateContactStatus = async (id: string, status: string, notes?: string) => {
        try {
            const updateData: any = { status };
            if (notes !== undefined) updateData.admin_notes = notes;
            if (status === 'replied') updateData.replied_at = new Date().toISOString();

            const { error } = await supabase
                .from('contact_submissions')
                .update(updateData)
                .eq('id', id);

            if (error) throw error;
            await loadContactSubmissions();
            setSuccess('Contact submission updated');
            setSelectedContact(null);
        } catch (err) {
            console.error('Error updating contact submission:', err);
            setError('Failed to update contact submission');
        }
    };

    const loadNewsUpdates = async () => {
        setNewsLoading(true);
        try {
            const { data, error } = await supabase
                .from('news_updates')
                .select('*')
                .order('published_at', { ascending: false });

            if (error) {
                if (error.message?.includes('does not exist') || error.code === '42P01') {
                    setError('News updates table does not exist. Please run migration 0024_create_news_updates_table.sql');
                    setNewsUpdates([]);
                    return;
                }
                throw error;
            }
            setNewsUpdates(data || []);
        } catch (err) {
            console.error('Error loading news updates:', err);
            setError('Failed to load news updates');
        } finally {
            setNewsLoading(false);
        }
    };

    const saveNewsUpdate = async () => {
        try {
            const newsData = {
                ...newNews,
                highlights: newNews.highlights.length > 0 ? newNews.highlights : [],
            };

            if (editingNews) {
                const { error } = await supabase
                    .from('news_updates')
                    .update(newsData)
                    .eq('id', editingNews.id);
                if (error) throw error;
                setSuccess('News update updated');
            } else {
                const { error } = await supabase
                    .from('news_updates')
                    .insert(newsData);
                if (error) throw error;
                setSuccess('News update created');
            }

            setShowNewsForm(false);
            setEditingNews(null);
            setNewNews({
                type: 'features',
                title: '',
                description: '',
                icon: '📢',
                version: '',
                date: new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
                highlights: [],
                cta_text: '',
                cta_action: '',
                is_published: true,
            });
            await loadNewsUpdates();
        } catch (err) {
            console.error('Error saving news update:', err);
            setError('Failed to save news update');
        }
    };

    const deleteNewsUpdate = async (id: string) => {
        if (!confirm('Are you sure you want to delete this news update?')) return;
        try {
            const { error } = await supabase
                .from('news_updates')
                .delete()
                .eq('id', id);
            if (error) throw error;
            setSuccess('News update deleted');
            await loadNewsUpdates();
        } catch (err) {
            console.error('Error deleting news update:', err);
            setError('Failed to delete news update');
        }
    };

    const handleCreateTopic = async () => {
        if (!newTopic.name || !newTopic.slug) {
            setError('Name and slug are required');
            return;
        }

        setLoading(true);
        try {
            const { data, error } = await supabase
                .from('law_topics')
                .insert({
                    name: newTopic.name,
                    slug: newTopic.slug,
                    description: newTopic.description || null,
                    rss_feed_url: newTopic.rss_feed_url || '',
                    is_active: newTopic.is_active,
                })
                .select()
                .single();

            if (error) throw error;

            setTopics([...topics, data]);
            setNewTopic({ name: '', slug: '', description: '', rss_feed_url: '', is_active: true });
            setShowTopicForm(false);
            setSuccess('Topic created successfully');
            setTimeout(() => setSuccess(null), 3000);
        } catch (err: any) {
            console.error('Error creating topic:', err);
            setError(err.message || 'Failed to create topic');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateTopic = async (topic: LawTopic) => {
        setLoading(true);
        try {
            const { error } = await supabase
                .from('law_topics')
                .update({
                    name: topic.name,
                    slug: topic.slug,
                    description: topic.description,
                    rss_feed_url: topic.rss_feed_url,
                    is_active: topic.is_active,
                })
                .eq('id', topic.id);

            if (error) throw error;

            setTopics(topics.map(t => t.id === topic.id ? topic : t));
            setEditingTopic(null);
            setSuccess('Topic updated successfully');
            setTimeout(() => setSuccess(null), 3000);
        } catch (err: any) {
            console.error('Error updating topic:', err);
            setError(err.message || 'Failed to update topic');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteTopic = async (topicId: string) => {
        if (!confirm('Are you sure you want to delete this topic? This will also delete all subscriptions.')) {
            return;
        }

        setLoading(true);
        try {
            const { error } = await supabase
                .from('law_topics')
                .delete()
                .eq('id', topicId);

            if (error) throw error;

            setTopics(topics.filter(t => t.id !== topicId));
            setSuccess('Topic deleted successfully');
            setTimeout(() => setSuccess(null), 3000);
        } catch (err: any) {
            console.error('Error deleting topic:', err);
            setError(err.message || 'Failed to delete topic');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateUserRole = async (userId: string, newRole: string) => {
        setUsersLoading(true);
        try {
            const { error } = await supabase
                .from('profiles')
                .update({ role: newRole })
                .eq('id', userId);

            if (error) throw error;

            setUsers(users.map(u => u.id === userId ? { ...u, role: newRole } : u));
            setSuccess('User role updated successfully');
            setTimeout(() => setSuccess(null), 3000);
        } catch (err: any) {
            console.error('Error updating user role:', err);
            setError(err.message || 'Failed to update user role');
        } finally {
            setUsersLoading(false);
        }
    };

    // Show error if not admin
    if (profile?.role !== 'admin' && profile?.role !== 'super_admin') {
        return (
            <div className="flex flex-col flex-1 p-4 md:p-6 lg:p-8 bg-luna-bg-light dark:bg-luna-bg-dark">
                <div className="max-w-4xl mx-auto w-full">
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
                        <span className="material-symbols-outlined text-4xl text-red-600 dark:text-red-400 mb-4">lock</span>
                        <h2 className="text-2xl font-bold text-red-800 dark:text-red-200 mb-2">Access Denied</h2>
                        <p className="text-red-600 dark:text-red-300">You do not have permission to access the admin dashboard.</p>
                        {onNavigate && (
                            <button
                                onClick={() => onNavigate('Dashboard')}
                                className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                            >
                                Return to Dashboard
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col flex-1 p-4 md:p-6 lg:p-8 bg-luna-bg-light dark:bg-luna-bg-dark">
            <div className="max-w-7xl mx-auto w-full">
                {/* Header */}
                <div className="mb-6">
                    <h1 className="text-3xl font-black text-luna-text-light dark:text-luna-text-dark mb-2">
                        Admin Dashboard
                    </h1>
                    <p className="text-luna-text-light/70 dark:text-luna-text-dark/70">
                        Manage users, topics, subscriptions, and system settings
                    </p>
                </div>

                {/* Error/Success Messages */}
                {error && (
                    <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center justify-between">
                        <span className="text-red-800 dark:text-red-200">{error}</span>
                        <button onClick={() => setError(null)} className="text-red-600 dark:text-red-400">
                            <span className="material-symbols-outlined">close</span>
                        </button>
                    </div>
                )}

                {success && (
                    <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-center justify-between">
                        <span className="text-green-800 dark:text-green-200">{success}</span>
                        <button onClick={() => setSuccess(null)} className="text-green-600 dark:text-green-400">
                            <span className="material-symbols-outlined">close</span>
                        </button>
                    </div>
                )}

                {/* Tabs */}
                <div className="mb-6 border-b border-luna-text-light/10 dark:border-luna-text-dark/20">
                    <nav className="flex gap-4">
                        {[
                            { id: 'overview', label: 'Overview', icon: 'dashboard' },
                            { id: 'topics', label: 'Law Topics', icon: 'gavel' },
                            { id: 'users', label: 'Users', icon: 'people' },
                            { id: 'subscriptions', label: 'Subscriptions', icon: 'subscriptions' },
                            { id: 'notifications', label: 'Notifications', icon: 'notifications' },
                            { id: 'contacts', label: 'Contact Forms', icon: 'mail' },
                            { id: 'news', label: 'News & Updates', icon: 'newspaper' },
                            { id: 'settings', label: 'Settings', icon: 'settings' },
                        ].map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id as any)}
                                className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${activeTab === tab.id
                                    ? 'border-luna-secondary text-luna-secondary'
                                    : 'border-transparent text-luna-text-light/60 dark:text-luna-text-dark/60 hover:text-luna-text-light dark:hover:text-luna-text-dark'
                                    }`}
                            >
                                <span className="material-symbols-outlined text-lg">{tab.icon}</span>
                                <span className="font-medium">{tab.label}</span>
                            </button>
                        ))}
                    </nav>
                </div>

                {/* Tab Content */}
                <div className="bg-white dark:bg-luna-primary rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 shadow-sm p-6">
                    {activeTab === 'overview' && (
                        <div>
                            <h2 className="text-2xl font-bold mb-6 text-luna-text-light dark:text-luna-text-dark">System Overview</h2>
                            {loading ? (
                                <div className="text-center py-8">
                                    <LoadingSpinner size="md" text="Loading statistics..." />
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                                    <div className="bg-luna-secondary/10 rounded-lg p-4 border border-luna-secondary/20">
                                        <div className="flex items-center gap-3 mb-2">
                                            <span className="material-symbols-outlined text-2xl text-luna-secondary">people</span>
                                            <div>
                                                <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">Total Users</p>
                                                <p className="text-2xl font-bold text-luna-text-light dark:text-luna-text-dark">{stats.totalUsers}</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="bg-luna-secondary/10 rounded-lg p-4 border border-luna-secondary/20">
                                        <div className="flex items-center gap-3 mb-2">
                                            <span className="material-symbols-outlined text-2xl text-luna-secondary">gavel</span>
                                            <div>
                                                <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">Law Topics</p>
                                                <p className="text-2xl font-bold text-luna-text-light dark:text-luna-text-dark">{stats.totalTopics}</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="bg-luna-secondary/10 rounded-lg p-4 border border-luna-secondary/20">
                                        <div className="flex items-center gap-3 mb-2">
                                            <span className="material-symbols-outlined text-2xl text-luna-secondary">subscriptions</span>
                                            <div>
                                                <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">Total Subscriptions</p>
                                                <p className="text-2xl font-bold text-luna-text-light dark:text-luna-text-dark">{stats.totalSubscriptions}</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="bg-luna-secondary/10 rounded-lg p-4 border border-luna-secondary/20">
                                        <div className="flex items-center gap-3 mb-2">
                                            <span className="material-symbols-outlined text-2xl text-luna-secondary">notifications_active</span>
                                            <div>
                                                <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">Active Subscriptions</p>
                                                <p className="text-2xl font-bold text-luna-text-light dark:text-luna-text-dark">{stats.activeSubscriptions}</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="bg-luna-secondary/10 rounded-lg p-4 border border-luna-secondary/20">
                                        <div className="flex items-center gap-3 mb-2">
                                            <span className="material-symbols-outlined text-2xl text-luna-secondary">email</span>
                                            <div>
                                                <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">Notifications Sent</p>
                                                <p className="text-2xl font-bold text-luna-text-light dark:text-luna-text-dark">{stats.totalNotifications}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'topics' && (
                        <div>
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-2xl font-bold text-luna-text-light dark:text-luna-text-dark">Law Topics Management</h2>
                                <button
                                    onClick={() => {
                                        setShowTopicForm(true);
                                        setEditingTopic(null);
                                        setNewTopic({ name: '', slug: '', description: '', rss_feed_url: '', is_active: true });
                                    }}
                                    className="flex items-center gap-2 px-4 py-2 bg-luna-secondary text-luna-primary text-sm font-bold rounded-lg hover:opacity-90 transition-opacity"
                                >
                                    <span className="material-symbols-outlined">add</span>
                                    Add Topic
                                </button>
                            </div>

                            {showTopicForm && (
                                <div className="mb-6 p-4 bg-luna-bg-light dark:bg-luna-bg-dark rounded-lg border border-luna-text-light/10 dark:border-luna-text-dark/20">
                                    <h3 className="text-lg font-bold mb-4 text-luna-text-light dark:text-luna-text-dark">
                                        {editingTopic ? 'Edit Topic' : 'Create New Topic'}
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label htmlFor="topic-name" className="block text-sm font-medium mb-2 text-luna-text-light dark:text-luna-text-dark">Name *</label>
                                            <input
                                                id="topic-name"
                                                type="text"
                                                value={editingTopic?.name || newTopic.name}
                                                onChange={(e) => editingTopic ? setEditingTopic({ ...editingTopic, name: e.target.value }) : setNewTopic({ ...newTopic, name: e.target.value })}
                                                className="w-full px-3 py-2 border border-luna-text-light/20 dark:border-luna-text-dark/20 rounded-lg bg-white dark:bg-luna-primary text-luna-text-light dark:text-luna-text-dark"
                                                placeholder="e.g., Digital Policy"
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="topic-slug" className="block text-sm font-medium mb-2 text-luna-text-light dark:text-luna-text-dark">Slug *</label>
                                            <input
                                                id="topic-slug"
                                                type="text"
                                                value={editingTopic?.slug || newTopic.slug}
                                                onChange={(e) => editingTopic ? setEditingTopic({ ...editingTopic, slug: e.target.value }) : setNewTopic({ ...newTopic, slug: e.target.value })}
                                                className="w-full px-3 py-2 border border-luna-text-light/20 dark:border-luna-text-dark/20 rounded-lg bg-white dark:bg-luna-primary text-luna-text-light dark:text-luna-text-dark"
                                                placeholder="e.g., digital-policy"
                                            />
                                        </div>
                                        <div className="md:col-span-2">
                                            <label htmlFor="topic-description" className="block text-sm font-medium mb-2 text-luna-text-light dark:text-luna-text-dark">Description</label>
                                            <textarea
                                                id="topic-description"
                                                value={editingTopic?.description || newTopic.description}
                                                onChange={(e) => editingTopic ? setEditingTopic({ ...editingTopic, description: e.target.value }) : setNewTopic({ ...newTopic, description: e.target.value })}
                                                className="w-full px-3 py-2 border border-luna-text-light/20 dark:border-luna-text-dark/20 rounded-lg bg-white dark:bg-luna-primary text-luna-text-light dark:text-luna-text-dark"
                                                rows={2}
                                                placeholder="Brief description of the topic"
                                            />
                                        </div>
                                        <div className="md:col-span-2">
                                            <label htmlFor="topic-rss" className="block text-sm font-medium mb-2 text-luna-text-light dark:text-luna-text-dark">RSS Feed URL</label>
                                            <input
                                                id="topic-rss"
                                                type="url"
                                                value={editingTopic?.rss_feed_url || newTopic.rss_feed_url}
                                                onChange={(e) => editingTopic ? setEditingTopic({ ...editingTopic, rss_feed_url: e.target.value }) : setNewTopic({ ...newTopic, rss_feed_url: e.target.value })}
                                                className="w-full px-3 py-2 border border-luna-text-light/20 dark:border-luna-text-dark/20 rounded-lg bg-white dark:bg-luna-primary text-luna-text-light dark:text-luna-text-dark"
                                                placeholder="https://eur-lex.europa.eu/.../rss"
                                            />
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="checkbox"
                                                checked={editingTopic?.is_active ?? newTopic.is_active}
                                                onChange={(e) => editingTopic ? setEditingTopic({ ...editingTopic, is_active: e.target.checked }) : setNewTopic({ ...newTopic, is_active: e.target.checked })}
                                                className="h-4 w-4 rounded border-gray-300 text-luna-secondary focus:ring-luna-secondary"
                                                id="is_active"
                                            />
                                            <label htmlFor="is_active" className="text-sm text-luna-text-light dark:text-luna-text-dark">Active</label>
                                        </div>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => editingTopic ? handleUpdateTopic(editingTopic) : handleCreateTopic()}
                                                disabled={loading}
                                                className="px-4 py-2 bg-luna-secondary text-luna-primary text-sm font-bold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
                                            >
                                                {loading ? 'Saving...' : editingTopic ? 'Update' : 'Create'}
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setShowTopicForm(false);
                                                    setEditingTopic(null);
                                                    setNewTopic({ name: '', slug: '', description: '', rss_feed_url: '', is_active: true });
                                                }}
                                                className="px-4 py-2 border border-luna-text-light/20 dark:border-luna-text-dark/20 text-luna-text-light dark:text-luna-text-dark text-sm font-bold rounded-lg hover:bg-luna-bg-light dark:hover:bg-luna-bg-dark transition-colors"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {loading ? (
                                <div className="text-center py-8">
                                    <LoadingSpinner size="md" text="Loading topics..." />
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {topics.map(topic => (
                                        <div key={topic.id} className="p-4 border border-luna-text-light/10 dark:border-luna-text-dark/20 rounded-lg">
                                            {editingTopic?.id === topic.id ? (
                                                <div className="space-y-3">
                                                    <input
                                                        type="text"
                                                        value={editingTopic.name}
                                                        onChange={(e) => setEditingTopic({ ...editingTopic, name: e.target.value })}
                                                        className="w-full px-3 py-2 border border-luna-text-light/20 dark:border-luna-text-dark/20 rounded-lg bg-white dark:bg-luna-primary text-luna-text-light dark:text-luna-text-dark"
                                                    />
                                                    <div className="flex gap-2">
                                                        <button
                                                            onClick={() => handleUpdateTopic(editingTopic)}
                                                            disabled={loading}
                                                            className="px-4 py-2 bg-luna-secondary text-luna-primary text-sm font-bold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
                                                        >
                                                            Save
                                                        </button>
                                                        <button
                                                            onClick={() => setEditingTopic(null)}
                                                            className="px-4 py-2 border border-luna-text-light/20 dark:border-luna-text-dark/20 text-luna-text-light dark:text-luna-text-dark text-sm font-bold rounded-lg"
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="flex items-start justify-between">
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <h3 className="text-lg font-bold text-luna-text-light dark:text-luna-text-dark">{topic.name}</h3>
                                                            {!topic.is_active && (
                                                                <span className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded">Inactive</span>
                                                            )}
                                                        </div>
                                                        <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 mb-2">{topic.description}</p>
                                                        <div className="flex items-center gap-4 text-xs text-luna-text-light/60 dark:text-luna-text-dark/60">
                                                            <span><strong>Slug:</strong> {topic.slug}</span>
                                                            {topic.rss_feed_url && (
                                                                <span className="flex items-center gap-1">
                                                                    <span className="material-symbols-outlined text-sm">rss_feed</span>
                                                                    RSS Configured
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="flex gap-2">
                                                        <button
                                                            onClick={() => {
                                                                setEditingTopic(topic);
                                                                setShowTopicForm(true);
                                                            }}
                                                            className="p-2 text-luna-text-light/60 dark:text-luna-text-dark/60 hover:text-luna-secondary transition-colors"
                                                            title="Edit"
                                                        >
                                                            <span className="material-symbols-outlined">edit</span>
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteTopic(topic.id)}
                                                            className="p-2 text-red-600 dark:text-red-400 hover:opacity-80 transition-opacity"
                                                            title="Delete"
                                                        >
                                                            <span className="material-symbols-outlined">delete</span>
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'users' && (
                        <div>
                            <h2 className="text-2xl font-bold mb-6 text-luna-text-light dark:text-luna-text-dark">User Management</h2>
                            {usersLoading ? (
                                <div className="text-center py-8">
                                    <LoadingSpinner size="md" text="Loading users..." />
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {users.map(user => (
                                        <div key={user.id} className="p-4 border border-luna-text-light/10 dark:border-luna-text-dark/20 rounded-lg">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <h3 className="font-bold text-luna-text-light dark:text-luna-text-dark">
                                                        {user.first_name || user.last_name
                                                            ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
                                                            : 'Unknown User'}
                                                    </h3>
                                                    <p className="text-sm text-luna-text-light/60 dark:text-luna-text-dark/60">ID: {user.id.substring(0, 8)}...</p>
                                                    <p className="text-xs text-luna-text-light/50 dark:text-luna-text-dark/50 mt-1">
                                                        Joined: {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'Unknown'}
                                                    </p>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <select
                                                        value={user.role || 'user'}
                                                        onChange={(e) => handleUpdateUserRole(user.id, e.target.value)}
                                                        className="px-3 py-2 border border-luna-text-light/20 dark:border-luna-text-dark/20 rounded-lg bg-white dark:bg-luna-primary text-luna-text-light dark:text-luna-text-dark text-sm"
                                                    >
                                                        <option value="user">User</option>
                                                        <option value="admin">Admin</option>
                                                        <option value="super_admin">Super Admin</option>
                                                    </select>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'subscriptions' && (
                        <div>
                            <h2 className="text-2xl font-bold mb-6 text-luna-text-light dark:text-luna-text-dark">Subscriptions Overview</h2>
                            {loading ? (
                                <div className="text-center py-8">
                                    <LoadingSpinner size="md" text="Loading subscriptions..." />
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {subscriptions.map((sub: any) => (
                                        <div key={sub.id} className="p-4 border border-luna-text-light/10 dark:border-luna-text-dark/20 rounded-lg">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <h3 className="font-bold text-luna-text-light dark:text-luna-text-dark">
                                                        {sub.law_topics?.name || 'Unknown Topic'}
                                                    </h3>
                                                    <p className="text-sm text-luna-text-light/60 dark:text-luna-text-dark/60">
                                                        User: {sub.profiles?.first_name || 'Unknown'} {sub.profiles?.last_name || ''}
                                                    </p>
                                                    <p className="text-xs text-luna-text-light/50 dark:text-luna-text-dark/50 mt-1">
                                                        Subscribed: {sub.created_at ? new Date(sub.created_at).toLocaleDateString() : 'Unknown'}
                                                    </p>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <span className={`px-3 py-1 text-xs font-medium rounded-full ${sub.email_enabled
                                                        ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200'
                                                        : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                                                        }`}>
                                                        {sub.email_enabled ? 'Emails Enabled' : 'Emails Disabled'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'notifications' && (
                        <div>
                            <h2 className="text-2xl font-bold mb-6 text-luna-text-light dark:text-luna-text-dark">Notifications History</h2>
                            {loading ? (
                                <div className="text-center py-8">
                                    <LoadingSpinner size="md" text="Loading notifications..." />
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {notifications.map((notif: any) => (
                                        <div key={notif.id} className="p-4 border border-luna-text-light/10 dark:border-luna-text-dark/20 rounded-lg">
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1">
                                                    <h3 className="font-bold text-luna-text-light dark:text-luna-text-dark mb-1">{notif.title}</h3>
                                                    <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 mb-2 line-clamp-2">{notif.summary}</p>
                                                    <div className="flex items-center gap-4 text-xs text-luna-text-light/60 dark:text-luna-text-dark/60">
                                                        <span>Topic: {notif.law_topics?.name || 'Unknown'}</span>
                                                        <span>User: {notif.profiles?.first_name || 'Unknown'}</span>
                                                        <span>Sent: {notif.created_at ? new Date(notif.created_at).toLocaleDateString() : 'Unknown'}</span>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <span className={`px-3 py-1 text-xs font-medium rounded-full ${notif.email_sent
                                                        ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200'
                                                        : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200'
                                                        }`}>
                                                        {notif.email_sent ? 'Sent' : 'Failed'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'contacts' && (
                        <div>
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-2xl font-bold text-luna-text-light dark:text-luna-text-dark">Contact Form Submissions</h2>
                                <button
                                    onClick={loadContactSubmissions}
                                    className="px-4 py-2 bg-luna-secondary text-luna-primary text-sm font-bold rounded-lg hover:opacity-90 transition-opacity"
                                >
                                    <span className="material-symbols-outlined text-base align-middle mr-1">refresh</span>
                                    Refresh
                                </button>
                            </div>
                            {contactSubmissionsLoading ? (
                                <div className="text-center py-8">
                                    <LoadingSpinner size="md" text="Loading contact submissions..." />
                                </div>
                            ) : error ? (
                                <div className="text-center py-12">
                                    <span className="material-symbols-outlined text-4xl text-red-500 dark:text-red-400 mb-4">error</span>
                                    <p className="text-red-500 dark:text-red-400 font-semibold mb-2">Error Loading Contact Submissions</p>
                                    <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 mb-4 max-w-md mx-auto">{error}</p>
                                    <button
                                        onClick={loadContactSubmissions}
                                        className="px-4 py-2 bg-luna-secondary text-luna-primary text-sm font-bold rounded-lg hover:opacity-90 transition-opacity"
                                    >
                                        Try Again
                                    </button>
                                </div>
                            ) : contactSubmissions.length === 0 ? (
                                <div className="text-center py-12">
                                    <span className="material-symbols-outlined text-4xl text-luna-text-light/30 dark:text-luna-text-dark/30 mb-4">mail</span>
                                    <p className="text-luna-text-light/70 dark:text-luna-text-dark/70">No contact submissions yet</p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {contactSubmissions.map((contact: any) => (
                                        <div
                                            key={contact.id}
                                            className={`p-4 border rounded-lg cursor-pointer transition-colors ${contact.status === 'new'
                                                ? 'border-luna-secondary/50 bg-luna-secondary/5 dark:bg-luna-secondary/10'
                                                : 'border-luna-text-light/10 dark:border-luna-text-dark/20'
                                                }`}
                                            onClick={() => setSelectedContact(contact)}
                                        >
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-3 mb-2">
                                                        <h3 className="font-bold text-luna-text-light dark:text-luna-text-dark">
                                                            {contact.name}
                                                        </h3>
                                                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${contact.status === 'new' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200' :
                                                            contact.status === 'read' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200' :
                                                                contact.status === 'replied' ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200' :
                                                                    'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                                                            }`}>
                                                            {contact.status.charAt(0).toUpperCase() + contact.status.slice(1)}
                                                        </span>
                                                    </div>
                                                    <a href={`mailto:${contact.email}`} className="text-sm text-luna-secondary hover:underline mb-1 block">
                                                        {contact.email}
                                                    </a>
                                                    <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 line-clamp-2 mt-2">
                                                        {contact.message}
                                                    </p>
                                                    <p className="text-xs text-luna-text-light/50 dark:text-luna-text-dark/50 mt-2">
                                                        {new Date(contact.created_at).toLocaleString()}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Contact Detail Modal */}
                            {selectedContact && (
                                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedContact(null)}>
                                    <div className="bg-white dark:bg-luna-primary rounded-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                                        <div className="flex items-center justify-between mb-4">
                                            <h3 className="text-xl font-bold text-luna-text-light dark:text-luna-text-dark">Contact Details</h3>
                                            <button onClick={() => setSelectedContact(null)} className="text-luna-text-light/60 dark:text-luna-text-dark/60">
                                                <span className="material-symbols-outlined">close</span>
                                            </button>
                                        </div>
                                        <div className="space-y-4">
                                            <div>
                                                <p className="text-sm font-medium text-luna-text-light/70 dark:text-luna-text-dark/70">Name</p>
                                                <p className="text-luna-text-light dark:text-luna-text-dark">{selectedContact.name}</p>
                                            </div>
                                            <div>
                                                <p className="text-sm font-medium text-luna-text-light/70 dark:text-luna-text-dark/70">Email</p>
                                                <p className="text-luna-text-light dark:text-luna-text-dark">
                                                    <a href={`mailto:${selectedContact.email}`} className="text-luna-secondary hover:underline">
                                                        {selectedContact.email}
                                                    </a>
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-sm font-medium text-luna-text-light/70 dark:text-luna-text-dark/70">Message</p>
                                                <p className="text-luna-text-light dark:text-luna-text-dark whitespace-pre-wrap bg-luna-bg-light dark:bg-luna-bg-dark p-3 rounded-lg mt-1">
                                                    {selectedContact.message}
                                                </p>
                                            </div>
                                            <div>
                                                <label htmlFor="contact-status" className="text-sm font-medium text-luna-text-light/70 dark:text-luna-text-dark/70">Status</label>
                                                <select
                                                    id="contact-status"
                                                    value={selectedContact.status}
                                                    onChange={(e) => updateContactStatus(selectedContact.id, e.target.value)}
                                                    className="mt-1 block w-full px-3 py-2 border border-luna-text-light/20 dark:border-luna-text-dark/20 rounded-lg bg-white dark:bg-luna-primary text-luna-text-light dark:text-luna-text-dark"
                                                >
                                                    <option value="new">New</option>
                                                    <option value="read">Read</option>
                                                    <option value="replied">Replied</option>
                                                    <option value="archived">Archived</option>
                                                </select>
                                            </div>
                                            {selectedContact.admin_notes && (
                                                <div>
                                                    <p className="text-sm font-medium text-luna-text-light/70 dark:text-luna-text-dark/70">Admin Notes</p>
                                                    <p className="text-luna-text-light dark:text-luna-text-dark bg-luna-bg-light dark:bg-luna-bg-dark p-3 rounded-lg mt-1">
                                                        {selectedContact.admin_notes}
                                                    </p>
                                                </div>
                                            )}
                                            <div className="flex gap-2 pt-4">
                                                <a
                                                    href={`mailto:${selectedContact.email}?subject=Re: Your inquiry`}
                                                    className="flex-1 px-4 py-2 bg-luna-secondary text-luna-primary text-sm font-bold rounded-lg hover:opacity-90 transition-opacity text-center"
                                                >
                                                    Reply via Email
                                                </a>
                                                <button
                                                    onClick={() => {
                                                        updateContactStatus(selectedContact.id, 'replied');
                                                    }}
                                                    className="px-4 py-2 bg-green-600 text-white text-sm font-bold rounded-lg hover:opacity-90 transition-opacity"
                                                >
                                                    Mark as Replied
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'news' && (
                        <div>
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-2xl font-bold text-luna-text-light dark:text-luna-text-dark">News & Updates Management</h2>
                                <button
                                    onClick={() => {
                                        setEditingNews(null);
                                        setShowNewsForm(true);
                                    }}
                                    className="px-4 py-2 bg-luna-secondary text-luna-primary text-sm font-bold rounded-lg hover:opacity-90 transition-opacity"
                                >
                                    <span className="material-symbols-outlined text-base align-middle mr-1">add</span>
                                    New Update
                                </button>
                            </div>
                            {newsLoading ? (
                                <div className="text-center py-8">
                                    <LoadingSpinner size="md" text="Loading news updates..." />
                                </div>
                            ) : (
                                <>
                                    {showNewsForm && (
                                        <div className="mb-6 p-6 border border-luna-text-light/10 dark:border-luna-text-dark/20 rounded-lg bg-luna-bg-light dark:bg-luna-bg-dark">
                                            <h3 className="text-lg font-bold mb-4 text-luna-text-light dark:text-luna-text-dark">
                                                {editingNews ? 'Edit News Update' : 'Create News Update'}
                                            </h3>
                                            <div className="space-y-4">
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div>
                                                        <label htmlFor={newsFormFieldIds.type} className="block text-sm font-medium mb-1 text-luna-text-light dark:text-luna-text-dark">Type</label>
                                                        <select
                                                            id={newsFormFieldIds.type}
                                                            value={newNews.type}
                                                            onChange={(e) => setNewNews({ ...newNews, type: e.target.value as any })}
                                                            className="w-full px-3 py-2 border border-luna-text-light/20 dark:border-luna-text-dark/20 rounded-lg bg-white dark:bg-luna-primary text-luna-text-light dark:text-luna-text-dark"
                                                        >
                                                            <option value="features">Features</option>
                                                            <option value="improvements">Improvements</option>
                                                            <option value="announcements">Announcements</option>
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <label htmlFor={newsFormFieldIds.icon} className="block text-sm font-medium mb-1 text-luna-text-light dark:text-luna-text-dark">Icon</label>
                                                        <input
                                                            id={newsFormFieldIds.icon}
                                                            type="text"
                                                            value={newNews.icon}
                                                            onChange={(e) => setNewNews({ ...newNews, icon: e.target.value })}
                                                            className="w-full px-3 py-2 border border-luna-text-light/20 dark:border-luna-text-dark/20 rounded-lg bg-white dark:bg-luna-primary text-luna-text-light dark:text-luna-text-dark"
                                                            placeholder="📢"
                                                        />
                                                    </div>
                                                </div>
                                                <div>
                                                    <label htmlFor={newsFormFieldIds.title} className="block text-sm font-medium mb-1 text-luna-text-light dark:text-luna-text-dark">Title</label>
                                                    <input
                                                        id={newsFormFieldIds.title}
                                                        type="text"
                                                        value={newNews.title}
                                                        onChange={(e) => setNewNews({ ...newNews, title: e.target.value })}
                                                        className="w-full px-3 py-2 border border-luna-text-light/20 dark:border-luna-text-dark/20 rounded-lg bg-white dark:bg-luna-primary text-luna-text-light dark:text-luna-text-dark"
                                                    />
                                                </div>
                                                <div>
                                                    <label htmlFor={newsFormFieldIds.description} className="block text-sm font-medium mb-1 text-luna-text-light dark:text-luna-text-dark">Description</label>
                                                    <textarea
                                                        id={newsFormFieldIds.description}
                                                        value={newNews.description}
                                                        onChange={(e) => setNewNews({ ...newNews, description: e.target.value })}
                                                        rows={3}
                                                        className="w-full px-3 py-2 border border-luna-text-light/20 dark:border-luna-text-dark/20 rounded-lg bg-white dark:bg-luna-primary text-luna-text-light dark:text-luna-text-dark"
                                                    />
                                                </div>
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div>
                                                        <label htmlFor={newsFormFieldIds.version} className="block text-sm font-medium mb-1 text-luna-text-light dark:text-luna-text-dark">Version</label>
                                                        <input
                                                            id={newsFormFieldIds.version}
                                                            type="text"
                                                            value={newNews.version}
                                                            onChange={(e) => setNewNews({ ...newNews, version: e.target.value })}
                                                            className="w-full px-3 py-2 border border-luna-text-light/20 dark:border-luna-text-dark/20 rounded-lg bg-white dark:bg-luna-primary text-luna-text-light dark:text-luna-text-dark"
                                                            placeholder="2.0"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label htmlFor={newsFormFieldIds.date} className="block text-sm font-medium mb-1 text-luna-text-light dark:text-luna-text-dark">Date</label>
                                                        <input
                                                            id={newsFormFieldIds.date}
                                                            type="text"
                                                            value={newNews.date}
                                                            onChange={(e) => setNewNews({ ...newNews, date: e.target.value })}
                                                            className="w-full px-3 py-2 border border-luna-text-light/20 dark:border-luna-text-dark/20 rounded-lg bg-white dark:bg-luna-primary text-luna-text-light dark:text-luna-text-dark"
                                                            placeholder="November 2024"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        id={newsFormFieldIds.published}
                                                        type="checkbox"
                                                        checked={newNews.is_published}
                                                        onChange={(e) => setNewNews({ ...newNews, is_published: e.target.checked })}
                                                        className="rounded"
                                                    />
                                                    <label htmlFor={newsFormFieldIds.published} className="text-sm text-luna-text-light dark:text-luna-text-dark cursor-pointer">Published</label>
                                                </div>
                                                <div className="flex gap-2 pt-2">
                                                    <button
                                                        onClick={saveNewsUpdate}
                                                        className="px-4 py-2 bg-luna-secondary text-luna-primary text-sm font-bold rounded-lg hover:opacity-90 transition-opacity"
                                                    >
                                                        {editingNews ? 'Update' : 'Create'}
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            setShowNewsForm(false);
                                                            setEditingNews(null);
                                                            setNewNews({
                                                                type: 'features',
                                                                title: '',
                                                                description: '',
                                                                icon: '📢',
                                                                version: '',
                                                                date: new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
                                                                highlights: [],
                                                                cta_text: '',
                                                                cta_action: '',
                                                                is_published: true,
                                                            });
                                                        }}
                                                        className="px-4 py-2 border border-luna-text-light/20 dark:border-luna-text-dark/20 text-luna-text-light dark:text-luna-text-dark text-sm font-bold rounded-lg hover:opacity-90 transition-opacity"
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    <div className="space-y-3">
                                        {newsUpdates.map((news: any) => (
                                            <div key={news.id} className="p-4 border border-luna-text-light/10 dark:border-luna-text-dark/20 rounded-lg">
                                                <div className="flex items-start justify-between">
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-3 mb-2">
                                                            <span className="text-2xl">{news.icon || '📢'}</span>
                                                            <div>
                                                                <h3 className="font-bold text-luna-text-light dark:text-luna-text-dark">
                                                                    {news.title}
                                                                </h3>
                                                                <div className="flex items-center gap-2 text-xs text-luna-text-light/60 dark:text-luna-text-dark/60">
                                                                    <span className="capitalize">{news.type}</span>
                                                                    {news.version && <span>• v{news.version}</span>}
                                                                    <span>• {news.date}</span>
                                                                    <span className={`px-2 py-0.5 rounded-full ${news.is_published
                                                                        ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200'
                                                                        : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                                                                        }`}>
                                                                        {news.is_published ? 'Published' : 'Draft'}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 line-clamp-2">
                                                            {news.description}
                                                        </p>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            onClick={() => {
                                                                setEditingNews(news);
                                                                setNewNews({
                                                                    type: news.type,
                                                                    title: news.title,
                                                                    description: news.description,
                                                                    icon: news.icon || '📢',
                                                                    version: news.version || '',
                                                                    date: news.date,
                                                                    highlights: news.highlights || [],
                                                                    cta_text: news.cta_text || '',
                                                                    cta_action: news.cta_action || '',
                                                                    is_published: news.is_published,
                                                                });
                                                                setShowNewsForm(true);
                                                            }}
                                                            className="px-3 py-1 text-xs bg-luna-secondary text-luna-primary font-bold rounded hover:opacity-90 transition-opacity"
                                                        >
                                                            Edit
                                                        </button>
                                                        <button
                                                            onClick={() => deleteNewsUpdate(news.id)}
                                                            className="px-3 py-1 text-xs bg-red-600 text-white font-bold rounded hover:opacity-90 transition-opacity"
                                                        >
                                                            Delete
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {activeTab === 'settings' && (
                        <div>
                            <h2 className="text-2xl font-bold mb-6 text-luna-text-light dark:text-luna-text-dark">System Settings</h2>
                            <div className="space-y-6">
                                <div className="p-4 border border-luna-text-light/10 dark:border-luna-text-dark/20 rounded-lg">
                                    <h3 className="text-lg font-bold mb-4 text-luna-text-light dark:text-luna-text-dark">Database Management</h3>
                                    <div className="space-y-2">
                                        <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">
                                            Database migrations and maintenance operations
                                        </p>
                                        <button
                                            onClick={async () => {
                                                if (confirm('This will refresh all statistics. Continue?')) {
                                                    await loadStatistics();
                                                    setSuccess('Statistics refreshed');
                                                    setTimeout(() => setSuccess(null), 3000);
                                                }
                                            }}
                                            className="px-4 py-2 bg-luna-secondary text-luna-primary text-sm font-bold rounded-lg hover:opacity-90 transition-opacity"
                                        >
                                            Refresh Statistics
                                        </button>
                                    </div>
                                </div>

                                {/* Auto-Processing Toggle */}
                                <div className="p-6 border-2 border-luna-secondary/30 dark:border-luna-secondary/20 rounded-lg bg-luna-secondary/5 dark:bg-luna-secondary/5">
                                    <div className="flex items-start justify-between gap-4 mb-4">
                                        <div className="flex-1">
                                            <h3 className="text-lg font-bold mb-2 text-luna-text-light dark:text-luna-text-dark flex items-center gap-2">
                                                <span className="material-symbols-outlined">schedule</span>
                                                Automatic Law Notifications
                                            </h3>
                                            <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 mb-2">
                                                Automatically process EU law updates and send notifications to subscribed users.
                                            </p>
                                            {autoProcessingStatus.loading ? (
                                                <div className="flex items-center gap-2 text-sm text-luna-text-light/60 dark:text-luna-text-dark/60">
                                                    <span className="material-symbols-outlined animate-spin text-sm">sync</span>
                                                    Checking status...
                                                </div>
                                            ) : autoProcessingStatus.enabled ? (
                                                <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                                                    <span className="material-symbols-outlined">check_circle</span>
                                                    <span className="font-medium">Auto-processing is ENABLED</span>
                                                    <span className="text-xs text-luna-text-light/60 dark:text-luna-text-dark/60 ml-2">
                                                        (Runs every 10 minutes)
                                                    </span>
                                                </div>
                                            ) : !autoProcessingStatus.available ? (
                                                <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                                                    <p className="text-sm text-yellow-800 dark:text-yellow-200">
                                                        <strong>Note:</strong> pg_cron requires Supabase Pro plan.
                                                        For Free plan, use GitHub Actions (see AUTOMATED_NOTIFICATIONS_SETUP.md)
                                                    </p>
                                                </div>
                                            ) : (
                                                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                                    <span className="material-symbols-outlined">pause_circle</span>
                                                    <span>Auto-processing is DISABLED</span>
                                                </div>
                                            )}
                                        </div>
                                        <button
                                            onClick={() => toggleAutoProcessing(!autoProcessingStatus.enabled)}
                                            disabled={autoProcessingStatus.loading || !autoProcessingStatus.available}
                                            className={`px-6 py-3 rounded-lg font-bold text-sm transition-all ${autoProcessingStatus.enabled
                                                ? 'bg-red-600 hover:bg-red-700 text-white'
                                                : 'bg-luna-secondary text-luna-primary hover:opacity-90'
                                                } ${autoProcessingStatus.loading || !autoProcessingStatus.available ? 'opacity-50 cursor-not-allowed' : ''}`}
                                        >
                                            {autoProcessingStatus.loading ? (
                                                <span className="flex items-center gap-2">
                                                    <span className="material-symbols-outlined animate-spin text-sm">sync</span>
                                                    Processing...
                                                </span>
                                            ) : autoProcessingStatus.enabled ? (
                                                <span className="flex items-center gap-2">
                                                    <span className="material-symbols-outlined text-sm">stop</span>
                                                    Disable
                                                </span>
                                            ) : (
                                                <span className="flex items-center gap-2">
                                                    <span className="material-symbols-outlined text-sm">play_arrow</span>
                                                    Enable Auto-Processing
                                                </span>
                                            )}
                                        </button>
                                    </div>
                                    {autoProcessingStatus.enabled && (
                                        <div className="mt-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                                            <p className="text-xs text-green-800 dark:text-green-200">
                                                <span className="material-symbols-outlined align-middle text-sm mr-1">info</span>
                                                Your system will automatically check for new EU laws every 10 minutes and send notifications to subscribed users.
                                            </p>
                                        </div>
                                    )}
                                </div>

                                <div className="p-4 border border-luna-text-light/10 dark:border-luna-text-dark/20 rounded-lg">
                                    <h3 className="text-lg font-bold mb-4 text-luna-text-light dark:text-luna-text-dark">Manual Processing</h3>
                                    <div className="space-y-2">
                                        <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">
                                            Manually trigger law notification processing
                                        </p>
                                        <button
                                            onClick={async () => {
                                                try {
                                                    setLoading(true);
                                                    const { error } = await supabase.functions.invoke('process-law-notifications', {
                                                        body: {},
                                                    });
                                                    if (error) throw error;
                                                    setSuccess('Law notifications processed successfully!');
                                                } catch (err: any) {
                                                    setError(err.message || 'Failed to process notifications');
                                                } finally {
                                                    setLoading(false);
                                                    setTimeout(() => {
                                                        setSuccess(null);
                                                        setError(null);
                                                    }, 5000);
                                                }
                                            }}
                                            disabled={loading}
                                            className="px-4 py-2 bg-luna-secondary text-luna-primary text-sm font-bold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
                                        >
                                            {loading ? 'Processing...' : 'Process Now'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// Super Admin CMS Dashboard with Military-Grade Security
export const SuperAdminPage: React.FC<{ session: Session; profile: Profile | null; onNavigate?: (page: string) => void; onShowAuth?: () => void }> = ({ session, profile, onNavigate, onShowAuth }) => {
    const [activeTab, setActiveTab] = useState<'dashboard' | 'users' | 'topics' | 'subscriptions' | 'notifications' | 'security' | 'system' | 'content'>('dashboard');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
    const [autoStatus, setAutoStatus] = useState<boolean | null>(null);

    // Statistics
    const [stats, setStats] = useState({
        totalUsers: 0,
        totalTopics: 0,
        totalSubscriptions: 0,
        totalNotifications: 0,
        activeSubscriptions: 0,
        securityEvents: 0,
    });

    // Data states
    const [users, setUsers] = useState<any[]>([]);
    const [topics, setTopics] = useState<LawTopic[]>([]);
    const [subscriptions, setSubscriptions] = useState<any[]>([]);
    const [notifications, setNotifications] = useState<any[]>([]);
    const [auditLogs, setAuditLogs] = useState<any[]>([]);
    const [ipWhitelist, setIpWhitelist] = useState<any[]>([]);
    const [sessions, setSessions] = useState<any[]>([]);
    const [becomingSuperAdmin, setBecomingSuperAdmin] = useState(false);

    // Security check function
    const logSecurityEvent = async (action: string, resourceType?: string, resourceId?: string | null, success: boolean = true, errorMessage?: string) => {
        try {
            const ipAddress = await fetch('https://api.ipify.org?format=json').then(r => r.json()).then(d => d.ip).catch(() => null);
            await supabase.rpc('log_security_event', {
                p_action: action,
                p_resource_type: resourceType || null,
                p_resource_id: resourceId || null,
                p_ip_address: ipAddress,
                p_user_agent: navigator.userAgent,
                p_success: success,
                p_error_message: errorMessage || null,
                p_metadata: {}
            });
        } catch (e) {
            console.error('Failed to log security event:', e);
        }
    };

    // Check IP whitelist
    const checkIpAccess = async () => {
        try {
            const ipResponse = await fetch('https://api.ipify.org?format=json');
            const ipData = await ipResponse.json();
            const { data, error } = await supabase.rpc('check_ip_whitelist', {
                p_ip_address: ipData.ip
            });
            if (!error && data === false) {
                await logSecurityEvent('ip_blocked', 'system', null, false, 'IP not in whitelist');
                setMessage({ type: 'error', text: 'Access denied: Your IP address is not authorized.' });
                return false;
            }
            return true;
        } catch (e) {
            console.error('IP check failed:', e);
            return true; // Allow access if check fails (fail open for now)
        }
    };

    const checkStatus = async () => {
        try {
            const { data, error } = await supabase.rpc('check_auto_processing_status');
            if (!error && data) setAutoStatus(data.enabled || false);
        } catch (e) {
            console.error(e);
        }
    };

    const loadStatistics = async () => {
        try {
            const [usersRes, topicsRes, subsRes, notifsRes, auditRes] = await Promise.all([
                supabase.from('profiles').select('id', { count: 'exact', head: true }),
                supabase.from('law_topics').select('id', { count: 'exact', head: true }),
                supabase.from('subscriptions').select('id', { count: 'exact', head: true }),
                supabase.from('notifications').select('id', { count: 'exact', head: true }),
                supabase.from('security_audit_logs').select('id', { count: 'exact', head: true })
            ]);

            setStats({
                totalUsers: usersRes.count || 0,
                totalTopics: topicsRes.count || 0,
                totalSubscriptions: subsRes.count || 0,
                totalNotifications: notifsRes.count || 0,
                activeSubscriptions: 0, // Calculate separately
                securityEvents: auditRes.count || 0,
            });
        } catch (e) {
            console.error('Failed to load statistics:', e);
        }
    };

    const loadUsers = async () => {
        setLoading(true);
        try {
            // Note: profiles table doesn't have created_at, using updated_at instead
            const { data, error } = await supabase.from('profiles').select('*').order('updated_at', { ascending: false }).limit(100);
            if (error) throw error;
            setUsers(data || []);
            await logSecurityEvent('view_users', 'user');
        } catch (e: any) {
            setMessage({ type: 'error', text: e.message || 'Failed to load users' });
            await logSecurityEvent('view_users', 'user', null, false, e.message);
        } finally {
            setLoading(false);
        }
    };

    const loadTopics = async () => {
        setLoading(true);
        try {
            const { data, error } = await supabase.from('law_topics').select('*').order('created_at', { ascending: false });
            if (error) throw error;
            setTopics(data || []);
            await logSecurityEvent('view_topics', 'topic');
        } catch (e: any) {
            setMessage({ type: 'error', text: e.message || 'Failed to load topics' });
        } finally {
            setLoading(false);
        }
    };

    const loadSubscriptions = async () => {
        setLoading(true);
        try {
            const { data, error } = await supabase.from('subscriptions').select('*').order('created_at', { ascending: false }).limit(100);
            if (error) throw error;
            setSubscriptions(data || []);
            await logSecurityEvent('view_subscriptions', 'subscription');
        } catch (e: any) {
            setMessage({ type: 'error', text: e.message || 'Failed to load subscriptions' });
        } finally {
            setLoading(false);
        }
    };

    const loadNotifications = async () => {
        setLoading(true);
        try {
            const { data, error } = await supabase.from('notifications').select('*').order('created_at', { ascending: false }).limit(100);
            if (error) throw error;
            setNotifications(data || []);
            await logSecurityEvent('view_notifications', 'notification');
        } catch (e: any) {
            setMessage({ type: 'error', text: e.message || 'Failed to load notifications' });
        } finally {
            setLoading(false);
        }
    };

    const loadAuditLogs = async () => {
        setLoading(true);
        try {
            const { data, error } = await supabase.from('security_audit_logs').select('*').order('created_at', { ascending: false }).limit(100);
            if (error) throw error;
            setAuditLogs(data || []);
        } catch (e: any) {
            setMessage({ type: 'error', text: e.message || 'Failed to load audit logs' });
        } finally {
            setLoading(false);
        }
    };

    const loadIpWhitelist = async () => {
        setLoading(true);
        try {
            const { data, error } = await supabase.from('ip_whitelist').select('*').order('created_at', { ascending: false });
            if (error) throw error;
            setIpWhitelist(data || []);
        } catch (e: any) {
            setMessage({ type: 'error', text: e.message || 'Failed to load IP whitelist' });
        } finally {
            setLoading(false);
        }
    };

    const loadSessions = async () => {
        setLoading(true);
        try {
            const { data, error } = await supabase.from('admin_sessions').select('*').order('last_activity', { ascending: false }).limit(50);
            if (error) throw error;
            setSessions(data || []);
        } catch (e: any) {
            setMessage({ type: 'error', text: e.message || 'Failed to load sessions' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (profile?.role === 'super_admin' && session) {
            checkStatus();
            if (activeTab === 'dashboard') loadStatistics();
            checkIpAccess();
            logSecurityEvent('access_cms', 'system');
        }
    }, [profile, session, activeTab]);

    useEffect(() => {
        if (activeTab === 'users' && profile?.role === 'super_admin') loadUsers();
        if (activeTab === 'topics' && profile?.role === 'super_admin') loadTopics();
        if (activeTab === 'subscriptions' && profile?.role === 'super_admin') loadSubscriptions();
        if (activeTab === 'notifications' && profile?.role === 'super_admin') loadNotifications();
        if (activeTab === 'security' && profile?.role === 'super_admin') {
            loadAuditLogs();
            loadIpWhitelist();
            loadSessions();
        }
    }, [activeTab, profile]);

    if (!session || profile?.role !== 'super_admin') {
        return (
            <div className="flex flex-col flex-1 p-8 items-center justify-center bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20">
                <div className="bg-white dark:bg-luna-primary rounded-2xl shadow-2xl p-8 max-w-md w-full border-4 border-red-500">
                    <div className="text-center mb-6">
                        <span className="material-symbols-outlined text-8xl text-red-600 mb-4 block">security</span>
                        <h2 className="text-3xl font-black text-red-800 mb-2">Military-Grade Security</h2>
                        <p className="text-red-600 font-semibold">Super Admin Access Required</p>
                    </div>
                    <div className="bg-red-50 dark:bg-red-900/20 border-2 border-red-300 rounded-lg p-4 mb-6">
                        <p className="text-sm text-red-800 font-medium">
                            🔐 This area requires <strong>Super Admin</strong> privileges.<br />
                            All access attempts are logged and monitored.
                        </p>
                    </div>
                    {!session ? (
                        <div className="text-center">
                            <p className="text-gray-600 dark:text-gray-300 mb-4">Please log in to continue</p>
                            <button
                                onClick={() => {
                                    console.log('[SuperAdmin] Login button clicked');
                                    if (onShowAuth) {
                                        console.log('[SuperAdmin] Calling onShowAuth');
                                        onShowAuth();
                                    } else if (onNavigate) {
                                        console.log('[SuperAdmin] Calling onNavigate to Dashboard');
                                        onNavigate('Dashboard');
                                    } else {
                                        console.log('[SuperAdmin] Using window.location fallback');
                                        // Store the SuperAdmin URL to return after login
                                        sessionStorage.setItem('returnToSuperAdmin', 'true');
                                        window.location.href = '/dashboard';
                                    }
                                }}
                                className="w-full px-6 py-3 bg-luna-secondary text-luna-primary font-bold rounded-lg hover:opacity-90 transition"
                            >
                                Go to Login
                            </button>
                        </div>
                    ) : (
                        <div className="text-center">
                            <p className="text-gray-600 dark:text-gray-300 mb-4">Your current role: <strong>{profile?.role || 'user'}</strong></p>

                            {/* Become Super Admin Button */}
                            {profile?.role !== 'super_admin' && (
                                <div className="mb-6">
                                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border-2 border-yellow-300 rounded-lg p-4 mb-4">
                                        <p className="text-sm text-yellow-800 dark:text-yellow-200 font-medium mb-3">
                                            ⚠️ <strong>Quick Access:</strong> You can grant yourself Super Admin privileges with one click.
                                        </p>
                                        <button
                                            onClick={async () => {
                                                if (!session?.user?.id) {
                                                    toast.warning('You must be logged in.');
                                                    return;
                                                }

                                                setBecomingSuperAdmin(true);

                                                try {
                                                    const { error } = await supabase
                                                        .from('profiles')
                                                        .update({ role: 'super_admin' })
                                                        .eq('id', session.user.id);

                                                    if (error) throw error;

                                                    window.location.reload();
                                                } catch (err: any) {
                                                    toast.error(`Failed: ${err.message}\n\nRun this SQL in Supabase:\nUPDATE public.profiles SET role = 'super_admin' WHERE id = auth.uid();`, {
                                                        duration: 10000,
                                                    });
                                                    setBecomingSuperAdmin(false);
                                                }
                                            }}
                                            disabled={becomingSuperAdmin}
                                            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {becomingSuperAdmin ? (
                                                <>
                                                    <span className="material-symbols-outlined animate-spin">sync</span>
                                                    <span>Granting Super Admin Access...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <span className="material-symbols-outlined">security</span>
                                                    <span>Become Super Admin</span>
                                                </>
                                            )}
                                        </button>

                                    </div>
                                </div>
                            )}

                            <p className="text-sm text-gray-500 mb-4">Contact a super admin to grant access</p>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col flex-1 bg-luna-bg-light dark:bg-luna-bg-dark">
            {/* Security Header */}
            <div className="bg-gradient-to-r from-red-600 to-red-800 text-white p-4 border-b-4 border-red-900">
                <div className="max-w-7xl mx-auto flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <span className="material-symbols-outlined text-3xl">security</span>
                        <div>
                            <h1 className="text-2xl font-black">🔐 Military-Grade CMS Dashboard</h1>
                            <p className="text-sm text-red-100">Super Admin Control Center</p>
                        </div>
                    </div>
                    <div className="text-right">
                        <p className="text-sm text-red-100">Logged in as: <strong>{profile?.first_name} {profile?.last_name}</strong></p>
                        <p className="text-xs text-red-200">All actions are logged and monitored</p>
                    </div>
                </div>
            </div>

            {/* Message Banner */}
            {message && (
                <div className={`p-4 border-b-2 ${message.type === 'success' ? 'bg-green-50 dark:bg-green-900/20 border-green-400' : 'bg-red-50 dark:bg-red-900/20 border-red-400'
                    }`}>
                    <div className="max-w-7xl mx-auto">
                        <p className={`font-bold ${message.type === 'success' ? 'text-green-800 dark:text-green-200' : 'text-red-800 dark:text-red-200'}`}>
                            {message.text}
                        </p>
                    </div>
                </div>
            )}

            <div className="flex-1 overflow-hidden">
                <div className="max-w-7xl mx-auto p-6">
                    {/* Tab Navigation */}
                    <div className="bg-white dark:bg-luna-primary rounded-xl border-2 border-luna-secondary/30 mb-6 overflow-x-auto">
                        <div className="flex border-b border-gray-200 dark:border-gray-700">
                            {[
                                { id: 'dashboard', label: '📊 Dashboard', icon: 'dashboard' },
                                { id: 'users', label: '👥 Users', icon: 'people' },
                                { id: 'topics', label: '📚 Law Topics', icon: 'book' },
                                { id: 'subscriptions', label: '💳 Subscriptions', icon: 'credit_card' },
                                { id: 'notifications', label: '🔔 Notifications', icon: 'notifications' },
                                { id: 'security', label: '🔐 Security', icon: 'security' },
                                { id: 'system', label: '⚙️ System', icon: 'settings' },
                                { id: 'content', label: '📝 Content', icon: 'edit_document' }
                            ].map(tab => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id as any)}
                                    className={`px-6 py-4 font-bold text-sm border-b-2 transition-all ${activeTab === tab.id
                                        ? 'border-luna-secondary text-luna-secondary bg-luna-secondary/10'
                                        : 'border-transparent text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                                        }`}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Tab Content */}
                    <div className="bg-white dark:bg-luna-primary rounded-xl border-2 border-luna-secondary/30 p-6 min-h-[500px]">
                        {loading && (
                            <div className="flex items-center justify-center py-12">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-luna-secondary"></div>
                            </div>
                        )}

                        {!loading && (
                            <>
                                {/* Dashboard Tab */}
                                {activeTab === 'dashboard' && (
                                    <div>
                                        <h2 className="text-2xl font-black mb-6 text-luna-text-light dark:text-luna-text-dark">Overview</h2>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                                            <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-xl p-6 border-2 border-blue-300">
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <p className="text-blue-600 dark:text-blue-300 text-sm font-medium">Total Users</p>
                                                        <p className="text-3xl font-black text-blue-800 dark:text-blue-100">{stats.totalUsers}</p>
                                                    </div>
                                                    <span className="material-symbols-outlined text-4xl text-blue-600">people</span>
                                                </div>
                                            </div>
                                            <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 rounded-xl p-6 border-2 border-green-300">
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <p className="text-green-600 dark:text-green-300 text-sm font-medium">Law Topics</p>
                                                        <p className="text-3xl font-black text-green-800 dark:text-green-100">{stats.totalTopics}</p>
                                                    </div>
                                                    <span className="material-symbols-outlined text-4xl text-green-600">book</span>
                                                </div>
                                            </div>
                                            <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 rounded-xl p-6 border-2 border-purple-300">
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <p className="text-purple-600 dark:text-purple-300 text-sm font-medium">Subscriptions</p>
                                                        <p className="text-3xl font-black text-purple-800 dark:text-purple-100">{stats.totalSubscriptions}</p>
                                                    </div>
                                                    <span className="material-symbols-outlined text-4xl text-purple-600">credit_card</span>
                                                </div>
                                            </div>
                                            <div className="bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20 rounded-xl p-6 border-2 border-orange-300">
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <p className="text-orange-600 dark:text-orange-300 text-sm font-medium">Notifications</p>
                                                        <p className="text-3xl font-black text-orange-800 dark:text-orange-100">{stats.totalNotifications}</p>
                                                    </div>
                                                    <span className="material-symbols-outlined text-4xl text-orange-600">notifications</span>
                                                </div>
                                            </div>
                                            <div className="bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20 rounded-xl p-6 border-2 border-red-300">
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <p className="text-red-600 dark:text-red-300 text-sm font-medium">Security Events</p>
                                                        <p className="text-3xl font-black text-red-800 dark:text-red-100">{stats.securityEvents}</p>
                                                    </div>
                                                    <span className="material-symbols-outlined text-4xl text-red-600">security</span>
                                                </div>
                                            </div>
                                            <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 dark:from-indigo-900/20 dark:to-indigo-800/20 rounded-xl p-6 border-2 border-indigo-300">
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <p className="text-indigo-600 dark:text-indigo-300 text-sm font-medium">Auto-Processing</p>
                                                        <p className={`text-xl font-black ${autoStatus ? 'text-green-800 dark:text-green-100' : 'text-gray-600 dark:text-gray-300'}`}>
                                                            {autoStatus === null ? 'Checking...' : autoStatus ? '✅ ENABLED' : '❌ DISABLED'}
                                                        </p>
                                                    </div>
                                                    <span className="material-symbols-outlined text-4xl text-indigo-600">autorenew</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="bg-gradient-to-br from-luna-secondary/10 to-luna-secondary/5 rounded-xl p-6 border-2 border-luna-secondary/30">
                                                <h3 className="text-xl font-bold mb-4">⚡ Quick Actions</h3>
                                                <div className="space-y-3">
                                                    <button
                                                        onClick={async () => {
                                                            setLoading(true);
                                                            try {
                                                                const { data, error } = await supabase.rpc('enable_auto_processing');
                                                                if (error) throw error;
                                                                setMessage({ type: 'success', text: data.message || 'Enabled!' });
                                                                setAutoStatus(true);
                                                                await logSecurityEvent('enable_auto_processing', 'system');
                                                            } catch (err: any) {
                                                                setMessage({ type: 'error', text: err.message || 'Failed' });
                                                                await logSecurityEvent('enable_auto_processing', 'system', null, false, err.message);
                                                            } finally {
                                                                setLoading(false);
                                                            }
                                                        }}
                                                        disabled={loading || autoStatus === true}
                                                        className="w-full px-4 py-3 bg-luna-secondary text-luna-primary font-bold rounded-lg disabled:opacity-50"
                                                    >
                                                        ✅ Enable Auto-Processing
                                                    </button>
                                                    <button
                                                        onClick={async () => {
                                                            setLoading(true);
                                                            try {
                                                                const { error } = await supabase.functions.invoke('process-law-notifications', { body: {} });
                                                                if (error) throw error;
                                                                setMessage({ type: 'success', text: 'Processed!' });
                                                                await logSecurityEvent('process_notifications', 'system');
                                                            } catch (err: any) {
                                                                setMessage({ type: 'error', text: err.message || 'Failed' });
                                                                await logSecurityEvent('process_notifications', 'system', null, false, err.message);
                                                            } finally {
                                                                setLoading(false);
                                                            }
                                                        }}
                                                        disabled={loading}
                                                        className="w-full px-4 py-3 bg-purple-600 text-white font-bold rounded-lg disabled:opacity-50"
                                                    >
                                                        🔔 Process Notifications Now
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Users Tab */}
                                {activeTab === 'users' && (
                                    <div>
                                        <div className="flex justify-between items-center mb-6">
                                            <h2 className="text-2xl font-black text-luna-text-light dark:text-luna-text-dark">Users Management</h2>
                                            <button onClick={loadUsers} className="px-4 py-2 bg-luna-secondary text-luna-primary font-bold rounded-lg">
                                                🔄 Refresh
                                            </button>
                                        </div>
                                        <div className="overflow-x-auto">
                                            <table className="w-full">
                                                <thead className="bg-gray-100 dark:bg-gray-800">
                                                    <tr>
                                                        <th className="px-4 py-3 text-left font-bold">Name</th>
                                                        <th className="px-4 py-3 text-left font-bold">Email</th>
                                                        <th className="px-4 py-3 text-left font-bold">Role</th>
                                                        <th className="px-4 py-3 text-left font-bold">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {users.map(user => (
                                                        <tr key={user.id} className="border-b border-gray-200 dark:border-gray-700">
                                                            <td className="px-4 py-3">{user.first_name} {user.last_name}</td>
                                                            <td className="px-4 py-3">{user.id}</td>
                                                            <td className="px-4 py-3">
                                                                <span className={`px-2 py-1 rounded text-xs font-bold ${user.role === 'super_admin' ? 'bg-red-100 text-red-800' :
                                                                    user.role === 'admin' ? 'bg-blue-100 text-blue-800' :
                                                                        'bg-gray-100 text-gray-800'
                                                                    }`}>
                                                                    {user.role || 'user'}
                                                                </span>
                                                            </td>
                                                            <td className="px-4 py-3">
                                                                <button
                                                                    onClick={async () => {
                                                                        const newRole = user.role === 'admin' ? 'user' : 'admin';
                                                                        try {
                                                                            const { error } = await supabase.from('profiles').update({ role: newRole }).eq('id', user.id);
                                                                            if (error) throw error;
                                                                            setMessage({ type: 'success', text: `Role updated to ${newRole}` });
                                                                            await logSecurityEvent('update_user_role', 'user', user.id);
                                                                            loadUsers();
                                                                        } catch (err: any) {
                                                                            setMessage({ type: 'error', text: err.message });
                                                                        }
                                                                    }}
                                                                    className="px-3 py-1 bg-blue-600 text-white text-xs font-bold rounded"
                                                                >
                                                                    Change Role
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}

                                {/* Topics Tab */}
                                {activeTab === 'topics' && (
                                    <div>
                                        <div className="flex justify-between items-center mb-6">
                                            <h2 className="text-2xl font-black text-luna-text-light dark:text-luna-text-dark">Law Topics Management</h2>
                                            <button onClick={loadTopics} className="px-4 py-2 bg-luna-secondary text-luna-primary font-bold rounded-lg">
                                                🔄 Refresh
                                            </button>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {topics.map(topic => (
                                                <div key={topic.id} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                                                    <div className="flex justify-between items-start mb-2">
                                                        <h3 className="font-bold text-lg">{topic.name}</h3>
                                                        <span className={`px-2 py-1 rounded text-xs ${topic.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                                                            {topic.is_active ? 'Active' : 'Inactive'}
                                                        </span>
                                                    </div>
                                                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">{topic.description}</p>
                                                    <p className="text-xs text-gray-500 font-mono">{topic.slug}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Subscriptions Tab */}
                                {activeTab === 'subscriptions' && (
                                    <div>
                                        <h2 className="text-2xl font-black mb-6 text-luna-text-light dark:text-luna-text-dark">Subscriptions</h2>
                                        <div className="overflow-x-auto">
                                            <table className="w-full">
                                                <thead className="bg-gray-100 dark:bg-gray-800">
                                                    <tr>
                                                        <th className="px-4 py-3 text-left font-bold">Plan</th>
                                                        <th className="px-4 py-3 text-left font-bold">Status</th>
                                                        <th className="px-4 py-3 text-left font-bold">Created</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {subscriptions.map(sub => (
                                                        <tr key={sub.id} className="border-b border-gray-200 dark:border-gray-700">
                                                            <td className="px-4 py-3">{sub.plan_name}</td>
                                                            <td className="px-4 py-3">
                                                                <span className={`px-2 py-1 rounded text-xs font-bold ${sub.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                                                                    }`}>
                                                                    {sub.status}
                                                                </span>
                                                            </td>
                                                            <td className="px-4 py-3">{new Date(sub.created_at).toLocaleDateString()}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}

                                {/* Notifications Tab */}
                                {activeTab === 'notifications' && (
                                    <div>
                                        <h2 className="text-2xl font-black mb-6 text-luna-text-light dark:text-luna-text-dark">Notifications</h2>
                                        <div className="space-y-4">
                                            {notifications.map(notif => (
                                                <div key={notif.id} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                                                    <div className="flex justify-between items-start">
                                                        <div>
                                                            <h3 className="font-bold">{notif.title}</h3>
                                                            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">{notif.summary}</p>
                                                        </div>
                                                        <span className={`px-2 py-1 rounded text-xs ${notif.email_sent ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                                            {notif.email_sent ? 'Sent' : 'Pending'}
                                                        </span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Security Tab */}
                                {activeTab === 'security' && (
                                    <div>
                                        <h2 className="text-2xl font-black mb-6 text-luna-text-light dark:text-luna-text-dark">🔐 Security Center</h2>

                                        {/* Audit Logs */}
                                        <div className="mb-8">
                                            <h3 className="text-xl font-bold mb-4">Security Audit Logs</h3>
                                            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 max-h-96 overflow-y-auto">
                                                <table className="w-full text-sm">
                                                    <thead className="bg-gray-100 dark:bg-gray-700 sticky top-0">
                                                        <tr>
                                                            <th className="px-3 py-2 text-left font-bold">Time</th>
                                                            <th className="px-3 py-2 text-left font-bold">Action</th>
                                                            <th className="px-3 py-2 text-left font-bold">IP</th>
                                                            <th className="px-3 py-2 text-left font-bold">Status</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {auditLogs.map(log => (
                                                            <tr key={log.id} className="border-b border-gray-200 dark:border-gray-700">
                                                                <td className="px-3 py-2">{new Date(log.created_at).toLocaleString()}</td>
                                                                <td className="px-3 py-2 font-medium">{log.action}</td>
                                                                <td className="px-3 py-2 font-mono text-xs">{log.ip_address}</td>
                                                                <td className="px-3 py-2">
                                                                    <span className={`px-2 py-1 rounded text-xs ${log.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                                        {log.success ? '✅' : '❌'}
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>

                                        {/* IP Whitelist */}
                                        <div className="mb-8">
                                            <div className="flex justify-between items-center mb-4">
                                                <h3 className="text-xl font-bold">IP Whitelist</h3>
                                                <button
                                                    onClick={async () => {
                                                        const ip = prompt('Enter IP address to whitelist:');
                                                        if (ip) {
                                                            try {
                                                                const { error } = await supabase.from('ip_whitelist').insert({ ip_address: ip, description: 'Manual entry' });
                                                                if (error) throw error;
                                                                setMessage({ type: 'success', text: 'IP added to whitelist' });
                                                                await logSecurityEvent('add_ip_whitelist', 'security', null, true);
                                                                loadIpWhitelist();
                                                            } catch (err: any) {
                                                                setMessage({ type: 'error', text: err.message });
                                                            }
                                                        }
                                                    }}
                                                    className="px-4 py-2 bg-green-600 text-white font-bold rounded-lg"
                                                >
                                                    + Add IP
                                                </button>
                                            </div>
                                            <div className="space-y-2">
                                                {ipWhitelist.map(ip => (
                                                    <div key={ip.id} className="flex justify-between items-center bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                                                        <div>
                                                            <p className="font-mono font-bold">{ip.ip_address}</p>
                                                            <p className="text-sm text-gray-600 dark:text-gray-300">{ip.description}</p>
                                                        </div>
                                                        <button
                                                            onClick={async () => {
                                                                try {
                                                                    const { error } = await supabase.from('ip_whitelist').delete().eq('id', ip.id);
                                                                    if (error) throw error;
                                                                    setMessage({ type: 'success', text: 'IP removed' });
                                                                    await logSecurityEvent('remove_ip_whitelist', 'security', ip.id);
                                                                    loadIpWhitelist();
                                                                } catch (err: any) {
                                                                    setMessage({ type: 'error', text: err.message });
                                                                }
                                                            }}
                                                            className="px-3 py-1 bg-red-600 text-white text-sm font-bold rounded"
                                                        >
                                                            Remove
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Active Sessions */}
                                        <div>
                                            <h3 className="text-xl font-bold mb-4">Active Sessions</h3>
                                            <div className="space-y-2">
                                                {sessions.map(session => (
                                                    <div key={session.id} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                                                        <div className="flex justify-between items-center">
                                                            <div>
                                                                <p className="font-mono text-sm">{session.ip_address}</p>
                                                                <p className="text-xs text-gray-600 dark:text-gray-300">
                                                                    Last activity: {new Date(session.last_activity).toLocaleString()}
                                                                </p>
                                                            </div>
                                                            <span className={`px-2 py-1 rounded text-xs ${session.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                                                                {session.is_active ? 'Active' : 'Inactive'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* System Tab */}
                                {activeTab === 'system' && (
                                    <div>
                                        <h2 className="text-2xl font-black mb-6 text-luna-text-light dark:text-luna-text-dark">⚙️ System Settings</h2>
                                        <div className="space-y-6">
                                            <div className="bg-gradient-to-br from-luna-secondary/10 to-luna-secondary/5 rounded-xl p-6 border-2 border-luna-secondary/30">
                                                <h3 className="text-xl font-bold mb-4">Auto-Processing</h3>
                                                <div className="flex gap-3 mb-4">
                                                    <button
                                                        onClick={async () => {
                                                            setLoading(true);
                                                            try {
                                                                const { data, error } = await supabase.rpc('enable_auto_processing');
                                                                if (error) throw error;
                                                                setMessage({ type: 'success', text: data.message || 'Enabled!' });
                                                                setAutoStatus(true);
                                                                await logSecurityEvent('enable_auto_processing', 'system');
                                                            } catch (err: any) {
                                                                setMessage({ type: 'error', text: err.message || 'Failed' });
                                                                await logSecurityEvent('enable_auto_processing', 'system', null, false, err.message);
                                                            } finally {
                                                                setLoading(false);
                                                            }
                                                        }}
                                                        disabled={loading || autoStatus === true}
                                                        className="px-6 py-3 bg-luna-secondary text-luna-primary font-bold rounded-lg disabled:opacity-50"
                                                    >
                                                        ✅ Enable
                                                    </button>
                                                    <button
                                                        onClick={async () => {
                                                            setLoading(true);
                                                            try {
                                                                const { error } = await supabase.rpc('disable_auto_processing');
                                                                if (error) throw error;
                                                                setMessage({ type: 'success', text: 'Disabled!' });
                                                                setAutoStatus(false);
                                                                await logSecurityEvent('disable_auto_processing', 'system');
                                                            } catch (err: any) {
                                                                setMessage({ type: 'error', text: err.message || 'Failed' });
                                                                await logSecurityEvent('disable_auto_processing', 'system', null, false, err.message);
                                                            } finally {
                                                                setLoading(false);
                                                            }
                                                        }}
                                                        disabled={loading || autoStatus === false}
                                                        className="px-6 py-3 bg-red-600 text-white font-bold rounded-lg disabled:opacity-50"
                                                    >
                                                        ❌ Disable
                                                    </button>
                                                    <button onClick={checkStatus} className="px-6 py-3 border-2 border-luna-secondary text-luna-secondary font-bold rounded-lg">
                                                        🔄 Check Status
                                                    </button>
                                                </div>
                                                {autoStatus !== null && (
                                                    <p className={`text-sm font-medium ${autoStatus ? 'text-green-600' : 'text-gray-600'}`}>
                                                        Status: {autoStatus ? '✅ ENABLED' : '❌ DISABLED'}
                                                    </p>
                                                )}
                                            </div>

                                            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                                                <h3 className="text-xl font-bold mb-4">Quick Actions</h3>
                                                <div className="grid grid-cols-2 gap-3">
                                                    <button
                                                        onClick={async () => {
                                                            setLoading(true);
                                                            try {
                                                                const { error } = await supabase.functions.invoke('process-law-notifications', { body: {} });
                                                                if (error) throw error;
                                                                setMessage({ type: 'success', text: 'Processed!' });
                                                                await logSecurityEvent('process_notifications', 'system');
                                                            } catch (err: any) {
                                                                setMessage({ type: 'error', text: err.message || 'Failed' });
                                                                await logSecurityEvent('process_notifications', 'system', null, false, err.message);
                                                            } finally {
                                                                setLoading(false);
                                                            }
                                                        }}
                                                        disabled={loading}
                                                        className="px-4 py-3 bg-purple-600 text-white font-bold rounded-lg disabled:opacity-50"
                                                    >
                                                        🔔 Process Now
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Content Tab */}
                                {activeTab === 'content' && (
                                    <div>
                                        <h2 className="text-2xl font-black mb-6 text-luna-text-light dark:text-luna-text-dark">📝 Content Management</h2>
                                        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 rounded-lg p-4">
                                            <p className="text-yellow-800 dark:text-yellow-200">
                                                <strong>Content Management</strong><br />
                                                <p className="text-sm text-luna-text-light/70 dark:text-luna-text-muted-dark mt-2">
                                                    Content management features are available through the Admin Panel.
                                                    Use the Admin Dashboard to manage news updates, topics, and notifications.
                                                </p>
                                                This section will allow you to manage templates, content, and SEO settings.
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

// Helper function to get display name for plan
const PRICE_ID_PLAN_MAP: Record<string, string> = Object.entries(PLAN_PRICE_IDS).reduce(
    (map, [planId, priceIds]) => {
        // Handle both object and array types for priceIds
        if (typeof priceIds === 'object' && priceIds !== null) {
            Object.values(priceIds).forEach((priceId: string | undefined) => {
                if (priceId) {
                    map[priceId] = planId;
                }
            });
        }
        return map;
    },
    {} as Record<string, string>
);

const slugifyPlanId = (value?: string | null) => {
    if (!value) return null;
    return value
        .toString()
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_|_$/g, '');
};

const normalizePlanName = (planName?: string | null, stripePriceId?: string | null): string => {
    const sanitizedPlan = slugifyPlanId(planName);
    const isFreeLike = sanitizedPlan === 'free' || sanitizedPlan === 'free (trial)';
    if (sanitizedPlan && !isFreeLike && sanitizedPlan !== 'unknown') {
        return sanitizedPlan;
    }

    if (stripePriceId && PRICE_ID_PLAN_MAP[stripePriceId]) {
        return PRICE_ID_PLAN_MAP[stripePriceId];
    }

    if (isFreeLike || sanitizedPlan === 'unknown') {
        return 'free';
    }

    return sanitizedPlan || 'free';
};

const getPlanDisplayName = (planName: string | null | undefined): string => {
    if (!planName) return 'Free (Trial)';

    const planNameMap: Record<string, string> = {
        'free': 'Free (Trial)',
        'civilian_plus': 'Civilian Plus',
        'civilian_pro': 'Civilian Pro',
        'lawyer_pro': 'Lawyer Pro',
        'firm': 'Firm Plan',
        'insurance_partnership': 'Insurance Partnership',
    };

    // Check if it's a known plan name
    if (planNameMap[planName.toLowerCase()]) {
        return planNameMap[planName.toLowerCase()];
    }

    // Fallback: format the plan name nicely (replace underscores, capitalize)
    return planName
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
};

const DEFAULT_NOTIFICATION_PREFS = {
    caseUpdates: true,
    newMessages: true,
    productNews: false,
    newsletterWeekly: false,
};

type NotificationPreferences = {
    caseUpdates: boolean;
    newMessages: boolean;
    productNews: boolean;
    newsletterWeekly: boolean;
};
type NotificationKey = keyof NotificationPreferences;

const NOTIFICATION_OPTIONS: Array<{ id: string; key: NotificationKey; label: string; description?: string }> = [
    {
        id: 'case-updates',
        key: 'caseUpdates',
        label: 'Case Updates',
        description: 'Get notified when case statuses, tasks, or deadlines change.',
    },
    {
        id: 'new-messages',
        key: 'newMessages',
        label: 'New Messages',
        description: 'Alerts when new collaboration messages arrive.',
    },
    {
        id: 'product-news',
        key: 'productNews',
        label: 'Product News & Updates',
        description: 'Occasional announcements about new releases and improvements.',
    },
    {
        id: 'newsletter-weekly',
        key: 'newsletterWeekly',
        label: 'Weekly Newsletter',
        description: 'Curated legal news and LegalRadar tips delivered once a week.',
    },
];

const areNotificationPrefsEqual = (a: NotificationPreferences, b: NotificationPreferences) =>
    Object.keys(DEFAULT_NOTIFICATION_PREFS).every((key) => a[key as NotificationKey] === b[key as NotificationKey]);

type SettingsCardProps = {
    icon?: string;
    title: string;
    description?: React.ReactNode;
    actions?: React.ReactNode;
    className?: string;
    children: React.ReactNode;
};

function SettingsCard({ icon, title, description, actions, className, children }: SettingsCardProps) {
    return (
        <section
            className={`bg-white dark:bg-luna-primary rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 shadow-sm p-6 ${className ?? ''}`}
        >
            <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                <div className="flex items-start gap-3">
                    {icon && <span className="material-symbols-outlined text-luna-secondary">{icon}</span>}
                    <div>
                        <h2 className="text-xl font-bold text-luna-text-light dark:text-luna-text-dark">{title}</h2>
                        {description && (
                            <div className="text-sm text-luna-text-light/60 dark:text-luna-text-muted-dark mt-1">{description}</div>
                        )}
                    </div>
                </div>
                {actions && <div className="flex-shrink-0">{actions}</div>}
            </div>
            {children}
        </section>
    );
}

type ToggleRowProps = {
    id: string;
    label: React.ReactNode;
    description?: React.ReactNode;
    checked: boolean;
    disabled?: boolean;
    onChange: (checked: boolean) => void;
};

function ToggleRow({ id, label, description, checked, disabled, onChange }: ToggleRowProps) {
    return (
        <label
            htmlFor={id}
            className={`flex items-start gap-3 rounded-lg p-2 ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:bg-luna-bg-light/80 dark:hover:bg-luna-bg-dark/80'
                } transition-colors`}
        >
            <input
                id={id}
                type="checkbox"
                checked={checked}
                disabled={disabled}
                onChange={(event) => onChange(event.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-luna-secondary focus:ring-luna-secondary"
            />
            <div>
                <div className="text-sm font-semibold text-luna-text-light dark:text-luna-text-dark">{label}</div>
                {description && (
                    <div className="text-xs text-luna-text-light/60 dark:text-luna-text-dark/60 mt-1 leading-relaxed">{description}</div>
                )}
            </div>
        </label>
    );
}

const SettingsPage: React.FC<{ session: Session; profile: Profile | null; onProfileUpdate: (updates: Partial<Profile>) => Promise<void>; onNavigate?: (page: string) => void }> = ({ session, profile, onProfileUpdate, onNavigate }) => {
    const [theme, setTheme] = useState<'light' | 'dark'>(() => {
        // Load theme from localStorage
        const savedTheme = localStorage.getItem('lrdene_theme');
        return (savedTheme === 'light' || savedTheme === 'dark') ? savedTheme : 'dark';
    });
    const [subscriptionDetails, setSubscriptionDetails] = useState(profile?.subscription ?? null);
    const [updatingSubscription, setUpdatingSubscription] = useState(false);
    const [refreshingSubscription, setRefreshingSubscription] = useState(false);

    // Update subscriptionDetails when profile changes
    useEffect(() => {
        setSubscriptionDetails(profile?.subscription ?? null);
    }, [profile]);

    // Notification preferences state
    const [notifications, setNotifications] = useState<NotificationPreferences>(() => ({
        ...DEFAULT_NOTIFICATION_PREFS,
    }));

    // Legal update preferences state (GDPR-compliant consent)
    const [legalUpdatePrefs, setLegalUpdatePrefs] = useState({
        email: false,
        inApp: true,
    });
    const [loadingConsent, setLoadingConsent] = useState(true);



    // Legal radar notification topics
    const [radarTopics, setRadarTopics] = useState<NotificationTopic[]>([]);
    const [radarSettings, setRadarSettings] = useState<Record<string, { email: boolean; inApp: boolean }>>({});
    const [loadingRadarTopics, setLoadingRadarTopics] = useState(true);
    const [updatingRadarPreference, setUpdatingRadarPreference] = useState<string | null>(null);
    const [radarMessage, setRadarMessage] = useState<string | null>(null);

    // AI provider preference state
    const [aiProvider, setAiProvider] = useState<'gemini' | 'openai' | null>(null);
    const aiProviderOptionIds = {
        gemini: 'ai-provider-gemini',
        openai: 'ai-provider-openai',
        auto: 'ai-provider-auto',
    };

    // Law topic subscriptions state
    const [topics, setTopics] = useState<LawTopic[]>([]);
    const [subscriptions, setSubscriptions] = useState<Map<string, TopicSubscription>>(new Map());
    const [loadingTopics, setLoadingTopics] = useState(true);
    const [updatingSubscriptions, setUpdatingSubscriptions] = useState<Set<string>>(new Set());

    const [saving, setSaving] = useState(false);
    const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    const successMessage = saveMessage?.type === 'success' ? saveMessage : null;
    const errorMessage = saveMessage?.type === 'error' ? saveMessage : null;

    const savedNotificationPrefs = useMemo<NotificationPreferences>(
        (): NotificationPreferences => ({
            caseUpdates: profile?.notification_preferences?.caseUpdates ?? DEFAULT_NOTIFICATION_PREFS.caseUpdates,
            newMessages: profile?.notification_preferences?.newMessages ?? DEFAULT_NOTIFICATION_PREFS.newMessages,
            productNews: profile?.notification_preferences?.productNews ?? DEFAULT_NOTIFICATION_PREFS.productNews,
            newsletterWeekly: profile?.notification_preferences?.newsletterWeekly ?? DEFAULT_NOTIFICATION_PREFS.newsletterWeekly,
        }),
        [profile?.notification_preferences]
    );

    const savedAiProvider = useMemo<Profile['ai_provider_preference']>(() => profile?.ai_provider_preference ?? null, [profile?.ai_provider_preference]);

    const hasUnsavedChanges = useMemo(() => {
        const hasNotificationChanges = !areNotificationPrefsEqual(notifications, savedNotificationPrefs);
        const hasAiProviderChanges = (savedAiProvider ?? null) !== (aiProvider ?? null);
        return hasNotificationChanges || hasAiProviderChanges;
    }, [notifications, savedNotificationPrefs, aiProvider, savedAiProvider]);

    // Load saved preferences from database on mount
    useEffect(() => {
        const savedPrefs = profile?.notification_preferences ?? {};
        setNotifications({
            caseUpdates: savedPrefs.caseUpdates ?? DEFAULT_NOTIFICATION_PREFS.caseUpdates,
            newMessages: savedPrefs.newMessages ?? DEFAULT_NOTIFICATION_PREFS.newMessages,
            productNews: savedPrefs.productNews ?? DEFAULT_NOTIFICATION_PREFS.productNews,
            newsletterWeekly: savedPrefs.newsletterWeekly ?? DEFAULT_NOTIFICATION_PREFS.newsletterWeekly,
        });
        setAiProvider(profile?.ai_provider_preference ?? null);
    }, [profile]);

    // Load legal update consent status (GDPR compliance)
    useEffect(() => {
        const loadConsent = async () => {
            if (!session?.user.id) {
                setLoadingConsent(false);
                return;
            }

            try {
                // Check email consent
                const { data: emailConsent } = await supabase.rpc('has_active_consent', {
                    p_user_id: session.user.id,
                    p_consent_type: 'legal_updates_email'
                });

                // Check in-app consent (defaults to true, can be disabled)
                const { data: inAppConsent } = await supabase.rpc('has_active_consent', {
                    p_user_id: session.user.id,
                    p_consent_type: 'legal_updates_in_app'
                });

                setLegalUpdatePrefs({
                    email: emailConsent || false,
                    inApp: inAppConsent !== false, // Default to true if not set
                });
            } catch (error) {
                console.error('Error loading consent:', error);
                // If consent table doesn't exist yet, default values are fine
            } finally {
                setLoadingConsent(false);
            }
        };

        loadConsent();
    }, [session]);

    // Load legal radar topics + preferences
    useEffect(() => {
        const loadRadarPreferences = async () => {
            if (!session?.user.id) {
                setRadarTopics([]);
                setRadarSettings({});
                setLoadingRadarTopics(false);
                return;
            }

            setLoadingRadarTopics(true);
            setRadarMessage(null);

            try {
                const { data: topicData, error: topicError } = await supabase
                    .from('notification_topics')
                    .select('id, key, label, description')
                    .eq('is_active', true)
                    .order('label');

                if (topicError) throw topicError;

                setRadarTopics(topicData ?? []);

                if (!topicData || topicData.length === 0) {
                    setRadarSettings({});
                    return;
                }

                const { data: settingData, error: settingsError } = await supabase
                    .from('user_notification_settings')
                    .select('topic_id, channel, enabled')
                    .eq('user_id', session.user.id);

                if (settingsError) throw settingsError;

                const nextSettings: Record<string, { email: boolean; inApp: boolean }> = {};
                topicData.forEach((topic) => {
                    nextSettings[topic.id] = {
                        email: false,
                        inApp: true,
                    };
                });

                (settingData ?? []).forEach((setting) => {
                    const current = nextSettings[setting.topic_id] ?? { email: false, inApp: true };
                    if (setting.channel === 'email') {
                        current.email = setting.enabled;
                    } else {
                        current.inApp = setting.enabled;
                    }
                    nextSettings[setting.topic_id] = current;
                });

                setRadarSettings(nextSettings);
            } catch (error) {
                console.error('Error loading notification topics:', error);
                setRadarMessage('Legal Radar preferences require the latest database migration.');
            } finally {
                setLoadingRadarTopics(false);
            }
        };

        loadRadarPreferences();
    }, [session?.user.id]);

    // Load law topics and subscriptions
    useEffect(() => {
        const loadTopicsAndSubscriptions = async () => {
            if (!session?.user.id) return;

            setLoadingTopics(true);
            try {
                // Load all active topics
                const { data: topicsData, error: topicsError } = await supabase
                    .from('law_topics')
                    .select('id, name, slug, description, rss_feed_url, is_active')
                    .eq('is_active', true)
                    .order('name');

                // Check if tables exist (migration not run yet)
                if (topicsError) {
                    // Check if it's a "relation does not exist" error
                    if (topicsError.message?.includes('does not exist') ||
                        topicsError.message?.includes('relation') ||
                        topicsError.code === '42P01') {
                        console.warn('Law topics table does not exist. Migration 0017 needs to be run.');
                        // Set empty arrays - feature not available yet
                        setTopics([]);
                        setSubscriptions(new Map());
                        setLoadingTopics(false);
                        return;
                    }
                    throw topicsError;
                }

                setTopics(topicsData || []);

                // Load user's subscriptions
                const { data: subsData, error: subsError } = await supabase
                    .from('user_topic_subscriptions')
                    .select('id, topic_id, email_enabled')
                    .eq('user_id', session.user.id);

                if (subsError) {
                    // Check if it's a "relation does not exist" error
                    if (subsError.message?.includes('does not exist') ||
                        subsError.message?.includes('relation') ||
                        subsError.code === '42P01') {
                        console.warn('User topic subscriptions table does not exist. Migration 0017 needs to be run.');
                        // Set empty map - feature not available yet
                        setSubscriptions(new Map());
                        setLoadingTopics(false);
                        return;
                    }
                    throw subsError;
                }

                // Convert to Map for easy lookup
                const subsMap = new Map<string, TopicSubscription>();
                (subsData || []).forEach((sub: TopicSubscription) => {
                    subsMap.set(sub.topic_id, sub);
                });
                setSubscriptions(subsMap);
            } catch (error) {
                console.error('Error loading topics:', error);
                const errorMessage = error instanceof Error ? error.message : 'Unknown error';

                // Provide helpful error message
                if (errorMessage.includes('does not exist') || errorMessage.includes('relation')) {
                    setSaveMessage({
                        type: 'error',
                        text: 'EU Law Updates feature is not set up yet. Please run migration 0017_create_law_notification_system.sql'
                    });
                } else if (errorMessage.includes('permission') || errorMessage.includes('RLS')) {
                    setSaveMessage({
                        type: 'error',
                        text: 'Permission denied. Please check Row-Level Security policies.'
                    });
                } else {
                    setSaveMessage({
                        type: 'error',
                        text: `Failed to load topic subscriptions: ${errorMessage}`
                    });
                }

                // Set empty state so UI doesn't break
                setTopics([]);
                setSubscriptions(new Map());
            } finally {
                setLoadingTopics(false);
            }
        };

        loadTopicsAndSubscriptions();
    }, [session]);

    // Apply theme to document
    useEffect(() => {
        document.documentElement.classList.toggle('dark', theme === 'dark');
        localStorage.setItem('lrdene_theme', theme);
    }, [theme]);

    const handleThemeChange = (newTheme: 'light' | 'dark') => {
        setTheme(newTheme);
    };

    const refreshSubscriptionFromDb = useCallback(async () => {
        if (!session?.user) return null;
        const userId = session.user.id;

        // Use the same comprehensive approach as fetchProfile to ensure we get the latest subscription data
        const { data: { user: userData } } = await supabase.auth.getUser();

        // Fetch profile data
        const { data: profileData, error: profileError } = await supabase
            .from('profiles')
            .select(`id, first_name, last_name, role, avatar_url, firm, notification_preferences, subscription_id`)
            .eq('id', userId)
            .single();

        if (profileError) {
            throw profileError;
        }

        // Check for unlinked subscription by email (if user just signed up after checkout)
        // OR if subscription exists but profile doesn't have subscription_id
        if (userData?.email) {
            // Check for unlinked subscription by email (user_id is null)
            const { data: unlinkedSub } = await supabase
                .from('subscriptions')
                .select('id, user_id')
                .eq('customer_email', userData.email)
                .order('created_at', { ascending: false })
                .limit(1)
                .maybeSingle();

            if (unlinkedSub) {
                // If subscription exists but isn't linked to this user
                if (!unlinkedSub.user_id || unlinkedSub.user_id !== userId) {
                    console.log('[refreshSubscriptionFromDb] Found unlinked subscription, linking to user:', userId);
                    // Link the subscription to this user
                    const { error: linkError } = await supabase
                        .from('subscriptions')
                        .update({ user_id: userId })
                        .eq('id', unlinkedSub.id);

                    if (!linkError) {
                        // Update profile with subscription_id
                        const { error: profileUpdateError } = await supabase
                            .from('profiles')
                            .update({ subscription_id: unlinkedSub.id })
                            .eq('id', userId);

                        if (!profileUpdateError) {
                            console.log('[refreshSubscriptionFromDb] ✅ Successfully linked subscription to profile');
                            // Update profileData for immediate display
                            profileData.subscription_id = unlinkedSub.id;
                        } else {
                            console.error('[refreshSubscriptionFromDb] Error updating profile with subscription:', profileUpdateError);
                        }
                    } else {
                        console.error('[refreshSubscriptionFromDb] Error linking subscription:', linkError);
                    }
                } else if (!profileData.subscription_id && unlinkedSub.user_id === userId) {
                    // Subscription is linked to user but profile doesn't have subscription_id
                    console.log('[refreshSubscriptionFromDb] Subscription linked to user but profile missing subscription_id, updating...');
                    const { error: profileUpdateError } = await supabase
                        .from('profiles')
                        .update({ subscription_id: unlinkedSub.id })
                        .eq('id', userId);

                    if (!profileUpdateError) {
                        console.log('[refreshSubscriptionFromDb] ✅ Updated profile with subscription_id');
                        profileData.subscription_id = unlinkedSub.id;
                    } else {
                        console.error('[refreshSubscriptionFromDb] Error updating profile:', profileUpdateError);
                    }
                }
            }
        }

        const subscriptionSelectFields = 'id, user_id, status, plan_name, current_period_end, cancel_at_period_end, stripe_subscription_id, stripe_price_id, created_at';
        const activeSubscriptionStatuses = ['active', 'trialing', 'past_due'];

        const fetchLatestSubscription = async (column: 'user_id' | 'customer_email', value: string, options?: { nonFreeOnly?: boolean }) => {
            let query = supabase
                .from('subscriptions')
                .select(subscriptionSelectFields)
                .eq(column, value)
                .in('status', activeSubscriptionStatuses)
                .order('created_at', { ascending: false })
                .limit(1);

            if (options?.nonFreeOnly) {
                query = query.neq('plan_name', 'free');
            }

            const { data } = await query.maybeSingle();
            return data || null;
        };

        const linkProfileToSubscription = async (subscriptionId: string) => {
            if (profileData.subscription_id === subscriptionId) return;
            const { error: linkError } = await supabase
                .from('profiles')
                .update({ subscription_id: subscriptionId })
                .eq('id', userId);
            if (linkError) {
                console.error('[refreshSubscriptionFromDb] Failed to link profile to subscription:', linkError);
                return;
            }
            profileData.subscription_id = subscriptionId;
        };

        const isMeaningfulPaidSubscription = (sub: any | null) => {
            if (!sub) return false;
            const normalizedPlan = normalizePlanName(sub.plan_name, sub.stripe_price_id);
            return normalizedPlan !== 'free' && normalizedPlan !== 'unknown' && !!sub.stripe_subscription_id;
        };

        // Fetch subscription if subscription_id exists
        // Also check for subscription by user_id or email if subscription_id is missing or outdated
        let subscription = null;
        if (profileData.subscription_id) {
            const { data: subData } = await supabase
                .from('subscriptions')
                .select(subscriptionSelectFields)
                .eq('id', profileData.subscription_id)
                .maybeSingle();

            if (subData) {
                subscription = {
                    ...subData,
                    plan_name: normalizePlanName(subData.plan_name, subData.stripe_price_id),
                };
            }
        }

        const shouldFindLatest = !subscription || subscription.plan_name === 'free' || !subscription.stripe_subscription_id;

        if (shouldFindLatest) {
            let latestSubscription = await fetchLatestSubscription('user_id', userId, { nonFreeOnly: true });
            if (!latestSubscription) {
                latestSubscription = await fetchLatestSubscription('user_id', userId);
            }

            if (!latestSubscription && userData?.email) {
                latestSubscription = await fetchLatestSubscription('customer_email', userData.email, { nonFreeOnly: true });
                if (!latestSubscription) {
                    latestSubscription = await fetchLatestSubscription('customer_email', userData.email);
                }
                if (latestSubscription && (!latestSubscription.user_id || latestSubscription.user_id !== userId)) {
                    const { error: attachError } = await supabase
                        .from('subscriptions')
                        .update({ user_id: userId })
                        .eq('id', latestSubscription.id);
                    if (attachError) {
                        console.error('[refreshSubscriptionFromDb] Failed to attach subscription to user:', attachError);
                    }
                }
            }

            if (latestSubscription && (isMeaningfulPaidSubscription(latestSubscription) || !subscription)) {
                subscription = {
                    ...latestSubscription,
                    plan_name: normalizePlanName(latestSubscription.plan_name, latestSubscription.stripe_price_id),
                };
                await linkProfileToSubscription(latestSubscription.id);
            }
        }

        const profileObj = {
            ...profileData,
            subscription: subscription,
        } as Profile;

        // Update the main profile state to ensure consistency
        if (onProfileUpdate) {
            try {
                // Update the main profile state with the refreshed data
                await onProfileUpdate(profileObj);
            } catch (error) {
                console.error('[refreshSubscriptionFromDb] Error updating main profile state:', error);
            }
        }

        // Also dispatch a global event to ensure all components are updated
        window.dispatchEvent(new CustomEvent('profileUpdated', { detail: profileObj }));

        // Update local subscription details state
        setSubscriptionDetails(subscription);

        return subscription;
    }, [session?.user?.id, onProfileUpdate]);

    const handleSubscriptionRefreshClick = async () => {
        if (!session?.user) {
            toast.info('Please sign in to sync your subscription.');
            return;
        }

        setRefreshingSubscription(true);
        try {
            const subscription = await refreshSubscriptionFromDb();
            window.dispatchEvent(new CustomEvent('refreshProfileRequested'));

            if (subscription) {
                if (subscriptionDetails) {
                    toast.success('Subscription refreshed successfully!', { duration: 3000 });
                } else {
                    toast.success('Subscription linked! You now have access to premium features.', { duration: 3000 });
                }
            } else {
                toast.info(
                    subscriptionDetails
                        ? 'No subscription changes detected. Try again later.'
                        : 'No subscription found yet. If you just upgraded, please wait a moment and try again.',
                    { duration: 4000 }
                );
            }
        } catch (error) {
            console.error('Error refreshing subscription:', error);
            toast.error('Unable to refresh subscription. Please try again.');
        } finally {
            setRefreshingSubscription(false);
        }
    };

    const handleSubscriptionAction = async (action: 'cancel' | 'resume', immediate = false) => {
        if (updatingSubscription) return;
        setUpdatingSubscription(true);
        try {
            const { data, error } = await supabase.functions.invoke('cancel-subscription', {
                body: { action, immediate },
            });

            if (error) {
                throw new Error(error.message || 'Failed to update subscription.');
            }

            if (data?.error) {
                throw new Error(data.error);
            }

            if (data?.subscription) {
                setSubscriptionDetails(data.subscription);
            }

            if (action === 'cancel') {
                toast.success(immediate ? 'Subscription canceled immediately.' : 'Subscription will end at the end of the billing period.');
            } else {
                toast.success('Subscription resumed successfully.');
            }
        } catch (err) {
            console.error('Subscription update failed:', err);
            toast.error(err instanceof Error ? err.message : 'Unable to update subscription. Please try again.');
        } finally {
            setUpdatingSubscription(false);
        }
    };

    const handleNotificationChange = (key: NotificationKey, value?: boolean) => {
        setNotifications(prev => ({
            ...prev,
            [key]: typeof value === 'boolean' ? value : !prev[key],
        }));
    };

    const handleRadarPreferenceChange = async (topicId: string, channel: 'email' | 'in_app', enabled: boolean) => {
        if (!session?.user.id) {
            toast.warning('You must be logged in.');
            return;
        }

        setUpdatingRadarPreference(`${topicId}-${channel}`);
        const consentVersion = 'legal-radar-2025-02';

        try {
            const { error: preferenceError } = await supabase.from('user_notification_settings').upsert({
                user_id: session.user.id,
                topic_id: topicId,
                channel,
                enabled,
                consent_version: consentVersion,
                consent_at: enabled ? new Date().toISOString() : null,
            });

            if (preferenceError) {
                throw preferenceError;
            }

            const payload = {
                topic_id: topicId,
                channel,
                enabled,
                consent_version: consentVersion,
            };

            await supabase.from('consent_log').insert({
                user_id: session.user.id,
                event_type: enabled ? 'legal_radar_opt_in' : 'legal_radar_opt_out',
                payload,
            });

            setRadarSettings((prev) => {
                const existing = prev[topicId] ?? { email: false, inApp: true };
                return {
                    ...prev,
                    [topicId]: {
                        email: channel === 'email' ? enabled : existing.email,
                        inApp: channel === 'in_app' ? enabled : existing.inApp,
                    },
                };
            });

            toast.success('Notification preference updated.');
        } catch (error) {
            console.error('Error updating radar preference:', error);
            toast.error('Unable to update preference. Please try again.');
        } finally {
            setUpdatingRadarPreference(null);
        }
    };

    // Handle legal update consent change (GDPR-compliant logging)
    const handleLegalUpdateConsent = async (type: 'email' | 'inApp', granted: boolean) => {
        if (!session?.user.id) {
            setSaveMessage({ type: 'error', text: 'You must be logged in to manage consent.' });
            return;
        }

        const consentType = type === 'email' ? 'legal_updates_email' : 'legal_updates_in_app';

        try {
            // Get IP address (if available, for audit trail)
            let ipAddress: string | null = null;
            try {
                const ipResponse = await fetch('https://api.ipify.org?format=json');
                const ipData = await ipResponse.json();
                ipAddress = ipData.ip;
            } catch (ipError) {
                // IP fetching is optional, continue without it
                console.warn('Could not fetch IP address:', ipError);
            }

            // Log consent change
            const { error: consentError } = await supabase.rpc('log_user_consent', {
                p_user_id: session.user.id,
                p_consent_type: consentType,
                p_granted: granted,
                p_ip_address: ipAddress,
                p_user_agent: navigator.userAgent,
                p_source: 'settings'
            });

            if (consentError) {
                // If consent table doesn't exist, show helpful message
                if (consentError.message?.includes('does not exist')) {
                    setSaveMessage({
                        type: 'error',
                        text: 'Consent tracking not set up. Please run migration 0029_add_consent_tracking.sql'
                    });
                    return;
                }
                throw consentError;
            }

            // Update local state
            setLegalUpdatePrefs(prev => ({ ...prev, [type]: granted }));

            // Show success message
            setSaveMessage({
                type: 'success',
                text: granted
                    ? `Consent for ${type === 'email' ? 'email' : 'in-app'} legal updates granted.`
                    : `Consent for ${type === 'email' ? 'email' : 'in-app'} legal updates revoked.`
            });

            // Clear message after 3 seconds
            setTimeout(() => setSaveMessage(null), 3000);
        } catch (error) {
            console.error('Error updating consent:', error);
            setSaveMessage({
                type: 'error',
                text: `Failed to update consent: ${error instanceof Error ? error.message : 'Unknown error'}`
            });
        }
    };

    // Toggle topic subscription
    const handleTopicSubscription = async (topicId: string, subscribe: boolean) => {
        if (!session?.user.id) {
            setSaveMessage({ type: 'error', text: 'You must be logged in to manage subscriptions.' });
            return;
        }

        // Check email consent before subscribing (GDPR compliance)
        if (subscribe) {
            try {
                const { data: hasEmailConsent } = await supabase.rpc('has_active_consent', {
                    p_user_id: session.user.id,
                    p_consent_type: 'legal_updates_email'
                });

                if (!hasEmailConsent) {
                    setSaveMessage({
                        type: 'error',
                        text: 'Please grant consent for email notifications in "Legal Information Updates" section above before subscribing to topics.'
                    });
                    return;
                }
            } catch (consentError) {
                // If consent check fails, allow subscription but warn
                console.warn('Could not check email consent:', consentError);
            }
        }

        setUpdatingSubscriptions(prev => new Set(prev).add(topicId));

        try {
            if (subscribe) {
                // Subscribe to topic (only enable email if consent is granted)
                let hasEmailConsent = false;
                try {
                    const { data } = await supabase.rpc('has_active_consent', {
                        p_user_id: session.user.id,
                        p_consent_type: 'legal_updates_email'
                    });
                    hasEmailConsent = Boolean(data);
                } catch (rpcError) {
                    console.warn('Consent check failed, defaulting to disabled email notifications:', rpcError);
                    hasEmailConsent = false;
                }

                const { data: newSubscription, error } = await supabase
                    .from('user_topic_subscriptions')
                    .insert({
                        user_id: session.user.id,
                        topic_id: topicId,
                        email_enabled: hasEmailConsent || false // Only enable if consent granted
                    })
                    .select()
                    .single();

                if (error) throw error;

                // Update local state with the returned subscription data
                if (newSubscription) {
                    setSubscriptions(prev => {
                        const newMap = new Map(prev);
                        newMap.set(topicId, {
                            id: newSubscription.id,
                            topic_id: topicId,
                            email_enabled: newSubscription.email_enabled
                        });
                        return newMap;
                    });
                }
            } else {
                // Unsubscribe from topic
                const subscription = subscriptions.get(topicId);
                if (subscription?.id) {
                    const { error } = await supabase
                        .from('user_topic_subscriptions')
                        .delete()
                        .eq('id', subscription.id);

                    if (error) throw error;
                }

                // Update local state
                setSubscriptions(prev => {
                    const newMap = new Map(prev);
                    newMap.delete(topicId);
                    return newMap;
                });
            }
        } catch (error) {
            console.error('Error updating subscription:', error);
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            setSaveMessage({
                type: 'error',
                text: `Failed to update subscription: ${errorMessage}. Please check the browser console for details.`
            });
            setTimeout(() => setSaveMessage(null), 5000);
        } finally {
            setUpdatingSubscriptions(prev => {
                const newSet = new Set(prev);
                newSet.delete(topicId);
                return newSet;
            });
        }
    };

    // Toggle email for a subscription
    const handleEmailToggle = async (topicId: string, enabled: boolean) => {
        if (!session?.user.id) return;

        const subscription = subscriptions.get(topicId);
        if (!subscription?.id) return;

        setUpdatingSubscriptions(prev => new Set(prev).add(topicId));

        try {
            const { error } = await supabase
                .from('user_topic_subscriptions')
                .update({ email_enabled: enabled })
                .eq('id', subscription.id);

            if (error) throw error;

            // Update local state
            setSubscriptions(prev => {
                const newMap = new Map<string, TopicSubscription>(prev);
                const sub = newMap.get(topicId);
                if (sub) {
                    newMap.set(topicId, {
                        id: sub.id,
                        topic_id: sub.topic_id,
                        email_enabled: enabled
                    });
                }
                return newMap;
            });
        } catch (error) {
            console.error('Error updating email preference:', error);
            setSaveMessage({
                type: 'error',
                text: 'Failed to update email preference.'
            });
            setTimeout(() => setSaveMessage(null), 3000);
        } finally {
            setUpdatingSubscriptions(prev => {
                const newSet = new Set(prev);
                newSet.delete(topicId);
                return newSet;
            });
        }
    };

    const handleSaveChanges = async () => {
        if (!session?.user.id) {
            setSaveMessage({ type: 'error', text: 'You must be logged in to save settings.' });
            return;
        }

        if (!hasUnsavedChanges) {
            setSaveMessage({ type: 'success', text: 'All settings are already synced.' });
            setTimeout(() => setSaveMessage(null), 2000);
            return;
        }

        setSaving(true);
        setSaveMessage(null);

        try {
            // Verify authentication first
            const { data: { user: currentUser }, error: authError } = await supabase.auth.getUser();

            if (authError || !currentUser || currentUser.id !== session.user.id) {
                throw new Error('Authentication error: Please refresh the page and try again.');
            }

            // Save notification preferences and AI provider preference to database
            const { error, data } = await supabase
                .from('profiles')
                .update({
                    notification_preferences: {
                        caseUpdates: notifications.caseUpdates,
                        newMessages: notifications.newMessages,
                        productNews: notifications.productNews,
                        newsletterWeekly: notifications.newsletterWeekly,
                    },
                    ai_provider_preference: aiProvider
                })
                .eq('id', session.user.id)
                .select();

            if (error) {
                console.error('Error saving settings - full error:', error);

                // Provide specific error messages based on error type
                let errorMessage = 'Failed to save settings. ';

                if (error.code === '42703' || error.message?.includes('column') || error.message?.includes('does not exist')) {
                    errorMessage = 'Settings cannot be saved: Required profile columns are missing. Quick fix: Run scripts/FIX_PROFILE_COLUMNS_NOW.sql in Supabase SQL Editor, or run migration 0027_ensure_all_profile_columns.sql. See docs/troubleshooting/FIX_PROFILE_COLUMNS.md for details.';
                } else if (error.message?.includes('infinite recursion') || error.message?.includes('recursion detected')) {
                    errorMessage = 'RLS Policy Error: Infinite recursion detected. Please run scripts/FIX_PROFILES_RLS_RECURSION.sql in Supabase SQL Editor to fix this issue.';
                } else if (error.code === '42501' || error.message?.includes('row-level security') || error.message?.includes('permission denied')) {
                    errorMessage = 'Permission denied: You may not have permission to update your profile. Please check your account permissions or contact support.';
                } else if (error.code === 'PGRST301' || error.message?.includes('JWT')) {
                    errorMessage = 'Authentication error: Your session may have expired. Please refresh the page and try again.';
                } else if (error.code === 'PGRST116') {
                    errorMessage = 'Profile not found: Your user profile may not exist. Please contact support.';
                } else {
                    errorMessage += error.message || 'Unknown error occurred. Please check the browser console for details.';
                }

                throw new Error(errorMessage);
            }

            // Verify the update was successful
            if (!data || data.length === 0) {
                throw new Error('Settings update completed but no profile was found. Please refresh the page.');
            }

            // Update local profile state
            await onProfileUpdate({} as Partial<Profile>); // Trigger profile refresh

            setSaveMessage({ type: 'success', text: 'Settings saved successfully!' });

            // Clear message after 3 seconds
            setTimeout(() => setSaveMessage(null), 3000);
        } catch (error) {
            console.error('Error saving settings:', error);
            const errorMessage = error instanceof Error ? error.message : 'Failed to save settings. Please try again.';
            setSaveMessage({
                type: 'error',
                text: errorMessage
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="flex flex-col flex-1 p-6 overflow-y-auto bg-luna-bg-light dark:bg-luna-bg-dark">
            <div className="mb-8">
                <div className="flex items-center gap-3 mb-2">
                    <div className="flex items-center justify-center size-12 rounded-xl bg-gradient-to-br from-luna-secondary/20 to-luna-secondary/5">
                        <span className="material-symbols-outlined text-2xl text-luna-secondary">settings</span>
                    </div>
                    <h1 className="text-3xl font-black">Settings</h1>
                </div>
                <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 ml-[60px]">
                    Manage your account preferences, notifications, and subscription
                </p>
            </div>
            <div className="space-y-6 max-w-4xl">
                {/* Save Status Message */}
                {saveMessage && (
                    <div className={`p-4 rounded-lg border ${saveMessage.type === 'success'
                        ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-800 dark:text-green-200'
                        : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-800 dark:text-red-200'
                        }`}>
                        <div className="flex items-center gap-2">
                            <span className="material-symbols-outlined text-base">
                                {saveMessage.type === 'success' ? 'check_circle' : 'error'}
                            </span>
                            <span className="text-sm font-medium">{saveMessage.text}</span>
                        </div>
                    </div>
                )}

                {/* Profile Settings */}
                <SettingsCard
                    icon="person"
                    title="Profile"
                    description="Personal details are read-only here. Update them from your main profile page."
                >
                    <div className="space-y-4">
                        <div>
                            <label htmlFor="name" className="block text-sm font-medium text-luna-text-light/80 dark:text-luna-text-dark/80 mb-1">Name</label>
                            <input type="text" id="name" value={profile ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || 'Not set' : 'Not set'} disabled className="w-full bg-luna-bg-light dark:bg-luna-bg-dark border border-luna-text-light/20 dark:border-luna-text-dark/30 rounded-lg p-2 focus:ring-2 focus:ring-luna-secondary focus:border-luna-secondary transition text-sm opacity-60" />
                            <p className="text-xs text-luna-text-light/60 dark:text-luna-text-muted-dark mt-1">Update your name in the Profile page</p>
                        </div>
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-luna-text-light/80 dark:text-luna-text-dark/80 mb-1">Email Address</label>
                            <input type="email" id="email" value={session?.user.email || 'Not set'} disabled className="w-full bg-luna-bg-light dark:bg-luna-bg-dark border border-luna-text-light/20 dark:border-luna-text-dark/30 rounded-lg p-2 focus:ring-2 focus:ring-luna-secondary focus:border-luna-secondary transition text-sm opacity-60" />
                            <p className="text-xs text-luna-text-light/60 dark:text-luna-text-muted-dark mt-1">Email cannot be changed here</p>
                        </div>
                        <div>
                            <label htmlFor="firm" className="block text-sm font-medium text-luna-text-light/80 dark:text-luna-text-dark/80 mb-1">Firm / Organization</label>
                            <input type="text" id="firm" value={profile?.firm || 'Not set'} disabled className="w-full bg-luna-bg-light dark:bg-luna-bg-dark border border-luna-text-light/20 dark:border-luna-text-dark/30 rounded-lg p-2 focus:ring-2 focus:ring-luna-secondary focus:border-luna-secondary transition text-sm opacity-60" />
                            <p className="text-xs text-luna-text-light/60 dark:text-luna-text-muted-dark mt-1">Update your firm in the Profile page</p>
                        </div>
                    </div>
                </SettingsCard>

                {/* Appearance Settings */}
                <SettingsCard
                    icon="palette"
                    title="Appearance"
                    description="Switch between themes to match your workspace. We remember your choice on this browser."
                >
                    <div>
                        <label htmlFor="theme-selector" className="block text-sm font-medium text-luna-text-light/80 dark:text-luna-text-dark/80 mb-2">Theme</label>
                        <div id="theme-selector" className="flex flex-wrap gap-3">
                            <button type="button" onClick={() => handleThemeChange('light')} className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${theme === 'light' ? 'border-luna-secondary bg-luna-secondary/10 text-luna-secondary' : 'border-luna-text-light/20 dark:border-luna-text-dark/30 hover:border-luna-secondary/50'}`}>
                                <span className="material-symbols-outlined">light_mode</span>
                                Light
                            </button>
                            <button type="button" onClick={() => handleThemeChange('dark')} className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${theme === 'dark' ? 'border-luna-secondary bg-luna-secondary/10 text-luna-secondary' : 'border-luna-text-light/20 dark:border-luna-text-dark/30 hover:border-luna-secondary/50'}`}>
                                <span className="material-symbols-outlined">dark_mode</span>
                                Dark
                            </button>
                        </div>
                        <p className="text-xs text-luna-text-light/60 dark:text-luna-text-muted-dark mt-2">Theme preference syncs instantly.</p>
                    </div>
                </SettingsCard>

                {/* Subscription Management */}
                <SettingsCard
                    icon="workspace_premium"
                    title="Subscription"
                    description="Manage plan status, renewal dates, and billing actions."
                    actions={
                        <button
                            type="button"
                            onClick={handleSubscriptionRefreshClick}
                            disabled={refreshingSubscription}
                            className="inline-flex items-center gap-2 rounded-lg border border-luna-text-light/20 dark:border-luna-text-dark/20 px-3 py-2 text-xs font-semibold text-luna-text-light dark:text-luna-text-dark disabled:opacity-60"
                        >
                            <span className="material-symbols-outlined text-base">
                                {refreshingSubscription ? 'progress_activity' : 'refresh'}
                            </span>
                            <span>
                                {refreshingSubscription
                                    ? 'Updating…'
                                    : subscriptionDetails
                                        ? 'Refresh Status'
                                        : 'Link Subscription'}
                            </span>
                        </button>
                    }
                >
                    {subscriptionDetails ? (
                        <div className="space-y-4">
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-medium text-luna-text-light/80 dark:text-luna-text-dark/80">Current Plan</span>
                                    <span className="text-sm font-bold text-luna-secondary">
                                        {getPlanDisplayName(normalizePlanName(subscriptionDetails.plan_name, subscriptionDetails.stripe_price_id))}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-medium text-luna-text-light/80 dark:text-luna-text-dark/80">Status</span>
                                    <span className={`text-sm font-bold capitalize ${subscriptionDetails.status === 'active' ? 'text-green-600 dark:text-green-400' :
                                        subscriptionDetails.status === 'canceled' ? 'text-red-600 dark:text-red-400' :
                                            'text-yellow-600 dark:text-yellow-400'
                                        }`}>
                                        {subscriptionDetails.status}
                                    </span>
                                </div>
                                {subscriptionDetails.current_period_end && (
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-sm font-medium text-luna-text-light/80 dark:text-luna-text-dark/80">
                                            {subscriptionDetails.cancel_at_period_end ? 'Ends on' : 'Renews on'}
                                        </span>
                                        <span className="text-sm text-luna-text-light/60 dark:text-luna-text-dark/60">
                                            {new Date(subscriptionDetails.current_period_end).toLocaleDateString()}
                                        </span>
                                    </div>
                                )}
                                {subscriptionDetails.cancel_at_period_end && (
                                    <div className="flex items-center justify-between mb-2 text-sm text-amber-600 dark:text-amber-400">
                                        Subscription will end after this billing period.
                                    </div>
                                )}
                            </div>
                            <div className="flex flex-wrap gap-3 pt-2 border-t border-luna-text-light/10 dark:border-luna-text-dark/10">
                                {subscriptionDetails.status === 'active' && !subscriptionDetails.cancel_at_period_end && (
                                    <button
                                        type="button"
                                        onClick={() => handleSubscriptionAction('cancel')}
                                        disabled={updatingSubscription}
                                        className="flex-1 min-w-[180px] px-4 py-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 rounded-lg text-sm font-medium hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors disabled:opacity-60"
                                    >
                                        {updatingSubscription ? 'Processing…' : 'Cancel Subscription'}
                                    </button>
                                )}
                                {subscriptionDetails.cancel_at_period_end && (
                                    <button
                                        type="button"
                                        onClick={() => handleSubscriptionAction('resume')}
                                        disabled={updatingSubscription}
                                        className="flex-1 min-w-[180px] px-4 py-2 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300 rounded-lg text-sm font-medium hover:bg-emerald-100 dark:hover:bg-emerald-900/30 transition-colors disabled:opacity-60"
                                    >
                                        {updatingSubscription ? 'Processing…' : 'Resume Subscription'}
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (onNavigate) {
                                            onNavigate('Pricing');
                                        } else {
                                            const url = generatePermalink('Pricing');
                                            window.location.href = url;
                                        }
                                    }}
                                    disabled={updatingSubscription}
                                    className="flex-1 min-w-[160px] px-4 py-2 bg-luna-secondary text-luna-primary rounded-lg text-sm font-bold hover:opacity-90 transition-opacity disabled:opacity-60"
                                >
                                    {subscriptionDetails.status === 'active' ? 'Change Plan' : 'Upgrade Plan'}
                                </button>
                            </div>
                            {subscriptionDetails.status === 'active' && !subscriptionDetails.cancel_at_period_end && (
                                <button
                                    type="button"
                                    onClick={() => handleSubscriptionAction('cancel', true)}
                                    disabled={updatingSubscription}
                                    className="w-full px-4 py-2 bg-transparent border border-red-200 dark:border-red-800 text-sm text-red-600 dark:text-red-400 rounded-lg hover:bg-red-50/70 dark:hover:bg-red-900/10 transition-colors disabled:opacity-60"
                                >
                                    {updatingSubscription ? 'Processing…' : 'Cancel Immediately'}
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-medium text-luna-text-light/80 dark:text-luna-text-dark/80">Current Plan</span>
                                    <span className="text-sm font-bold text-luna-secondary">Free (Trial)</span>
                                </div>
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-medium text-luna-text-light/80 dark:text-luna-text-dark/80">Status</span>
                                    <span className="text-sm font-bold text-green-600 dark:text-green-400 capitalize">Active</span>
                                </div>
                            </div>
                            <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 pt-2">
                                Upgrade to unlock premium features.
                            </p>
                            <button
                                type="button"
                                onClick={() => {
                                    if (onNavigate) {
                                        onNavigate('Pricing');
                                    } else {
                                        const url = generatePermalink('Pricing');
                                        window.location.href = url;
                                    }
                                }}
                                className="w-full px-4 py-2 bg-luna-secondary text-luna-primary rounded-lg text-sm font-bold hover:opacity-90 transition-opacity"
                            >
                                View Plans
                            </button>
                        </div>
                    )}
                </SettingsCard>

                {/* AI Provider Settings */}
                <SettingsCard
                    icon="smart_toy"
                    title="AI Provider"
                    description="Pick your preferred AI engine. We automatically fall back to the alternative provider if needed."
                >
                    <div className="space-y-3">
                        <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70 mb-2">
                            Changing this option affects chat and document analysis experiences.
                        </p>
                        <div className="space-y-2">
                            {[
                                {
                                    id: aiProviderOptionIds.gemini,
                                    label: 'Google Gemini',
                                    description: 'Fast, citation-friendly responses with a generous free tier.',
                                    value: 'gemini' as const,
                                    badge: 'Default',
                                    badgeClass: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200',
                                },
                                {
                                    id: aiProviderOptionIds.openai,
                                    label: 'OpenAI ChatGPT',
                                    description: 'More conversational, better reasoning. Requires OpenAI API key configuration.',
                                    value: 'openai' as const,
                                    badge: 'Premium',
                                    badgeClass: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200',
                                },
                                {
                                    id: aiProviderOptionIds.auto,
                                    label: 'Auto (Recommended)',
                                    description: 'Let LegalRadar choose the best provider based on availability and workload.',
                                    value: null,
                                },
                            ].map((option) => (
                                <div
                                    key={option.id}
                                    className={`flex items-center p-3 rounded-lg border transition-colors hover:bg-luna-bg-light dark:hover:bg-luna-bg-dark ${aiProvider === option.value
                                        ? 'border-luna-secondary bg-luna-secondary/10 dark:bg-luna-secondary/20'
                                        : 'border-luna-text-light/20 dark:border-luna-text-dark/30'
                                        }`}
                                >
                                    <input
                                        id={option.id}
                                        type="radio"
                                        name="ai-provider"
                                        value={option.value ?? 'auto'}
                                        checked={aiProvider === option.value}
                                        onChange={() => setAiProvider(option.value)}
                                        className="h-4 w-4 text-luna-secondary focus:ring-luna-secondary cursor-pointer"
                                    />
                                    <label htmlFor={option.id} className="ml-3 flex-1 cursor-pointer">
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-bold text-luna-text-light dark:text-luna-text-dark">{option.label}</span>
                                            {option.badge && (
                                                <span className={`px-2 py-0.5 text-xs rounded ${option.badgeClass}`}>
                                                    {option.badge}
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-xs text-luna-text-light/60 dark:text-luna-text-dark/60 mt-0.5">
                                            {option.description}
                                        </p>
                                    </label>
                                </div>
                            ))}
                        </div>
                        <p className="text-xs text-luna-text-light/50 dark:text-luna-text-dark/50 mt-1">
                            💡 Your preference is saved when you click “Save Changes”.
                        </p>
                    </div>
                </SettingsCard>

                {/* Notification Settings */}
                <SettingsCard
                    icon="notifications"
                    title="Notifications"
                    description="Decide what activity triggers email alerts. These preferences save when you click “Save Changes”."
                >
                    <div className="space-y-1.5">
                        {NOTIFICATION_OPTIONS.map((option) => (
                            <ToggleRow
                                key={option.id}
                                id={option.id}
                                label={option.label}
                                description={option.description}
                                checked={notifications[option.key]}
                                onChange={(checked) => handleNotificationChange(option.key, checked)}
                            />
                        ))}
                    </div>
                </SettingsCard>

                {/* Legal Information Updates Consent (GDPR-compliant) */}
                <SettingsCard
                    icon="info"
                    title="Legal Information Updates"
                    description="Receive informational updates about new legal developments (not legal advice)."
                >
                    <LegalDisclaimer className="mb-4" variant="updates" />

                    {loadingConsent ? (
                        <div className="flex items-center justify-center py-4">
                            <span className="material-symbols-outlined animate-spin text-luna-secondary">sync</span>
                            <span className="ml-2 text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">Loading consent status...</span>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            <ToggleRow
                                id="legal-updates-email"
                                label="Email Notifications for Legal Updates"
                                description={
                                    <>
                                        Receive email notifications about new laws, court decisions, and regulatory changes in areas you follow.{' '}
                                        <strong className="text-luna-text-light dark:text-luna-text-dark">Requires explicit consent (GDPR Art. 6(1)(a)).</strong>
                                    </>
                                }
                                checked={legalUpdatePrefs.email}
                                onChange={(checked) => handleLegalUpdateConsent('email', checked)}
                            />
                            <ToggleRow
                                id="legal-updates-in-app"
                                label="In-App Notifications"
                                description="Receive LegalRadar updates inside the product. You can disable this anytime."
                                checked={legalUpdatePrefs.inApp}
                                onChange={(checked) => handleLegalUpdateConsent('inApp', checked)}
                            />
                            <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                                <p className="text-xs text-blue-800 dark:text-blue-200">
                                    <span className="material-symbols-outlined align-middle text-sm mr-1">info</span>
                                    <strong>What you&apos;ll receive:</strong> Summaries of new laws, regulations, and court decisions.{' '}
                                    <strong>What you won&apos;t receive:</strong> Personalized legal advice or specific action recommendations.
                                </p>
                            </div>
                        </div>
                    )}
                </SettingsCard>

                {/* Legal Radar Preferences */}
                <SettingsCard
                    icon="radar"
                    title="Legal Radar Preferences"
                    description="Enable per-topic notifications for the Legal Updates dashboard. Email requires explicit consent."
                >

                    {radarMessage && (
                        <div className="mb-4 rounded-lg border border-yellow-200 dark:border-yellow-800 bg-yellow-50/70 dark:bg-yellow-900/20 p-3 text-xs text-yellow-900 dark:text-yellow-100">
                            {radarMessage}
                        </div>
                    )}

                    {loadingRadarTopics ? (
                        <div className="flex items-center gap-2 text-sm text-luna-text-light/60 dark:text-luna-text-dark/70">
                            <span className="material-symbols-outlined animate-spin text-luna-secondary">sync</span>
                            Loading radar topics...
                        </div>
                    ) : radarTopics.length === 0 ? (
                        <p className="text-sm text-luna-text-light/60 dark:text-luna-text-muted-dark py-2">
                            No radar topics available yet.
                        </p>
                    ) : (
                        <div className="space-y-3">
                            {radarTopics.map((topic) => {
                                const settings = radarSettings[topic.id] ?? { email: false, inApp: true };
                                return (
                                    <div
                                        key={topic.id}
                                        className="rounded-lg border border-luna-text-light/10 dark:border-luna-text-dark/20 bg-luna-bg-light dark:bg-luna-bg-dark p-4"
                                    >
                                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                            <div className="flex-1">
                                                <p className="text-sm font-semibold text-luna-text-light dark:text-white">
                                                    {topic.label}
                                                </p>
                                                <p className="text-xs text-luna-text-light/70 dark:text-luna-text-muted-dark mt-1">
                                                    {topic.description || 'Informational updates from trusted sources.'}
                                                </p>
                                            </div>
                                            <div className="flex flex-col sm:flex-row gap-3">
                                                <label className="flex items-center gap-2 text-xs font-semibold text-luna-text-light dark:text-luna-text-dark">
                                                    <input
                                                        type="checkbox"
                                                        className="h-4 w-4 rounded border-gray-300 text-luna-secondary focus:ring-luna-secondary cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                                                        checked={settings.email}
                                                        disabled={!legalUpdatePrefs.email || updatingRadarPreference === `${topic.id}-email`}
                                                        title={!legalUpdatePrefs.email ? 'Enable email consent above to turn on Legal Radar emails.' : undefined}
                                                        onChange={(event) =>
                                                            handleRadarPreferenceChange(topic.id, 'email', event.target.checked)
                                                        }
                                                    />
                                                    Email
                                                </label>
                                                <label className="flex items-center gap-2 text-xs font-semibold text-luna-text-light dark:text-luna-text-dark">
                                                    <input
                                                        type="checkbox"
                                                        className="h-4 w-4 rounded border-gray-300 text-luna-secondary focus:ring-luna-secondary cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                                                        checked={settings.inApp}
                                                        disabled={updatingRadarPreference === `${topic.id}-in_app`}
                                                        onChange={(event) =>
                                                            handleRadarPreferenceChange(topic.id, 'in_app', event.target.checked)
                                                        }
                                                    />
                                                    In-App
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </SettingsCard>

                {/* Law Topic Subscriptions */}
                <SettingsCard
                    icon="gavel"
                    title="Legal Information Topics"
                    description="Choose which legal areas you want to follow (requires email consent above)."
                >

                    {loadingTopics ? (
                        <div className="flex items-center justify-center py-8">
                            <span className="material-symbols-outlined animate-spin text-luna-secondary">sync</span>
                            <span className="ml-2 text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">Loading topics...</span>
                        </div>
                    ) : topics.length === 0 && saveMessage?.type === 'error' && saveMessage.text.includes('not set up') ? (
                        <div className="py-4">
                            <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg mb-4">
                                <div className="flex items-start gap-3">
                                    <span className="material-symbols-outlined text-yellow-600 dark:text-yellow-400 flex-shrink-0">info</span>
                                    <div className="flex-1">
                                        <h4 className="text-sm font-bold text-yellow-800 dark:text-yellow-200 mb-1">
                                            Setup Required
                                        </h4>
                                        <p className="text-xs text-yellow-700 dark:text-yellow-300 mb-2">
                                            The EU Law Updates feature requires database tables to be created. Run the migration to enable this feature.
                                        </p>
                                        <p className="text-xs text-yellow-600 dark:text-yellow-400 font-mono bg-yellow-100 dark:bg-yellow-900/30 px-2 py-1 rounded inline-block">
                                            0017_create_law_notification_system.sql
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <p className="text-sm text-luna-text-light/60 dark:text-luna-text-muted-dark">
                                No topics available until migration is run.
                            </p>
                        </div>
                    ) : topics.length === 0 ? (
                        <p className="text-sm text-luna-text-light/60 dark:text-luna-text-muted-dark py-4">
                            No topics available at the moment.
                        </p>
                    ) : (
                        <div className="space-y-3">
                            {topics.map((topic) => {
                                const isSubscribed = subscriptions.has(topic.id);
                                const subscription = subscriptions.get(topic.id);
                                const isUpdating = updatingSubscriptions.has(topic.id);
                                const rssConfigured = topic.rss_feed_url && topic.rss_feed_url.trim() !== '';

                                return (
                                    <div
                                        key={topic.id}
                                        className={`p-4 rounded-lg border transition-colors ${isSubscribed
                                            ? 'bg-luna-secondary/5 border-luna-secondary/20 dark:border-luna-secondary/30'
                                            : 'bg-luna-bg-light dark:bg-luna-bg-dark border-luna-text-light/10 dark:border-luna-text-dark/20'
                                            }`}
                                    >
                                        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <h3 className="text-sm font-bold text-luna-text-light dark:text-luna-text-dark">
                                                        {topic.name}
                                                    </h3>
                                                    {!rssConfigured && (
                                                        <span className="px-2 py-0.5 text-xs bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 rounded">
                                                            RSS Not Configured
                                                        </span>
                                                    )}
                                                </div>
                                                {topic.description && (
                                                    <p className="text-xs text-luna-text-light/60 dark:text-luna-text-muted-dark mb-2">
                                                        {topic.description}
                                                    </p>
                                                )}
                                                {isSubscribed && subscription && (
                                                    <div className="flex items-center gap-2 mt-2">
                                                        <input
                                                            id={`email-toggle-${topic.id}`}
                                                            type="checkbox"
                                                            checked={subscription.email_enabled}
                                                            onChange={(e) => handleEmailToggle(topic.id, e.target.checked)}
                                                            disabled={isUpdating}
                                                            className="h-3.5 w-3.5 rounded border-gray-300 text-luna-secondary focus:ring-luna-secondary cursor-pointer disabled:opacity-50"
                                                        />
                                                        <label htmlFor={`email-toggle-${topic.id}`} className="text-xs text-luna-text-light/70 dark:text-luna-text-dark/70 cursor-pointer">
                                                            Enable email notifications
                                                        </label>
                                                    </div>
                                                )}
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => handleTopicSubscription(topic.id, !isSubscribed)}
                                                disabled={isUpdating}
                                                className={`px-4 py-2 text-xs font-bold rounded-lg transition-opacity whitespace-nowrap ${isSubscribed
                                                    ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30'
                                                    : 'bg-luna-secondary text-luna-primary hover:opacity-90'
                                                    } ${isUpdating ? 'opacity-50 cursor-not-allowed' : ''}`}
                                            >
                                                {isUpdating ? (
                                                    <span className="material-symbols-outlined animate-spin text-xs">sync</span>
                                                ) : isSubscribed ? (
                                                    'Unsubscribe'
                                                ) : (
                                                    'Subscribe'
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                        <p className="text-xs text-blue-800 dark:text-blue-200">
                            <span className="material-symbols-outlined align-middle text-sm mr-1">info</span>
                            You'll receive informational summaries when new legal developments matching your subscribed topics are published.
                            <strong> Note:</strong> These are informational updates, not personalized legal advice.
                        </p>
                    </div>
                </SettingsCard>

                {/* Admin Access Section removed - may be re-added in the future */}
                {false && profile?.role !== 'admin' && profile?.role !== 'super_admin' && (
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-6">
                        <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                                <h3 className="text-lg font-bold text-yellow-800 dark:text-yellow-200 mb-2">
                                    <span className="material-symbols-outlined align-middle mr-2">admin_panel_settings</span>
                                    Admin Access
                                </h3>
                                <p className="text-sm text-yellow-700 dark:text-yellow-300 mb-4">
                                    Need admin access? Click the button below to become an administrator.
                                </p>
                                {successMessage ? (
                                    <div className="mb-4 p-4 bg-green-100 dark:bg-green-900/30 border-2 border-green-400 dark:border-green-600 rounded-lg">
                                        <div className="flex items-start gap-3">
                                            <span className="material-symbols-outlined text-2xl text-green-600 dark:text-green-400">check_circle</span>
                                            <div className="flex-1">
                                                <p className="text-sm font-bold text-green-800 dark:text-green-200 mb-2">{successMessage?.text}</p>
                                                <button
                                                    onClick={() => window.location.reload()}
                                                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-bold rounded-lg transition-colors"
                                                >
                                                    Refresh Page Now
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ) : null}
                                {errorMessage ? (
                                    <div className="mb-4 p-4 bg-red-100 dark:bg-red-900/30 border-2 border-red-400 dark:border-red-600 rounded-lg">
                                        <div className="flex items-start gap-3">
                                            <span className="material-symbols-outlined text-2xl text-red-600 dark:text-red-400">error</span>
                                            <div className="flex-1">
                                                <p className="text-sm font-bold text-red-800 dark:text-red-200 mb-2">{errorMessage?.text}</p>
                                                <div className="text-xs text-red-700 dark:text-red-300 space-y-1">
                                                    <p><strong>Solution:</strong></p>
                                                    <ol className="list-decimal list-inside space-y-1 ml-2">
                                                        <li>Go to Supabase SQL Editor</li>
                                                        <li>Copy and paste this SQL:</li>
                                                        <li>
                                                            <button
                                                                onClick={() => {
                                                                    navigator.clipboard.writeText("UPDATE public.profiles SET role = 'admin' WHERE id = auth.uid();");
                                                                    toast.success('SQL copied to clipboard! Paste it in Supabase SQL Editor.');
                                                                }}
                                                                className="mt-2 px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-xs font-bold rounded transition-colors"
                                                                type="button"
                                                            >
                                                                Copy SQL to Clipboard
                                                            </button>
                                                        </li>
                                                    </ol>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ) : null}
                                <button
                                    onClick={async () => {
                                        if (!session?.user.id) {
                                            setSaveMessage({ type: 'error', text: 'You must be logged in.' });
                                            return;
                                        }

                                        if (!confirm('Are you sure you want to become an admin? This will grant you full access to the admin dashboard.')) {
                                            return;
                                        }

                                        setSaving(true);
                                        try {
                                            const { data, error } = await supabase
                                                .from('profiles')
                                                .update({ role: 'admin' })
                                                .eq('id', session.user.id)
                                                .select();

                                            if (error) {
                                                console.error('Update error:', error);
                                                throw error;
                                            }

                                            console.log('Update successful:', data);

                                            setSaveMessage({
                                                type: 'success',
                                                text: 'Success! You are now an admin. Refreshing page...'
                                            });

                                            // Update local profile immediately
                                            if (data && data[0]) {
                                                await onProfileUpdate({ role: data[0].role });
                                            }

                                            // Force immediate refresh - try multiple methods
                                            setTimeout(() => {
                                                // Method 1: Force reload
                                                window.location.reload();
                                            }, 500);

                                            // Backup: If reload doesn't work, redirect to admin page
                                            setTimeout(() => {
                                                if (onNavigate) {
                                                    onNavigate('Admin');
                                                } else {
                                                    window.location.href = '/admin';
                                                }
                                            }, 2000);
                                        } catch (err: any) {
                                            console.error('Error setting admin role:', err);
                                            const errorMsg = err.message || err.toString();
                                            setSaveMessage({
                                                type: 'error',
                                                text: `Failed: ${errorMsg}. If you see a permission error, you may need to run ADD_ADMIN_RLS_POLICIES.sql first.`
                                            });
                                            setSaving(false);
                                        }
                                    }}
                                    disabled={saving}
                                    className="flex items-center gap-2 px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white text-sm font-bold rounded-lg transition-colors disabled:opacity-50"
                                >
                                    {saving ? (
                                        <>
                                            <span className="material-symbols-outlined animate-spin">sync</span>
                                            <span>Setting up...</span>
                                        </>
                                    ) : (
                                        <>
                                            <span className="material-symbols-outlined">admin_panel_settings</span>
                                            <span>Become Admin</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Save Changes Button */}
                <div className="sticky bottom-0 bg-luna-bg-light dark:bg-luna-bg-dark pt-6 pb-2 border-t border-luna-text-light/10 dark:border-luna-text-dark/10 -mx-6 px-6 mt-8">
                    <button
                        className={`w-full flex items-center justify-center gap-2 h-12 bg-luna-secondary text-luna-primary text-base font-bold rounded-lg transition-opacity shadow-lg shadow-luna-secondary/30 ${saving || !hasUnsavedChanges ? 'opacity-60 cursor-not-allowed' : 'hover:opacity-90'
                            }`}
                        onClick={handleSaveChanges}
                        disabled={saving || !hasUnsavedChanges}
                    >
                        {saving ? (
                            <>
                                <span className="material-symbols-outlined animate-spin">sync</span>
                                <span>Saving...</span>
                            </>
                        ) : hasUnsavedChanges ? (
                            <>
                                <span className="material-symbols-outlined">save</span>
                                <span>Save Changes</span>
                            </>
                        ) : (
                            <>
                                <span className="material-symbols-outlined">task_alt</span>
                                <span>All Changes Saved</span>
                            </>
                        )}
                    </button>
                    <p className="text-xs text-center text-luna-text-light/60 dark:text-luna-text-dark/60 mt-3">
                        Legal consents, radar preferences, and topic subscriptions save instantly. Notification and AI settings save when you click “Save Changes”.
                    </p>
                </div>
            </div>
        </div>
    );
};

const ProfilePage: React.FC<{ profile: Profile | null; onProfileUpdate: (updates: Partial<Profile>) => Promise<void>; userEmail: string | undefined }> = ({ profile, onProfileUpdate, userEmail }) => {
    const [message, setMessage] = useState('');

    const profileForm = useForm<ProfileUpdateFormData>({
        resolver: zodResolver(profileUpdateSchema),
        defaultValues: {
            firstName: profile?.first_name || '',
            lastName: profile?.last_name || '',
            firm: profile?.firm || '',
        },
    });

    useEffect(() => {
        if (profile) {
            profileForm.reset({
                firstName: profile.first_name || '',
                lastName: profile.last_name || '',
                firm: profile.firm || '',
            });
        }
    }, [profile]); // Only depend on profile - profileForm.reset is stable from react-hook-form

    const handleSave = async (data: ProfileUpdateFormData) => {
        if (!profile) return;

        setMessage('');

        const updates: Partial<Profile> = {
            first_name: data.firstName,
            last_name: data.lastName,
            firm: data.firm || undefined,
        };

        try {
            await onProfileUpdate(updates);
            setMessage('Profile updated successfully!');
        } catch (error) {
            setMessage('Error updating profile.');
            console.error('Profile update error:', error);
        }
    };

    const displayName = profile ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || 'User' : 'User';
    const displayFirm = profile?.firm || 'Legal Professional';

    return (
        <div className="flex flex-col flex-1 p-6 overflow-y-auto bg-luna-bg-light dark:bg-luna-bg-dark">
            <h1 className="text-2xl font-bold mb-6">Your Profile</h1>
            <div className="space-y-8 max-w-4xl">
                <div className="bg-white dark:bg-luna-primary rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 shadow-sm p-6">
                    <div className="flex items-center space-x-6">
                        <img
                            alt={getAvatarAlt(getProfileFullName(profile))}
                            className="size-24 rounded-full object-cover ring-4 ring-luna-secondary/20"
                            src={profile?.avatar_url || "https://lh3.googleusercontent.com/aida-public/AB6AXuC6oq33dNp6A-9NrO3YTfoAiNzhgWchFlJuFX0rTR88Ia5vLnjO8OS23dxBfNeB2iFSmP6TfzlTtpIZJPYNQ3aD2g5i5Td_5WlHtD9f8MXuQWh5pUE_Lp26Ayht0Pu3FHP34E3pEN8WfbEA5P2GZzoXMyfkpTBcDhl3WQ56kuDkn-RAOr2xCQpVII4BEDRoLmjtNSthqKVF4uReXtrxQlDVWbQZG_0U2MY9G0Z5smvb9KvpYrbwQhi4-G6kFaqf-OskjR16cIS0_ZaS"}
                            onError={handleImageError}
                        />
                        <div>
                            <h2 className="text-2xl font-bold">{displayName}</h2>
                            <p className="text-luna-text-light/70 dark:text-luna-text-muted-dark">{displayFirm}</p>
                        </div>
                    </div>
                </div>
                <form onSubmit={profileForm.handleSubmit(handleSave)} className="space-y-8">
                    <div className="bg-white dark:bg-luna-primary rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/20 shadow-sm p-6">
                        <h2 className="text-xl font-bold mb-4">Personal Information</h2>
                        <div className="space-y-4">
                            <div>
                                <label htmlFor="firstName" className="block text-sm font-medium text-luna-text-light/80 dark:text-luna-text-dark/80 mb-1">First Name</label>
                                <input
                                    {...profileForm.register('firstName')}
                                    type="text"
                                    id="firstName"
                                    className={`w-full bg-luna-bg-light dark:bg-luna-bg-dark border rounded-lg p-2 focus:ring-2 focus:ring-luna-secondary focus:border-luna-secondary transition text-sm ${profileForm.formState.errors.firstName
                                        ? 'border-red-500 dark:border-red-500'
                                        : 'border-luna-text-light/20 dark:border-luna-text-dark/30'
                                        }`}
                                />
                                {profileForm.formState.errors.firstName && (
                                    <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                                        {profileForm.formState.errors.firstName.message}
                                    </p>
                                )}
                            </div>
                            <div>
                                <label htmlFor="lastName" className="block text-sm font-medium text-luna-text-light/80 dark:text-luna-text-dark/80 mb-1">Last Name</label>
                                <input
                                    {...profileForm.register('lastName')}
                                    type="text"
                                    id="lastName"
                                    className={`w-full bg-luna-bg-light dark:bg-luna-bg-dark border rounded-lg p-2 focus:ring-2 focus:ring-luna-secondary focus:border-luna-secondary transition text-sm ${profileForm.formState.errors.lastName
                                        ? 'border-red-500 dark:border-red-500'
                                        : 'border-luna-text-light/20 dark:border-luna-text-dark/30'
                                        }`}
                                />
                                {profileForm.formState.errors.lastName && (
                                    <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                                        {profileForm.formState.errors.lastName.message}
                                    </p>
                                )}
                            </div>
                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-luna-text-light/80 dark:text-luna-text-dark/80 mb-1">Email Address</label>
                                <input
                                    type="email"
                                    id="email"
                                    defaultValue={userEmail || ''}
                                    disabled
                                    className="w-full bg-luna-bg-light/50 dark:bg-luna-bg-dark/50 border border-luna-text-light/20 dark:border-luna-text-dark/30 rounded-lg p-2 text-sm cursor-not-allowed"
                                />
                            </div>
                            <div>
                                <label htmlFor="firm" className="block text-sm font-medium text-luna-text-light/80 dark:text-luna-text-dark/80 mb-1">Firm / Organization</label>
                                <input
                                    {...profileForm.register('firm')}
                                    type="text"
                                    id="firm"
                                    className={`w-full bg-luna-bg-light dark:bg-luna-bg-dark border rounded-lg p-2 focus:ring-2 focus:ring-luna-secondary focus:border-luna-secondary transition text-sm ${profileForm.formState.errors.firm
                                        ? 'border-red-500 dark:border-red-500'
                                        : 'border-luna-text-light/20 dark:border-luna-text-dark/30'
                                        }`}
                                />
                                {profileForm.formState.errors.firm && (
                                    <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                                        {profileForm.formState.errors.firm.message}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="flex justify-end items-center gap-4">
                        {message && (
                            <p className={`text-sm ${message.startsWith('Error') ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                                {message}
                            </p>
                        )}
                        <button
                            type="submit"
                            disabled={profileForm.formState.isSubmitting}
                            className="flex items-center justify-center gap-2 h-10 px-6 bg-luna-secondary text-luna-primary text-sm font-bold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
                        >
                            {profileForm.formState.isSubmitting ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const AuthPage: React.FC<{ onNavigate?: (page: string) => void; initialMode?: 'signup' | 'login' }> = ({ onNavigate, initialMode = 'login' }) => {
    const [isSignUp, setIsSignUp] = useState(initialMode === 'signup');
    const [loading, setLoading] = useState(false);
    const [authError, setAuthError] = useState<string | null>(null);

    // Sign up form
    const signUpForm = useForm<SignUpFormData>({
        resolver: zodResolver(signUpSchema),
        defaultValues: {
            fullName: '',
            email: '',
            password: '',
            termsAccepted: false,
        },
    });

    // Sign in form
    const signInForm = useForm<SignInFormData>({
        resolver: zodResolver(signInSchema),
        defaultValues: {
            email: '',
            password: '',
        },
    });

    // Sync isSignUp state when initialMode prop changes
    useEffect(() => {
        setIsSignUp(initialMode === 'signup');
        setAuthError(null); // Clear any previous errors when switching modes
        // Reset forms when switching modes
        signUpForm.reset();
        signInForm.reset();
    }, [initialMode, signUpForm, signInForm]);

    const handleSignUp = async (data: SignUpFormData) => {
        setLoading(true);
        setAuthError(null);

        try {
            // Split full name into first and last name for user metadata
            const nameParts = data.fullName.trim().split(/\s+/);
            const firstName = nameParts[0] || '';
            const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';

            const { error: signUpError } = await supabase.auth.signUp({
                email: data.email,
                password: data.password,
                options: {
                    data: {
                        first_name: firstName,
                        last_name: lastName,
                        role: 'user',
                    }
                }
            });

            if (signUpError) {
                setAuthError(signUpError.message);
            } else {
                // Success - Supabase handles session setting
                // Welcome email will be sent automatically by database trigger
                signUpForm.reset();

                // Optionally trigger welcome email Edge Function directly (as backup)
                try {
                    await supabase.functions.invoke('send-welcome-email', {
                        body: {
                            email: data.email,
                            firstName: firstName,
                            lastName: lastName,
                            fullName: data.fullName
                        }
                    });
                } catch (emailError) {
                    // Don't fail signup if email fails
                    console.warn('Welcome email failed to send:', emailError);
                }
            }
        } catch (error) {
            setAuthError(error instanceof Error ? error.message : 'An unexpected error occurred');
        } finally {
            setLoading(false);
        }
    };

    const handleSignIn = async (data: SignInFormData) => {
        setLoading(true);
        setAuthError(null);

        try {
            const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
                email: data.email,
                password: data.password
            });

            if (signInError) {
                setAuthError(signInError.message);
                setLoading(false);
            } else if (signInData.session) {
                // CRITICAL FIX: Immediately redirect after successful login
                console.log('[AuthPage handleSignIn] ✅ Login successful! Session exists:', !!signInData.session);
                signInForm.reset();

                // Check if user was trying to access Admin page
                const returnToAdmin = sessionStorage.getItem('returnToAdmin') === 'true';
                const targetPage = returnToAdmin ? 'Admin' : 'Dashboard';

                if (returnToAdmin) {
                    sessionStorage.removeItem('returnToAdmin');
                    console.log('[AuthPage handleSignIn] Redirecting to Admin page');
                }

                // Call onNavigate immediately - don't wait for auth state change
                console.log('[AuthPage handleSignIn] onNavigate exists:', !!onNavigate);
                if (onNavigate) {
                    console.log(`[AuthPage handleSignIn] Calling onNavigate("${targetPage}")`);
                    onNavigate(targetPage);
                    console.log(`[AuthPage handleSignIn] onNavigate("${targetPage}") called`);
                } else {
                    console.error('[AuthPage handleSignIn] ❌ ERROR: onNavigate is undefined!');
                }
            } else {
                console.warn('[AuthPage handleSignIn] ⚠️ No error but no session either');
            }
        } catch (error) {
            setAuthError(error instanceof Error ? error.message : 'An unexpected error occurred');
            setLoading(false);
        }
    };

    const handleOAuthSignIn = async (provider: 'google' | 'azure') => {
        setLoading(true);
        setAuthError(null);

        try {
            // CRITICAL: Check if we're on SuperAdmin page and preserve it
            // Use full URL with hash for Chrome compatibility (PKCE flow)
            const redirectTo = `${window.location.origin}${window.location.pathname}`;

            const { error } = await supabase.auth.signInWithOAuth({
                provider,
                options: {
                    redirectTo: redirectTo,
                    // Use PKCE flow for better Chrome compatibility
                    queryParams: {
                        access_type: 'offline',
                        prompt: 'consent',
                    }
                }
            });

            if (error) {
                // Check if it's the "provider not enabled" error
                if (error.message.includes('not enabled') || error.code === 'validation_failed') {
                    const providerName = provider === 'google' ? 'Google' : 'Microsoft (Azure)';
                    setAuthError(
                        `${providerName} authentication is not enabled in your Supabase project. ` +
                        `Please enable it in the Supabase Dashboard: Authentication → Providers → ${providerName}.`
                    );
                } else {
                    setAuthError(error.message);
                }
                setLoading(false);
            }
            // No need to set loading=false on success, as the redirect handles it.
        } catch (error) {
            setAuthError(error instanceof Error ? error.message : 'An unexpected error occurred');
            setLoading(false);
        }
    };

    const handlePasswordReset = async () => {
        const email = isSignUp ? signUpForm.getValues('email') : signInForm.getValues('email');
        if (!email) {
            toast.warning('Please enter your email address to reset your password.');
            return;
        }
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
            redirectTo: window.location.origin,
        });
        if (error) {
            toast.error(error.message);
        } else {
            toast.success('Password reset email sent! Please check your inbox.');
        }
    };

    return (
        <div className="relative flex min-h-screen w-full flex-col items-center justify-center bg-dots-pattern">
            <div className="absolute inset-0 bg-luna-bg-light/80 dark:bg-luna-bg-dark/80 backdrop-blur-sm"></div>
            <main className="relative z-10 flex w-full max-w-md flex-col items-center p-6 sm:p-8">
                <button
                    className="mb-8 flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity focus:outline-none focus:ring-2 focus:ring-luna-secondary focus:ring-offset-2 rounded-lg p-2 -ml-2"
                    onClick={(e) => {
                        e.preventDefault();
                        if (onNavigate) {
                            onNavigate('Landing');
                        }
                    }}
                    aria-label="Go to home page"
                    type="button"
                >
                    <div className="size-8 text-luna-secondary">
                        <svg fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93s3.05-7.44 7-7.93v15.86zm2-15.86c1.03.13 2 .45 2.87.93L15.87 5c-1.03-.13-2-.45-2.87-.93zM12 5.95c.57.06 1.13.2 1.66.42l.66-.66C13.66 5.3 12.85 5.06 12 5V5.95zm5.5 1.05c-1.3-1.3-3.11-2.05-5-2.05s-3.7.75-5 2.05l-1.41-1.41C7.89 3.84 9.87 3 12 3s4.11.84 5.91 2.59l-1.41 1.41z"></path></svg>
                    </div>
                    <h1 className="text-2xl font-bold leading-tight tracking-[-0.015em]">LRDEnE</h1>
                </button>
                <div className="w-full rounded-xl border border-luna-text-light/10 dark:border-luna-text-dark/10 bg-white/50 dark:bg-luna-primary/20 shadow-lg backdrop-blur-md">
                    <div className="p-8">
                        <div className="text-center">
                            <h2 className="text-xl font-bold">{isSignUp ? 'Create Your Account' : 'Welcome Back'}</h2>
                            <p className="mt-1 text-sm text-luna-text-light/70 dark:text-luna-text-muted-dark">{isSignUp ? 'Start your free trial today.' : 'Log in to access your dashboard.'}</p>
                        </div>
                        {authError && (
                            <div className="mt-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                                <p className="text-sm text-red-800 dark:text-red-200">{authError}</p>
                            </div>
                        )}
                        {isSignUp ? (
                            <form className="mt-8 space-y-5" onSubmit={signUpForm.handleSubmit(handleSignUp)}>
                                <div>
                                    <label htmlFor="fullName" aria-label="Full Name"></label>
                                    <input
                                        type="text"
                                        id="fullName"
                                        className="w-full px-4 py-3 bg-luna-bg-light dark:bg-luna-bg-dark border-2 border-luna-text-light/20 dark:border-luna-text-dark/30 rounded-lg focus:ring-2 focus:ring-luna-secondary focus:border-luna-secondary transition-all"
                                        placeholder="Full Name"
                                        {...signUpForm.register('fullName')}
                                    />                                    {signUpForm.formState.errors.fullName && (
                                        <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                                            {signUpForm.formState.errors.fullName.message}
                                        </p>
                                    )}
                                </div>
                                <div>
                                    <label className="text-sm font-medium" htmlFor="email">Email Address</label>
                                    <input
                                        {...signUpForm.register('email')}
                                        className={`mt-1.5 block w-full rounded-lg border bg-luna-bg-light/50 dark:bg-luna-bg-dark/50 focus:border-luna-secondary focus:ring-luna-secondary transition duration-200 ${signUpForm.formState.errors.email
                                            ? 'border-red-500 dark:border-red-500'
                                            : 'border-luna-text-light/20 dark:border-luna-text-dark/20'
                                            }`}
                                        id="email"
                                        placeholder="you@example.com"
                                        type="email"
                                    />
                                    {signUpForm.formState.errors.email && (
                                        <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                                            {signUpForm.formState.errors.email.message}
                                        </p>
                                    )}
                                </div>
                                <div>
                                    <label className="text-sm font-medium" htmlFor="password">Password</label>
                                    <input
                                        {...signUpForm.register('password')}
                                        className={`mt-1.5 block w-full rounded-lg border bg-luna-bg-light/50 dark:bg-luna-bg-dark/50 focus:border-luna-secondary focus:ring-luna-secondary transition duration-200 ${signUpForm.formState.errors.password
                                            ? 'border-red-500 dark:border-red-500'
                                            : 'border-luna-text-light/20 dark:border-luna-text-dark/20'
                                            }`}
                                        id="password"
                                        placeholder="••••••••"
                                        type="password"
                                    />
                                    {signUpForm.formState.errors.password && (
                                        <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                                            {signUpForm.formState.errors.password.message}
                                        </p>
                                    )}
                                    {isSignUp && (
                                        <p className="mt-1 text-xs text-luna-text-light/60 dark:text-luna-text-muted-dark">
                                            Must be 8+ characters with uppercase, lowercase, number, and special character
                                        </p>
                                    )}
                                </div>
                                <div className="flex items-start gap-2">
                                    <input
                                        {...signUpForm.register('termsAccepted')}
                                        className="h-4 w-4 mt-0.5 rounded border-gray-300 text-luna-secondary focus:ring-luna-secondary"
                                        id="terms"
                                        type="checkbox"
                                    />
                                    <label htmlFor="terms" className="text-sm text-luna-text-light/80 dark:text-luna-text-dark/80">
                                        <input
                                            type="checkbox"
                                            id="terms"
                                            className="mr-2 rounded border-luna-text-light/30 dark:border-luna-text-dark/40 bg-luna-bg-light dark:bg-luna-bg-dark focus:ring-luna-secondary text-luna-secondary"
                                            {...signUpForm.register('termsAccepted')}
                                        />
                                        I agree to the <a href="/terms.html" target="_blank" className="underline">Terms and Conditions</a>
                                    </label>
                                </div>
                                {signUpForm.formState.errors.termsAccepted && (
                                    <p className="text-xs text-red-600 dark:text-red-400 -mt-3">
                                        {signUpForm.formState.errors.termsAccepted.message}
                                    </p>
                                )}
                                <button className="flex w-full min-w-[84px] max-w-[480px] cursor-pointer items-center justify-center overflow-hidden rounded-lg h-11 px-4 bg-luna-secondary text-luna-primary text-sm font-bold leading-normal tracking-[0.015em] hover:opacity-90 transition-opacity shadow-lg shadow-luna-secondary/20 disabled:opacity-50" type="submit" disabled={loading}>
                                    <span className="truncate">{loading ? 'Creating Account...' : 'Sign Up'}</span>
                                </button>
                            </form>
                        ) : (
                            <form className="mt-8 space-y-5" onSubmit={signInForm.handleSubmit(handleSignIn)}>
                                <div>
                                    <label className="text-sm font-medium" htmlFor="signin-email">Email Address</label>
                                    <input
                                        {...signInForm.register('email')}
                                        className={`mt-1.5 block w-full rounded-lg border bg-luna-bg-light/50 dark:bg-luna-bg-dark/50 focus:border-luna-secondary focus:ring-luna-secondary transition duration-200 ${signInForm.formState.errors.email
                                            ? 'border-red-500 dark:border-red-500'
                                            : 'border-luna-text-light/20 dark:border-luna-text-dark/20'
                                            }`}
                                        id="signin-email"
                                        placeholder="you@example.com"
                                        type="email"
                                    />
                                    {signInForm.formState.errors.email && (
                                        <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                                            {signInForm.formState.errors.email.message}
                                        </p>
                                    )}
                                </div>
                                <div>
                                    <label className="text-sm font-medium" htmlFor="signin-password">Password</label>
                                    <input
                                        {...signInForm.register('password')}
                                        className={`mt-1.5 block w-full rounded-lg border bg-luna-bg-light/50 dark:bg-luna-bg-dark/50 focus:border-luna-secondary focus:ring-luna-secondary transition duration-200 ${signInForm.formState.errors.password
                                            ? 'border-red-500 dark:border-red-500'
                                            : 'border-luna-text-light/20 dark:border-luna-text-dark/20'
                                            }`}
                                        id="signin-password"
                                        placeholder="••••••••"
                                        type="password"
                                    />
                                    {signInForm.formState.errors.password && (
                                        <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                                            {signInForm.formState.errors.password.message}
                                        </p>
                                    )}
                                </div>
                                <div className="flex items-center justify-between text-sm">
                                    <div className="flex items-center gap-2">
                                        <input className="h-4 w-4 rounded border-gray-300 text-luna-secondary focus:ring-luna-secondary" id="remember" type="checkbox" />
                                        <label htmlFor="remember">Remember me</label>
                                    </div>
                                    <button type="button" className="font-medium text-luna-secondary hover:underline cursor-pointer bg-transparent border-0 p-0" onClick={handlePasswordReset}>Forgot password?</button>
                                </div>
                                <button className="flex w-full min-w-[84px] max-w-[480px] cursor-pointer items-center justify-center overflow-hidden rounded-lg h-11 px-4 bg-luna-secondary text-luna-primary text-sm font-bold leading-normal tracking-[0.015em] hover:opacity-90 transition-opacity shadow-lg shadow-luna-secondary/20 disabled:opacity-50" type="submit" disabled={loading}>
                                    <span className="truncate">{loading ? 'Signing In...' : 'Log In'}</span>
                                </button>
                            </form>
                        )}
                        <div className="relative my-6">
                            <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-luna-text-light/10 dark:border-luna-text-dark/10"></div>
                            </div>
                            <div className="relative flex justify-center text-sm">
                                <span className="bg-white/50 dark:bg-luna-primary/20 px-2 text-luna-text-light/60 dark:text-luna-text-muted-dark">Or continue with</span>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <button onClick={() => handleOAuthSignIn('google')} className="flex w-full items-center justify-center gap-3 rounded-md border border-luna-text-light/20 dark:border-luna-text-dark/20 py-2.5 text-sm font-medium hover:bg-luna-bg-light dark:hover:bg-luna-bg-dark/50 transition-colors" disabled={loading}>
                                <img alt="Google logo" className="h-5 w-5" src="https://lh3.googleusercontent.com/aida-public/AB6AXuCPtZAzCg2TQqdItawkRHlEranYWW7CUojibudARnx7TPZ_cQkghnWhsce3CCDnKiEPaB5sGF99XCbWiEZldxFBdROAkXBqgepLN8iaBISBqrTiTetKePrv0Afl6wPLp4WsrWF6f0mfNHRNUu2N17ShLAn0Q3HdDUbYMxdAMfL064vFR9gSfT4fgKoaKJhIG2tjKKE9xV6-zWyRtVf_0bmJdkAC4uUIWTjoOXsGAOEB1mJiOMSVykuAZKrMs8b-8KWnWbO5OlJOa1Ab" onError={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    target.style.display = 'none';
                                }} />
                                Google
                            </button>
                            <button onClick={() => handleOAuthSignIn('azure')} className="flex w-full items-center justify-center gap-3 rounded-md border border-luna-text-light/20 dark:border-luna-text-dark/20 py-2.5 text-sm font-medium hover:bg-luna-bg-light dark:hover:bg-luna-bg-dark/50 transition-colors opacity-50 cursor-not-allowed" disabled={true} title="Coming soon!">
                                <img alt="Microsoft logo" className="h-5 w-5" src="https://lh3.googleusercontent.com/aida-public/AB6AXuBMU0XeK8sBMxKK0XLN5jYA5pyD92FVRRZ-xuIeITzCzdLd2yDj8a4zZQXg_SjEmdfP9DaJgINQ0RFFeKxDjbX1emIkAam7XiM_-0n7Q4vCadFzeb-IJ6W88dvYAQVqEidKqtg83l5jNnjMxoo49Qt4fmbeZbPEbHfnrl5RghUQt3kQLYmmLAhP93C7Hf1RxkmlqoFRzTgxUMWDU1QJ2gx_H9B_E7RuKby-3foRmDv8nhYclgppTXovL5x9P1tyhpEqUFndQjxNAaSK" onError={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    target.style.display = 'none';
                                }} />
                                Microsoft <span className="text-xs text-luna-text-light/60 dark:text-luna-text-muted-dark">(Coming soon!)</span>
                            </button>
                        </div>
                    </div>
                    <div className="border-t border-luna-text-light/10 dark:border-luna-text-dark/10 bg-black/5 dark:bg-black/10 px-8 py-5 text-center text-sm">
                        <p className="text-luna-text-light/80 dark:text-luna-text-dark/80">
                            {isSignUp ? 'Already have an account?' : "Don't have an account?"}
                            <button type="button" className="font-bold text-luna-secondary hover:underline cursor-pointer ml-1 bg-transparent border-0 p-0" onClick={() => {
                                setIsSignUp(!isSignUp);
                                setAuthError(null);
                                signUpForm.reset();
                                signInForm.reset();
                            }}>
                                {isSignUp ? 'Log In' : 'Sign up for free'}
                            </button>
                        </p>
                    </div>
                </div>
            </main>
        </div>
    );
};


const App: React.FC = () => {
    // Global loading and error states
    const [isLoading, setIsLoading] = useState(false);
    const [globalError, setGlobalError] = useState<string | null>(null);

    // Initialize error tracking on mount
    useEffect(() => {
        initSentry().catch((error) => {
            logger.warn('Failed to initialize error tracking:', error);
        });
    }, []);

    // Track online/offline status
    useEffect(() => {
        const handleOnline = () => {
            if (globalError && isRetryableError({ message: globalError })) {
                setGlobalError(null); // Clear retryable errors on reconnect
            }
        };
        const handleOffline = () => {
            // No-op placeholder to keep symmetrical listeners; reserved for future use
        };

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, [globalError]);

    // Initialize from URL path route
    // BUT: Check for Stripe redirect parameters first - if present, override to Dashboard
    const initializeFromURL = (): { page: string; caseId: string | null; templateSlug: string | null } => {
        // CRITICAL: Check if we're on SuperAdmin page first - always allow it (highest priority)
        const pathname = window.location.pathname;
        console.log('[initializeFromURL] Checking pathname:', pathname);
        // SuperAdmin route removed - may be re-added in the future

        // Check for Stripe redirect parameters first
        const urlParams = new URLSearchParams(window.location.search);
        const sessionId = urlParams.get('session_id');
        const subscription = urlParams.get('subscription');
        const redirectTo = urlParams.get('redirect');

        // If Stripe success redirect detected, initialize to Dashboard regardless of path
        const isStripeSuccess = (sessionId && subscription === 'success') ||
            (redirectTo === 'dashboard' && sessionId && subscription === 'success');

        if (isStripeSuccess) {
            console.log('[Init] Stripe redirect detected in initializeFromURL, setting initial route to Dashboard');
            return {
                page: 'Dashboard',
                caseId: null,
                templateSlug: null,
            };
        }

        const route = parsePathRoute();
        // If route is invalid, set to NotFound
        if (!route.isValid && route.page === 'NotFound') {
            return {
                page: 'NotFound',
                caseId: null,
                templateSlug: null,
            };
        }
        return {
            page: route.page,
            caseId: route.caseId || null,
            templateSlug: route.templateSlug || null,
        };
    };

    const initialRoute = initializeFromURL();

    // CRITICAL: Force SuperAdmin if URL matches (override any other logic)
    const finalInitialPage = initialRoute.page;

    console.log('[App Init] Initial route:', initialRoute.page, 'Final page:', finalInitialPage);

    const [currentPage, setCurrentPage] = useState<string>(finalInitialPage);
    const currentPageRef = useRef<string>(finalInitialPage); // Keep ref in sync for useEffect
    const [session, setSession] = useState<Session | null>(null);
    const [profile, setProfile] = useState<Profile | null>(null); // New state for user profile
    const [showAuthPage, setShowAuthPage] = useState(false);
    const [authMode, setAuthMode] = useState<'signup' | 'login'>('login');
    const [selectedCaseId, setSelectedCaseId] = useState<string | null>(initialRoute.caseId);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
    const [cases, setCases] = useState<Case[]>([]);
    const [folders, setFolders] = useState<Folder[]>([]);
    const [pendingNavigation, setPendingNavigation] = useState<{
        page: string;
        caseId?: string | null;
        templateSlug?: string | null;
        replace?: boolean;
    } | null>(null);

    // Cookie consent state for GDPR compliance
    const [showCookieBanner, setShowCookieBanner] = useState(false);
    const [showCookieSettings, setShowCookieSettings] = useState(false);
    const [cookiePreferences, setCookiePreferences] = useState({
        essential: true, // Always true, cannot be disabled
        functional: false,
        analytics: false
    });

    // Check cookie consent on mount
    useEffect(() => {
        const cookieConsent = localStorage.getItem('lrdene_cookie_consent');
        if (!cookieConsent) {
            setShowCookieBanner(true);
        } else {
            try {
                const saved = JSON.parse(cookieConsent);
                setCookiePreferences({
                    essential: true,
                    functional: saved.functional || false,
                    analytics: saved.analytics || false
                });
            } catch (e) {
                // Invalid format, show banner
                setShowCookieBanner(true);
            }
        }
    }, []);

    const saveCookiePreferences = (prefs: typeof cookiePreferences) => {
        localStorage.setItem('lrdene_cookie_consent', JSON.stringify({
            ...prefs,
            timestamp: new Date().toISOString()
        }));
        setShowCookieBanner(false);
        setShowCookieSettings(false);
    };

    const handleCookieAccept = () => {
        const prefs = {
            essential: true,
            functional: true,
            analytics: true
        };
        setCookiePreferences(prefs);
        saveCookiePreferences(prefs);
    };

    const handleCookieReject = () => {
        const prefs = {
            essential: true,
            functional: false,
            analytics: false
        };
        setCookiePreferences(prefs);
        saveCookiePreferences(prefs);
    };

    const handleCookieSave = () => {
        saveCookiePreferences(cookiePreferences);
    };

    // Admin/SuperAdmin functionality removed - may be re-added in the future

    // Initialize session immediately on mount (before other effects)
    // This prevents showing logged out state when user is actually logged in
    useEffect(() => {
        // Immediately check for existing session to avoid showing logged out state
        supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
            if (initialSession) {
                // CRITICAL: Set session state immediately
                setSession(initialSession);
                // Mark this as a restored session (not a new login) by setting the ref
                // This prevents false "login" detection when onAuthStateChange fires
                previousSessionRef.current = initialSession;
                setShowAuthPage(false);
                // Fetch profile for logged-in users
                fetchProfile(initialSession.user.id);
            }
            // If no session, explicitly set ref to null so onAuthStateChange can detect new logins
            else {
                previousSessionRef.current = null;
            }
        }).catch((error) => {
            console.error('Error getting initial session:', error);
            // On error, set ref to null so we can detect new logins
            previousSessionRef.current = null;
        });
    }, []); // Run only on mount

    // Initialize template from URL if needed
    useEffect(() => {
        if (initialRoute.templateSlug) {
            const templates: Template[] = [
                { id: '1', icon: '📝', title: 'DSGVO Auskunft', description: 'Request personal data information under GDPR.', slug: 'dsgvo-auskunft' },
                { id: '2', icon: '🗑️', title: 'DSGVO Löschung', description: 'Request deletion of personal data under GDPR.', slug: 'dsgvo-loeschung' },
                { id: '3', icon: '🏠', title: 'Mängelanzeige (Mietrecht)', description: 'Notify landlord of apartment defects.', slug: 'miet-maengelanzeige' },
                { id: '4', icon: '📉', title: 'Mietminderung (Mietrecht)', description: 'Demand rent reduction due to defects.', slug: 'miet-minderung' },
                { id: '5', icon: '⚠️', title: 'Abmahnung Widerspruch', description: 'Draft a response to a warning letter.', slug: 'abmahnung-widerspruch' },
                { id: '6', icon: '💳', title: 'SCHUFA Bestreitung', description: 'Dispute incorrect SCHUFA entries.', slug: 'schufa-bestreitung' },
                { id: '7', icon: '✅', title: 'SCHUFA Lösung', description: 'Request resolution for SCHUFA issues.', slug: 'schufa-loesung' },
            ];
            const template = templates.find(t => t.slug === initialRoute.templateSlug);
            if (template) {
                setSelectedTemplate(template);
            }
        }
    }, []);

    const fetchProfile = useCallback(async (userId: string) => {
        // Fetch profile
        const { data: profileData, error: profileError } = await supabase
            .from('profiles')
            .select(`id, first_name, last_name, role, avatar_url, firm, notification_preferences, subscription_id`)
            .eq('id', userId)
            .single();

        if (profileError) {
            console.error('Error fetching profile:', profileError);
            setProfile(null);
            return;
        }

        // Check for unlinked subscription by email (if user just signed up after checkout)
        // OR if subscription exists but profile doesn't have subscription_id
        const { data: { user: userData } } = await supabase.auth.getUser();
        if (userData?.email) {
            // Check for unlinked subscription by email (user_id is null)
            const { data: unlinkedSub } = await supabase
                .from('subscriptions')
                .select('id, user_id')
                .eq('customer_email', userData.email)
                .order('created_at', { ascending: false })
                .limit(1)
                .maybeSingle();

            if (unlinkedSub) {
                // If subscription exists but isn't linked to this user
                if (!unlinkedSub.user_id || unlinkedSub.user_id !== userId) {
                    console.log('[fetchProfile] Found unlinked subscription, linking to user:', userId);
                    // Link the subscription to this user
                    const { error: linkError } = await supabase
                        .from('subscriptions')
                        .update({ user_id: userId })
                        .eq('id', unlinkedSub.id);

                    if (!linkError) {
                        // Update profile with subscription_id
                        const { error: profileUpdateError } = await supabase
                            .from('profiles')
                            .update({ subscription_id: unlinkedSub.id })
                            .eq('id', userId);

                        if (!profileUpdateError) {
                            console.log('[fetchProfile] ✅ Successfully linked subscription to profile');
                            // Update profileData for immediate display
                            profileData.subscription_id = unlinkedSub.id;
                        } else {
                            console.error('[fetchProfile] Error updating profile with subscription:', profileUpdateError);
                        }
                    } else {
                        console.error('[fetchProfile] Error linking subscription:', linkError);
                    }
                } else if (!profileData.subscription_id && unlinkedSub.user_id === userId) {
                    // Subscription is linked to user but profile doesn't have subscription_id
                    console.log('[fetchProfile] Subscription linked to user but profile missing subscription_id, updating...');
                    const { error: profileUpdateError } = await supabase
                        .from('profiles')
                        .update({ subscription_id: unlinkedSub.id })
                        .eq('id', userId);

                    if (!profileUpdateError) {
                        console.log('[fetchProfile] ✅ Updated profile with subscription_id');
                        profileData.subscription_id = unlinkedSub.id;
                    } else {
                        console.error('[fetchProfile] Error updating profile:', profileUpdateError);
                    }
                }
            }
        }

        const subscriptionSelectFields = 'id, user_id, status, plan_name, current_period_end, cancel_at_period_end, stripe_subscription_id, stripe_price_id, created_at';
        const activeSubscriptionStatuses = ['active', 'trialing', 'past_due'];

        const fetchLatestSubscription = async (column: 'user_id' | 'customer_email', value: string, options?: { nonFreeOnly?: boolean }) => {
            let query = supabase
                .from('subscriptions')
                .select(subscriptionSelectFields)
                .eq(column, value)
                .in('status', activeSubscriptionStatuses)
                .order('created_at', { ascending: false })
                .limit(1);

            if (options?.nonFreeOnly) {
                query = query.neq('plan_name', 'free');
            }

            const { data } = await query.maybeSingle();
            return data || null;
        };

        const linkProfileToSubscription = async (subscriptionId: string) => {
            if (profileData.subscription_id === subscriptionId) return;
            const { error: linkError } = await supabase
                .from('profiles')
                .update({ subscription_id: subscriptionId })
                .eq('id', userId);
            if (linkError) {
                console.error('[fetchProfile] Failed to link profile to subscription:', linkError);
                return;
            }
            profileData.subscription_id = subscriptionId;
        };

        const isMeaningfulPaidSubscription = (sub: any | null) => {
            if (!sub) return false;
            const normalizedPlan = normalizePlanName(sub.plan_name, sub.stripe_price_id);
            return normalizedPlan !== 'free' && normalizedPlan !== 'unknown' && !!sub.stripe_subscription_id;
        };

        // Fetch subscription if subscription_id exists
        // Also check for subscription by user_id or email if subscription_id is missing or outdated
        let subscription = null;
        if (profileData.subscription_id) {
            const { data: subData } = await supabase
                .from('subscriptions')
                .select(subscriptionSelectFields)
                .eq('id', profileData.subscription_id)
                .maybeSingle();

            if (subData) {
                subscription = {
                    ...subData,
                    plan_name: normalizePlanName(subData.plan_name, subData.stripe_price_id),
                };
            }
        }

        const shouldFindLatest = !subscription || subscription.plan_name === 'free' || !subscription.stripe_subscription_id;

        if (shouldFindLatest) {
            let latestSubscription = await fetchLatestSubscription('user_id', userId, { nonFreeOnly: true });
            if (!latestSubscription) {
                latestSubscription = await fetchLatestSubscription('user_id', userId);
            }

            if (!latestSubscription && userData?.email) {
                latestSubscription = await fetchLatestSubscription('customer_email', userData.email, { nonFreeOnly: true });
                if (!latestSubscription) {
                    latestSubscription = await fetchLatestSubscription('customer_email', userData.email);
                }
                if (latestSubscription && (!latestSubscription.user_id || latestSubscription.user_id !== userId)) {
                    const { error: attachError } = await supabase
                        .from('subscriptions')
                        .update({ user_id: userId })
                        .eq('id', latestSubscription.id);
                    if (attachError) {
                        console.error('[fetchProfile] Failed to attach subscription to user:', attachError);
                    }
                }
            }

            if (latestSubscription && (isMeaningfulPaidSubscription(latestSubscription) || !subscription)) {
                subscription = {
                    ...latestSubscription,
                    plan_name: normalizePlanName(latestSubscription.plan_name, latestSubscription.stripe_price_id),
                };
                await linkProfileToSubscription(latestSubscription.id);
            }
        }

        const profileObj = {
            ...profileData,
            subscription: subscription,
        } as Profile;
        setProfile(profileObj);

        // Update error tracking with profile context
        errorTracking.addBreadcrumb(
            'Profile loaded',
            'auth',
            'info',
            { userId: profileObj.id, role: profileObj.role }
        );
    }, []);

    // Add useEffect to listen for profile refresh requests
    useEffect(() => {
        const handleRefreshProfile = () => {
            if (session?.user?.id) {
                fetchProfile(session.user.id);
            }
        };

        const handleProfileUpdated = (event: CustomEvent) => {
            const updatedProfile = event.detail;
            setProfile(updatedProfile);
        };

        window.addEventListener('refreshProfileRequested', handleRefreshProfile);
        window.addEventListener('profileUpdated', handleProfileUpdated as EventListener);
        return () => {
            window.removeEventListener('refreshProfileRequested', handleRefreshProfile);
            window.removeEventListener('profileUpdated', handleProfileUpdated as EventListener);
        };
    }, [session?.user?.id, fetchProfile]);

    // Track if Stripe redirect has been processed to avoid duplicate processing
    const stripeRedirectProcessedRef = useRef(false);
    const isStripeRedirectRef = useRef(false);

    // Handle Stripe checkout success redirect globally
    // This runs IMMEDIATELY on mount - even before other useEffects
    useEffect(() => {
        // Skip if already processed
        if (stripeRedirectProcessedRef.current) {
            return;
        }

        const urlParams = new URLSearchParams(window.location.search);
        const sessionId = urlParams.get('session_id');
        const subscription = urlParams.get('subscription');
        const canceled = urlParams.get('canceled');
        const redirectTo = urlParams.get('redirect');

        // Handle canceled checkout
        if (canceled === 'true') {
            console.log('[App] Stripe checkout canceled');
            stripeRedirectProcessedRef.current = true;
            // Clean URL
            const cleanPath = window.location.pathname;
            window.history.replaceState({}, '', cleanPath);
            // Stay on current page (likely Pricing)
            return;
        }

        // Handle successful checkout - check for both formats:
        // 1. /dashboard?session_id=...&subscription=success
        // 2. /?redirect=dashboard&session_id=...&subscription=success
        const hasSessionId = !!sessionId;
        const hasSubscriptionSuccess = subscription === 'success';
        const hasRedirectDashboard = redirectTo === 'dashboard';

        console.log('[App] Stripe redirect check:', {
            sessionId: hasSessionId,
            subscription: subscription,
            subscriptionSuccess: hasSubscriptionSuccess,
            redirectTo: redirectTo,
            hasRedirectDashboard: hasRedirectDashboard,
            pathname: window.location.pathname,
            fullUrl: window.location.href
        });

        const isStripeSuccess = (hasSessionId && hasSubscriptionSuccess) ||
            (hasRedirectDashboard && hasSessionId && hasSubscriptionSuccess);

        console.log('[App] isStripeSuccess result:', isStripeSuccess);

        if (isStripeSuccess) {
            console.log('[App] ✅ Stripe checkout success detected, sessionId:', sessionId);
            stripeRedirectProcessedRef.current = true;
            isStripeRedirectRef.current = true; // Mark that this is a Stripe redirect

            // CRITICAL: Set currentPage to Dashboard IMMEDIATELY and synchronously
            // Use functional update to ensure it happens
            console.log('[App] Immediately setting currentPage to Dashboard (sync)');
            setCurrentPage('Dashboard');
            currentPageRef.current = 'Dashboard';

            // Clean URL immediately - this is critical
            window.history.replaceState({}, '', '/dashboard');
            console.log('[App] URL cleaned, pathname is now:', window.location.pathname);

            // Show success message
            setTimeout(() => {
                toast.success('Subscription successful! Your account has been upgraded. You can view your subscription in Settings.', {
                    duration: 5000,
                });
            }, 100);

            // Then check session and finalize navigation
            const handleSuccess = async () => {
                try {
                    // Check session
                    const { data: { session: currentSession }, error: sessionError } = await supabase.auth.getSession();

                    if (sessionError) {
                        console.error('[App] Error checking session:', sessionError);
                    }

                    if (currentSession?.user) {
                        console.log('[App] User logged in, ensuring Dashboard navigation');

                        // Queue navigation through the central handler so all related state stays in sync
                        setPendingNavigation({ page: 'Dashboard', replace: true });

                        // Fetch profile immediately to get updated subscription
                        // Note: Webhook processing may take 5-30 seconds, so we'll poll for subscription
                        try {
                            await fetchProfile(currentSession.user.id);
                            console.log('[App] Profile fetched, subscription should be updated');

                            // Check if subscription was linked - if not, webhook might still be processing
                            const { data: { session: checkSession } } = await supabase.auth.getSession();
                            if (checkSession?.user) {
                                const { data: profileCheck } = await supabase
                                    .from('profiles')
                                    .select('subscription_id')
                                    .eq('id', checkSession.user.id)
                                    .single();

                                if (!profileCheck?.subscription_id) {
                                    console.log('[App] Subscription not linked yet, webhook may still be processing. Will retry...');
                                    // Webhook processing can take 5-30 seconds, poll for subscription
                                    let retryCount = 0;
                                    const maxRetries = 10; // 10 retries = ~30 seconds
                                    const pollInterval = 3000; // 3 seconds between retries

                                    const pollForSubscription = setInterval(async () => {
                                        retryCount++;
                                        console.log(`[App] Polling for subscription (attempt ${retryCount}/${maxRetries})...`);

                                        try {
                                            await fetchProfile(checkSession.user.id);
                                            const { data: updatedProfile } = await supabase
                                                .from('profiles')
                                                .select('subscription_id')
                                                .eq('id', checkSession.user.id)
                                                .single();

                                            if (updatedProfile?.subscription_id) {
                                                console.log('[App] ✅ Subscription linked! Profile updated.');
                                                clearInterval(pollForSubscription);
                                                toast.success('Subscription activated! Your account has been upgraded.', {
                                                    duration: 5000,
                                                });
                                            } else if (retryCount >= maxRetries) {
                                                console.log('[App] Max retries reached. Subscription may need manual linking.');
                                                clearInterval(pollForSubscription);
                                                toast.info('Payment successful! Subscription is being processed. Please check Settings in a few moments.', {
                                                    duration: 8000,
                                                });
                                            }
                                        } catch (pollError) {
                                            console.error('[App] Error polling for subscription:', pollError);
                                            if (retryCount >= maxRetries) {
                                                clearInterval(pollForSubscription);
                                            }
                                        }
                                    }, pollInterval);
                                } else {
                                    console.log('[App] ✅ Subscription already linked!');
                                }
                            }
                        } catch (profileError) {
                            console.error('[App] Error fetching profile:', profileError);
                            // Retry after a delay
                            setTimeout(async () => {
                                try {
                                    await fetchProfile(currentSession.user.id);
                                } catch (retryError) {
                                    console.error('[App] Retry fetch profile failed:', retryError);
                                }
                            }, 3000);
                        }
                    } else {
                        console.log('[App] User not logged in, checking for subscription by email...');

                        // Try to retrieve checkout session from Stripe to get customer email
                        // This allows us to show a better message
                        const shouldSignUp = confirm(
                            '✅ Payment successful! To access your subscription, please create an account or sign in.\n\n' +
                            'Use the same email address you used for checkout.\n\n' +
                            'Click OK to go to sign up, or Cancel to sign in.'
                        );

                        if (shouldSignUp) {
                            setShowAuthPage(true);
                            setAuthMode('signup');
                            setCurrentPage('Landing');
                            currentPageRef.current = 'Landing';
                            updateURL('Landing');
                        } else {
                            setShowAuthPage(true);
                            setAuthMode('login');
                            setCurrentPage('Landing');
                            currentPageRef.current = 'Landing';
                            updateURL('Landing');
                        }
                    }
                } catch (error) {
                    console.error('[App] Error in handleSuccess:', error);
                    toast.error('There was an error processing your subscription. Please check your account in Settings.');
                }
            };

            // Execute immediately
            handleSuccess();
        }
    }, [fetchProfile]); // Run only on mount - check URL params immediately

    // Save currentPage to localStorage whenever it changes
    useEffect(() => {
        if (currentPage) {
            localStorage.setItem('lrdene_currentPage', currentPage);
        }
    }, [currentPage]);

    const handleProfileUpdate = async (updates: Partial<Profile>) => {
        if (!session?.user.id) throw new Error("User not logged in.");

        const { error } = await supabase
            .from('profiles')
            .update(updates)
            .eq('id', session.user.id);

        if (error) {
            throw error;
        }

        // Re-fetch profile to update state immediately
        await fetchProfile(session.user.id);
    };

    // Track previous session state to detect actual login/logout transitions
    const previousSessionRef = useRef<Session | null>(null);

    useEffect(() => {
        // CRITICAL FIX: Check session on initial mount - if logged in and on Landing, redirect to Dashboard
        // BUT: Skip if Stripe redirect is being handled
        console.log('[Init] Checking initial session...');

        // Check if Stripe redirect is present - if so, don't interfere
        const urlParams = new URLSearchParams(window.location.search);
        const sessionId = urlParams.get('session_id');
        const subscription = urlParams.get('subscription');
        const redirectTo = urlParams.get('redirect');
        const isStripeSuccess = (sessionId && subscription === 'success') ||
            (redirectTo === 'dashboard' && sessionId && subscription === 'success');

        if (isStripeSuccess) {
            console.log('[Init] Stripe redirect detected - skipping initial session redirect to avoid conflict');
            return;
        }

        supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
            console.log('[Init] Initial session check result:', !!initialSession);
            if (initialSession) {
                console.log('[Init] ✅ Session found! Setting session state');
                setSession(initialSession);

                const currentRoute = parsePathRoute();
                console.log('[Init] Current route:', currentRoute.page, 'pathname:', window.location.pathname);

                // If logged in and on Landing page, immediately redirect to Dashboard
                if (currentRoute.page === 'Landing' || currentRoute.page === null || window.location.pathname === '/') {
                    console.log('[Init] 🔄 Logged in user on Landing - redirecting to Dashboard');
                    // Use React navigation to preserve state
                    setTimeout(() => {
                        console.log('[Init] Executing redirect to Dashboard');
                        handleNavigate('Dashboard', undefined, undefined, true);
                    }, 100);
                } else {
                    console.log('[Init] Already on authenticated page:', currentRoute.page);
                    fetchProfile(initialSession.user.id);
                }
            }
        });

        // Note: Stripe redirect handling is now done in a separate useEffect that runs on mount
        // This ensures it processes redirect params immediately when the page loads

        // Set up auth state change listener - this is the PRIMARY way we handle sessions
        const { data: { subscription: authSubscription } } = supabase.auth.onAuthStateChange((event, session) => {
            console.log('[Auth onAuthStateChange] 🔔 Event:', event, 'Session:', !!session, 'Current page:', currentPageRef.current);

            // CRITICAL: Always update session state immediately - this is the PRIMARY source of truth
            // This MUST happen first, before any other logic
            setSession(session);
            console.log('[Auth onAuthStateChange] ✅ Session state updated');

            // Update error tracking user context
            if (session?.user) {
                errorTracking.setUser(session.user.id, session.user.email);
            } else {
                errorTracking.clearUser();
            }

            // Update ref for transition detection BEFORE updating ref
            const previousSession = previousSessionRef.current;
            const isLogin = !previousSession && session;
            const isLogout = previousSession && !session;

            // Debug logging for login flow
            console.log('[Auth onAuthStateChange] Previous session:', !!previousSession, 'isLogin:', isLogin, 'isLogout:', isLogout);

            // Update ref AFTER checking for transitions
            previousSessionRef.current = session;

            if (session) {
                // User has a session - always hide auth page and maintain logged-in state
                setShowAuthPage(false);

                // CRITICAL: Only handle navigation on actual new logins
                // Prevent navigation loops by checking if we're already on an authenticated page
                const authenticatedPages = ['Dashboard', 'Chat', 'Documents', 'Templates', 'Settings', 'News', 'Profile', 'TemplateWizard', 'TemplateView', 'Admin', 'SuperAdmin'];
                const currentPageState = currentPageRef.current;
                const isAlreadyOnAuthenticatedPage = authenticatedPages.includes(currentPageState || '');

                // Only navigate on actual login events, not on token refresh or other events
                // Also check if we're already on an authenticated page to prevent loops
                if ((event === 'SIGNED_IN' && isLogin) || (event === 'INITIAL_SESSION' && isLogin && !isAlreadyOnAuthenticatedPage)) {
                    console.log('[Auth onAuthStateChange] 🔐 LOGIN DETECTED! Event:', event, 'Navigating...');

                    // User just logged in - ALWAYS navigate to Dashboard (or saved authenticated page)
                    // CRITICAL: Never navigate to Landing page after login
                    const savedPage = localStorage.getItem('lrdene_currentPage');

                    // Check current route and currentPage state
                    const urlRoute = parsePathRoute();

                    // CRITICAL: Check for Admin page FIRST (before Landing/invalid check)
                    if (urlRoute.page === 'Admin' || sessionStorage.getItem('returnToAdmin') === 'true') {
                        console.log('[Auth] 🔐 User trying to access Admin page, navigating to Admin');
                        sessionStorage.removeItem('returnToAdmin');
                        if (currentPageState !== 'Admin') {
                            handleNavigate('Admin', undefined, undefined);
                        }
                        fetchProfile(session.user.id);
                        return;
                    }

                    // CRITICAL: Allow SuperAdmin page to stay (don't redirect away from it)
                    if (urlRoute.page === 'SuperAdmin') {
                        // Allow staying on SuperAdmin page - component will show access denied if not authorized
                        if (currentPageState !== 'SuperAdmin') {
                            handleNavigate('SuperAdmin', undefined, undefined, false);
                        }
                        fetchProfile(session.user.id);
                        return;
                    }

                    // CRITICAL: If on Landing page (either URL or state), ALWAYS redirect to Dashboard
                    if (urlRoute.page === 'Landing' || urlRoute.page === null || urlRoute.page === undefined ||
                        currentPageState === 'Landing' || !currentPageState ||
                        (!authenticatedPages.includes(urlRoute.page || '') && urlRoute.page !== 'SuperAdmin') ||
                        (!authenticatedPages.includes(currentPageState) && currentPageState !== 'SuperAdmin')) {

                        let targetPage = 'Dashboard';
                        if (savedPage && authenticatedPages.includes(savedPage)) {
                            targetPage = savedPage;
                        }

                        // Only navigate if not already on target page
                        if (currentPageState !== targetPage) {
                            if (import.meta.env.DEV) console.log('[Auth] Redirecting from Landing/invalid to:', targetPage);
                            handleNavigate(targetPage, undefined, undefined, true);
                        }
                    } else if (urlRoute.page === 'Chat' && urlRoute.caseId && currentPageState !== 'Chat') {
                        // Allow staying on case chat if that's the route
                        handleNavigate('Chat', urlRoute.caseId);
                    } else if (urlRoute.page === 'TemplateView' && urlRoute.templateSlug && currentPageState !== 'TemplateView') {
                        // Allow staying on template view if that's the route
                        handleNavigate('TemplateView', undefined, urlRoute.templateSlug);
                    } else if (urlRoute.page === 'TemplateWizard' && urlRoute.templateSlug && currentPageState !== 'TemplateWizard') {
                        // Allow staying on template wizard if that's the route
                        handleNavigate('TemplateWizard', undefined, urlRoute.templateSlug);
                    } else if (urlRoute.page && (authenticatedPages.includes(urlRoute.page) || urlRoute.page === 'SuperAdmin') && currentPageState !== urlRoute.page) {
                        // Valid authenticated page from URL (including SuperAdmin) - only navigate if different
                        handleNavigate(urlRoute.page, urlRoute.caseId || undefined, urlRoute.templateSlug || undefined);
                    } else if (savedPage && authenticatedPages.includes(savedPage) && currentPageState !== savedPage) {
                        // Use saved page - only navigate if different
                        handleNavigate(savedPage);
                    } else if (!isAlreadyOnAuthenticatedPage && currentPageState !== 'Dashboard') {
                        // Default to Dashboard - only navigate if not already on authenticated page
                        handleNavigate('Dashboard', undefined, undefined, true);
                    }
                    fetchProfile(session.user.id);
                } else {
                    // Session exists (restored, refreshed, or other events) - just update profile
                    // This allows logged-in users to navigate freely without interruption
                    // Users can go to Landing page and remain logged in
                    // CRITICAL: Don't navigate on TOKEN_REFRESHED or other non-login events
                    fetchProfile(session.user.id);
                }
            } else {
                // No session - user is logged out
                if (event === 'SIGNED_OUT' || isLogout) {
                    // Check if we're on SuperAdmin page - allow staying (component will show access denied)
                    const urlRoute = parsePathRoute();
                    if (urlRoute.page === 'SuperAdmin') {
                        // Don't redirect - let component show "Super Admin Only" message
                        setProfile(null);
                        return;
                    }
                    handleNavigate('Landing');
                    setProfile(null);
                }
            }
        });

        return () => authSubscription.unsubscribe()
    }, [fetchProfile]); // Removed currentPage from dependencies to prevent redirects when user navigates to Landing

    const fetchCases = async (showLoading = true) => {
        if (showLoading) setIsLoading(true);
        setGlobalError(null);

        try {
            const { data, error } = await supabase
                .from('cases')
                .select('*')
                .order('created_at', { ascending: false });

            if (error) {
                throw error;
            }

            setCases(data as Case[]);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to load cases';
            setGlobalError(errorMessage);
            console.error('Error fetching cases:', err);

            // If retryable, show retry option
            if (isRetryableError(err)) {
                // Error will be shown via ErrorMessage component
            }
        } finally {
            if (showLoading) setIsLoading(false);
        }
    };

    const fetchFolders = async () => {
        if (!session?.user.id) return;

        try {
            const { data, error } = await supabase
                .from('folders')
                .select('*')
                .eq('user_id', session.user.id)
                .order('name', { ascending: true });

            if (error) {
                console.error('Error fetching folders:', error);
                return;
            }

            setFolders(data as Folder[]);
        } catch (err) {
            console.error('Error fetching folders:', err);
        }
    };

    const handleDeleteCase = async (caseId: string) => {
        if (!session?.user.id) {
            setGlobalError('You must be logged in to delete a case');
            return;
        }

        try {
            const { error } = await supabase
                .from('cases')
                .delete()
                .eq('id', caseId)
                .eq('user_id', session.user.id);

            if (error) {
                throw error;
            }

            // Remove from local state
            setCases(prevCases => prevCases.filter(c => c.id !== caseId));

            // If deleted case was selected, navigate to dashboard
            if (selectedCaseId === caseId) {
                handleNavigate('Dashboard');
            }
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to delete case';
            setGlobalError(errorMessage);
            console.error('Error deleting case:', err);
        }
    };

    const handleRenameCase = async (caseId: string, newTitle: string) => {
        if (!session?.user.id) {
            setGlobalError('You must be logged in to rename a case');
            return;
        }

        if (!newTitle.trim()) {
            setGlobalError('Case title cannot be empty');
            return;
        }

        try {
            const { error } = await supabase
                .from('cases')
                .update({ title: newTitle.trim() })
                .eq('id', caseId)
                .eq('user_id', session.user.id);

            if (error) {
                throw error;
            }

            // Update local state
            setCases(prevCases => prevCases.map(c =>
                c.id === caseId ? { ...c, title: newTitle.trim() } : c
            ));
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to rename case';
            setGlobalError(errorMessage);
            console.error('Error renaming case:', err);
        }
    };

    const handleMoveToFolder = async (caseId: string, folderId: string | null) => {
        if (!session?.user.id) {
            setGlobalError('You must be logged in to move a case');
            return;
        }

        try {
            const { error } = await supabase
                .from('cases')
                .update({ folder_id: folderId })
                .eq('id', caseId)
                .eq('user_id', session.user.id);

            if (error) {
                throw error;
            }

            // Update local state
            setCases(prevCases => prevCases.map(c =>
                c.id === caseId ? { ...c, folder_id: folderId } : c
            ));
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to move case';
            setGlobalError(errorMessage);
            console.error('Error moving case:', err);
        }
    };

    const handleCreateFolder = async (name: string) => {
        if (!session?.user.id) {
            setGlobalError('You must be logged in to create a folder');
            return;
        }

        if (!name.trim()) {
            setGlobalError('Folder name cannot be empty');
            return;
        }

        try {
            const { data, error } = await supabase
                .from('folders')
                .insert({ name: name.trim(), user_id: session.user.id })
                .select()
                .single();

            if (error) {
                throw error;
            }

            // Update local state
            setFolders(prevFolders => [...prevFolders, data as Folder].sort((a, b) => a.name.localeCompare(b.name)));
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to create folder';
            setGlobalError(errorMessage);
            console.error('Error creating folder:', err);
        }
    };

    const handleDeleteFolder = async (folderId: string) => {
        if (!session?.user.id) {
            setGlobalError('You must be logged in to delete a folder');
            return;
        }

        try {
            // First, move all cases in this folder to "No Folder" (null)
            const { error: updateError } = await supabase
                .from('cases')
                .update({ folder_id: null })
                .eq('folder_id', folderId)
                .eq('user_id', session.user.id);

            if (updateError) {
                throw updateError;
            }

            // Then delete the folder
            const { error: deleteError } = await supabase
                .from('folders')
                .delete()
                .eq('id', folderId)
                .eq('user_id', session.user.id);

            if (deleteError) {
                throw deleteError;
            }

            // Update local state
            setFolders(prevFolders => prevFolders.filter(f => f.id !== folderId));
            setCases(prevCases => prevCases.map(c =>
                c.folder_id === folderId ? { ...c, folder_id: null } : c
            ));
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to delete folder';
            setGlobalError(errorMessage);
            console.error('Error deleting folder:', err);
        }
    };

    const handleRenameFolder = async (folderId: string, newName: string) => {
        if (!session?.user.id) {
            setGlobalError('You must be logged in to rename a folder');
            return;
        }

        if (!newName.trim()) {
            setGlobalError('Folder name cannot be empty');
            return;
        }

        try {
            const { error } = await supabase
                .from('folders')
                .update({ name: newName.trim() })
                .eq('id', folderId)
                .eq('user_id', session.user.id);

            if (error) {
                throw error;
            }

            // Update local state
            setFolders(prevFolders => prevFolders.map(f =>
                f.id === folderId ? { ...f, name: newName.trim() } : f
            ).sort((a, b) => a.name.localeCompare(b.name)));
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to rename folder';
            setGlobalError(errorMessage);
            console.error('Error renaming folder:', err);
        }
    };

    useEffect(() => {
        if (session) {
            fetchCases();
            fetchFolders();
        }
    }, [session]);


    const handleLogout = async () => {
        await supabase.auth.signOut();
    };

    const handleNavigateToAuth = (mode: 'signup' | 'login' = 'login') => {
        setAuthMode(mode);
        setShowAuthPage(true);
    };

    const handleNavigate = useCallback((page: string, caseId?: string, templateSlug?: string, replace: boolean = false) => {
        console.log('[handleNavigate] 🧭 Navigating to:', page, 'caseId:', caseId, 'replace:', replace, 'currentPage before:', currentPageRef.current);

        // CRITICAL: Ensure page is valid - fix 'case' to 'Chat'
        const validPage = page === 'case' ? 'Chat' : page;

        const incomingCaseId = caseId || null;
        const currentCaseId = selectedCaseId;
        const currentTemplateSlug = selectedTemplate?.slug || null;

        // Guard: avoid unnecessary re-renders if nothing actually changes
        if (
            currentPageRef.current === validPage &&
            currentCaseId === incomingCaseId &&
            (currentTemplateSlug === (templateSlug || null))
        ) {
            console.log('[handleNavigate] ⚠️ Navigation aborted — already on page:', validPage);
            if (replace) {
                updateURL(validPage, { caseId, templateSlug }, true);
            }
            return;
        }

        setCurrentPage(validPage);
        currentPageRef.current = validPage; // Keep ref in sync
        console.log('[handleNavigate] ✅ currentPage updated to:', validPage, 'currentPageRef.current:', currentPageRef.current);
        setSelectedCaseId(caseId || null);

        // Handle template selection from slug
        if (templateSlug) {
            const templates: Template[] = [
                { id: '1', icon: '📝', title: 'DSGVO Auskunft', description: 'Request personal data information under GDPR.', slug: 'dsgvo-auskunft' },
                { id: '2', icon: '🗑️', title: 'DSGVO Löschung', description: 'Request deletion of personal data under GDPR.', slug: 'dsgvo-loeschung' },
                { id: '3', icon: '🏠', title: 'Mängelanzeige (Mietrecht)', description: 'Notify landlord of apartment defects.', slug: 'miet-maengelanzeige' },
                { id: '4', icon: '📉', title: 'Mietminderung (Mietrecht)', description: 'Demand rent reduction due to defects.', slug: 'miet-minderung' },
                { id: '5', icon: '⚠️', title: 'Abmahnung Widerspruch', description: 'Draft a response to a warning letter.', slug: 'abmahnung-widerspruch' },
                { id: '6', icon: '💳', title: 'SCHUFA Bestreitung', description: 'Dispute incorrect SCHUFA entries.', slug: 'schufa-bestreitung' },
                { id: '7', icon: '✅', title: 'SCHUFA Lösung', description: 'Request resolution for SCHUFA issues.', slug: 'schufa-loesung' },
            ];
            const template = templates.find(t => t.slug === templateSlug);
            setSelectedTemplate(template || null);
        } else if (validPage !== 'TemplateWizard' && validPage !== 'TemplateView') {
            // Reset template when navigating away from template pages
            setSelectedTemplate(null);
        }

        // Update URL with permalink - use replace for login redirects
        // CRITICAL: Always allow SuperAdmin page to update URL correctly
        // For Documents page, pass caseId so it can filter documents by case
        // Use validPage instead of page to ensure correct URL
        updateURL(validPage, { caseId, templateSlug }, replace);
    }, []);

    useEffect(() => {
        if (!pendingNavigation) {
            return;
        }

        const { page, caseId, templateSlug, replace } = pendingNavigation;
        handleNavigate(page, caseId ?? undefined, templateSlug ?? undefined, replace ?? false);
        isStripeRedirectRef.current = false;
        setPendingNavigation(null);
    }, [handleNavigate, pendingNavigation]);

    // Clean up any hash from URL on mount and handle browser back/forward buttons
    useEffect(() => {
        // Remove any hash from URL on initial load
        if (window.location.hash) {
            const cleanPath = window.location.pathname + window.location.search;
            window.history.replaceState({}, '', cleanPath);
        }

        const handlePopState = () => {
            // Remove hash if present
            if (window.location.hash) {
                const cleanPath = window.location.pathname + window.location.search;
                window.history.replaceState({}, '', cleanPath);
            }

            const route = parsePathRoute();
            // Handle 404 routes
            if (!route.isValid && route.page === 'NotFound') {
                setCurrentPage('NotFound');
                currentPageRef.current = 'NotFound';
                setSelectedCaseId(null);
                setSelectedTemplate(null);
                return;
            }

            // CRITICAL FIX: If logged in user navigates back to Landing via browser back button, redirect to Dashboard
            supabase.auth.getSession().then(({ data: { session } }) => {
                // CRITICAL: Always allow SuperAdmin and Admin pages - don't redirect away from them
                if (route.page === 'SuperAdmin') {
                    setCurrentPage('SuperAdmin');
                    currentPageRef.current = 'SuperAdmin';
                    return;
                }

                if (route.page === 'Admin') {
                    setCurrentPage('Admin');
                    currentPageRef.current = 'Admin';
                    return;
                }

                if (session && (route.page === 'Landing' || route.page === null || window.location.pathname === '/')) {
                    if (import.meta.env.DEV) console.log('[PopState] Logged in user navigated to Landing - redirecting to Dashboard');
                    handleNavigate('Dashboard', undefined, undefined, true);
                    return; // Don't update page state - redirect handles it
                }

                // Only update page state if we're not redirecting
                if (route.page && route.page !== 'Landing') {
                    setCurrentPage(route.page);
                    currentPageRef.current = route.page;
                }

                if (route.caseId) {
                    setSelectedCaseId(route.caseId);
                } else {
                    setSelectedCaseId(null);
                }
                if (route.templateSlug) {
                    const templates: Template[] = [
                        { id: '1', icon: '📝', title: 'DSGVO Auskunft', description: 'Request personal data information under GDPR.', slug: 'dsgvo-auskunft' },
                        { id: '2', icon: '🗑️', title: 'DSGVO Löschung', description: 'Request deletion of personal data under GDPR.', slug: 'dsgvo-loeschung' },
                        { id: '3', icon: '🏠', title: 'Mängelanzeige (Mietrecht)', description: 'Notify landlord of apartment defects.', slug: 'miet-maengelanzeige' },
                        { id: '4', icon: '📉', title: 'Mietminderung (Mietrecht)', description: 'Demand rent reduction due to defects.', slug: 'miet-minderung' },
                        { id: '5', icon: '⚠️', title: 'Abmahnung Widerspruch', description: 'Draft a response to a warning letter.', slug: 'abmahnung-widerspruch' },
                        { id: '6', icon: '💳', title: 'SCHUFA Bestreitung', description: 'Dispute incorrect SCHUFA entries.', slug: 'schufa-bestreitung' },
                        { id: '7', icon: '✅', title: 'SCHUFA Lösung', description: 'Request resolution for SCHUFA issues.', slug: 'schufa-loesung' },
                    ];
                    const template = templates.find(t => t.slug === route.templateSlug);
                    setSelectedTemplate(template || null);
                } else {
                    setSelectedTemplate(null);
                }
            });
        };

        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, []);

    // Sync invalid currentPage values with URL - handle state updates properly in useEffect
    useEffect(() => {
        const validPages = ['Dashboard', 'Documents', 'Templates', 'Chat', 'News', 'TemplateWizard', 'TemplateView', 'Settings', 'Profile', 'Features', 'Pricing', 'AboutUs', 'Contact', 'Legal', 'Landing', 'NotFound', 'Benefits', 'Collaboration', 'LegalInsurance', 'PartnershipTier', 'Admin', 'SuperAdmin', 'LegalUpdates'];

        // Check if currentPage is invalid (like 'case')
        if (currentPage === 'case' || !validPages.includes(currentPage)) {
            const route = parsePathRoute();
            const pageToRender = route.page || 'Dashboard';

            // Only update if the resolved page is different from current (prevents loops)
            if (pageToRender !== currentPage) {
                console.log('[useEffect] Syncing invalid currentPage:', currentPage, 'to:', pageToRender);

                // Update state synchronously in useEffect (this is safe)
                setCurrentPage(pageToRender);
                currentPageRef.current = pageToRender;

                if (route.caseId) {
                    setSelectedCaseId(route.caseId);
                } else if (pageToRender !== 'Chat') {
                    setSelectedCaseId(null);
                }
            }
        }
    }, [currentPage]); // Only run when currentPage changes

    const handleNewCase = async () => {
        console.log('[handleNewCase] Button clicked!');
        const { data: { user } } = await supabase.auth.getUser();
        console.log('[handleNewCase] User check:', !!user);
        if (!user) {
            console.error('[handleNewCase] No user found');
            setGlobalError('You must be logged in to create a case');
            toast.error('You must be logged in to create a case');
            return;
        }

        // Check subscription limits before creating case
        // Use profile subscription if available, otherwise fetch it
        let planName = 'free';
        if (profile?.subscription?.plan_name) {
            planName = profile.subscription.plan_name;
        } else {
            // Fallback: fetch subscription if profile doesn't have it
            const { data: profileData } = await supabase
                .from('profiles')
                .select('subscription_id')
                .eq('id', user.id)
                .single();

            if (profileData?.subscription_id) {
                const { data: subData } = await supabase
                    .from('subscriptions')
                    .select('plan_name, stripe_price_id')
                    .eq('id', profileData.subscription_id)
                    .single();
                planName = normalizePlanName(subData?.plan_name, subData?.stripe_price_id);
            }
        }
        const limitCheck = await canCreateCase(user.id, planName);
        console.log('[handleNewCase] Limit check result:', limitCheck);

        if (!limitCheck.allowed) {
            console.error('[handleNewCase] Case limit reached:', limitCheck.reason);
            setGlobalError(limitCheck.reason || 'You have reached your case limit. Please upgrade your plan.');
            toast.error(limitCheck.reason || 'You have reached your case limit. Please upgrade your plan.');
            // Show upgrade prompt
            setTimeout(() => {
                showUpgradeToast(
                    'Upgrade to create unlimited cases',
                    () => { handleNavigate('Pricing'); },
                    'Unlimited Cases'
                );
            }, 1000);
            return;
        }

        setIsLoading(true);
        setGlobalError(null);

        try {
            console.log('[handleNewCase] Creating new case...');
            const newCase: Omit<Case, 'id' | 'created_at'> = {
                user_id: user.id,
                title: `New Case ${cases.length + 1}`,
                type: 'General',
                status: 'Active',
                deadline: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
            };

            const { data, error } = await supabase.from('cases').insert(newCase).select();

            if (error) {
                console.error('[handleNewCase] Database error:', error);
                throw error;
            }

            if (data && data[0]) {
                console.log('[handleNewCase] Case created successfully:', data[0].id);
                setCases(prevCases => [data[0] as Case, ...prevCases]);
                handleNavigate('Chat', data[0].id);
            }
        } catch (err) {
            console.error('[handleNewCase] Error:', err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to create case';
            setGlobalError(errorMessage);
            toast.error(errorMessage);
            logger.error('Error creating new case:', err);
        } finally {
            setIsLoading(false);
        }
    };

    // Keyboard shortcuts with app context - must be after handleNewCase and handleNavigate are defined
    useKeyboardShortcuts({
        shortcuts: createAppShortcuts(handleNewCase),
        enabled: true,
    });

    const toggleSidebar = () => {
        setIsSidebarCollapsed(!isSidebarCollapsed);
    };

    const handleGenerateTemplate = (template: Template) => {
        setSelectedTemplate(template);
        setCurrentPage('TemplateWizard');
        currentPageRef.current = 'TemplateWizard';
        updateURL('TemplateWizard', { templateSlug: template.slug });
    };

    const handleBackToTemplates = () => {
        setSelectedTemplate(null);
        setCurrentPage('Templates');
        currentPageRef.current = 'Templates';
        updateURL('Templates');
    };

    const handleTemplateGenerate = async (template: Template, formData: TemplateFormData): Promise<string> => {
        if (!session?.user.id) throw new Error('User not authenticated');

        // Create a temporary case for template generation (or use a default case)
        let caseId = selectedCaseId;
        if (!caseId && cases.length > 0) {
            caseId = cases[0].id;
        } else if (!caseId) {
            // Create a temporary case for templates
            const { data: newCase, error } = await supabase
                .from('cases')
                .insert({
                    user_id: session.user.id,
                    title: `Template: ${template.title}`,
                    type: 'Template',
                    status: 'Active',
                })
                .select()
                .single();

            if (error || !newCase) {
                throw new Error('Failed to create template case');
            }
            caseId = newCase.id;
            setCases(prev => [newCase as Case, ...prev]);
        }

        // Build the prompt with template data
        const formDataString = Object.entries(formData)
            .map(([key, value]) => `${key}: ${value}`)
            .join('\n');

        const templatePrompt = `Generate a complete, professional German legal document for: ${template.title}

Template Type: ${template.slug}

User Information:
${formDataString}

Requirements:
1. Replace all placeholders like [Ihr Name], [Ihre Adresse], etc. with the provided user information
2. Use formal German legal language appropriate for this document type
3. Include proper legal citations (e.g., "gemäß Artikel 15 DSGVO" for DSGVO templates)
4. Format as a professional letter with proper German date format
5. Ensure all sections are complete and legally accurate
6. Use proper formal German greetings and closings

Generate the complete document text only, without explanations or markdown formatting.`;

        try {
            // Call chat-ai Edge Function with the template generation prompt
            const { data, error } = await supabase.functions.invoke('chat-ai', {
                body: {
                    message: templatePrompt,
                    caseId: caseId,
                    userId: session.user.id,
                    conversationHistory: [],
                },
            });

            if (error) {
                console.error('Edge Function error:', error);
                throw new Error(error.message || 'Failed to generate template');
            }

            // Check for error in response data
            if (data && data.error) {
                console.error('AI service error:', data.error);
                throw new Error(data.error.details || data.error || 'AI service returned an error');
            }

            // The chat-ai function returns { success: true, message: string, messageId: string }
            if (!data || !data.message) {
                console.error('Invalid response structure:', data);
                throw new Error('Invalid response from AI: missing message field');
            }

            return data.message;
        } catch (err) {
            console.error('Template generation error:', err);
            console.error('Error details:', err);

            // Provide more helpful error messages
            if (err instanceof Error) {
                if (err.message.includes('Failed to send a request')) {
                    throw new Error('Cannot reach AI service. Please check that the chat-ai Edge Function is deployed.');
                }
                if (err.message.includes('Case not found')) {
                    throw new Error('Case not found. Please create a case first.');
                }
                throw err;
            }

            throw err instanceof Error ? err : new Error('Failed to generate template');
        }
    };

    const currentCase = selectedCaseId ? cases.find(c => c.id === selectedCaseId) : null;

    // CRITICAL: Show AuthPage immediately if showAuthPage is true - don't check URL or anything else
    if (showAuthPage) {
        console.log('[App] 🔐 Showing AuthPage - showAuthPage is true, authMode:', authMode);
        return <AuthPage key={authMode} initialMode={authMode} onNavigate={async (page) => {
            console.log('[App] 🔄 AuthPage onNavigate called with page:', page);
            console.log('[App] Current session before update:', !!session);
            console.log('[App] Current currentPage:', currentPage);
            console.log('[App] Current pathname:', window.location.pathname);

            // CRITICAL: Check session immediately before navigating
            const { data: { session: currentSession } } = await supabase.auth.getSession();
            console.log('[App] Retrieved session from getSession():', !!currentSession);

            if (currentSession) {
                setSession(currentSession);
                console.log('[App] ✅ Session state updated via setSession()');

                // CRITICAL: If page is SuperAdmin, always navigate to SuperAdmin
                if (page === 'SuperAdmin') {
                    console.log('[App] 🔐✅ Page is SuperAdmin, navigating to SuperAdmin');
                    sessionStorage.removeItem('returnToSuperAdmin');
                    setShowAuthPage(false);
                    // Fetch profile to check role
                    if (currentSession.user) {
                        console.log('[App] Fetching profile for user:', currentSession.user.id);
                        fetchProfile(currentSession.user.id);
                    }
                    // Navigate to SuperAdmin
                    handleNavigate('SuperAdmin', undefined, undefined, true);
                    return;
                }

                // CRITICAL: Check if user was trying to access Admin page
                if (page === 'Admin' || sessionStorage.getItem('returnToAdmin') === 'true') {
                    console.log('[App] 🔐✅ Page is Admin, navigating to Admin');
                    sessionStorage.removeItem('returnToAdmin');
                    setShowAuthPage(false);
                    // Fetch profile to check role
                    if (currentSession.user) {
                        console.log('[App] Fetching profile for user:', currentSession.user.id);
                        fetchProfile(currentSession.user.id);
                    }
                    // Navigate to Admin
                    handleNavigate('Admin', undefined, undefined, true);
                    return;
                }

                // CRITICAL: Check if user was trying to access SuperAdmin (even if page is Dashboard)
                // Admin/SuperAdmin redirect logic removed
            } else {
                console.warn('[App] ⚠️ No session found in getSession()');
            }

            setShowAuthPage(false);
            console.log('[App] showAuthPage set to false');

            // Navigate immediately - session is now set
            console.log('[App] Calling handleNavigate with page:', page);
            handleNavigate(page, undefined, undefined, true);
            console.log('[App] handleNavigate completed. currentPage should now be:', page);
        }} />;
    }

    const renderPage = () => {
        // CRITICAL: Always check URL FIRST - highest priority
        const currentPath = window.location.pathname;
        console.log('[renderPage] 🎯 START Render - Pathname:', currentPath, 'currentPage:', currentPage);

        // Fix: If currentPage is 'case' (invalid), use URL as source of truth for rendering
        // Note: State updates are handled in useEffect, not here during render
        const validPages = ['Dashboard', 'Documents', 'Templates', 'Chat', 'News', 'TemplateWizard', 'TemplateView', 'Settings', 'Profile', 'Features', 'Pricing', 'AboutUs', 'Contact', 'Legal', 'Landing', 'NotFound', 'Benefits', 'Collaboration', 'LegalInsurance', 'PartnershipTier', 'Admin', 'SuperAdmin'];

        // For rendering purposes, use URL if currentPage is invalid
        // The useEffect hook will sync the state, but we need correct rendering now
        let effectivePage = currentPage;
        if (currentPage === 'case' || !validPages.includes(currentPage)) {
            const route = parsePathRoute();
            effectivePage = route.page || 'Dashboard';
            console.log('[renderPage] ⚠️ currentPage is invalid or "case", using URL for rendering. Route:', route, 'effectivePage:', effectivePage);
        }

        // Handle 404 page for both logged in and logged out users
        if (effectivePage === 'NotFound') {
            return <NotFoundPage onNavigate={handleNavigate} />;
        }

        // CRITICAL FIX: If user is on Dashboard, ALWAYS show Dashboard (even if session check is pending)
        // This prevents redirecting to Landing during login transitions
        if (effectivePage === 'Dashboard') {
            console.log('[renderPage] 🎯 Rendering Dashboard. Session:', !!session, 'currentPage:', currentPage);
            // Double-check session in background
            if (!session) {
                console.log('[renderPage] ⏳ No session yet, checking in background...');
                supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
                    if (currentSession) {
                        console.log('[renderPage] ✅ Found session in background check, updating state');
                        setSession(currentSession);
                    } else {
                        console.warn('[renderPage] ⚠️ Background session check returned no session');
                    }
                });
            }
            // ALWAYS render Dashboard if currentPage is Dashboard
            console.log('[renderPage] 📊 Returning Dashboard component');
            return <DashboardPage
                onNavigate={handleNavigate}
                cases={cases}
                onNewCase={handleNewCase}
                isLoading={!session}
                error={null}
                onRetry={() => fetchCases()}
                onDeleteCase={handleDeleteCase}
                onRenameCase={handleRenameCase}
                onMoveToFolder={handleMoveToFolder}
                folders={folders}
                session={session}
            />;
        }

        if (!session) {
            switch (effectivePage) {
                case 'Admin':
                    // Show AdminLayout even when not logged in - it will show login prompt
                    return <AdminLayout session={null} profile={null} onNavigate={handleNavigate} onShowAuth={() => {
                        setAuthMode('login');
                        setShowAuthPage(true);
                    }} />;
                case 'Features':
                    return <FeaturesPage />;
                case 'Pricing':
                    return <PricingPageComponent
                        session={null}
                        profile={null}
                    />;
                case 'AboutUs':
                    return <AboutUsPage />;
                case 'Collaboration':
                    return <CollaborationPage onNavigateToAuth={handleNavigateToAuth} onNavigate={handleNavigate} />;
                case 'LegalInsurance':
                    return <LegalInsurancePage onNavigate={handleNavigate} />;
                case 'PartnershipTier': {
                    const routeParams = parsePathRoute();
                    return <PartnershipTierPage tierSlug={routeParams.tierSlug} onNavigateToAuth={handleNavigateToAuth} onNavigate={handleNavigate} />;
                }
                case 'Contact':
                    return <ContactPage />;
                case 'Legal':
                    return <LegalPage />;
                case 'LegalUpdates':
                    return <LegalUpdatesPage onNavigate={handleNavigate} />;
                case 'Landing':
                default:
                    return <LandingPage onNavigateToAuth={handleNavigateToAuth} onNavigate={handleNavigate} currentPage={currentPage} />;
            }
        }

        // Logged in pages
        switch (effectivePage) {
            case 'Landing':
                // Allow logged-in users to view Landing page, but hide the header since Navbar is shown
                return <LandingPage onNavigateToAuth={handleNavigateToAuth} onNavigate={handleNavigate} currentPage={currentPage} hideHeader={!!session} />;
            case 'Features':
                return <FeaturesPage />;
            case 'Pricing':
                return <PricingPageComponent
                    session={session}
                    profile={profile}
                />;
            case 'AboutUs':
                return <AboutUsPage />;
            case 'LegalInsurance':
                return <LegalInsurancePage onNavigate={handleNavigate} />;
            case 'PartnershipTier': {
                const routeParamsLoggedIn = parsePathRoute();
                return <PartnershipTierPage tierSlug={routeParamsLoggedIn.tierSlug} onNavigateToAuth={handleNavigateToAuth} onNavigate={handleNavigate} />;
            }
            case 'Contact':
                return <ContactPage />;
            case 'Legal':
                return <LegalPage />;
            case 'LegalUpdates':
                return <LegalUpdatesPage onNavigate={handleNavigate} />;
            case 'Dashboard':
                console.log('[renderPage] 🎯 Rendering Dashboard. Session:', !!session, 'currentPage:', currentPage);
                // Double-check session in background
                if (!session) {
                    console.log('[renderPage] ⏳ No session yet, checking in background...');
                    supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
                        if (currentSession) {
                            console.log('[renderPage] ✅ Found session in background check, updating state');
                            setSession(currentSession);
                        } else {
                            console.warn('[renderPage] ⚠️ Background session check returned no session');
                        }
                    });
                }
                // ALWAYS render Dashboard if currentPage is Dashboard
                console.log('[renderPage] 📊 Returning Dashboard component');
                return <DashboardPage
                    onNavigate={handleNavigate}
                    cases={cases}
                    onNewCase={handleNewCase}
                    isLoading={isLoading}
                    error={globalError}
                    onRetry={() => fetchCases()}
                    onDeleteCase={handleDeleteCase}
                    onRenameCase={handleRenameCase}
                    onMoveToFolder={handleMoveToFolder}
                    folders={folders}
                    session={session}
                />;
            case 'NotFound':
                return <NotFoundPage onNavigate={handleNavigate} />;
            case 'Chat':
                return currentCase ? <CaseChatView selectedCase={currentCase} session={session} profile={profile} /> : <DashboardPage onNavigate={handleNavigate} cases={cases} onNewCase={handleNewCase} onDeleteCase={handleDeleteCase} onRenameCase={handleRenameCase} onMoveToFolder={handleMoveToFolder} folders={folders} session={session} />;
            case 'Documents':
                return <DocumentsPageView profile={profile} session={session} />;
            case 'Templates':
                return <TemplatesPage onGenerate={handleGenerateTemplate} />;
            case 'BatchProcessing':
                return session ? (
                    <BatchProcessingPage session={session} onNavigate={handleNavigate} />
                ) : (
                    <DashboardPage onNavigate={handleNavigate} cases={cases} onNewCase={handleNewCase} onDeleteCase={handleDeleteCase} onRenameCase={handleRenameCase} onMoveToFolder={handleMoveToFolder} folders={folders} session={session} />
                );
            case 'Settings':
                return <SettingsPage session={session} profile={profile} onProfileUpdate={handleProfileUpdate} onNavigate={handleNavigate} />;
            case 'News':
                return <NewsUpdatesPage onNavigate={handleNavigate} />;
            case 'Benefits':
                return <BenefitsPage onNavigate={handleNavigate} onNavigateToAuth={handleNavigateToAuth} />;
            case 'Profile':
                return <ProfilePage profile={profile} onProfileUpdate={handleProfileUpdate} userEmail={session?.user.email} />;
            case 'TemplateWizard':
                return selectedTemplate ? (
                    <TemplateWizard
                        template={selectedTemplate}
                        profile={profile}
                        session={session}
                        onGenerate={(formData) => handleTemplateGenerate(selectedTemplate, formData)}
                        onBack={handleBackToTemplates}
                        onSaveTemplate={async (content, template) => {
                            if (!session?.user.id) throw new Error('User not authenticated');

                            // Get or create a case for templates
                            let caseId = selectedCaseId;
                            if (!caseId && cases.length > 0) {
                                caseId = cases[0].id;
                            } else if (!caseId) {
                                const { data: newCase, error } = await supabase
                                    .from('cases')
                                    .insert({
                                        user_id: session.user.id,
                                        title: `Template: ${template.title}`,
                                        type: 'Template',
                                        status: 'Active',
                                    })
                                    .select()
                                    .single();

                                if (error || !newCase) {
                                    throw new Error('Failed to create template case');
                                }
                                caseId = newCase.id;
                                setCases(prev => [newCase as Case, ...prev]);
                            }

                            // Upload to storage
                            const fileName = `${template.slug}-${Date.now()}.txt`;
                            const filePath = `${session.user.id}/${fileName}`;
                            const fileBlob = new Blob([content], { type: 'text/plain' });

                            const { error: uploadError } = await supabase.storage
                                .from('documents')
                                .upload(filePath, fileBlob, {
                                    contentType: 'text/plain',
                                    upsert: false,
                                });

                            if (uploadError) {
                                throw new Error(`Upload failed: ${uploadError.message}`);
                            }

                            // Get public URL
                            const { data: urlData } = supabase.storage
                                .from('documents')
                                .getPublicUrl(filePath);

                            // Save document record
                            const { error: docError } = await supabase
                                .from('documents')
                                .insert({
                                    case_id: caseId,
                                    user_id: session.user.id,
                                    file_name: fileName,
                                    file_type: 'text/plain',
                                    file_size: fileBlob.size,
                                    file_url: urlData.publicUrl,
                                    storage_path: filePath,
                                });

                            if (docError) {
                                console.error('Error saving document:', docError);
                                throw new Error('Failed to save template as document');
                            }

                            toast.success('Template saved successfully! You can find it in the Documents section.');
                            handleNavigate('Documents');
                        }}
                    />
                ) : <TemplatesPage onGenerate={handleGenerateTemplate} />;
            case 'TemplateView':
                return selectedTemplate ? (
                    <TemplateView
                        template={selectedTemplate}
                        content={DUMMY_TEMPLATE_CONTENT[selectedTemplate.slug] || 'Template content not available'}
                        onBack={handleBackToTemplates}
                        profile={profile}
                        session={session}
                        caseId={selectedCaseId}
                    />
                ) : <TemplatesPage onGenerate={handleGenerateTemplate} />;
            case 'Admin':
                // Always render AdminLayout - it will handle access control and show appropriate messages
                return <AdminLayout session={session} profile={profile} onNavigate={handleNavigate} onShowAuth={() => {
                    setAuthMode('login');
                    setShowAuthPage(true);
                }} />;
            case 'SuperAdmin':
                return <SuperAdminPage session={session!} profile={profile} onNavigate={handleNavigate} onShowAuth={() => setShowAuthPage(true)} />;
            case 'Teams':
                return session ? <TeamManagement session={session} /> : <DashboardPage onNavigate={handleNavigate} cases={cases} onNewCase={handleNewCase} onDeleteCase={handleDeleteCase} onRenameCase={handleRenameCase} onMoveToFolder={handleMoveToFolder} folders={folders} session={session} />;
            default:
                return <DashboardPage onNavigate={handleNavigate} cases={cases} onNewCase={handleNewCase} onDeleteCase={handleDeleteCase} onRenameCase={handleRenameCase} onMoveToFolder={handleMoveToFolder} folders={folders} session={session} />;
        }
    };

    // Determine if we should show navigation elements
    // Define pages explicitly to avoid ambiguity
    const isLandingPage = currentPage === 'Landing';

    // Pages that should show the sidebar (core dashboard functionality)
    const sidebarPages = ['Dashboard', 'Documents', 'Templates', 'Chat'];
    // Use effectivePage to handle invalid currentPage values (like 'case')
    const effectivePageForSidebar = (currentPage === 'case' || !sidebarPages.includes(currentPage))
        ? (parsePathRoute().page || 'Dashboard')
        : currentPage;
    const shouldShowSidebar = sidebarPages.includes(effectivePageForSidebar) && !!session;

    // Navbar: always show when logged in (regardless of page)
    // When not logged in: show on all pages except Landing (Landing has its own header)
    const showNavbar = !!session || !isLandingPage;

    // Sidebar: only show on specific dashboard pages when logged in
    // This prevents sidebar from showing on Settings, Profile, or public pages
    const showSidebar = shouldShowSidebar;

    // Footer: only show on Landing page when not logged in
    const showFooter = !session && isLandingPage;

    // SEO Configuration per route
    const getSEOConfig = () => {
        const baseUrl = window.location.origin;
        const currentUrl = `${baseUrl}${window.location.pathname}`;

        const defaultStructuredData = {
            "@context": "https://schema.org",
            "@type": "LegalService",
            "name": "LRDEnE",
            "description": "AI-powered legal assistant for German and EU law",
            "url": baseUrl,
            "areaServed": ["DE", "EU"],
            "serviceType": ["Legal Document Analysis", "Legal Templates", "Legal Consultation"],
        };

        switch (currentPage) {
            case 'Landing':
                return {
                    title: 'LRDEnE - AI-Powered Legal Assistant for Germany & EU',
                    description: 'Transform your legal workflow with LRDEnE. AI-powered document analysis, template generation, and legal advice for German and EU law. Start free today.',
                    keywords: 'legal AI, German law, EU law, legal document analysis, GDPR, legal templates, legal assistant',
                    url: `${baseUrl}/`,
                    structuredData: defaultStructuredData,
                };
            case 'Features':
                return {
                    title: 'Features - LRDEnE AI Legal Assistant',
                    description: 'Discover LRDEnE features: AI document analysis, legal template generation, intelligent case management, and expert legal advice powered by advanced AI.',
                    keywords: 'legal AI features, document analysis, legal templates, case management, legal software features',
                    url: `${baseUrl}/features`,
                    structuredData: defaultStructuredData,
                };
            case 'Pricing':
                return {
                    title: 'Pricing Plans - LRDEnE Legal AI Platform',
                    description: 'Choose the right LRDEnE plan for your needs. Free tier available, with flexible pricing for individuals and professionals. Start free, upgrade anytime.',
                    keywords: 'legal AI pricing, legal software cost, LRDEnE pricing, legal assistant plans',
                    url: `${baseUrl}/pricing`,
                    structuredData: defaultStructuredData,
                };
            case 'Collaboration':
                return {
                    title: 'Collaboration Platform - LRDEnE',
                    description: 'Connect lawyers, legal insurances, and people. Streamline legal workflows and make legal assistance more accessible.',
                    keywords: 'legal collaboration, lawyer network, legal insurance, legal platform, lawyer collaboration',
                    url: `${baseUrl}/collaboration`,
                    structuredData: defaultStructuredData,
                };
            case 'AboutUs':
                return {
                    title: 'About Us - LRDEnE',
                    description: 'Learn about LRDEnE - revolutionizing legal workflows with AI-powered solutions for German and EU law compliance.',
                    keywords: 'about LRDEnE, legal tech company, legal AI company',
                    url: `${baseUrl}/about`,
                    structuredData: defaultStructuredData,
                };
            case 'Contact':
                return {
                    title: 'Contact Us - LRDEnE',
                    description: 'Get in touch with LRDEnE. We\'re here to help with your legal technology needs.',
                    keywords: 'contact LRDEnE, legal tech support, LRDEnE support',
                    url: `${baseUrl}/contact`,
                    structuredData: defaultStructuredData,
                };
            case 'Legal':
                return {
                    title: 'Legal Information - Terms, Privacy & Imprint - LRDEnE',
                    description: 'LRDEnE legal information including terms of service, privacy policy, and imprint. Transparency and compliance are at our core.',
                    keywords: 'LRDEnE legal, terms of service, privacy policy, imprint, legal information',
                    url: `${baseUrl}/legal`,
                    structuredData: defaultStructuredData,
                };
            case 'Dashboard':
                return {
                    title: 'Dashboard - LRDEnE',
                    description: 'Your LRDEnE dashboard - manage cases, documents, and legal workflows in one place.',
                    url: `${baseUrl}/dashboard`,
                    noindex: true, // Private pages shouldn't be indexed
                };
            case 'Templates':
                return {
                    title: 'Legal Templates - LRDEnE',
                    description: 'Access LRDEnE legal document templates for GDPR requests, rental disputes, and more.',
                    url: `${baseUrl}/templates`,
                    noindex: true,
                };
            case 'Documents':
                return {
                    title: 'Documents - LRDEnE',
                    description: 'Manage your legal documents with LRDEnE.',
                    url: `${baseUrl}/documents`,
                    noindex: true,
                };
            case 'Chat':
                return {
                    title: 'Case Chat - LRDEnE',
                    description: 'AI-powered legal consultation for your case.',
                    url: selectedCaseId ? `${baseUrl}/case/${selectedCaseId}` : `${baseUrl}/dashboard`,
                    noindex: true,
                };
            case 'Benefits':
                return {
                    title: 'Benefits of LRDEnE Account - AI Legal Assistant',
                    description: 'Discover the benefits of your LRDEnE account: AI document analysis, legal templates, case management, EU law updates, and more.',
                    keywords: 'LRDEnE benefits, legal AI features, document analysis benefits, legal software advantages',
                    url: `${baseUrl}/benefits`,
                };
            case 'NotFound':
                return {
                    title: '404 - Page Not Found - LRDEnE',
                    description: 'The page you\'re looking for doesn\'t exist. Return to LRDEnE home or dashboard.',
                    url: currentUrl,
                    noindex: true,
                };
            default:
                return {
                    title: 'LRDEnE - AI-Powered Legal Assistant',
                    description: 'LRDEnE provides AI-powered legal document analysis, template generation, and legal advice for German and EU law.',
                    url: currentUrl,
                    structuredData: defaultStructuredData,
                };
        }
    };

    const seoConfig = getSEOConfig();

    return (
        <>
            <SEOHead
                title={seoConfig.title}
                description={seoConfig.description}
                keywords={seoConfig.keywords}
                url={seoConfig.url}
                structuredData={seoConfig.structuredData}
                noindex={seoConfig.noindex}
            />
            <OfflineIndicator
                onOnline={() => {
                    // Auto-retry failed requests when back online
                    if (globalError && isRetryableError({ message: globalError })) {
                        setGlobalError(null);
                        if (session) {
                            fetchCases(false); // Retry without showing loading
                        }
                    }
                }}
            />
            <div className="relative flex h-screen min-h-screen w-full flex-col overflow-hidden">
                <SkipLink />
                {showNavbar && (
                    <Navbar
                        currentPage={currentPage}
                        onNavigate={handleNavigate}
                        isLoggedIn={!!session}
                        onLogout={handleLogout}
                        onNavigateToAuth={() => setShowAuthPage(true)}
                        profile={profile}
                        session={session}
                    />
                )}
                <div className={`flex flex-1 overflow-hidden ${!session ? 'flex-col' : ''}`}>
                    {showSidebar && <Sidebar
                        currentPage={currentPage}
                        onNavigate={handleNavigate}
                        selectedCaseId={selectedCaseId}
                        isCollapsed={isSidebarCollapsed}
                        toggleSidebar={toggleSidebar}
                        cases={cases}
                        onNewCase={handleNewCase}
                        profile={profile}
                        folders={folders}
                        onCreateFolder={handleCreateFolder}
                        onDeleteFolder={handleDeleteFolder}
                        onRenameFolder={handleRenameFolder}
                    />}
                    <main id="main-content" className="flex-1 flex flex-col overflow-y-auto" tabIndex={-1}>
                        <Suspense fallback={<LoadingSpinner />}>
                            {renderPage()}
                        </Suspense>
                    </main>
                </div>
                {showFooter && <Footer onNavigate={handleNavigate} />}

                {/* Toast Notifications */}
                <Toaster />

                {/* GDPR Cookie Consent Banner */}
                {showCookieBanner && (
                    <div className="fixed bottom-0 left-0 right-0 z-50 bg-luna-primary border-t border-luna-secondary/20 shadow-2xl p-4 md:p-6">
                        <div className="container mx-auto max-w-6xl">
                            {!showCookieSettings ? (
                                <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
                                    <div className="flex-1">
                                        <h3 className="text-lg font-bold text-white mb-2">🍪 Cookie-Einstellungen</h3>
                                        <p className="text-sm text-white/90 leading-relaxed">
                                            Wir verwenden Cookies, um Ihnen die bestmögliche Erfahrung zu bieten. Notwendige Cookies sind für die Funktionalität erforderlich.
                                            Sie können Ihre Einstellungen jederzeit anpassen.{' '}
                                            <a
                                                href="/cookies.html"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="underline hover:text-luna-secondary ml-1"
                                                onClick={(e) => {
                                                    // Allow the link to work normally - open in new tab
                                                    e.stopPropagation();
                                                    // Force navigation to the actual file
                                                    window.open('/cookies.html', '_blank');
                                                }}
                                            >
                                                Mehr erfahren
                                            </a>
                                        </p>
                                    </div>
                                    <div className="flex flex-col sm:flex-row gap-3 shrink-0">
                                        <button
                                            onClick={handleCookieReject}
                                            className="px-4 py-2 text-sm font-medium text-white border border-white/30 rounded-lg hover:bg-white/10 transition-colors"
                                        >
                                            Ablehnen
                                        </button>
                                        <button
                                            onClick={() => setShowCookieSettings(true)}
                                            className="px-4 py-2 text-sm font-medium text-white border border-white/30 rounded-lg hover:bg-white/10 transition-colors"
                                        >
                                            Anpassen
                                        </button>
                                        <button
                                            onClick={handleCookieAccept}
                                            className="px-4 py-2 text-sm font-medium bg-luna-secondary text-luna-primary rounded-lg hover:opacity-90 transition-opacity"
                                        >
                                            Alle akzeptieren
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <h3 className="text-lg font-bold text-white">Cookie-Einstellungen anpassen</h3>
                                        <button
                                            onClick={() => setShowCookieSettings(false)}
                                            className="text-white/80 hover:text-white transition-colors"
                                            aria-label="Einstellungen schließen"
                                            type="button"
                                        >
                                            <span className="material-symbols-outlined" aria-hidden="true">close</span>
                                        </button>
                                    </div>

                                    <div className="space-y-4 bg-white/10 rounded-lg p-4">
                                        {/* Essential Cookies - Always enabled */}
                                        <div className="flex items-start justify-between gap-4">
                                            <div className="flex-1">
                                                <h4 className="text-white font-semibold mb-1">Notwendige Cookies</h4>
                                                <p className="text-sm text-white/80">
                                                    Diese Cookies sind für das Funktionieren der Website unerlässlich und können nicht deaktiviert werden.
                                                </p>
                                            </div>
                                            <div className="flex items-center">
                                                <input
                                                    type="checkbox"
                                                    checked={true}
                                                    disabled={true}
                                                    className="w-5 h-5 rounded border-white/30 bg-white/20 text-luna-secondary focus:ring-luna-secondary"
                                                    aria-label="Notwendige Cookies (immer aktiviert)"
                                                />
                                            </div>
                                        </div>

                                        {/* Functional Cookies */}
                                        <div className="flex items-start justify-between gap-4 border-t border-white/20 pt-4">
                                            <div className="flex-1">
                                                <h4 className="text-white font-semibold mb-1">Funktionale Cookies</h4>
                                                <p className="text-sm text-white/80">
                                                    Diese Cookies ermöglichen erweiterte Funktionalität und Personalisierung (z.B. Theme-Präferenzen).
                                                </p>
                                            </div>
                                            <div className="flex items-center">
                                                <label className="relative inline-flex items-center cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={cookiePreferences.functional}
                                                        onChange={(e) => setCookiePreferences({ ...cookiePreferences, functional: e.target.checked })}
                                                        className="sr-only peer"
                                                    />
                                                    <div className="w-11 h-6 bg-white/20 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-luna-secondary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-luna-secondary"></div>
                                                </label>
                                            </div>
                                        </div>

                                        {/* Analytics Cookies */}
                                        <div className="flex items-start justify-between gap-4 border-t border-white/20 pt-4">
                                            <div className="flex-1">
                                                <h4 className="text-white font-semibold mb-1">Analyse-Cookies</h4>
                                                <p className="text-sm text-white/80">
                                                    Diese Cookies helfen uns zu verstehen, wie Besucher mit unserer Website interagieren, um unsere Services zu verbessern.
                                                </p>
                                            </div>
                                            <div className="flex items-center">
                                                <label className="relative inline-flex items-center cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={cookiePreferences.analytics}
                                                        onChange={(e) => setCookiePreferences({ ...cookiePreferences, analytics: e.target.checked })}
                                                        className="sr-only peer"
                                                    />
                                                    <div className="w-11 h-6 bg-white/20 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-luna-secondary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-luna-secondary"></div>
                                                </label>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex flex-col sm:flex-row gap-3 justify-end">
                                        <a
                                            href="/cookies.html"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="px-4 py-2 text-sm font-medium text-white/80 hover:text-white underline text-center"
                                        >
                                            Cookie-Richtlinie lesen
                                        </a>
                                        <button
                                            onClick={handleCookieSave}
                                            className="px-6 py-2 text-sm font-medium bg-luna-secondary text-luna-primary rounded-lg hover:opacity-90 transition-opacity"
                                        >
                                            Einstellungen speichern
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

ReactDOM.createRoot(document.getElementById('root')!).render(
    <ErrorBoundary>
        <App />
    </ErrorBoundary>
);
